import React, { useState, useEffect, useRef, useMemo, useCallback, Suspense, lazy } from 'react';
import { Save, Upload, Download, Plus, Settings, Coins, Package, Image, Trash2, DollarSign, ArrowRight, X, ChevronRight, ChevronDown, Eye, Edit2, User, Users, Zap, Book, Sparkles, Shield, Info, Dices, Award, Archive, LifeBuoy, HelpCircle } from 'lucide-react';
import GridComponent from './components/GridComponent';
// [REMOVED - MAR 2026]: GridComponentSimple merged into GridComponent with enableDragDrop flag
import EditItemDialog from './components/EditItemDialog';
import EditShopDialog from './components/EditShopDialog';
import ContainerModal from './components/ContainerModal';
import VaultModal from './components/VaultModal';
import VaultEditDialog from './components/VaultEditDialog';
import ItemDialog from './components/ItemDialog';
// [UI-MOBILEMODAL-ADOPTION MOBILEMODAL-APPJSX-INLINE — 2026-07-09]: shared modal shell
// for the three inline App.jsx dialogs (Quickcast, Custom Shop Type, Add Multiclass).
import MobileModal from './components/MobileModal';
import CombatStatusStrip from './components/CombatStatusStrip'; // [UI-COMBAT-STATUS-STRIP — 2026-07-11]: header vitals strip (direct import — always-visible chrome, lazy would flash)
import VaultDialog from './components/VaultDialog';
import SharedVaultModal from './components/SharedVaultModal';
import DMToolsPanel from './components/DMToolsPanel';
import ContextMenuComponent from './components/ContextMenuComponent';
import MobileNavigation from './components/MobileNavigation';
import CurrencyPanel from './components/CurrencyPanel';
import StatsPanel from './components/StatsPanel';
import ClassResourcesPanel from './components/ClassResourcesPanel';
// [FIX APR 2026 Task 44]: Warlock pact/invocations picker (self-gates on Warlock level)
import WarlockPactPanel from './components/WarlockPactPanel';
import CollapsibleSection from './components/CollapsibleSection';
// [PHASE 4.3]: Error handling components for graceful degradation
import ErrorBoundary from './components/ErrorBoundary';
import ComponentErrorFallback from './components/ComponentErrorFallback';
// [PERFORMANCE]: Heavy data imports (~196 KB) removed - now loaded asynchronously below
import { formatCurrency, autoPurchase } from './utils/currencyUtils';
import { generateHTMLExport as generateHTMLExportFromUtil } from './utils/characterExport';
import CustomRollTableDialog from './components/CustomRollTableDialog';
import spellCastingService from './services/spellCastingService';
import rollLoggingService from './services/rollLoggingService';
import { effectCalculator } from './services/effectCalculator';
import { hasAnySavedCharacters, selectAutoLoadCharacter, shouldForceCreationWizard } from './utils/characterAutoLoad';
// [Sub-plan 1U-impl-A 2026-05-27]: Direct register after drinkPotion setActiveQuickBonuses
//   append — mirrors QuickBonusesPanel.js:536 pattern. syncAllToRegistry is never called
//   from production paths; QB-state changes alone do not propagate to the registry.
import effectRegistry from './services/effectRegistry';
import { systemBus, EVENTS } from './services/systemBus';
import { reportError } from './services/errorReporter';
// [B16]: Schema migration — runs on every character load + import to step pre-v1 saves forward
import { migrate } from './services/characterMigrations';
// [D3 / #103]: Runtime gate at the save boundary — refuses to serialize
//   character data that contains writes to v1.x reserved paths. Mirrors
//   the setPlayerStats wrapper in CharacterContext for the save side.
import { assertNoReservedWrites } from './services/reservedFields';
import { initSpellEffectExpiry } from './services/spellEffectExpiry';
import { useEnhancedPartySync } from './hooks/useEnhancedPartySync';
import useKeyboardShortcuts from './hooks/useKeyboardShortcuts';
// [PHASE 7 INTEGRATION 2025-10-10]: Auto-update hooks for race/class/item changes
import useRaceEffects from './hooks/useRaceEffects';
import useClassEffects from './hooks/useClassEffects';
import useItemEffects from './hooks/useItemEffects';
// [FIX JAN 13, 2026]: Re-enabled - TDZ error was caused by something else
import useFeatEffects from './hooks/useFeatEffects'; // [UX-20 - NOV 16, 2025]: Mobile feat speed bonus fix
import useBoonEffects from './hooks/useBoonEffects'; // [Sub-plan 3A — 2026-05-30]: Register custom boon passiveEffects in effectRegistry
// [Phase 4b bucket 2 — APR 2026]: Class feature effects (Extra Attack, Rage, Martial Arts)
import useClassFeatureEffects from './hooks/useClassFeatureEffects';
// [Phase 4b bucket 4 — APR 2026]: Race trait effects (darkvision, racial resistances, save advantage, racial proficiencies)
import useRaceFeatureEffects from './hooks/useRaceFeatureEffects';
import useSubclassFeatureEffects from './hooks/useSubclassFeatureEffects';   // ← NEW
import useExpertiseEffects from './hooks/useExpertiseEffects';   // [Phase 4b wizard-picks — APR 2026]: Closes Expertise / Knowledge Blessings breadcrumbs
// [Phase B / #336 — Spec 1b Task 18]: One-time warning when loaded character has non-proficient armor
import { useArmorProficiencyWarning } from './hooks/useArmorProficiencyWarning';
import { ref, set, update, remove } from 'firebase/database';
import { database } from './firebase/config';
import SourcesPanel from './components/SourcesPanel';
// [RESTORED 2025-10-10]: QuickActionsBar re-implemented with effectCalculator integration
import QuickActionsBar from './components/QuickActionsBar';
import KeyboardShortcutsHelp from './components/KeyboardShortcutsHelp';
import LevelUpConfetti from './components/LevelUpConfetti';
import {
  initializeSourcesSystem,
  getItemsFromSources,
  getSpellsFromSources,
  addItemToSource,
  addSpellToSource,
  // [CW-TRIO-SOURCES 2026-07-12]: in-place sourcebook deletes for the
  //   Race/Class/Subclass/Spell trio Load drawers (deleteSourceContent).
  removeSpellFromSource,
  removeRaceFromSource,
  removeClassFromSource,
  removeSubclassFromSource,
  sourcesManager,
  // [PHASE B / #067 — 2026-05-07]: Source-item fallback for the
  //   InventoryItem constructor — closes v1.0.1 §1.11 emission audit
  //   so source items resolve cleanly instead of falling through to the
  //   Batch 15 + 17 defensive `{}` stub.
  findItemInSources
} from './data/sources/initializeSources';
import { RECHARGE_LABELS } from './data/rechargeTypes';
import { MAGIC_ITEM_TABLES, DMG_LOOT_TABLES } from './data/magicItemTables';
// [P1.2+P1.3 fix - Apr 29, 2026]: Single canonical lookup for BASE_ITEMS.
// Replaces 6 open-coded `for (const category of Object.keys(BASE_ITEMS))`
// loops scattered through the shop-generation paths. See
// src/data/gameData.js for the helper definition and motivation.
import { getItemDefinition } from './data/gameData';
import { rollFromTable, findRollTableById } from './services/shopGenerator';
// [APR 30 2026]: Template resolver — converts pseudo-ID table entries
// (weapon_plus_n, armor_plus_n, ammunition_plus_n, shield_plus_n,
// belt_of_giant_strength) into concrete BASE_ITEMS instances at roll-time.
import { resolveTemplateEntry } from './services/templateResolvers';
import { shouldOfferInspiration, consumeInspiration, getInspirationThreshold } from './utils/inspirationUtils'; // [NEW - Week 2, Day 10]: Inspiration system
// [BSR-009 REMOVED — MAY 2026]: processBastionTurn / generateBastionItem
//   imports were dead — CharacterContext subscriber owns the flow now.
// import { processBastionTurn, generateBastionItem } from './data/bastionData';
import { grantRollResult } from './utils/grantRollResult'; // [Phase B #111]: Loot claim pipeline for party members
// [A33 — 2026-06-01]: Pure message builders for party-panel mechanical actions; logged via logActivity (Rule 13)
import { lootItemClaimedMessage, lootCurrencyClaimedMessage, dmStatusMessage, pushedShopVisitedMessage } from './utils/partyActivityLog';
import { removeCustomEntry, removeCustomFeatEverywhere, removeCustomMonster } from './utils/customContentDeleteOps'; // [CW-DELETE-AFFORDANCE — 2026-07-11]
// [DUAL STORAGE FIX - NOV 19, 2025]: Character data migration utilities
import { migrateCharacterData, validateCharacterData } from './utils/characterMigration';
import { reconcileMaxHP, levelUpHpRollDelta } from './utils/maxHPMirror'; // [MAXHP-UNIFICATION — 2026-07-25]: stored maxHitPoints mirrors calculateMaxHP
import { resolveTempHPGrant } from './utils/tempHPRule'; // [SIM-TEMPHP-NO-STACKING-RULE — one rule, every receiving surface]
import { safeLocalStorageSet } from './utils/storageValidator';
import { autosaveDelay } from './utils/autosaveDelay'; // [AUTOSAVE-DEBOUNCE-STARVATION — 2026-07-18]: trailing debounce + max-wait scheduling
import { normalizeRestoredCharacterId } from './utils/restoreCharacterId';
import { migratePairedSlots, migrateLensSlots } from './utils/equipSlotMigration'; // [SUB-PLAN 1R-RENDERGAP STONE 1 / 2026-05-29]: promote bare 'ring' equipSlot → ring1/ring2 on load; [STONE 3a / 2026-05-27]: extended to earring → earring1/earring2; [1R-eyes-data / 2026-05-27]: migrateLensSlots promotes legacy lens/goggle items from 'head' → 'eyes'
import { isSpellOnClassList, getOffListArcanaDC } from './utils/offListScrollCheck'; // [Sub-plan 4M — 2026-05-28]: Off-list scroll cast detection helpers
// [MAY 2026 — IDB image migration]: Lifts portrait/icon/gallery dataUrls into
//   IndexedDB so localStorage payloads stay under quota. Strip runs before
//   every autosave; restore runs after every load. See src/utils/imageBlobStore.js.
import { hydrateAll as hydrateImageStore, getIdbHealth } from './utils/imageBlobStore';
import { buildDebugBundle, triggerBundleDownload } from './utils/debugBundle';
import {
  stripImagesFromState,
  restoreImagesIntoState,
} from './utils/imageStateSerializer';
// [FIX S2-APR2026]: Skill proficiency helpers extracted from App.jsx for unit testing.
//                   Root cause of Athletics +3 vs +5: these helpers had no test coverage.
import { convertSkillNamesToObject, normalizeSkillProficiencies } from './utils/skillProficiencies';
import { mergeAndPlaceWizardItems } from './utils/wizardEquipmentMerge';
import { buildShopItem } from './utils/shopItemBuilder';
import { resolveCustomInvRef } from './utils/customInvRef';
import applyOnTakeDamageTriggers from './utils/applyOnTakeDamageTriggers'; // [Sub-plan 4N/4G — 2026-05-29]: keyboard-damage parity w/ CombatTab (resistance halving + on_take_damage triggers)
import consumeTriggeredSource from './utils/consumeTriggeredSource'; // [Sub-plan 4N/4G — 2026-05-29]: consume one-time triggered sources fired by keyboard damage
import { resolveCurrentCharges } from './utils/chargedItemDefaults'; // [SUB-PLAN 1C ITEM 1 / 2026-05-23]: Default current = max when missing
import { buildAllyBuffPayload } from './utils/spellAllyBuffPayload';
import { stampCharacterIds } from './utils/stampCharacterIds';
// [Phase B / #044 — MAY 2026]: levelProgression autoStatBoost dispatch
//   replaces the hardcoded Barbarian L20 Primal Champion bump.
import { applyLevelProgressionAutoStatBoosts } from './utils/autoStatBoost';
// [FIX NOV 24, 2025]: EquipmentResolver for proper display names in starter packs
import { EquipmentResolver } from './utils/equipmentResolver';
// [v1.0]: Content resolver for unified SRD + custom content lookup
import { getClassData as resolveClass, getAllClasses, usesSpellbook } from './utils/contentResolver';
// [Spec A Task 6]: Routes Wizard L18/L20 picks into customClassPicks (generic
//   bucket) instead of Wizard-specific paths; Warlock + Bard paths unchanged.
import { routeHighLevelSpellPicks } from './utils/highLevelSpellPickRouting';
import { archiveById, unarchiveById, renameById } from './utils/rosterActions';
// [Sub-plan 1I(c) 2026-05-26]: Pure helper extracted from drinkPotion — converts a potion
//   effect to the appliedEffect shape; also used to directly test the resistance-reader
//   regression fix (effect.value not effect.target). Rule 22 — testable production code.
// [Sub-plan 1U-impl-E 2026-05-27]: buildAppliedPotionEffect was direct-imported pre-refactor;
//   now it's re-exported via planPotionEffects which internally calls it. Remove direct import.
import { planPotionEffects, buildCastPotionResolveMessage } from './utils/drinkPotionLoop';
import { buildContentAuthoredEvent, buildCombatStartedEvent, buildTabViewEvent, trackFeatureEvent } from './utils/analyticsEvents';
// [Sub-plan 1Q-F-5b-ii — 2026-05-28]: quickBonusKinds registry — retires raw
//   b.type === '<kind>' string checks (Rule 11 / Rule 17).
import { findQuickBonusOfKind } from './data/quickBonusKinds';
// [v1.0]: Auth + Premium system
import { useAuth } from './contexts/AuthContext';
import { usePremiumAccess } from './hooks/usePremiumAccess';
import { useConfirm } from './contexts/ConfirmContext';
import useIsMobile from './hooks/useIsMobile';
import useReviewPrompt from './hooks/useReviewPrompt'; // [S10]: In-app review prompt trigger
import AuthModal from './components/AuthModal';
import OffListScrollCastModal from './components/OffListScrollCastModal'; // [Sub-plan 4M — 2026-05-28]: Off-list scroll casting Arcana check modal
import UsableAbilityModal from './components/UsableAbilityModal'; // [Sub-plan A1-cast-potions 2026-05-30]: DC save modal for Animal Friendship + Detect Thoughts potions
import ConsentPrompt from './components/ConsentPrompt';
import AccountPanel from './components/AccountPanel';
import SettingsPanel from './components/SettingsPanel';
import PremiumGate from './components/PremiumGate';
// [REMOVED P4-BATCH5]: TutorialOverlay import removed — replaced by TutorialSpotlight + WelcomePopup
// [PHASE 4B - OCT 24, 2025]: React Context architecture for state management
import AppProviders from './contexts/AppProviders';
import { useSettings } from './contexts/SettingsContext';
import { useUI } from './contexts/UIContext';
import { useInventory } from './contexts/InventoryContext';
import { useCharacter } from './contexts/CharacterContext';
import { useParty } from './contexts/PartyContext';
import { PartyCreateJoinSplash } from './components/PartyCreateJoinSplash';
// [FEB 2026]: Contextual Tutorial System
import { useTutorial } from './contexts/TutorialContext';
import TutorialSpotlight from './components/tutorial/TutorialSpotlight';
import TutorialPrompt from './components/tutorial/TutorialPrompt';
import { Z } from './constants/zLayers';
import { DISPLAY_MODES } from './constants/enums';
import { APP_VERSION } from './constants/version';
// [S6]: Analytics tracking for core user actions
import { trackEvent } from './services/analytics';
// [PHASE B / #001]: Moved from later in the file so all `import` statements
//                   precede module body — fixes ESLint `import/first`.
import ContentWorkshopSection from './components/ContentWorkshopSection';
import SupportSection from './components/SupportSection';
import SaveIndicator from './components/SaveIndicator';

// [PERFORMANCE FIX]: Lazy load heavy components to reduce initial bundle parse time
const WelcomePopup = lazy(() => import('./components/tutorial/WelcomePopup'));
const TutorialHelpMenu = lazy(() => import('./components/tutorial/TutorialHelpMenu'));
const EquipmentPanel = lazy(() => import('./components/EquipmentPanel'));
const SkillsPanel = lazy(() => import('./components/SkillsPanel'));
const FeatsPanel = lazy(() => import('./components/FeatsPanel'));
const RestPanel = lazy(() => import('./components/RestPanel'));
const CalendarPanel = lazy(() => import('./components/CalendarPanel'));
const DowntimesPanel = lazy(() => import('./components/DowntimesPanel')); // [NEW 2025-10-11]: D&D 5e downtime activities system
const SpellSelectionDialog = lazy(() => import('./components/SpellSelectionDialog'));
const CustomSpellCreator = lazy(() => import('./components/CustomSpellCreator'));
// [STONE 6 / #70 — 2026-05-18]: Item Creator authoring surface for Content Workshop.
//   Persists via addItemToSource → sourcesManager. Catalog authoring only —
//   does NOT add to inventory (that's ItemDialog's path).
// [2026-05-25 items 6+7 refactor]: CustomItemCreator deleted — it was a
// half-baked parallel to ItemDialog. Workshop's "Create Item" button now
// delegates to the canonical ItemDialog (setShowItemDialog).
// [STONE 6 CLOSURE / #67 Option B — 2026-05-18]: Custom Feat Creator
//   (Workshop-hosted). Mirrors CustomItemCreator. Persists via parent's
//   onSave callback (writes to playerStats.customFeats — NOT sourcesManager
//   because feats are per-character authored content). The inline creator
//   inside FeatsPanel stays for backward compat.
const CustomFeatCreator = lazy(() => import('./components/CustomFeatCreator'));
const CustomConditionCreator = lazy(() => import('./components/CustomConditionCreator')); // [Sub-plan 3B — 2026-05-29]: Workshop-hosted condition authoring (writes playerStats.customConditions)
const CustomBoonCreator = lazy(() => import('./components/CustomBoonCreator')); // [Sub-plan 3A — 2026-05-30]: Workshop-hosted boon authoring (writes playerStats.customBoons)
const CustomBackgroundCreator = lazy(() => import('./components/CustomBackgroundCreator')); // [Sub-plan 3C — 2026-05-30]: Workshop-hosted background authoring (writes playerStats.customBackgrounds)
// [2026-05-25 cleanup]: CatalogDrawer is no longer used in App.jsx — the
// Inventory Quick-Pick affordance is removed. The drawer component remains
// alive (CombatTab Bestiary + MonsterTrioDrawers import it directly).
// [2026-05-25 refactor]: Inventory's "Add Item" and Workshop's "Create Item"
// both open the SAME ItemDialog — Workshop in mode='create' (full edit
// fields), Inventory in mode='find' (edit fields hidden; user picks an
// authored item, sets qty, adds to inventory). No parallel dialog.
// [MOVED - MAR 2026]: CustomRaceCreator, CustomClassCreator, CustomSubclassCreator lazy imports
// moved to ContentWorkshopSection.js (standalone component)
const CharacterCreationWizard = lazy(() => import('./components/CharacterCreationWizard'));
const RosterTab = lazy(() => import('./components/RosterTab'));
const RosterRenameDialog = lazy(() => import('./components/RosterRenameDialog'));
const LevelUpWizard = lazy(() => import('./components/LevelUpWizard'));
const PartyPanel = lazy(() => import('./components/PartyPanel'));
const CharacterBasicsPanel = lazy(() => import('./components/CharacterBasicsPanel'));
const CharacterIdentityHeader = lazy(() => import('./components/CharacterIdentityHeader'));
const VitalStatsCard = lazy(() => import('./components/VitalStatsCard'));
const MulticlassManager = lazy(() => import('./components/MulticlassManager'));
const ConditionsTracker = lazy(() => import('./components/ConditionsTracker'));
const SensesMovementPanel = lazy(() => import('./components/SensesMovementPanel'));
const DiceTab = lazy(() => import('./components/DiceTab')); // [NEW - Week 2, Day 10]: Comprehensive dice rolling interface
const CombatTab = lazy(() => import('./components/CombatTab')); // [SPEC D 2026-05-17]: Combat Manager — initiative + token canvas + CPI/SPI difficulty
const MonsterEditor = lazy(() => import('./components/MonsterEditor')); // [SPEC D 2026-05-17]: CW-pattern monster editor with CPI/SPI live drawer
// [STONE 9 / #62 + #81 — 2026-05-18]: Workshop View+Edit+Template trio
//   substrate. Three drawers (Load / View / Template) + read-only View
//   modal. Layout contract for every future content type.
const MonsterTrioDrawers = lazy(() => import('./components/MonsterTrioDrawers'));
const MonsterViewModal = lazy(() => import('./components/MonsterViewModal'));
// [SUB-PLAN 1E — 2026-05-24]: Trio drawers + view modals for Items, Feats, Spells.
// Race/Class/Subclass trios are local to ContentWorkshopSection.
const ItemTrioDrawers = lazy(() => import('./components/ItemTrioDrawers'));
const ItemViewModal = lazy(() => import('./components/ItemViewModal'));
const FeatTrioDrawers = lazy(() => import('./components/FeatTrioDrawers'));
const FeatViewModal = lazy(() => import('./components/FeatViewModal'));
const SpellTrioDrawers = lazy(() => import('./components/SpellTrioDrawers'));
const SpellViewModal = lazy(() => import('./components/SpellViewModal'));
const InspirationPromptDialog = lazy(() => import('./components/InspirationPromptDialog')); // [NEW - Week 2, Day 10]: Inspiration reroll prompt
const ReviewPromptDialog = lazy(() => import('./components/ReviewPromptDialog')); // [S10]: In-app review / feedback prompt
const DmPurchasePopup = lazy(() => import('./components/DmPurchasePopup')); // [S11]: Post-purchase DM tour offer
const BastionPanel = lazy(() => import('./components/BastionPanel')); // [NEW 2025-10-10]: D&D 2024 Bastion System (DMG rules)
const PartyBastionPanel = lazy(() => import('./components/PartyBastionPanel')); // [Phase B #111]: Shared party stronghold (homebrew)
const SpellCopyDialog = lazy(() => import('./components/SpellCopyDialog')); // [NEW 2025-10-12]: Wizard spell copying from scrolls/spellbooks
const QuickBonusesPanel = lazy(() => import('./components/QuickBonusesPanel')); // [PHASE 4C - NOV 1, 2025]: Quick bonuses & status effects panel
const ActivityLogTab = lazy(() => import('./components/tabs/ActivityLogTab')); // [PHASE 4C - NOV 1, 2025]: Activity log tab with filtering and search
const JournalTab = lazy(() => import('./components/tabs/JournalTab')); // [PHASE 4C - NOV 1, 2025]: Campaign journal with roll table integration
const RollTablesTab = lazy(() => import('./components/tabs/RollTablesTab')); // [PHASE 4C - NOV 1, 2025]: Roll tables hub for encounters, loot, events, NPCs
const MagicItemsTab = lazy(() => import('./components/tabs/MagicItemsTab')); // [PHASE 4C - NOV 1, 2025]: Magic items and abilities management tab
const SpellbookTab = lazy(() => import('./components/tabs/SpellbookTab')); // [PHASE 4C - NOV 2, 2025]: Spellbook & casting management tab
const ShopTab = lazy(() => import('./components/tabs/ShopTab')); // [PHASE 4C - NOV 2, 2025]: Shop generation & purchasing system - ACHIEVES 12K GOAL!
const ScrollScribeDialog = lazy(() => import('./components/ScrollScribeDialog')); // [NEW 2025-10-12]: Wizard scroll crafting system

// [FIX P4-14]: Settings that belong to the user/device, NOT to a character save.
// [WHY]: Loading a saved character was overwriting isPremium, autosaveInterval, etc.
const GLOBAL_SETTINGS_KEYS = ['isPremium', 'debugMode', 'autosaveInterval', 'mobileInteractionMode', 'rememberLastCharacter'];

// [PERFORMANCE FIX CRITICAL]: Lazy load DATA files (~196 KB) that were blocking JavaScript execution for 30-90 seconds
// These contain thousands of D&D items, spells, races, classes - load them asynchronously after UI renders
// [TDZ BUG FIX - NOV 17, 2025]: Changed from `let` to `var` to fix production minifier TDZ error
// [WHY]: InventoryItem class constructor accesses these before useEffect initializes them
// [CAUSE]: `let` has Temporal Dead Zone, `var` does not - minified production build caught this
var BASE_ITEMS = null;
var STARTER_PACKS = null;
var SRD_RACES = null;
var SRD_CLASSES = null;
var SRD_BACKGROUNDS = null;
var SRD_FEATS = null;
var FaerunianDate = null;
var getSeasonColor = null;
var BUNDLED_SHOPS = null;
var getAvailableShops = null;
var getShopTemplate = null;
var calculateSpellSlots = null;
var getSpellsByClass = null;
var WIZARD_SPELLBOOK_RULES = null;
var getComponentDetails = null;
var hasComponentPouch = null;
var hasSpecificComponents = null;
var componentsConsumed = null;
var SPELL_COMPONENTS = null;

// ========================================
// CONSTANTS & BASE DATA
// ========================================

// [v1.0.0]: APP_VERSION moved to src/constants/version.js (derives from package.json).
//           Originally bumped to 1.2.1 in S1; reset to 1.0.0 in S2 — first release was always intended to be v1.0.
// Re-exported here for backward compatibility with any external imports.
export { APP_VERSION };

const CONTAINER_SIZE_HIERARCHY = {
  'tiny': 0,
  'small': 1,
  'medium': 2,
  'large': 3,
  'huge': 4
};

// ========================================
// CORE DATA MODELS
// ========================================

class InventoryItem {
  constructor(baseType, category, customData = {}) {
    // [BATCH 15 / B15-T7 — MAY 2026]: Defensive lookup. Pre-fix this was
    //   `BASE_ITEMS[category][baseType]` — when (category, baseType) didn't
    //   match a BASE_ITEMS row (e.g., CW custom items advertised by the
    //   SearchableItemSelector but stored in customRaces/customClasses),
    //   baseItem was undefined and every subsequent `baseItem.X` read threw
    //   "Cannot read properties of undefined (reading 'description')". The
    //   sim harness pageerror handler caught two instances on seed 1001;
    //   pre-fix this would have been silent and broken the inventory flow.
    // [PHASE B / #067 — 2026-05-07]: Source-item fallback. The picker
    //   advertises `(category, itemKey)` pairs from BOTH BASE_ITEMS and
    //   `getItemsFromSources()` (Tasha's, Xanathar's, custom CW). When
    //   the user picks a source item, BASE_ITEMS misses but
    //   `findItemInSources` now resolves the same item the picker
    //   surfaced — so description / size / value / charges flow through
    //   the constructor instead of being silently dropped to defaults.
    //   The `{}` final fallback stays as defense-in-depth for genuinely
    //   orphaned pairs (sim-harness fuzz, deprecated migrations, etc.).
    // [BATCH 17 / B17-T1 — MAY 2026]: One-shot console.warn when ALL
    //   lookups miss, so genuinely orphaned pairs surface in QA without
    //   grepping logs. Source-resolved pairs no longer trip the warn.
    const fromBase = BASE_ITEMS[category]?.[baseType];
    const fromSource = fromBase ? null : findItemInSources(category, baseType);
    const baseItem = fromBase || fromSource || {};
    if (!fromBase && !fromSource) {
      // Don't crash; do flag. Catalog discovery (simulation/actions/inventory.ts)
      // and SearchableItemSelector should advertise pairs that resolve here.
      console.warn(`[InventoryItem] No BASE_ITEMS or source entry for "${category}":"${baseType}"; using stub. Upstream picker should not advertise this pair (v1.0.1 audit).`);
    }
    this.id = customData.id || `item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    this.baseType = baseType;
    this.category = category;
    // [APR 21 2026 FIX]: Prefer base-item display name over the raw key. Without
    // this, unnamed gameData magical items render as "wand_magic_missiles" in
    // inventory headers. SearchableItemSelector's fallback chain already does
    // formatDisplayName; consumer code here mirrors that.
    // [PHASE B / #012 — B2-4 / May 2026]: 26 of 239 BASE_ITEMS.magical_items
    //   rows omit `name` (potion_climbing, cloak_billowing, dread_helm,
    //   bag_of_holding, ...). Pre-fix the chain bottomed out at `baseType`,
    //   so the user saw the raw key as the inventory name and the
    //   description directly under it — perceived as "the description IS
    //   the name." Routing through EquipmentResolver.formatDisplayName
    //   delivers the special-name dictionary entry when available
    //   ("Bag of Holding" for `bag_of_holding`) and a Title Case fallback
    //   otherwise, matching the SearchableItemSelector display string.
    this.name = customData.name || baseItem.name || EquipmentResolver.formatDisplayName(baseType);
    this.description = customData.description || baseItem.description || '';
    this.weight = customData.weight !== undefined ? customData.weight : baseItem.weight;
    // [BATCH 17 / B17-T1 — MAY 2026]: Guard the spread. Bare `[...baseItem.size]`
    //   throws TypeError when baseItem={} (Batch 15 missing-baseItem fallback path)
    //   because spread of undefined is not iterable. Sim seed-1001 caught 2
    //   instances at App.jsx:220 in the 130-step run on 2026-05-04. Default to
    //   1×1 so the item lands on the grid without breaking the inventory dialog.
    this.size = customData.size || (baseItem.size ? [...baseItem.size] : [1, 1]);
    this.value = customData.value !== undefined ? customData.value : baseItem.baseValue;
    this.position = customData.position || null;
    // [APR 21 2026 FIX]: Copy charges/maxCharges from baseItem when not on
    // customData. Previously charges were silently null on newly-added wands
    // which made the same wand render in BOTH "Spell Items" (because it had
    // spells) AND "Spell Equipment" (because the `!charges` filter matched).
    // User reported two cast buttons for one wand.
    //
    // [SUB-PLAN 1C ITEM 1 — 2026-05-23]: When the resolved maxCharges>0 but
    //   the resolved current charges is missing (null/undefined), default
    //   current to maxCharges (full pool). Mirrors the CustomItemCreator
    //   default-current-to-max pattern (CustomItemCreator.js:508-521) so the
    //   shape stays consistent regardless of which creator made the item.
    //   `resolveCurrentCharges` also clamps current ≤ maxCharges defensively.
    const _resolvedMaxCharges = customData.maxCharges !== undefined && customData.maxCharges !== null
      ? customData.maxCharges
      : (baseItem.maxCharges !== undefined ? baseItem.maxCharges : null);
    const _resolvedCurrentCharges = customData.charges !== undefined && customData.charges !== null
      ? customData.charges
      : (baseItem.charges !== undefined ? baseItem.charges : undefined);
    this.maxCharges = _resolvedMaxCharges;
    this.charges = resolveCurrentCharges(_resolvedCurrentCharges, _resolvedMaxCharges);
    this.chargesPerDay = customData.chargesPerDay || baseItem.chargesPerDay || false;
    this.rechargeType = customData.rechargeType || baseItem.rechargeType || 'none';
    this.icon = baseItem.icon;
    this.customImage = customData.customImage || null;

    // [BUG FIX 2025-10-10]: Add quantity support for stackable items (rope, arrows, etc.)
    // [WHY]: Creating "50 ft rope" was creating 50 individual items instead of one item with quantity=50
    // [APR 21 2026 FIX]: Honor bundledQuantity so bundles (e.g., 20 arrows) land
    // as a single stack with qty=20 rather than a single qty=1 item. Prior
    // behavior silently dropped the bundle size from equipmentResolver.
    this.quantity = customData.quantity || customData.bundledQuantity || baseItem.bundledQuantity || 1;

    // Magical item properties
    if (baseItem.spells || customData.spells) {
      this.spells = customData.spells || baseItem.spells?.map(spell => ({ ...spell }));
    }
    this.requiresAttunement = customData.requiresAttunement || baseItem.requiresAttunement || false;
    // [A.5 APR 2026]: Derive default equipSlot for weapons and armor when not set.
    // gameData.js weapon/armor entries historically omitted equipSlot; that left
    // item.equipSlot === null at runtime, which made srdFeats.js checks like
    // `item.slot === 'armor'` silently false (even after the A.6 rename to
    // item.equipSlot). Deriving from category + structural properties keeps the
    // logic data-driven without requiring 40+ individual data edits.
    let defaultEquipSlot = null;
    if (category === 'weapons') {
      const isRanged = (baseItem.properties || []).some(p => String(p).includes('Ammunition'));
      const isThrown = (baseItem.properties || []).some(p => String(p).includes('Thrown'));
      defaultEquipSlot = (isRanged && !isThrown) ? 'ranged' : 'mainhand';
    } else if (category === 'armor') {
      defaultEquipSlot = baseItem.armorType === 'shield' ? 'shield' : 'armor';
    }
    this.equipSlot = customData.equipSlot || baseItem.equipSlot || defaultEquipSlot || null;
    this.equipped = customData.equipped || false;
    this.magicalBonus = customData.magicalBonus || 0;

    // [FIX NOV 22, 2025]: Store ALL properties from customData to preserve advanced item properties
    // Weapon properties
    this.damage = customData.damage || customData.damageDice || baseItem.damage || null;
    this.damageType = customData.damageType || baseItem.damageType || null;
    this.weaponProperties = customData.weaponProperties || baseItem.properties || [];
    this.normalRange = customData.normalRange || baseItem.normalRange || null;
    this.longRange = customData.longRange || baseItem.longRange || null;
    // [BATCH 4 / P0 C-3 — APR 2026]: ammoType field is data-driven detection
    //   for both ranged weapons (longbow → 'arrows') AND ammunition entries
    //   (arrows → 'arrows'). consumeAmmunition matches an ammo stack by
    //   item.ammoType === weapon.ammoType. Per Rule 9, InventoryItem must
    //   carry every BASE_ITEMS field its consumers need.
    this.ammoType = customData.ammoType || baseItem.ammoType || null;

    // Armor properties
    this.ac = customData.ac || baseItem.ac || null;
    this.acFormula = customData.acFormula || baseItem.acFormula || null;
    this.stealthDisadvantage = customData.stealthDisadvantage || baseItem.stealthDisadvantage || false;
    // [FIX JAN 2026]: Add missing armor properties for AC calculation
    this.armorType = customData.armorType || baseItem.armorType || null;
    this.maxDexMod = customData.maxDexMod !== undefined ? customData.maxDexMod : (baseItem.maxDexMod !== undefined ? baseItem.maxDexMod : null);

    // [FIX JAN 2026]: Add stackable property for ammunition/consumables
    this.stackable = customData.stackable !== undefined ? customData.stackable : (baseItem.stackable || false);
    // [FIX MAY 2026 / Rule 9]: Copy maxStack from BASE_ITEMS so the cap
    //   survives `new InventoryItem(...)`. Custom override wins (Pass D
    //   per-instance maxStack from EditItemDialog Advanced Properties);
    //   falls back to BASE_ITEMS row (e.g. arrows: 20, potions: 99); null
    //   means unbounded. addItem / mergeStackOnto / combineStacks read this.
    this.maxStack = customData.maxStack !== undefined && customData.maxStack !== null
      ? customData.maxStack
      : (baseItem.maxStack !== undefined ? baseItem.maxStack : null);

    // Material and crafting
    this.material = customData.material || null;
    this.rarity = customData.rarity || baseItem.rarity || 'common';

    // Advanced magical properties (Phase 10 restored)
    this.rechargeType = customData.rechargeType || baseItem.rechargeType || 'none';
    this.attunementPrerequisite = customData.attunementPrerequisite || null;
    this.isCursed = customData.isCursed || baseItem.cursed || false;
    this.curseDescription = customData.curseDescription || null;

    // [SUB-PLAN 1C ITEM 3 / 2026-05-23]: Identification flag propagation.
    //   Default is TRUE (identified) — only stamped as false when EITHER side
    //   explicitly sets isIdentified=false. CustomItemCreator emits the flag
    //   ONLY when the author unchecks the Identified checkbox (default-true
    //   convention from Sub-plan 1A); BASE_ITEMS rows never declare it. The
    //   per-instance flag persists across save/load so a player can hand a
    //   sealed loot bag to a friend without the friend's app silently
    //   identifying it on import.
    this.isIdentified = customData.isIdentified !== false && baseItem.isIdentified !== false;

    // Stat modifications
    this.statBonus = customData.statBonus || null;
    this.statValue = customData.statValue || null;
    this.resistances = customData.resistances || [];
    this.immunities = customData.immunities || [];
    this.conditionImmunities = customData.conditionImmunities || [];

    // [SUB-PLAN 1I(j) / 2026-05-29 / Rule 9]: Effects array propagation.
    //   BASE_ITEMS rows (and findItemInSources rows) declare passive /
    //   triggered / consumable effects via `effects: [{ type, operation,
    //   value, ... }]`. Pre-fix this field was NOT copied by the
    //   constructor — every InventoryItem instance had `effects === undefined`
    //   at runtime, and every consumer (drinkPotion at App.jsx:4006,
    //   buildItemRegistrationsFromInventory at utils/itemEffectsRegistration.js:33,
    //   useItemEffects, SensesMovementPanel, ContextMenuComponent,
    //   EditItemDialog, craftingRules.isMagical) silently saw zero effects.
    //   This broke ALL healing potions (no HP heal), Heroism tempHP
    //   (Patch 4 / SHA 90bc44d), spell-on-potion (Patch 5 / SHA bd4d5aa),
    //   every resistance potion, and every magical item whose effects
    //   feed effectRegistry via the InventoryContext re-registration
    //   useEffect. Initializing to null (not undefined) lets downstream
    //   readers distinguish "no effects on this row" from "uninitialized
    //   field" — matches the constructor's convention for damage / ac /
    //   armorType / ammoType which all default to null. Unblocks
    //   Patches 4+5 retroactively. Bug-investigator trace:
    //   .claude/findings/2026-05-29-bug-C-potion-healing-hp-wire.md.
    //   Rule 31 data-model trace:
    //   .claude/findings/2026-05-29-datamodel-inventoryItem-effects-propagation-1Ij7a3f.md.
    this.effects = customData.effects || baseItem.effects || null;

    // Movement and senses
    this.speedBonus = customData.speedBonus || null;
    this.flyingSpeed = customData.flyingSpeed || null;
    this.swimmingSpeed = customData.swimmingSpeed || null;
    this.darkvision = customData.darkvision || null;
    this.blindsight = customData.blindsight || null;
    this.tremorsense = customData.tremorsense || null;

    // Skill and save bonuses
    this.skillBonuses = customData.skillBonuses || [];
    this.savingThrowBonuses = customData.savingThrowBonuses || [];
    this.advantageOn = customData.advantageOn || [];
    
    // [BATCH 11 / P1 A-15 — APR 2026]: Property-based container detection.
    //   Pre-fix bare `category === 'containers'` missed CW-authored containers
    //   that stamp `customData.isContainer` without adopting the SRD category
    //   string. Now any of three signals trips the init: explicit category,
    //   customData flag, or the BASE_ITEMS containerSlots property. Rule 11.
    if (category === 'containers' || customData.isContainer || baseItem?.containerSlots) {
      this.isContainer = true;
      // Never leave a container with null slots: a container needs a valid grid,
      // and unguarded containerSlots[0] reads crashed the whole MainApp render for
      // a Wizard's slotless starting container (sim-fuzzer L1 catch, 2026-07-13).
      // [6,8] matches the established containerSlots?.[0]||6 fallbacks elsewhere.
      this.containerSlots = customData.containerSlots ?? baseItem?.containerSlots ?? [6, 8];
      this.containedItems = customData.containedItems || [];
      this.weightReduction = customData.weightReduction ?? baseItem?.weightReduction ?? 0;
      this.containerSize = customData.containerSize ?? baseItem?.containerSize ?? 'medium';
      this.maxWeight = customData.maxWeight ?? baseItem?.maxWeight ?? null;
      this.extradimensional = customData.extradimensional ?? baseItem?.extradimensional ?? false;
      this.cursed = customData.cursed ?? baseItem?.cursed ?? false;
      this.rarity = customData.rarity ?? baseItem?.rarity ?? 'common';
    }
  }

  getTotalWeight() {
    // [BUG FIX 2025-10-10]: Multiply base weight by quantity for stackable items
    // [BUG FIX 2025-11-10]: Guard against undefined weight (prevents NaN)
    let weight = (this.weight || 0) * (this.quantity || 1);

    if (this.isContainer && this.containedItems) {
      const containedWeight = this.containedItems.reduce((sum, containedItem) => {
        const itemWithMethods = Object.assign(Object.create(InventoryItem.prototype), containedItem);
        return sum + (itemWithMethods.getTotalWeight() || 0);
      }, 0);
      // [FIX APR 2026]: Guard weightReduction — plain objects from JSON may lack this property
      weight += containedWeight * (1 - (this.weightReduction || 0));
    }
    // [FIX APR 2026]: Final NaN guard — never return NaN to callers
    return isNaN(weight) ? 0 : weight;
  }
  
  getTotalValue() {
    // [BUG FIX 2025-10-10]: Multiply base value by quantity for stackable items
    // [BUG FIX 2025-11-10]: Guard against undefined value (prevents NaN in inventory display)
    let value = (this.value || 0) * (this.quantity || 1);

    if (this.isContainer && this.containedItems) {
      value += this.containedItems.reduce((sum, containedItem) => {
        const itemWithMethods = Object.assign(Object.create(InventoryItem.prototype), containedItem);
        return sum + (itemWithMethods.getTotalValue() || 0);
      }, 0);
    }
    // [FIX APR 2026]: Final NaN guard — never return NaN to callers
    return isNaN(value) ? 0 : value;
  }
}

// [SUB-PLAN 1I(j) / 2026-05-29 / Rule 22]: Named export so Rule 22 producer→
//   consumer tests can exercise the actual production constructor (not an
//   inline replica). The Patch 4 + Patch 5 tests used hand-built effect
//   objects instead of constructing InventoryItem from BASE_ITEMS, which
//   is the exact Rule 22 failure mode that hid the silent-drop where
//   instance.effects was undefined for the past several months.
//
//   `__setDataForTests` is a controlled escape hatch that lets test
//   harnesses synchronously populate the module-level BASE_ITEMS (and
//   any other lazy-loaded data tables they reach) before exercising
//   the constructor. In production, the same variables are populated
//   asynchronously inside TabletopInventoryManager's useEffect (see
//   App.jsx ~L1130-1170) — that path is unchanged.
//
//   The export name uses the `__` prefix to make grep-discoverability
//   obvious and signal "test-only escape hatch, not production API".
export { InventoryItem };
export function __setDataForTests(overrides = {}) {
  if (overrides.BASE_ITEMS !== undefined) BASE_ITEMS = overrides.BASE_ITEMS;
  if (overrides.STARTER_PACKS !== undefined) STARTER_PACKS = overrides.STARTER_PACKS;
  if (overrides.SRD_RACES !== undefined) SRD_RACES = overrides.SRD_RACES;
  if (overrides.SRD_CLASSES !== undefined) SRD_CLASSES = overrides.SRD_CLASSES;
  if (overrides.SRD_BACKGROUNDS !== undefined) SRD_BACKGROUNDS = overrides.SRD_BACKGROUNDS;
  if (overrides.SRD_FEATS !== undefined) SRD_FEATS = overrides.SRD_FEATS;
}

// [MOVED S2-APR2026]: SKILL_NAME_TO_KEY / convertSkillNamesToObject /
//                     normalizeSkillProficiencies extracted to
//                     ./utils/skillProficiencies.js so they can be unit-tested.
//                     See Session 2 handoff for context.

// ========================================
// MAIN APP COMPONENT
// ========================================

// [MOVED - MAR 2026]: ContentWorkshopSection extracted to standalone component
// [PHASE B / #001]: Their `import` lines were moved to the top of the file
//                   to satisfy ESLint `import/first`; this block-comment
//                   stays here as a navigation hint.

// [TIER 1.3 FIX APR 2026 — Calendar/Rest desync]: Re-hydrate timeSystem
//   Date fields after JSON.parse. JSON.stringify serializes Date → ISO
//   string, so unless we explicitly parse them back, downstream consumers
//   that do `currentTime.getTime()` would crash with "is not a function".
//   The helper is null-safe and forgiving — pass any object (or null/
//   undefined) and it returns the same shape with currentTime,
//   lastShortRest, lastLongRest converted to Date if they were strings.
//   ALSO seeds currentTime to game-day-1 8 AM if the saved data predates
//   the field — without this, legacy saves would have undefined
//   currentTime and the rest-button enable check would silently fall
//   back to wall clock again.
const TIER13_GAME_DAY_1_EPOCH = () => new Date(2024, 0, 1, 8, 0, 0);
const hydrateTimeSystem = (raw) => {
  if (!raw || typeof raw !== 'object') return raw;
  const hydrated = { ...raw };
  for (const field of ['currentTime', 'lastShortRest', 'lastLongRest']) {
    const value = hydrated[field];
    if (typeof value === 'string') {
      const parsed = new Date(value);
      if (!isNaN(parsed.getTime())) {
        hydrated[field] = parsed;
      }
    }
  }
  // Seed currentTime if missing (legacy save) — required so canRest
  // checks compare against a real Date, not undefined.
  if (!(hydrated.currentTime instanceof Date)) {
    hydrated.currentTime = TIER13_GAME_DAY_1_EPOCH();
  }
  return hydrated;
};

// [PHASE 4B - OCT 24, 2025]: MainApp contains all existing app logic
// [WHY]: Separated from export to allow wrapping with AppProviders context
// [INTEGRATION]: All state will gradually migrate to contexts over Phase 4B
const MainApp = () => {
  // ============================================================================
  // PHASE 4B: REACT CONTEXT HOOKS (Gradually replacing local state)
  // ============================================================================

  // [PHASE 4B - Step 1]: Settings from SettingsContext
  const {
    settings,
    setSettings,
    updateSetting,
    updateSettings,
    savedCharacters,
    setSavedCharacters,
    sessionStartTime,
    setSessionStartTime
  } = useSettings();

  // [v1.0]: Auth state
  const { isAuthenticated, isPremium, currentUser, requiresConsent } = useAuth();
  const hasPremium = usePremiumAccess(); // [v1.1]: Centralized premium check
  const confirm = useConfirm();
  const [showAuthModal, setShowAuthModal] = useState(false);
  // [Sub-plan 4M — 2026-05-28]: Off-list scroll casting modal state
  const [showOffListScrollModal, setShowOffListScrollModal] = useState(false);
  const [offListScrollPendingData, setOffListScrollPendingData] = useState(null); // { scrollItem, spell, spellLevel, arcanaMod }
  // [Sub-plan A1-cast-potions 2026-05-30]: DC save modal for cast-type potions (Animal Friendship WIS, Detect Thoughts INT)
  const [castPotionModalOpen, setCastPotionModalOpen] = useState(false);
  const [castPotionModalData, setCastPotionModalData] = useState(null); // { spellName, saveAbility, dc, itemName }
  // [Sub-plan 1H-G-bw-S3a — 2026-06-03]: Reaction offer modal when damage is taken
  const [pendingReactionOffer, setPendingReactionOffer] = useState(null); // { reactions: [], selectedIndex: number, damageAmount: number, showInvocation: bool }
  // [REMOVED P4-BATCH5]: showTutorial state removed — TutorialOverlay replaced by TutorialContext

  // [PHASE 4B - Step 2]: UI State from UIContext
  const {
    // Navigation
    activeTab,
    setActiveTab,
    // Status & Messaging
    statusMessage,
    setStatusMessage,
    statusTooltip,
    setStatusTooltip,
    statusType,
    showStatus,
    // View Modes
    showSpreadsheetView,
    setShowSpreadsheetView,
    // Collapsed Panels
    statsCollapsed,
    setStatsCollapsed,
    equipmentCollapsed,
    setEquipmentCollapsed,
    quickBonusesCollapsed,
    setQuickBonusesCollapsed,
    gridConfigCollapsed,
    setGridConfigCollapsed,
    // Character Dialogs
    showPresetManager,
    setShowPresetManager,
    hiddenPresets, // [PHASE 4C]
    setHiddenPresets, // [PHASE 4C]
    customBonus, // [PHASE 4C]
    setCustomBonus, // [PHASE 4C]
    // Shop Dialogs
    showShopTables,
    setShowShopTables,
    generatedShop,
    setGeneratedShop,
    activeShops,
    setActiveShops,
    isAddingToShop,
    setIsAddingToShop,
    selectedShopIndex,
    setSelectedShopIndex,
    showShopPurchase, // [PHASE 4C - SESSION 7]
    setShowShopPurchase, // [PHASE 4C - SESSION 7]
    showCustomShopCreator,
    setShowCustomShopCreator,
    showCustomTableCreator,
    setShowCustomTableCreator,
    // [PHASE B / #057 — 2026-05-07]: Edit Shop dialog state
    showEditShopDialog,
    setShowEditShopDialog,
    editingShop,
    setEditingShop,
    // [PHASE B / #059 — 2026-05-07]: Edit Custom Shop Type — non-null
    //   when the Custom Shop Type Creator is open in edit mode.
    editingCustomShopType,
    setEditingCustomShopType,
    recentShopRolls,
    setRecentShopRolls,
    shopSettings, // [PHASE 4C - SESSION 7]
    setShopSettings, // [PHASE 4C - SESSION 7]
    customShopTypes, // [PHASE 4C - SESSION 7]
    setCustomShopTypes, // [PHASE 4C - SESSION 7]
    // Roll Table Dialogs
    showTableCreator,
    setShowTableCreator,
    editingTable,
    setEditingTable,
    selectedTableCategory,
    setSelectedTableCategory,
    tableType,
    setTableType,
    weightedEntries,
    setWeightedEntries,
    // Journal Dialogs
    showJournalEditor,
    setShowJournalEditor,
    editingJournalEntry,
    setEditingJournalEntry,
    journalFilter, // [PHASE 4C]
    setJournalFilter, // [PHASE 4C]
    journalRollTables, // [PHASE 4C]
    setJournalRollTables, // [PHASE 4C]
    selectedRollTable, // [PHASE 4C]
    setSelectedRollTable, // [PHASE 4C]
    // Spell Dialogs
    showQuickcastDialog,
    setShowQuickcastDialog,
    quickcastSpell,
    setQuickcastSpell,
    showSpellSelection,
    setShowSpellSelection,
    showCustomSpellCreator,
    setShowCustomSpellCreator,
    templateSpell,
    setTemplateSpell,
    editingSpell,
    setEditingSpell,
    showSpellCopyDialog,
    setShowSpellCopyDialog,
    spellCopyData,
    setSpellCopyData,
    showScrollScribeDialog,
    setShowScrollScribeDialog,
    // Touch Gestures
    touchStart,
    setTouchStart,
    touchEnd,
    setTouchEnd,
    // Autosave
    hasUnsavedChanges,
    setHasUnsavedChanges,
    lastAutosave,
    setLastAutosave,
    getRelativeTime,
    isSaving,
    setIsSaving,
    // Activity Log UI
    expandedLogEntries,
    setExpandedLogEntries,
    logFilters, // [PHASE 4C]
    setLogFilters, // [PHASE 4C]
    logSearchTerm, // [PHASE 4C]
    setLogSearchTerm, // [PHASE 4C]
    // Dialog Actions
  } = useUI();

  // [PHASE 4B - Step 3]: Inventory State from InventoryContext
  const {
    // Core State
    inventory,
    setInventory,
    vaults,
    setVaults,
    // Drag & Drop State
    draggedItem,
    setDraggedItem,
    dragSource,
    setDragSource,
    dragPosition,
    setDragPosition,
    selectedItem,
    setSelectedItem,
    selectedItemSource,
    setSelectedItemSource,
    hoveredGrid,
    setHoveredGrid,
    hoveredCell,
    setHoveredCell,
    dragDataRef,
    // Container & Vault State
    openContainer,
    setOpenContainer,
    showVaultModal,
    setShowVaultModal,
    expandedContainers,
    setExpandedContainers,
    editingVault,
    setEditingVault,
    directAddToVault,
    setDirectAddToVault,
    // Item Management State
    editingItem,
    setEditingItem,
    showItemDialog,
    setShowItemDialog,
    showVaultDialog,
    setShowVaultDialog,
    contextMenu,
    setContextMenu,
    // Sorting & Filtering State
    sortBy,
    setSortBy,
    sortOrder,
    setSortOrder,
    filterType,
    setFilterType,
    searchQuery,
    setSearchQuery,
    magicItemsFilter,
    setMagicItemsFilter,
    magicItemsSort,
    setMagicItemsSort,
    // Inventory Actions
    addItem,
    removeItem,
    updateItem,
    moveItem,
    transferToVault,
    transferFromVault,
    // [STACK INVESTIGATION APR 30 2026]: Stack actions for drag-merge.
    mergeStackOnto,
    // Vault Actions
    addVault,
    removeVault,
    updateVault,
    // Container Actions
    toggleContainer,
    expandContainer,
    collapseContainer,
    collapseAllContainers,
    // Drag & Drop Actions
    startDrag,
    endDrag,
    updateDragPosition,
    // [PHASE 4B STEP 6 - Added 2025-10-25]: Equipment & Item Management Functions
    // Utility Functions
    getItemDetails,
    canEquip,
    getEquipSlots,
    // Bonus Management
    applyItemBonus,
    removeItemBonus,
    // Equipment Management
    equipItem,
    unequipItem,
    attuneToItem,
    unattuneItem,
    consumeAmmunition,
    // Item Actions
    sellItem,
    deleteItem
  } = useInventory();

  // [PHASE 4B - Step 4]: Character State from CharacterContext
  const {
    // Core Character State
    playerStats,
    setPlayerStats,
    currencies,
    setCurrencies,
    skillProficiencies,
    setSkillProficiencies,
    temporaryStats,
    setTemporaryStats,
    // Time and Calendar
    currentGameDay,
    setCurrentGameDay,
    timeSystem,
    setTimeSystem,
    calendarSettings,
    setCalendarSettings,
    // Spell System
    knownSpells,
    setKnownSpells,
    preparedSpells,
    setPreparedSpells,
    customSpells,
    setCustomSpells,
    spellSlots,
    setSpellSlots,
    manualSpellSlots,
    setManualSpellSlots,
    activeSpellbookClass,
    setActiveSpellbookClass,
    collapsedSpellLevels,
    setCollapsedSpellLevels,
    customComponents,
    setCustomComponents,
    // Activity and Journal
    activityLog,
    setActivityLog,
    journalEntries,
    setJournalEntries,
    rollTables, // [PHASE 4C]
    setRollTables, // [PHASE 4C]
    craftingProjects,
    setCraftingProjects,
    dmEvents,
    setDmEvents,
    // Quick Bonuses
    activeQuickBonuses,
    setActiveQuickBonuses,
    customQuickBonuses,
    setCustomQuickBonuses,
    inspirationPrompt,
    setInspirationPrompt,
    // UI State
    showCharacterWizard,
    setShowCharacterWizard,
    showLevelUpWizard,
    setShowLevelUpWizard,
    showKeyboardHelp,
    setShowKeyboardHelp,
    showConfetti,
    setShowConfetti,
    levelUpLevel,
    setLevelUpLevel,
    // Actions
    logActivity,
    updateHP,
    updateMaxHP,
    updateTemporaryHP,
    updateDeathSaves,
    resetDeathSaves,
    updateInspiration,
    updateLevel,
    updateXP,
    updateCurrency,
    addCurrency,
    subtractCurrency,
    useSpellSlot,
    restoreSpellSlot,
    restoreAllSpellSlots,
    addKnownSpell,
    removeKnownSpell,
    prepareSpell,
    unprepareSpell,
    addQuickBonus,
    removeQuickBonus,
    clearAllQuickBonuses
  } = useCharacter();

  const { createPartyHandler, joinPartyHandler } = useParty();

  // [MIGRATION]: Canonical helper variables for flat→nested migration
  // Use these instead of playerStats.class / playerStats.race in all READ paths
  const primaryClass = playerStats.classes?.[0]?.className || '';
  const primaryRace = playerStats.basics?.race || '';

  // [S10]: In-app review prompt — fires once after first character + (3 sessions OR 3 days)
  const hasCharacter = !!(playerStats?.name && String(playerStats.name).trim().length > 0);
  const reviewPrompt = useReviewPrompt({ hasCharacter });

  // [S11]: Post-purchase DM tutorial offer
  // [PURPOSE]: First time a user is recognized as premium, offer the DM tools tour.
  // [WHY]: Premium unlocks the DM-only feature surface (vault sharing, shop generator,
  //        bastion, downtime, Content Workshop). One-time per device — guarded by
  //        the dm_tutorial_offered localStorage flag set by DmPurchasePopup itself.
  // [INTEGRATION]: Listens to AuthContext.isPremium. Fires after a 1.5s settle delay so
  //        the welcome popup / consent prompt / status messages render first.
  const [showDmPurchasePopup, setShowDmPurchasePopup] = useState(false);
  useEffect(() => {
    if (!isPremium) return;
    // [FIX APR 2026 Phase 3C.2]: Do not offer DM tour while consent prompt is active.
    // Previously the 1.5s timer fired unconditionally and DmPurchasePopup's z-9999
    // rendered above the ConsentPrompt overlay, confusing users who hadn't accepted
    // terms yet. Now we wait for consent to settle first.
    if (requiresConsent) return;
    let alreadyOffered = false;
    try {
      alreadyOffered = localStorage.getItem('dm_tutorial_offered') === '1';
    } catch (e) {
      // localStorage blocked — fail safe by skipping the popup
      return;
    }
    if (alreadyOffered) return;

    const timer = setTimeout(() => {
      trackEvent('dm_tutorial_offered');
      setShowDmPurchasePopup(true);
    }, 1500);

    return () => clearTimeout(timer);
  }, [isPremium, requiresConsent]);

  // [FIX APR 2026 Phase 3C.1]: Subscribe to tutorial tab-switch requests.
  // When TutorialContext.startTutorial() emits TUTORIAL_REQUEST_TAB, auto-navigate
  // so the spotlight can find its targets. Prevents grey-screen tutorials.
  useEffect(() => {
    const unsubscribe = systemBus.on(EVENTS.TUTORIAL_REQUEST_TAB, (data) => {
      if (data?.tab && typeof setActiveTab === 'function') {
        setActiveTab(data.tab);
      }
    });
    return () => unsubscribe && unsubscribe();
  }, [setActiveTab]);

  // [FEB 2026]: Contextual Tutorial System
  const {
    showWelcome,
    activeTutorial,
    onTabVisit,
    dismissPrompt,
    startTutorial,
    resetAllTutorials,
    pendingPrompt,
  } = useTutorial();

  // [FEB 2026]: Track whether character creation should have guided tutorial
  const [guidedCreation, setGuidedCreation] = useState(false);
  // [TUTORIAL-1]: Browse / Replay Tours modal visible from header "?" button
  const [showTutorialHelpMenu, setShowTutorialHelpMenu] = useState(false);

  // [S6]: Track app_open on mount for DAU metrics (fires once per session)
  useEffect(() => {
    trackEvent('app_open', { app_version: APP_VERSION });
  }, []);

  // [FEB 2026]: Notify tutorial context when tab changes
  // [FIX]: Dismiss any pending prompt from previous tab before checking new tab
  useEffect(() => {
    if (activeTab) {
      dismissPrompt();
      onTabVisit(activeTab);
    }
  }, [activeTab, onTabVisit, dismissPrompt]);

  // ============================================================================
  // Core State Management (TODO: Migrate to contexts)
  // ============================================================================

  // [PHASE 4B - MIGRATED]: inventory, vaults, drag/drop now from InventoryContext (line 311)
  // [REMOVED]: const [inventory, setInventory] = useState([]);
  // [REMOVED]: const [vaults, setVaults] = useState([]);
  // [REMOVED]: const [draggedItem, setDraggedItem] = useState(null);
  // [REMOVED]: const [dragSource, setDragSource] = useState(null);
  // [REMOVED]: const [dragPosition, setDragPosition] = useState(null);
  // [REMOVED]: const [selectedItem, setSelectedItem] = useState(null);
  // [REMOVED]: const [selectedItemSource, setSelectedItemSource] = useState(null);
  // [PHASE 4B - MIGRATED]: Touch gestures (touchStart, touchEnd) now in UIContext (line 214)
  // [REMOVED]: const [hoveredGrid, setHoveredGrid] = useState(null);
  // [REMOVED]: const [hoveredCell, setHoveredCell] = useState(null);
  // [REMOVED]: const [openContainer, setOpenContainer] = useState(null);
  // [REMOVED]: const [showVaultModal, setShowVaultModal] = useState(null);
  // [REMOVED]: const [magicItemsFilter, setMagicItemsFilter] = useState('activatable');
  // [REMOVED]: const [magicItemsSort, setMagicItemsSort] = useState('name');
  // [REMOVED]: const [editingItem, setEditingItem] = useState(null);
  // [REMOVED]: const [editingVault, setEditingVault] = useState(null);
  // [PHASE 4B - MIGRATED]: statusMessage, showSpreadsheetView now in UIContext (line 214)
  // [PHASE 4B - MIGRATED]: savedCharacters and sessionStartTime now in SettingsContext (line 202)
  // [REMOVED]: const [expandedContainers, setExpandedContainers] = useState(new Set());
  // [PHASE 4B - MIGRATED]: Collapsed panels (stats, equipment, quickBonuses) now in UIContext (line 214)
  // [REMOVED]: const [activeQuickBonuses, setActiveQuickBonuses] = useState([]);
  // [REMOVED]: const [temporaryStats, setTemporaryStats] = useState({});
  // [PHASE 4C - MIGRATED]: hiddenPresets now in UIContext (line 70)
  // [REMOVED]: const [hiddenPresets, setHiddenPresets] = useState([]);
  // [REMOVED]: const [customQuickBonuses, setCustomQuickBonuses] = useState([]);
  // [PHASE 4B - MIGRATED]: Dialog states (showPresetManager, showShopTables, recentShopRolls) now in UIContext
  // [PHASE 4C - MIGRATED]: customBonus now in UIContext (line 71)
  // [REMOVED]: const [customBonus, setCustomBonus] = useState({});

  // [PHASE 4B - MIGRATED]: Inventory sorting and filtering now in InventoryContext (line 311)
  // [REMOVED]: const [sortBy, setSortBy] = useState('name');
  // [REMOVED]: const [sortOrder, setSortOrder] = useState('asc');
  // [REMOVED]: const [filterType, setFilterType] = useState('all');
  // [REMOVED]: const [searchQuery, setSearchQuery] = useState('');

  // [PERFORMANCE FIX CRITICAL]: Data loading state
  // Loading ~196 KB of game data asynchronously to prevent 30-90 second JavaScript blocking
  const [gameDataLoaded, setGameDataLoaded] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);

  // [MAY 2026 — IDB image migration]: Track when the IndexedDB image cache has
  //   been hydrated. Both the autosave-restore effect and loadCharacter gate
  //   on this flag so portrait/icon tokens are inflated as soon as state
  //   mounts. See src/utils/imageBlobStore.js + imageStateSerializer.js.
  const [imageStoreReady, setImageStoreReady] = useState(false);
  useEffect(() => {
    let cancelled = false;
    hydrateImageStore().finally(() => {
      if (!cancelled) setImageStoreReady(true);
    });
    return () => { cancelled = true; };
  }, []);

  // [PARTY SYNC]: Party state with localStorage persistence
  const [partyId, setPartyId] = useState(() => localStorage.getItem('partyId') || null);
  const [partyName, setPartyName] = useState(() => localStorage.getItem('partyName') || '');

  // [F-009 FIX 2026-05-09]: Header dropdown tap-to-toggle for mobile.
  // Pre-fix, Load Character / Export dropdowns were hover-only (group-hover).
  // Mobile browsers don't fire :hover reliably; users tapped the icon and
  // nothing happened. Now: tap toggles, outside-click closes, hover still
  // works on desktop.
  const [headerDropdownOpen, setHeaderDropdownOpen] = useState(null);
  useEffect(() => {
    if (!headerDropdownOpen) return undefined;
    const handler = (e) => {
      if (!e.target.closest('[data-header-dropdown]')) {
        setHeaderDropdownOpen(null);
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [headerDropdownOpen]);

  // [PERFORMANCE FIX CRITICAL]: Load heavy data files asynchronously on mount
  // This reduces initial bundle from 183 kB to ~100 kB and prevents 30-90 second JavaScript blocking
  useEffect(() => {
    const loadGameData = async () => {
      try {

        // Load all data modules in parallel
        const [
          gameDataModule,
          srdCharacterDataModule,
          faerunianCalendarModule,
          bundledShopsModule,
          srdSpellsModule,
          spellComponentsModule,
          srdFeatsModule
        ] = await Promise.all([
          import('./data/gameData'),
          import('./data/srdCharacterData'),
          import('./data/faerunianCalendar'),
          import('./data/bundledShops'),
          import('./data/srdSpells'),
          import('./data/spellComponents'),
          import('./data/srdFeats')
        ]);

        // Assign to module-level variables so rest of code works unchanged
        BASE_ITEMS = gameDataModule.BASE_ITEMS;
        STARTER_PACKS = gameDataModule.STARTER_PACKS;
        SRD_RACES = srdCharacterDataModule.SRD_RACES;
        SRD_CLASSES = srdCharacterDataModule.SRD_CLASSES;
        SRD_BACKGROUNDS = srdCharacterDataModule.SRD_BACKGROUNDS;
        SRD_FEATS = srdFeatsModule.SRD_FEATS;
        FaerunianDate = faerunianCalendarModule.FaerunianDate;
        getSeasonColor = faerunianCalendarModule.getSeasonColor;
        BUNDLED_SHOPS = bundledShopsModule.BUNDLED_SHOPS;
        getAvailableShops = bundledShopsModule.getAvailableShops;
        getShopTemplate = bundledShopsModule.getShopTemplate;
        calculateSpellSlots = srdSpellsModule.calculateSpellSlots;
        getSpellsByClass = srdSpellsModule.getSpellsByClass;
        WIZARD_SPELLBOOK_RULES = srdSpellsModule.WIZARD_SPELLBOOK_RULES;
        getComponentDetails = spellComponentsModule.getComponentDetails;
        hasComponentPouch = spellComponentsModule.hasComponentPouch;
        hasSpecificComponents = spellComponentsModule.hasSpecificComponents;
        componentsConsumed = spellComponentsModule.componentsConsumed;
        SPELL_COMPONENTS = spellComponentsModule.SPELL_COMPONENTS;


        // [SOURCES SYSTEM]: Initialize sources system after data loads
        initializeSourcesSystem();

        setGameDataLoaded(true);
      } catch (error) {
        console.error('❌ Failed to load game data:', error);
        // Still mark as loaded to prevent infinite loading screen
        setGameDataLoaded(true);
      }
    };

    loadGameData();
  }, []); // Run once on mount

  // [PARTY SYNC]: Persist partyId to localStorage
  useEffect(() => {
    if (partyId) {
      safeLocalStorageSet('partyId', partyId);
    } else {
      localStorage.removeItem('partyId');
    }
  }, [partyId]);

  // [PARTY SYNC]: Persist partyName to localStorage
  useEffect(() => {
    if (partyName) {
      safeLocalStorageSet('partyName', partyName);
    } else {
      localStorage.removeItem('partyName');
    }
  }, [partyName]);

  // [PHASE 4B - MIGRATED]: showStatus function now comes from UIContext (line 214)
  // [PHASE 4B - MIGRATED]: logActivity now comes from CharacterContext (line 453)
  // [REMOVED 11 lines]: Local logActivity function replaced by context action

  // Function to save roll results as parchment
  const saveRollAsParchment = useCallback((rollText, rollType = 'Roll Result') => {
    const parchmentItem = {
      id: `parchment_${Date.now()}`,
      name: `${rollType} - ${new Date().toLocaleDateString()}`,
      icon: '📜',
      category: 'miscellaneous',
      size: [1, 1],
      weight: 0.01,
      value: 0,
      description: rollText,
      parchmentContent: rollText,
      isParchment: true,
      position: null
    };

    setInventory(prev => [...prev, parchmentItem]);

    // [INTEGRATION 2025-10-24]: Emit INVENTORY_ITEM_ADDED for roll table result saved as parchment
    systemBus.emit(EVENTS.INVENTORY_ITEM_ADDED, {
      item: parchmentItem,
      quantity: 1,
      source: 'loot'
    });

    showStatus(`✅ ${rollType} saved as parchment`);
    logActivity(`Saved ${rollType} as parchment`, 'items', { content: rollText });
    return parchmentItem;
  }, [showStatus, logActivity]);

  // [PHASE 4B - MIGRATED]: currentGameDay now comes from CharacterContext (line 402)
  // [REMOVED]: const [currentGameDay, setCurrentGameDay] = useState(1);

  // Function to save roll results to journal
  const saveRollToJournal = useCallback((rollText, rollType = 'Roll Result') => {
    const journalEntry = {
      id: Date.now().toString(),
      title: `${rollType} - ${new Date().toLocaleString()}`,
      category: 'session',
      content: rollText,
      tags: ['roll', rollType.toLowerCase().replace(/\s+/g, '-')],
      date: new Date().toISOString(),
      gameDay: currentGameDay  // Faerûnian calendar integration
    };

    setJournalEntries(prev => [journalEntry, ...prev]);
    showStatus(`✅ ${rollType} added to journal`);
    logActivity(`Saved ${rollType} to journal`, 'character', { content: rollText });
    return journalEntry;
  }, [showStatus, logActivity, currentGameDay]);

  // [PHASE 4B - MIGRATED]: dragDataRef now comes from InventoryContext (line 333)
  // [REMOVED]: const dragDataRef = useRef({ item: null, source: null, isDragging: false });

  // [PHASE 4B - MIGRATED]: Character state now comes from CharacterContext (line 390)
  // [REMOVED]: const [skillProficiencies, setSkillProficiencies] = useState({});
  // [REMOVED]: const [timeSystem, setTimeSystem] = useState({ currentHour: 8, lastShortRest: null, lastLongRest: null, exhaustionLevel: 0, timeSinceLastRest: 0 });
  // [REMOVED]: const [calendarSettings, setCalendarSettings] = useState({ progressDayOnLongRest: true, showSeasonalEffects: true, startYear: 1492, showLunarPhases: true, trackHolidays: true });
  // [REMOVED 28 lines]: playerStats object content
  // [REMOVED 3 lines]: const [currencies, setCurrencies] = useState({ cp: 0, sp: 0, ep: 0, gp: 0, pp: 0 });
  // [REMOVED 26 lines]: customComponents useState with localStorage initialization
  // [REMOVED 3 lines]: customComponents localStorage save useEffect
  // [REMOVED 20 lines]: customComponents BASE_ITEMS integration useEffect

  // [FIX]: Stable refs for bastion effects to avoid re-render dependency issues
  const showStatusRef = useRef(showStatus);
  useEffect(() => { showStatusRef.current = showStatus; }, [showStatus]);

  // [CW-DELETE-AFFORDANCE — 2026-07-11]: shared red-confirm delete flow for
  // the trio Load drawers (monster/feat/condition/boon/background). `apply`
  // is one of the pure customContentDeleteOps transforms — the same functions
  // the guard tests drive. Confirm is mandatory: destructive, no undo exists.
  // Race/Class/Subclass/Spell deletes ride the storage-disconnect fix
  // (board CW-TRIO-STORAGE-DISCONNECT) — their content doesn't live in
  // playerStats slots.
  const deleteCustomContent = useCallback(async (entry, label, apply) => {
    const ok = await confirm({
      title: `Delete ${label}?`,
      message: `Delete "${entry.name}"? This can't be undone.`,
      confirmText: 'Delete',
      confirmColor: 'red',
    });
    if (!ok) return;
    setPlayerStats((prev) => apply(prev));
    logActivity(`Deleted custom ${label.toLowerCase()}: ${entry.name}`, 'custom-content', { id: entry.id });
    showStatus(`🗑️ Deleted ${label.toLowerCase()}: ${entry.name}`, 3000);
  }, [confirm, setPlayerStats, logActivity, showStatus]);

  // [CW-TRIO-SOURCES 2026-07-12]: sources-backed sibling of deleteCustomContent
  //   for the Race/Class/Subclass/Spell trios — their content lives in
  //   sourcesManager, so the remove targets the owning sourcebook and the
  //   re-render arrives via the SOURCE_CONTENT_CHANGED bus event, not
  //   setPlayerStats. Catalog deletes never touch a character's learned/used
  //   copies (the confirm copy says so).
  const deleteSourceContent = useCallback(async (entry, label, removeFromSource) => {
    if (!entry || !entry._source || !entry._key) return;
    const ok = await confirm({
      title: `Delete ${label}?`,
      message: `Delete "${entry.name}" from your sourcebook? Characters already using it keep their copies. This can't be undone.`,
      confirmText: 'Delete',
      confirmColor: 'red',
    });
    if (!ok) return;
    removeFromSource(entry._source, entry._key);
    logActivity(`Deleted custom ${label.toLowerCase()}: ${entry.name}`, 'custom-content', { key: entry._key, sourceId: entry._source });
    showStatus(`🗑️ Deleted ${label.toLowerCase()}: ${entry.name}`, 3000);
  }, [confirm, logActivity, showStatus]);

  // [CW-TRIO-SOURCES 2026-07-12]: legacy knownSpells-resident customs (created
  //   with "Save to Source" unchecked, or pre-dating it) have no sourcebook
  //   entry — deleting one removes it from this character's spellbook instead.
  const deleteLegacyKnownSpell = useCallback(async (entry) => {
    const ok = await confirm({
      title: 'Delete Spell?',
      message: `"${entry.name}" lives only in this character's spellbook. Delete it from the spellbook? This can't be undone.`,
      confirmText: 'Delete',
      confirmColor: 'red',
    });
    if (!ok) return;
    setKnownSpells((prev) => prev.filter((s) => s.id !== entry.id));
    logActivity(`Deleted custom spell: ${entry.name}`, 'custom-content', { id: entry.id });
    showStatus(`🗑️ Deleted spell: ${entry.name}`, 3000);
  }, [confirm, setKnownSpells, logActivity, showStatus]);

  // [DELETED MAY 2026]: Bastion auto-unlock + auto-process duplicates removed.
  //   These duplicated the canonical handlers in CharacterContext.js (D2.7 / Apr
  //   2026 refactor) and caused double-firing on calendar advances + double
  //   activity-log entries. The CharacterContext copies are the single source of
  //   truth. See SMOKE_TEST_TRIAGE_2026-05-04.md §P1-1 for the incident.
  //
  //   The deleted auto-process body included an ordersCompleted.forEach that
  //   created bastion-crafted inventory items. That logic now lives in the
  //   BASTION_ORDER_COMPLETED subscriber below — CharacterContext's bastion
  //   handler emits the event per order; this subscriber creates the item.

  // [FIX MAY 2026]: Bastion-crafted inventory item creation. CharacterContext
  //   emits BASTION_ORDER_COMPLETED per order from its DAY_ADVANCED subscriber;
  //   we create the inventory item here because App.jsx has setInventory access
  //   while CharacterContext doesn't. Mirrors the deleted duplicate's behaviour.
  useEffect(() => {
    const unsub = systemBus.on(EVENTS.BASTION_ORDER_COMPLETED, ({ order, gameDay }) => {
      const reward = order?.result;
      if (!reward?.createItem || !reward?.itemData) return;
      const { itemKey, category, displayName } = reward.itemData;
      if (!BASE_ITEMS || !BASE_ITEMS[category] || !BASE_ITEMS[category][itemKey]) {
        console.warn(`⚠️ Bastion item creation failed: ${itemKey} not found in BASE_ITEMS[${category}]`);
        return;
      }
      const baseItem = BASE_ITEMS[category][itemKey];
      const newItem = {
        id: `${itemKey}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        name: displayName,
        icon: baseItem.icon,
        category: category === 'weapons' ? 'weapon' : category === 'armor' ? 'armor' : 'miscellaneous',
        size: baseItem.size,
        weight: baseItem.weight,
        value: baseItem.baseValue,
        position: null,
        ...baseItem,
        bastionCrafted: true,
        craftedDay: gameDay,
        craftedBy: 'Bastion Order',
      };
      setInventory((prev) => [...prev, newItem]);
      systemBus.emit(EVENTS.INVENTORY_ITEM_ADDED, { item: newItem, quantity: 1, source: 'craft' });
      logActivity(`Bastion crafted: ${displayName}`, 'bastion', {
        itemName: displayName,
        facilityId: order.facilityId,
        orderType: order.order,
        value: baseItem.baseValue,
      });
    });
    return unsub;
  }, [setInventory, logActivity]);

  // [PHASE 4B - MIGRATED]: settings now comes from SettingsContext (line 202)
  // [REMOVED]: 70 lines of settings useState initialization
  // [WHY]: Centralized in SettingsContext for better separation of concerns

  // [PHASE 4B - MIGRATED]: hasUnsavedChanges, lastAutosave, getRelativeTime now in UIContext (line 214)

  // [PHASE 4B - MIGRATED]: Character wizard state now in CharacterContext (line 390)
  // [REMOVED]: const [showConfetti, setShowConfetti] = useState(false);
  // [REMOVED]: const [levelUpLevel, setLevelUpLevel] = useState(null);

  // [PHASE 4B - MIGRATED]: getRelativeTime and relativeTimeUpdate now in UIContext (line 214)

  // Save settings to localStorage whenever they change
  useEffect(() => {
    safeLocalStorageSet('tabletopInventory_settings', JSON.stringify(settings));
  }, [settings]);

  // [PHASE 4B - MIGRATED]: showItemDialog, showVaultDialog now in InventoryContext (line 311)
  // [REMOVED]: const [showItemDialog, setShowItemDialog] = useState(false);
  // [REMOVED]: const [showVaultDialog, setShowVaultDialog] = useState(false);
  const [showSharedVault, setShowSharedVault] = useState(false); // [NEW]: Party shared vault
  const [showMonsterEditor, setShowMonsterEditor] = useState(false); // [SPEC D 2026-05-17]: Combat Tab → Monster Editor modal
  // [STONE 9 / #62 + #81 — 2026-05-18]: View+Edit+Template trio state.
  //   - editingMonster: passed to MonsterEditor as initialMonster
  //     (null = create new; populated = edit existing / fork)
  //   - viewingMonster: drives MonsterViewModal read-only sheet
  //   - loadMonsterDrawerOpen / viewMonsterDrawerOpen /
  //     templateMonsterDrawerOpen: drive MonsterTrioDrawers visibility
  const [editingMonster, setEditingMonster] = useState(null);
  const [viewingMonster, setViewingMonster] = useState(null);
  const [loadMonsterDrawerOpen, setLoadMonsterDrawerOpen] = useState(false);
  const [viewMonsterDrawerOpen, setViewMonsterDrawerOpen] = useState(false);
  const [templateMonsterDrawerOpen, setTemplateMonsterDrawerOpen] = useState(false);
  // [SUB-PLAN 1E — 2026-05-24]: Trio drawer + view modal state for Items, Feats, Spells.
  const [viewingItem, setViewingItem] = useState(null);
  const [loadItemDrawerOpen, setLoadItemDrawerOpen] = useState(false);
  const [viewItemDrawerOpen, setViewItemDrawerOpen] = useState(false);
  const [templateItemDrawerOpen, setTemplateItemDrawerOpen] = useState(false);
  const [viewingFeat, setViewingFeat] = useState(null);
  const [loadFeatDrawerOpen, setLoadFeatDrawerOpen] = useState(false);
  const [viewFeatDrawerOpen, setViewFeatDrawerOpen] = useState(false);
  const [templateFeatDrawerOpen, setTemplateFeatDrawerOpen] = useState(false);
  const [viewingSpell, setViewingSpell] = useState(null);
  const [loadSpellDrawerOpen, setLoadSpellDrawerOpen] = useState(false);
  const [viewSpellDrawerOpen, setViewSpellDrawerOpen] = useState(false);
  const [templateSpellDrawerOpen, setTemplateSpellDrawerOpen] = useState(false);
  // [STONE 6 / #70 — 2026-05-18]: Custom Item Creator modal. Distinct from
  //   editingItem (used by EditItemDialog for per-instance inventory edits).
  // [2026-05-25 items 6+7 refactor]: showCustomItemCreator + editingCatalogItem
  // states removed alongside the deleted CustomItemCreator parallel. The
  // Workshop "Create Item" button now opens ItemDialog (setShowItemDialog).
  // [STONE 6 CLOSURE / #67 Option B — 2026-05-18]: Custom Feat Creator modal.
  //   Persists to playerStats.customFeats via the onSave callback below.
  const [showCustomFeatCreator, setShowCustomFeatCreator] = useState(false);
  const [showCustomConditionCreator, setShowCustomConditionCreator] = useState(false); // [Sub-plan 3B — 2026-05-29]
  const [editingCustomCondition, setEditingCustomCondition] = useState(null); // [Sub-plan 3B — 2026-05-29]: edit-mode hydration
  const [showCustomBoonCreator, setShowCustomBoonCreator] = useState(false); // [Sub-plan 3A — 2026-05-30]
  const [editingCustomBoon, setEditingCustomBoon] = useState(null); // [Sub-plan 3A — 2026-05-30]: edit-mode hydration
  const [showCustomBackgroundCreator, setShowCustomBackgroundCreator] = useState(false); // [Sub-plan 3C — 2026-05-30]
  const [editingCustomBackground, setEditingCustomBackground] = useState(null); // [Sub-plan 3C — 2026-05-30]: edit-mode hydration
  const [editingCustomFeat, setEditingCustomFeat] = useState(null);
  // [2026-05-25 refactor]: itemDialogMode toggles ItemDialog between
  // 'create' (Workshop's Create Item — full edit fields) and 'find'
  // (Inventory's Add Item — edit fields hidden, picker + qty + Add only).
  // Default 'create' so direct setShowItemDialog(true) callers (legacy)
  // get the full authoring surface.
  const [itemDialogMode, setItemDialogMode] = useState('create');
  // [ROSTER GALLERY]: ID of character being renamed (null when dialog closed)
  const [renameCharacterId, setRenameCharacterId] = useState(null);
  // [2026-05-11]: Roster drawer state — slide-out alternative to the Roster tab
  const [rosterDrawerOpen, setRosterDrawerOpen] = useState(false);
  // [REMOVED]: const [inspirationPrompt, setInspirationPrompt] = useState(null);
  // [PHASE 4B - MIGRATED]: activeTab now in UIContext (line 214)
  // [REMOVED]: const [contextMenu, setContextMenu] = useState(null);
  // [PHASE 4B - MIGRATED]: Shop dialogs (generatedShop, activeShops, isAddingToShop, selectedShopIndex, showCustomShopCreator, showCustomTableCreator) now in UIContext (line 214)
  // [PHASE 4C - SESSION 7 - MIGRATED]: Shop state (shopSettings, customShopTypes, showShopPurchase) now in UIContext (line 85-95, 81)
  const [customRollTables, setCustomRollTables] = useState([]); // Keep for backward compatibility with shops

  // [PHASE 4C - NOV 1, 2025 - MIGRATED]: Roll tables state now in CharacterContext (line 126)
  // [REMOVED]: const [rollTables, setRollTables] = useState([]);
  // [REMOVED]: const [playerTables, setPlayerTables] = useState([]); // Unused

  // [PHASE 4C - TEMPORARY]: Keep tableCategories until RollTablesTab is extracted
  const tableCategories = ['encounters', 'loot', 'events', 'shops', 'npcs', 'custom'];

  // [PHASE 4B - MIGRATED]: Roll table dialogs (showTableCreator, editingTable, selectedTableCategory) now in UIContext (line 214)
  // [PHASE 4B - MIGRATED]: Table creator state (tableType, weightedEntries) now in UIContext (line 214)

  // [PHASE 4B - MIGRATED]: Spell state now comes from CharacterContext (line 390)
  // [REMOVED]: const [knownSpells, setKnownSpells] = useState([]);
  // [REMOVED]: const [preparedSpells, setPreparedSpells] = useState([]);
  // [REMOVED]: const [customSpells, setCustomSpells] = useState([]);
  // [REMOVED]: const [manualSpellSlots, setManualSpellSlots] = useState(false);
  // [REMOVED 11 lines]: const [spellSlots, setSpellSlots] = useState({ 1-9: { max: 0, used: 0 } });
  // [B16]: Migration metadata — captures unknown top-level keys from loaded/imported
  // character data so v1.x reserved fields survive round-trip through v1 saves.
  // Set by loadCharacter and importInventory after migrate(); spread back to top
  // level by buildCurrentCharacterData on save (NOT serialized as _unknownFields itself).
  // Spec: docs/superpowers/specs/2026-04-28-r1-release-readiness-design.md §B16.
  const [unknownFields, setUnknownFields] = useState({});

  const [lastSpellRoll, setLastSpellRoll] = useState(null);
  const [selectedAllyTarget, setSelectedAllyTarget] = useState(null); // [NEW]: For casting spells on allies
  const [spellAttackDC, setSpellAttackDC] = useState(''); // [PHASE 4B STEP 7 SPELL ATTACKS]: Optional DC for inspiration checks on spell attack rolls
  const [castAsRitual, setCastAsRitual] = useState(false); // [NEW NOV 13, 2025]: Ritual casting option (PHB p.201)
  // [REMOVED]: const [activeSpellbookClass, setActiveSpellbookClass] = useState('all');
  // [REMOVED 8 lines]: collapsedSpellLevels useState with localStorage
  // [REMOVED 8 lines]: collapsedSpellLevels localStorage save useEffect

  // [NEW 2025-10-10]: Quick menu draggable position state with localStorage persistence
  // [PURPOSE]: Track and persist the position of the draggable quick menu button
  // [WHY]: Users can position the quick menu anywhere on screen, preference should persist
  const [quickMenuPosition, setQuickMenuPosition] = useState(() => {
    try {
      const saved = localStorage.getItem('quickMenuPosition');
      return saved ? JSON.parse(saved) : null; // null = use default (right center)
    } catch {
      return null;
    }
  });

  // [PURPOSE]: Save quick menu position to localStorage when changed
  // [WHY]: Persist user's position preference across sessions
  useEffect(() => {
    if (quickMenuPosition) {
      try {
        safeLocalStorageSet('quickMenuPosition', JSON.stringify(quickMenuPosition));
      } catch (error) {
        console.error('Failed to save quick menu position:', error);
      }
    }
  }, [quickMenuPosition]);

  // [PHASE 4B - MIGRATED]: Activity and journal state now in CharacterContext (line 390)
  // [REMOVED]: const [activityLog, setActivityLog] = useState([]);
  // [REMOVED]: const [expandedLogEntries, setExpandedLogEntries] = useState(new Set()); // Actually in UIContext
  // [REMOVED]: const [journalEntries, setJournalEntries] = useState([]);
  // [REMOVED]: const [craftingProjects, setCraftingProjects] = useState([]);
  // [REMOVED]: const [dmEvents, setDmEvents] = useState([]);

  // [PHASE 4C - NOV 1, 2025 - MIGRATED]: Journal UI state now in UIContext (lines 173, 180)
  // [REMOVED]: const [journalRollTables, setJournalRollTables] = useState([]);
  // [REMOVED]: const [selectedRollTable, setSelectedRollTable] = useState(null);
  // [REMOVED]: const [journalFilter, setJournalFilter] = useState({ category: 'all', tags: '', sortBy: 'newest' });

  // [PHASE 4B - MIGRATED]: Wizard state now in CharacterContext (line 390)
  // [REMOVED]: const [showCharacterWizard, setShowCharacterWizard] = useState(false);
  // [REMOVED]: const [showLevelUpWizard, setShowLevelUpWizard] = useState(false);
  // [REMOVED]: const [showKeyboardHelp, setShowKeyboardHelp] = useState(false);

  // [PARTY SYNC]: Enhanced real-time party synchronization hook
  const {
    partyMembers,
    partyActivityLog,
    isConnected,
    syncError,
    // [NEW]: DM Features
    isDM,
    claimDM,  // [FIX JAN 2026 - Issue #20]: Claim/release DM status
    canPushContent,
    partyMetadata,  // [FIX JAN 13, 2026]: For DM badge display
    // [NEW]: Shared Vault
    sharedVaultItems,
    addToSharedVault,
    takeFromSharedVault,
    removeFromSharedVault,
    // [NEW]: DM Push Content
    pushShopToParty,
    pushNoteToParty,
    pushCalendarEventToParty,
    pushLootRollToParty,
    claimLootItem,
    markPushedContentViewed, // [B1-B3 FIX]: Acknowledge notes/events/shops
    pushedContent,
    // [NEW]: Spell Effects on Allies
    applyEffectToAlly,
    healAllyInParty,  // [C2 FIX]: Update ally HP display after healing spells
    removeEffectFromAlly,
    activeEffects,
    allPartyEffects
  } = useEnhancedPartySync(partyId, playerStats.id, playerStats, activityLog);

  // [A32-followup-party-shop-events]: feature-usage analytics — fire once per tab entry.
  // [WHY]: Records the user's "party size / shop / bastion usage" ask without hooking the
  //   live-Firebase join event (A33-class, not headless-testable). Party size is read at
  //   view-time from the loaded member map. Keyed on activeTab only so it fires once per
  //   transition (NOT on every party-sync member update) — partySize is read inside the
  //   effect at fire-time, intentionally excluded from deps to avoid over-counting.
  // [INTEGRATION]: buildTabViewEvent is the pure tab→event mapper in utils/analyticsEvents.
  useEffect(() => {
    const partySize = Object.keys(partyMembers || {}).length;
    trackFeatureEvent(buildTabViewEvent(activeTab, { partySize }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // [PURPOSE]: Integrate party spell effects with quick bonus system
  // [WHY]: When allies cast spells on you, they should appear as active bonuses
  // [INTEGRATION]: Converts Firebase activeEffects to quick bonus format
  useEffect(() => {
    // If no active effects, clear party bonuses
    if (!activeEffects || activeEffects.length === 0) {
      setActiveQuickBonuses(prev => prev.filter(b => b.source !== 'party'));
      return;
    }

    // [FIX JAN 13, 2026]: Static mapping instead of string matching (ARC-014)
    // [PURPOSE]: Defines how party buff spells translate to quick bonuses
    // [WHY]: Cleaner than string matching, documents supported spells explicitly
    const PARTY_SPELL_EFFECTS = {
      'Bless': { bonus: '1d4', affects: 'Attack rolls, saving throws', type: 'advantage' },
      'Guidance': { bonus: '1d4', affects: ['ability_checks'], type: 'bonus' },
      'Shield of Faith': { bonus: '+2', affects: 'AC', type: 'bonus' },
      'Enhance Ability': { affects: ['ability_checks'], type: 'advantage' },
      'Heroism': { affects: 'Immunity to frightened', type: 'condition' },
      'Haste': { bonus: '+2', affects: 'AC, DEX saves', type: 'advantage' },
      'Aid': { affects: 'Max HP increase', type: 'buff' },
      'Resistance': { bonus: '1d4', affects: ['saving_throws'], type: 'bonus' },
      'Protection from Evil and Good': { affects: 'Protection vs aberrations/celestials/etc', type: 'condition' },
      'Barkskin': { bonus: '16', affects: 'AC minimum', type: 'buff' },
      'Protection from Energy': { affects: 'Resistance to chosen damage type', type: 'condition' },
      'Stoneskin': { affects: 'Resistance to nonmagical B/P/S', type: 'condition' }
    };

    // Convert party effects to quick bonus format
    const partyBonuses = activeEffects.map(effect => {
      const effectData = effect.data || {};
      const spellName = effectData.spell || '';

      // Look up spell effects from mapping (case-insensitive match)
      const spellEffect = Object.entries(PARTY_SPELL_EFFECTS).find(
        ([name]) => spellName.toLowerCase() === name.toLowerCase()
      )?.[1] || {};

      return {
        name: `${effectData.spell || 'Party Effect'} (from ${effect.casterName || 'Ally'})`,
        bonus: spellEffect.bonus || '+0',
        affects: spellEffect.affects || 'Special',
        type: spellEffect.type || 'bonus',
        source: 'party',
        effectId: effect.id,
        duration: effect.duration,
        caster: effect.casterName
      };
    });

    // Merge party bonuses with local quick bonuses (avoid duplicates)
    setActiveQuickBonuses(prev => {
      // Remove old party bonuses
      const nonPartyBonuses = prev.filter(b => b.source !== 'party');
      // Add new party bonuses
      return [...nonPartyBonuses, ...partyBonuses];
    });
  }, [activeEffects]);

  // [PHASE 4C - MIGRATED]: logFilters, logSearchTerm now in UIContext (line 124, 133)
  // [REMOVED]: const [logFilters, setLogFilters] = useState({...});
  // [REMOVED]: const [logSearchTerm, setLogSearchTerm] = useState('');
  
  // Grid dimensions - Dynamic based on strength or custom
  const calculateStrengthGrid = (strength) => {
    // [UX-4 - NOV 15, 2025]: Fixed grid scaling to be proportional to STR
    // [USER REPORT]: "Why do I get less cells for having more str?" (New Prompts line 25)
    // [WHY]: Old formula used √ which caused diminishing returns - high STR got fewer cells per lb
    // [FIX]: Linear scaling - cells per lb stays constant regardless of STR

    // D&D 5e: STR × 15 lbs capacity (this is correct and unchanged)
    const carryCapacity = strength * 15;

    // More generous scaling to handle light items (potions 0.3 lbs, scrolls, etc.)
    // 1 lb capacity = 0.8 cells (generous - was 0.5 which penalized high STR)
    const totalCells = Math.floor(carryCapacity * 0.8);

    // Calculate square grid size
    const gridSize = Math.ceil(Math.sqrt(totalCells));

    // Clamp between 5×5 and 25×25 (increased max from 20 for high STR characters)
    return Math.max(5, Math.min(25, gridSize));

    // New results (cells per lb now CONSTANT at ~0.8):
    // STR 10: 150 lbs → 120 cells → 11×11 grid (0.80 cells/lb) [was 9×9, 0.54 cells/lb]
    // STR 15: 225 lbs → 180 cells → 14×14 grid (0.80 cells/lb) [was 11×11, 0.54 cells/lb]
    // STR 18: 270 lbs → 216 cells → 15×15 grid (0.80 cells/lb) [was 12×12, 0.53 cells/lb]
    // STR 25: 375 lbs → 300 cells → 18×18 grid (0.80 cells/lb) [was 14×14, 0.52 cells/lb]
  };

  // [TDZ BUG FIX - NOV 17, 2025]: Move effectiveStrength BEFORE getGridDimensions
  // [WHY]: getGridDimensions() uses effectiveStrength, must be defined first
  // [CAUSE]: TDZ error - const accessed before initialization
  // [INTEGRATION-4 - NOV 15, 2025]: Calculate effective strength for grid sizing and displays
  // [PURPOSE]: Grid size should use effective STR (including Belt of Giant Strength, etc.)
  // [WHY]: Manual calculations were using base STR, ignoring item effects
  const effectiveStrength = useMemo(() => {
    return effectCalculator.getEffectiveAbilityScore(
      playerStats,
      inventory,
      settings.activeQuickBonuses || [],
      'strength'
    );
  }, [playerStats, inventory, settings.activeQuickBonuses]);

  const getGridDimensions = () => {
    if (!playerStats.useBagLimit) {
      return [20, 20]; // Unlimited mode
    }

    if (settings.useCustomGrid) {
      return playerStats.bagSize; // Custom grid size
    }

    // [INTEGRATION-4 - NOV 15, 2025]: Use effective strength for grid sizing
    // [FIX]: Belt of Giant Strength and similar items now affect grid size
    const size = calculateStrengthGrid(effectiveStrength);
    return [size, size];
  };

  const [gridCols, gridRows] = getGridDimensions();
  const vaultGridSize = [8, 8];

  // ========================================
  // WEIGHT CALCULATIONS
  // ========================================

  const totalWeight = useMemo(() => {
    const itemWeight = inventory.reduce((sum, item) => {
      const itemWithMethods = Object.assign(Object.create(InventoryItem.prototype), item);
      return sum + itemWithMethods.getTotalWeight();
    }, 0);

    // [FIX NOV 4 SESSION 7]: Add null coalescing for currencies
    const currencyWeight = settings.currencyHasWeight ?
      Object.entries(currencies || {}).reduce((sum, [type, amount]) => {
        return sum + (amount * 0.02);
      }, 0) : 0;
    const total = itemWeight + currencyWeight;
    // [FIX APR 2026]: Defensive guard — NaN must never propagate to display
    return isNaN(total) ? 0 : total;
  }, [inventory, currencies, settings.currencyHasWeight]);

  // [PHASE 4.4]: Derive carry capacity instead of storing it
  // [FIX NOV 13, 2025]: Extract .capacity from object to avoid React render error
  // [PURPOSE]: Ensure we return a number, not an object, to prevent React rendering errors
  const carryCapacity = useMemo(() => {
    const result = effectCalculator.calculateCarryCapacity(
      playerStats,
      inventory,
      settings.activeQuickBonuses || []
    );
    // [NULL SAFETY]: Always return a valid number, default to base strength * 15
    return result?.capacity ?? ((playerStats.strength || 10) * 15);
  }, [playerStats, inventory, settings.activeQuickBonuses]);

  const encumbranceLevel = useMemo(() => {
    if (!playerStats.useWeightLimit) return 'none';
    const ratio = totalWeight / carryCapacity;
    if (ratio <= 0.33) return 'light';
    if (ratio <= 0.66) return 'medium';
    if (ratio <= 1) return 'heavy';
    return 'encumbered';
  }, [totalWeight, carryCapacity, playerStats.useWeightLimit]);

  // [PERFORMANCE CRITICAL]: Memoize sorted/filtered inventory to prevent re-computation on every render
  const sortedAndFilteredInventory = useMemo(() => {
    let filteredItems = [...inventory];

    // Apply search filter
    if (searchQuery) {
      filteredItems = filteredItems.filter(item =>
        (item.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (item.description && item.description.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    // Apply type filter
    if (filterType !== 'all') {
      filteredItems = filteredItems.filter(item => {
        const category = item.category || 'misc';
        switch(filterType) {
          case 'weapons': return category === 'weapons';
          case 'armor': return category === 'armor';
          // [BATCH 11 / P1 A-15 — APR 2026]: Property-based consumable
          //   detection. CW-authored consumables can stamp `consumable: true`
          //   regardless of category; the SRD strings remain as fallback for
          //   gameData rows that don't carry the property. Rule 11.
          case 'consumables': return item.consumable === true || category === 'consumables' || category === 'potions';
          case 'containers': return item.isContainer;
          case 'misc': return category === 'adventuringGear' || category === 'tools' || category === 'misc';
          default: return true;
        }
      });
    }

    // Apply sorting
    filteredItems.sort((a, b) => {
      let comparison = 0;
      switch(sortBy) {
        case 'name':
          // [FIX JUL 14, 2026 — L7 fleet catch]: never let a nameless item kill the
          // render. A crafted item built outside the canonical InventoryItem class
          // (DowntimesPanel handleCompleteActivity, now fixed) arrived with
          // name: undefined; since sortBy defaults to 'name' (InventoryContext.js:111)
          // this comparator threw on the FIRST render after crafting and took MainApp
          // down through the ErrorBoundary. The producer is fixed; this guard means no
          // future nameless-item source can reach a whole-app crash. Mirrors the
          // 'type' case below, which has always defaulted with `|| 'misc'`.
          comparison = (a.name || '').localeCompare(b.name || '');
          break;
        case 'weight':
          comparison = (a.weight || 0) - (b.weight || 0);
          break;
        case 'value':
          comparison = (a.value || 0) - (b.value || 0);
          break;
        case 'type':
          comparison = (a.category || 'misc').localeCompare(b.category || 'misc');
          break;
        default:
          comparison = 0;
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return filteredItems;
  }, [inventory, searchQuery, filterType, sortBy, sortOrder]);

  // [PERFORMANCE]: Memoize container items list
  const containerItems = useMemo(() =>
    inventory.filter(i => i.isContainer),
    [inventory]
  );

  // [PERFORMANCE]: Memoize valuable items count
  const valuableItemsCount = useMemo(() =>
    inventory.filter(item => item.value > 0).length,
    [inventory]
  );

  // ========================================
  // GRID VALIDATION
  // ========================================

  // [APR18-FIX — ASTRAL RUPTURE]: Guard ref so the confirmation dialog doesn't fire
  // multiple times for the same item pair during the same drag interaction.
  const rupturePromptedRef = useRef(null);

  // [APR18-FIX — ASTRAL RUPTURE]: Handle the Bag-of-Holding-in-Bag-of-Holding Easter egg.
  // Per DMG p.154: "Placing a bag of holding inside an extradimensional space ... instantly
  // destroys both items and opens a gate to the Astral Plane." Simplified for TTI:
  //   - Confirmation prompt (lets player cancel if they misclicked)
  //   - 4d6 force damage on confirm
  //   - Both items deleted (contents lost to the Astral Plane)
  //   - Activity log entry + celebratory toast
  const triggerExtradimensionalRupture = async (sourceItem, targetContainer) => {
    const confirmed = await confirm({
      title: '💥 Extradimensional rupture?',
      message: `Placing ${sourceItem.name} inside ${targetContainer.name} will tear a hole to the Astral Plane. Both items and all their contents will be lost, and you'll take 4d6 force damage. Are you sure?`,
      confirmText: 'Do it anyway',
      confirmColor: 'red',
      cancelText: 'On second thought...'
    });
    if (!confirmed) return;

    // Roll 4d6 force damage
    let damage = 0;
    for (let i = 0; i < 4; i++) damage += 1 + Math.floor(Math.random() * 6);

    // Apply damage via CharacterContext
    const currentHP = playerStats?.currentHitPoints ?? 0;
    updateHP(currentHP - damage);

    // Delete both items from inventory (and anything they contained)
    setInventory(prev => prev.filter(i => i.id !== sourceItem.id && i.id !== targetContainer.id));

    // Log + toast — flavor matters for an Easter egg
    logActivity(
      `💥 Extradimensional rupture! ${sourceItem.name} + ${targetContainer.name} destroyed. Both lost to the Astral Plane. Took ${damage} force damage.`,
      'combat',
      { damage, source: 'astral_plane_rupture', items: [sourceItem.name, targetContainer.name] }
    );
    showStatus(`💥 The Astral Plane said thanks for the free stuff. (${damage} force damage)`, 6000);
  };

  const isValidPosition = (item, x, y, targetGrid, excludeItemId = null) => {
    let items, cols, rows;
    
    // Note: Containers CAN be placed in vaults, but cannot be opened while there
    // This is handled in the UI when trying to open them
    
    // Check container restrictions
    if (targetGrid.startsWith('container_')) {
      const targetContainerId = targetGrid.replace('container_', '');
      
      // Prevent containers from being placed inside themselves
      if (item.id === targetContainerId) {
        return false;
      }
      
      // Check if we're trying to put a container inside another container
      if (item.isContainer && !settings.allowContainersInContainers) {
        // Enhanced error message with guidance - longer duration for important constraints
        showStatus("🚫 Container-in-Container Blocked: This feature is disabled. Go to Character tab → Settings → 'Allow containers inside containers' to enable.", 6000);
        
        return false;
      }
      
      // If containers in containers is allowed, check size hierarchy
      if (item.isContainer && settings.allowContainersInContainers) {
        const targetContainer = inventory.find(i => i.id === targetContainerId);

        // [APR18-FIX — ASTRAL RUPTURE]: Bag of Holding + Bag of Holding / Portable Hole +
        //   Portable Hole / any two extradimensional spaces = DMG p.154 rupture.
        //   Instead of silently blocking, trigger the Easter egg.
        if (targetContainer && item.extradimensional && targetContainer.extradimensional) {
          const pairKey = `${item.id}->${targetContainer.id}`;
          if (rupturePromptedRef.current !== pairKey) {
            rupturePromptedRef.current = pairKey;
            // Run async flow (confirm → damage → delete). Fire-and-forget; block the drop.
            triggerExtradimensionalRupture(item, targetContainer).finally(() => {
              // Clear the guard shortly after so future attempts work
              setTimeout(() => { rupturePromptedRef.current = null; }, 1000);
            });
          }
          return false;
        }

        if (targetContainer && targetContainer.containerSize && item.containerSize) {
          const targetSizeLevel = CONTAINER_SIZE_HIERARCHY[targetContainer.containerSize] || 2;
          const itemSizeLevel = CONTAINER_SIZE_HIERARCHY[item.containerSize] || 2;

          if (itemSizeLevel >= targetSizeLevel) {
            showStatus(`Cannot place a ${item.containerSize} container inside a ${targetContainer.containerSize} container.`);
            return false;
          }
        }
      }
    }
    
    if (targetGrid === 'inventory') {
      items = inventory;
      cols = gridCols;
      rows = gridRows;
    } else if (targetGrid.startsWith('container_')) {
      const container = inventory.find(i => i.id === targetGrid.replace('container_', ''));
      if (!container) return false;
      items = container.containedItems || [];
      cols = container.containerSlots?.[0] || 6;
      rows = container.containerSlots?.[1] || 8;
    } else {
      const vault = vaults.find(v => v.id === targetGrid);
      if (!vault) return false;
      items = vault.items || [];
      cols = (vault.gridSize && vault.gridSize[0]) || vaultGridSize[0];
      rows = (vault.gridSize && vault.gridSize[1]) || vaultGridSize[1];
    }
    
    if (x < 0 || y < 0 || x + item.size[0] > cols || y + item.size[1] > rows) {
      return false;
    }
    
    for (let i = x; i < x + item.size[0]; i++) {
      for (let j = y; j < y + item.size[1]; j++) {
        const occupied = items.some(invItem => {
          if (excludeItemId && invItem.id === excludeItemId) return false;
          if (!invItem.position) return false;
          
          const [itemX, itemY] = invItem.position;
          return i >= itemX && i < itemX + invItem.size[0] &&
                 j >= itemY && j < itemY + invItem.size[1];
        });
        
        if (occupied) return false;
      }
    }
    
    return true;
  };
  
  // ========================================
  // MOUSE-BASED DRAG SYSTEM
  // ========================================
  
  const getGridCellFromPoint = (x, y) => {
    const grids = document.querySelectorAll('[data-grid-container]');
    
    for (const grid of grids) {
      const rect = grid.getBoundingClientRect();
      // Add 20px margin around grid for more forgiving drops
      const margin = 20;
      if (x >= rect.left - margin && x <= rect.right + margin && 
          y >= rect.top - margin && y <= rect.bottom + margin) {
        // Get the actual padding from computed styles
        const computedStyle = window.getComputedStyle(grid);
        const paddingLeft = parseFloat(computedStyle.paddingLeft) || 8;
        const paddingTop = parseFloat(computedStyle.paddingTop) || 8;
        
        const relativeX = x - rect.left - paddingLeft;
        const relativeY = y - rect.top - paddingTop;

        // [FIX]: Cells are 48px + 2px borders = 50px total per cell
        const cellSize = 50; // 48px content + 1px border on each side

        // Clamp to grid bounds instead of rejecting
        const cellX = Math.max(0, Math.min(Math.floor(relativeX / cellSize), parseInt(grid.dataset.gridCols) - 1));
        const cellY = Math.max(0, Math.min(Math.floor(relativeY / cellSize), parseInt(grid.dataset.gridRows) - 1));
        
        const gridId = grid.dataset.gridId;
        const gridCols = parseInt(grid.dataset.gridCols);
        const gridRows = parseInt(grid.dataset.gridRows);
        
        // Always return a valid cell if we're near the grid
        if (cellX >= 0 && cellX < gridCols && cellY >= 0 && cellY < gridRows) {
          return { gridId, x: cellX, y: cellY };
        }
      }
    }
    
    return null;
  };
  
  // SIMPLE move function that ACTUALLY WORKS
  const moveItemSimple = (item, sourceGrid, x, y, targetGrid) => {
    
    // Check if position is valid
    if (!isValidPosition(item, x, y, targetGrid, item.id)) {
      return false;
    }
    
    // Handle different grid combinations
    if (sourceGrid === 'inventory' && targetGrid === 'inventory') {
      // Moving within inventory
      const oldPosition = item.position || [0, 0];
      setInventory(prev => prev.map(invItem =>
        invItem.id === item.id
          ? { ...invItem, position: [x, y] }
          : invItem
      ));

      // [INTEGRATION 2025-10-24]: Emit INVENTORY_ITEM_MOVED for position change within inventory
      systemBus.emit(EVENTS.INVENTORY_ITEM_MOVED, {
        item: { ...item, position: [x, y] },
        oldPosition: oldPosition,
        newPosition: [x, y],
        source: 'inventory',
        target: 'inventory'
      });

      return true;
    } else if (sourceGrid.startsWith('container_') && targetGrid === 'inventory') {
      // Moving from container to inventory
      const containerId = sourceGrid.replace('container_', '');
      
      // Remove from container
      setInventory(prev => prev.map(invItem => {
        if (invItem.id === containerId) {
          return {
            ...invItem,
            containedItems: (invItem.containedItems || []).filter(cItem => cItem.id !== item.id)
          };
        }
        return invItem;
      }));
      
      // Add to inventory
      const movedItem = { ...item, position: [x, y] };
      setInventory(prev => [...prev, movedItem]);

      // [INTEGRATION 2025-10-24]: Emit INVENTORY_ITEM_MOVED for container to inventory
      systemBus.emit(EVENTS.INVENTORY_ITEM_MOVED, {
        item: movedItem,
        oldPosition: item.position || null,
        newPosition: [x, y],
        source: `container_${containerId}`,
        target: 'inventory'
      });

      return true;
    } else if (sourceGrid === 'inventory' && targetGrid.startsWith('container_')) {
      // Moving from inventory to container
      const containerId = targetGrid.replace('container_', '');
      
      // Remove from inventory
      setInventory(prev => prev.filter(invItem => invItem.id !== item.id));
      
      // Add to container
      const movedItem = { ...item, position: [x, y] };
      setInventory(prev => prev.map(invItem => {
        if (invItem.id === containerId) {
          return {
            ...invItem,
            containedItems: [...(invItem.containedItems || []), movedItem]
          };
        }
        return invItem;
      }));

      // [INTEGRATION 2025-10-24]: Emit INVENTORY_ITEM_MOVED for inventory to container
      systemBus.emit(EVENTS.INVENTORY_ITEM_MOVED, {
        item: movedItem,
        oldPosition: item.position || null,
        newPosition: [x, y],
        source: 'inventory',
        target: `container_${containerId}`
      });

      return true;
    } else if (sourceGrid.startsWith('container_') && targetGrid.startsWith('container_')) {
      // Moving within the same container or between containers
      const sourceContainerId = sourceGrid.replace('container_', '');
      const targetContainerId = targetGrid.replace('container_', '');
      
      if (sourceContainerId === targetContainerId) {
        // Moving within the same container
        const oldPosition = item.position || [0, 0];
        setInventory(prev => prev.map(invItem => {
          if (invItem.id === sourceContainerId) {
            return {
              ...invItem,
              containedItems: (invItem.containedItems || []).map(cItem =>
                cItem.id === item.id
                  ? { ...cItem, position: [x, y] }
                  : cItem
              )
            };
          }
          return invItem;
        }));

        // [INTEGRATION 2025-10-24]: Emit INVENTORY_ITEM_MOVED for within same container
        systemBus.emit(EVENTS.INVENTORY_ITEM_MOVED, {
          item: { ...item, position: [x, y] },
          oldPosition: oldPosition,
          newPosition: [x, y],
          source: `container_${sourceContainerId}`,
          target: `container_${targetContainerId}`
        });

        return true;
      } else {
        // Moving between different containers
        const movedItem = { ...item, position: [x, y] };
        
        setInventory(prev => prev.map(invItem => {
          // Remove from source container
          if (invItem.id === sourceContainerId) {
            return {
              ...invItem,
              containedItems: (invItem.containedItems || []).filter(cItem => cItem.id !== item.id)
            };
          }
          // Add to target container
          if (invItem.id === targetContainerId) {
            return {
              ...invItem,
              containedItems: [...(invItem.containedItems || []), movedItem]
            };
          }
          return invItem;
        }));

        // [INTEGRATION 2025-10-24]: Emit INVENTORY_ITEM_MOVED for between different containers
        systemBus.emit(EVENTS.INVENTORY_ITEM_MOVED, {
          item: movedItem,
          oldPosition: item.position || null,
          newPosition: [x, y],
          source: `container_${sourceContainerId}`,
          target: `container_${targetContainerId}`
        });

        return true;
      }
    }
    
    return false;
  };
  
  // Handle cell click for tap-to-move mode
  const handleCellClick = (x, y, targetGrid) => {
    if (!selectedItem || settings.mobileInteractionMode !== 'tap') {
      return;
    }
    
    // Check if position is valid
    if (isValidPosition(selectedItem, x, y, targetGrid, selectedItem.id)) {
      // Handle cross-grid movement
      if (selectedItemSource !== targetGrid) {
        // Remove from source
        if (selectedItemSource === 'inventory') {
          setInventory(prev => prev.filter(item => item.id !== selectedItem.id));
        } else if (selectedItemSource.startsWith('vault_')) {
          const vaultIndex = parseInt(selectedItemSource.split('_')[1]);
          setVaults(prev => prev.map((vault, idx) => 
            idx === vaultIndex 
              ? { ...vault, items: vault.items.filter(item => item.id !== selectedItem.id) }
              : vault
          ));
        } else if (selectedItemSource.startsWith('container_')) {
          const containerId = selectedItemSource.replace('container_', '');
          setInventory(prev => prev.map(item => 
            item.id === containerId
              ? { ...item, containedItems: (item.containedItems || []).filter(cItem => cItem.id !== selectedItem.id) }
              : item
          ));
        }
        
        // Add to target
        const movedItem = { ...selectedItem, position: [x, y] };
        if (targetGrid === 'inventory') {
          setInventory(prev => [...prev, movedItem]);
        } else if (targetGrid.startsWith('vault_')) {
          const vaultIndex = parseInt(targetGrid.split('_')[1]);
          setVaults(prev => prev.map((vault, idx) => 
            idx === vaultIndex 
              ? { ...vault, items: [...vault.items, movedItem] }
              : vault
          ));
        } else if (targetGrid.startsWith('container_')) {
          const containerId = targetGrid.replace('container_', '');
          setInventory(prev => prev.map(item => 
            item.id === containerId
              ? { ...item, containedItems: [...(item.containedItems || []), movedItem] }
              : item
          ));
        }

        // [INTEGRATION 2025-10-24]: Emit events for cross-grid movements
        const isVaultTransfer = selectedItemSource.startsWith('vault_') || targetGrid.startsWith('vault_');
        if (!isVaultTransfer) {
          // Regular inventory/container movement
          systemBus.emit(EVENTS.INVENTORY_ITEM_MOVED, {
            item: movedItem,
            oldPosition: selectedItem.position || null,
            newPosition: [x, y],
            source: selectedItemSource,
            target: targetGrid
          });
        } else {
          // Vault transfer
          const direction = selectedItemSource.startsWith('vault_') ? 'from_vault' : 'to_vault';
          const vaultId = direction === 'to_vault'
            ? targetGrid.replace('vault_', '')
            : selectedItemSource.replace('vault_', '');

          systemBus.emit(EVENTS.INVENTORY_VAULT_TRANSFER, {
            item: movedItem,
            quantity: 1,
            vault: `vault_${vaultId}`,
            direction: direction
          });
        }
      } else {
        // Same grid movement - just update position
        if (targetGrid === 'inventory') {
          const oldPosition = selectedItem.position || [0, 0];
          setInventory(prev => prev.map(invItem =>
            invItem.id === selectedItem.id
              ? { ...invItem, position: [x, y] }
              : invItem
          ));

          // [INTEGRATION 2025-10-24]: Emit INVENTORY_ITEM_MOVED for same grid - inventory
          systemBus.emit(EVENTS.INVENTORY_ITEM_MOVED, {
            item: { ...selectedItem, position: [x, y] },
            oldPosition: oldPosition,
            newPosition: [x, y],
            source: 'inventory',
            target: 'inventory'
          });
        } else if (targetGrid.startsWith('vault_')) {
          const vaultIndex = parseInt(targetGrid.split('_')[1]);
          setVaults(prev => prev.map((vault, idx) => 
            idx === vaultIndex 
              ? {
                  ...vault,
                  items: vault.items.map(vItem =>
                    vItem.id === selectedItem.id
                      ? { ...vItem, position: [x, y] }
                      : vItem
                  )
                }
              : vault
          ));
        } else if (targetGrid.startsWith('container_')) {
          const containerId = targetGrid.replace('container_', '');
          const oldPosition = selectedItem.position || [0, 0];
          setInventory(prev => prev.map(invItem =>
            invItem.id === containerId
              ? {
                  ...invItem,
                  containedItems: (invItem.containedItems || []).map(cItem =>
                    cItem.id === selectedItem.id
                      ? { ...cItem, position: [x, y] }
                      : cItem
                  )
                }
              : invItem
          ));

          // [INTEGRATION 2025-10-24]: Emit INVENTORY_ITEM_MOVED for same grid - container
          systemBus.emit(EVENTS.INVENTORY_ITEM_MOVED, {
            item: { ...selectedItem, position: [x, y] },
            oldPosition: oldPosition,
            newPosition: [x, y],
            source: `container_${containerId}`,
            target: `container_${containerId}`
          });
        }
      }
      
      // Clear selection after successful move
      setSelectedItem(null);
      setSelectedItemSource(null);
      showStatus(`Moved ${selectedItem.name}!`);
    } else {
      showStatus('Cannot place item there!');
    }
  };

  const handleMouseDown = (e, item, source) => {
    if (e.button !== 0) return;
    
    // Prevent default to stop unwanted behaviors
    if (e.preventDefault) e.preventDefault();
    if (e.stopPropagation) e.stopPropagation();
    
    
    // Store original position for fallback
    const originalPosition = item.position ? [...item.position] : null;
    
    // Lock body scroll on mobile during drag to prevent swaying
    if (window.innerWidth < 768) {
      // Save current scroll position before locking
      const scrollY = window.scrollY;
      const scrollX = window.scrollX;
      
      // Apply position fixed with the current scroll offset
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = `-${scrollX}px`;
      
      // Store scroll position in dragDataRef
      dragDataRef.current = {
        item: item,
        source: source,
        isDragging: true,
        originalPosition: originalPosition,
        scrollPosition: { x: scrollX, y: scrollY }
      };
    } else {
      dragDataRef.current = {
        item: item,
        source: source,
        isDragging: true,
        originalPosition: originalPosition
      };
    }
    
    setDraggedItem(item);
    setDragSource(source);
    setDragPosition({ x: e.clientX, y: e.clientY });
    
    // Define cleanup function first
    const cleanup = () => {
      // Save scroll position BEFORE clearing dragDataRef
      const savedScrollPos = dragDataRef.current.scrollPosition;
      
      dragDataRef.current = {
        item: null,
        source: null,
        isDragging: false,
        scrollPosition: null
      };
      setDraggedItem(null);
      setDragSource(null);
      setDragPosition(null);
      setHoveredGrid(null);
      setHoveredCell(null);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleMouseMove);
      document.removeEventListener('touchend', handleMouseUp);
      
      // Force remove any visual drag states
      setTimeout(() => {
        setDraggedItem(null);
        setDragPosition(null);
        setHoveredGrid(null);
        setHoveredCell(null);
      }, 0);
      
      // Restore body scroll on mobile
      if (window.innerWidth < 768 && savedScrollPos) {
        // Remove the fixed positioning
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.width = '';
        document.body.style.top = '';
        document.body.style.left = '';
        
        // Restore the scroll position without jumping
        requestAnimationFrame(() => {
          window.scrollTo({
            top: savedScrollPos.y,
            left: savedScrollPos.x,
            behavior: 'instant'
          });
        });
      }
    };
    
    const handleMouseMove = (moveEvent) => {
      if (!dragDataRef.current.isDragging) return;
      
      // Prevent scrolling on mobile during drag
      if (moveEvent.type === 'touchmove') {
        moveEvent.preventDefault();
      }
      
      // Handle both mouse and touch events
      const clientX = moveEvent.clientX || (moveEvent.touches && moveEvent.touches[0]?.clientX);
      const clientY = moveEvent.clientY || (moveEvent.touches && moveEvent.touches[0]?.clientY);
      
      if (!clientX || !clientY) return;
      
      setDragPosition({ x: clientX, y: clientY });
      
      const cell = getGridCellFromPoint(clientX, clientY);
      if (cell) {
        setHoveredGrid(cell.gridId);
        setHoveredCell({ x: cell.x, y: cell.y });
      } else {
        setHoveredGrid(null);
        setHoveredCell(null);
      }
    };
    
    // [UI-CONFIRM-UNIFICATION 2026-07-04]: async so the weight-limit override
    // can await the styled useConfirm dialog instead of window.confirm.
    const handleMouseUp = async (upEvent) => {
      try {
        if (!dragDataRef.current.isDragging || !dragDataRef.current.item) {
          cleanup();
          return;
        }
        
        // Prevent default to stop any scrolling
        if (upEvent.preventDefault) {
          upEvent.preventDefault();
        }
        
        // Handle both mouse and touch events
        const clientX = upEvent.clientX || (upEvent.changedTouches && upEvent.changedTouches[0]?.clientX);
        const clientY = upEvent.clientY || (upEvent.changedTouches && upEvent.changedTouches[0]?.clientY);
      
      if (!clientX || !clientY) {
        cleanup();
        return;
      }
      
      const dropCell = getGridCellFromPoint(clientX, clientY);
      
      // Try to find nearest valid cell
      let finalDropCell = dropCell;
      
      if (!dropCell) {
        // Find all grid containers on the page
        const gridContainers = document.querySelectorAll('[data-grid-container="true"]');
        let nearestGrid = null;
        let minDistance = Infinity;
        
        gridContainers.forEach(container => {
          const rect = container.getBoundingClientRect();
          const centerX = rect.left + rect.width / 2;
          const centerY = rect.top + rect.height / 2;
          const distance = Math.sqrt(Math.pow(clientX - centerX, 2) + Math.pow(clientY - centerY, 2));
          
          if (distance < minDistance && distance < 200) { // Within 200px of a grid
            minDistance = distance;
            const gridId = container.getAttribute('data-grid-id');
            const cols = parseInt(container.getAttribute('data-grid-cols'));
            const rows = parseInt(container.getAttribute('data-grid-rows'));
            
            // Find nearest cell within the grid
            const relativeX = Math.max(0, Math.min(cols - 1, Math.floor((clientX - rect.left) / 48)));
            const relativeY = Math.max(0, Math.min(rows - 1, Math.floor((clientY - rect.top) / 48)));
            
            nearestGrid = {
              gridId: gridId,
              x: relativeX,
              y: relativeY
            };
          }
        });
        
        if (nearestGrid) {
          finalDropCell = nearestGrid;
        }
      } else {
        // Even if we have a dropCell, find the nearest valid position for the item
        const item = dragDataRef.current.item;
        if (item && item.size) {
          // Check if current position is valid
          if (!isValidPosition(item, dropCell.x, dropCell.y, dropCell.gridId, item.id)) {
            // [STACK INVESTIGATION APR 30 2026]: Before treating "occupied"
            //   as invalid, check whether the occupant is an identical
            //   stackable. If so, leave finalDropCell alone — the merge
            //   logic below will pick it up. Without this, the nearest-
            //   valid-cell search yanks the drop target away from the
            //   stack the user was aiming for.
            const itemsAtTile = (() => {
              if (dropCell.gridId === 'inventory') return inventory;
              if (dropCell.gridId.startsWith('container_')) {
                const c = inventory.find(i => i.id === dropCell.gridId.replace('container_', ''));
                return c?.containedItems || [];
              }
              const v = vaults.find(vlt => vlt.id === dropCell.gridId);
              return v?.items || [];
            })();
            const occupant = itemsAtTile.find(it => {
              if (!it.position || it.id === item.id) return null;
              const [ix, iy] = it.position;
              const [iw, ih] = it.size || [1, 1];
              return dropCell.x >= ix && dropCell.x < ix + iw &&
                     dropCell.y >= iy && dropCell.y < iy + ih;
            });
            const isIdenticalStackable = occupant &&
              item.stackable &&
              occupant.stackable &&
              occupant.name === item.name &&
              occupant.category === item.category;

            if (!isIdenticalStackable) {
              // Search for nearest valid position in a small radius
              let found = false;
              let searchRadius = 2; // Search within 2 cells

              for (let radius = 1; radius <= searchRadius && !found; radius++) {
                for (let dy = -radius; dy <= radius && !found; dy++) {
                  for (let dx = -radius; dx <= radius && !found; dx++) {
                    const testX = dropCell.x + dx;
                    const testY = dropCell.y + dy;

                    if (isValidPosition(item, testX, testY, dropCell.gridId, item.id)) {
                      finalDropCell = {
                        ...dropCell,
                        x: testX,
                        y: testY
                      };
                      found = true;
                    }
                  }
                }
              }
            }
          }
        }
      }
      
      if (finalDropCell) {
        const item = dragDataRef.current.item;
        const source = dragDataRef.current.source;

        // [STACK INVESTIGATION APR 30 2026]: Drag-merge — if the drop tile
        //   already holds an identical stackable item (same name + category,
        //   both stackable: true), merge into that stack instead of
        //   rejecting as "tile occupied". This is the #1 missing gesture
        //   the user expected for stack management.
        //
        //   Only applies when source + dest are both 'inventory' (drag-merge
        //   across containers/vaults still uses move-then-merge via
        //   addItem's existing maxStack-aware merge code).
        if (
          item.stackable &&
          source === 'inventory' &&
          finalDropCell.gridId === 'inventory'
        ) {
          const targetItem = inventory.find(it => {
            if (!it.position || it.id === item.id) return false;
            if (!it.stackable) return false;
            if (it.name !== item.name || it.category !== item.category) return false;
            const [ix, iy] = it.position;
            const [iw, ih] = it.size || [1, 1];
            return finalDropCell.x >= ix && finalDropCell.x < ix + iw &&
                   finalDropCell.y >= iy && finalDropCell.y < iy + ih;
          });
          if (targetItem && mergeStackOnto) {
            const merged = mergeStackOnto(item.id, targetItem.id);
            if (merged) {
              showStatus(`Merged ${item.name} stacks!`);
              cleanup();
              return;
            }
            // Else: fall through to normal placement (e.g., target is full).
          }
        }

        // Check if dropped on a container
        const containerAtPosition = inventory.find(container => {
          if (!container.isContainer || !container.position) return false;
          const [containerX, containerY] = container.position;
          return finalDropCell.x >= containerX &&
                 finalDropCell.x < containerX + container.size[0] &&
                 finalDropCell.y >= containerY &&
                 finalDropCell.y < containerY + container.size[1] &&
                 finalDropCell.gridId === 'inventory';
        });
        
        if (containerAtPosition) {
          
          // Find first available position in the container
          const containerItems = containerAtPosition.containedItems || [];
          const containerCols = containerAtPosition.containerSlots?.[0] || 6;
          const containerRows = containerAtPosition.containerSlots?.[1] || 8;
          
          let foundPosition = null;
          for (let y = 0; y <= containerRows - item.size[1]; y++) {
            for (let x = 0; x <= containerCols - item.size[0]; x++) {
              if (isValidPosition(item, x, y, `container_${containerAtPosition.id}`, item.id)) {
                foundPosition = [x, y];
                break;
              }
            }
            if (foundPosition) break;
          }
          
          if (foundPosition) {
            const movedItem = { ...item, position: foundPosition };
            
            // Remove from source
            if (source === 'inventory') {
              setInventory(prev => prev.filter(i => i.id !== item.id));
            } else if (source.startsWith('container_')) {
              const sourceContainerId = source.replace('container_', '');
              setInventory(prev => prev.map(invItem => {
                if (invItem.id === sourceContainerId) {
                  return {
                    ...invItem,
                    containedItems: (invItem.containedItems || []).filter(containedItem => containedItem.id !== item.id)
                  };
                }
                return invItem;
              }));
            }
            
            // Add to target container
            setInventory(prev => prev.map(invItem => {
              if (invItem.id === containerAtPosition.id) {
                return {
                  ...invItem,
                  containedItems: [...(invItem.containedItems || []), movedItem]
                };
              }
              return invItem;
            }));
            
            // Open the container to show the result (if setting enabled)
            if (settings.autoOpenContainers) {
              setOpenContainer(containerAtPosition);
            }
            showStatus(`${item.name} added to ${containerAtPosition.name}!`);
            cleanup();
            return;
          } else {
            showStatus(`No space in ${containerAtPosition.name}!`);
            cleanup();
            return;
          }
        }
        
        
        if (isValidPosition(item, finalDropCell.x, finalDropCell.y, finalDropCell.gridId, item.id)) {
          // Check weight limit when moving to inventory
          // [D.4 APR 2026]: Allow override via confirm dialog — user can "Add Anyway".
          if (finalDropCell.gridId === 'inventory' && source !== 'inventory' && !source.startsWith('container_')) {
            if (playerStats.useWeightLimit && totalWeight + (item.weight || 0) > carryCapacity) {
              const proceed = await confirm({
                title: 'Over Carry Capacity',
                message: `Adding ${item.name || 'this item'} would exceed your carry capacity (${(totalWeight + (item.weight || 0)).toFixed(1)} / ${carryCapacity} lb). You'll be overencumbered.`,
                confirmText: 'Add Anyway',
              });
              if (!proceed) {
                cleanup();
                return;
              }
            }
          }
          
          const movedItem = { ...item, position: [finalDropCell.x, finalDropCell.y] };
          
          // Handle the move based on source and target
          if (source === 'inventory' && finalDropCell.gridId === 'inventory') {
            // Moving within inventory
            setInventory(prev => prev.map(i => i.id === item.id ? movedItem : i));
          } else if (source === 'inventory') {
            // Moving from inventory to somewhere else
            setInventory(prev => prev.filter(i => i.id !== item.id));
            
            if (finalDropCell.gridId.startsWith('container_')) {
              // Moving to a container
              const targetContainerId = finalDropCell.gridId.replace('container_', '');
              setInventory(prev => prev.map(invItem => {
                if (invItem.id === targetContainerId) {
                  return {
                    ...invItem,
                    containedItems: [...(invItem.containedItems || []), movedItem]
                  };
                }
                return invItem;
              }));
            } else {
              // Moving to a vault
              setVaults(prev => prev.map(v => {
                if (v.id === finalDropCell.gridId) {
                  return { ...v, items: [...(v.items || []), movedItem] };
                }
                return v;
              }));
            }
          } else if (source.startsWith('container_')) {
            // Moving from a container
            const sourceContainerId = source.replace('container_', '');
            
            if (finalDropCell.gridId === source) {
              // Moving within the same container
              setInventory(prev => prev.map(invItem => {
                if (invItem.id === sourceContainerId) {
                  return {
                    ...invItem,
                    containedItems: invItem.containedItems.map(i => 
                      i.id === item.id ? movedItem : i
                    )
                  };
                }
                return invItem;
              }));
            } else {
              // Moving from container to somewhere else
              setInventory(prev => prev.map(invItem => {
                if (invItem.id === sourceContainerId) {
                  return {
                    ...invItem,
                    containedItems: invItem.containedItems.filter(i => i.id !== item.id)
                  };
                }
                return invItem;
              }));
              
              if (finalDropCell.gridId === 'inventory') {
                // Moving to inventory
                setInventory(prev => [...prev, movedItem]);
                logActivity(`Moved ${movedItem.name} to inventory`, 'items', { 
                  item: movedItem.name,
                  from: 'container',
                  to: 'inventory'
                });
              } else if (finalDropCell.gridId.startsWith('container_')) {
                // Moving to another container
                const targetContainerId = finalDropCell.gridId.replace('container_', '');
                setInventory(prev => prev.map(invItem => {
                  if (invItem.id === targetContainerId) {
                    return {
                      ...invItem,
                      containedItems: [...(invItem.containedItems || []), movedItem]
                    };
                  }
                  return invItem;
                }));
              } else {
                // Moving to a vault
                setVaults(prev => prev.map(v => {
                  if (v.id === finalDropCell.gridId) {
                    return { ...v, items: [...(v.items || []), movedItem] };
                  }
                  return v;
                }));
              }
            }
          } else {
            // Moving from a vault
            if (finalDropCell.gridId === source) {
              // Moving within the same vault
              setVaults(prev => prev.map(v => {
                if (v.id === source) {
                  return {
                    ...v,
                    items: v.items.map(i => i.id === item.id ? movedItem : i)
                  };
                }
                return v;
              }));
            } else {
              // Moving from vault to somewhere else
              setVaults(prev => prev.map(v => {
                if (v.id === source) {
                  return { ...v, items: v.items.filter(i => i.id !== item.id) };
                }
                return v;
              }));
              
              if (finalDropCell.gridId === 'inventory') {
                // Moving to inventory
                setInventory(prev => [...prev, movedItem]);
                logActivity(`Moved ${movedItem.name} to inventory`, 'items', { 
                  item: movedItem.name,
                  from: 'container',
                  to: 'inventory'
                });
              } else if (finalDropCell.gridId.startsWith('container_')) {
                // Moving to a container
                const targetContainerId = finalDropCell.gridId.replace('container_', '');
                setInventory(prev => prev.map(invItem => {
                  if (invItem.id === targetContainerId) {
                    return {
                      ...invItem,
                      containedItems: [...(invItem.containedItems || []), movedItem]
                    };
                  }
                  return invItem;
                }));
              } else {
                // Moving to another vault
                setVaults(prev => prev.map(v => {
                  if (v.id === finalDropCell.gridId) {
                    return { ...v, items: [...(v.items || []), movedItem] };
                  }
                  return v;
                }));
              }
            }
          }
        } else {
          // Return item to original position if drop is invalid
          showStatus('Cannot place item there - returning to original position');
        }
      } else {
        // No valid drop cell found, return to original position
        showStatus('Drop outside grid - returning to original position');
      }
      
      cleanup();
      } catch (error) {
        console.error('Error in drag drop:', error);
        // Always cleanup on error
        cleanup();
      }
    };
    
    // Add both mouse and touch listeners
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    // Add passive: false to allow preventDefault on touchmove for scroll prevention
    document.addEventListener('touchmove', handleMouseMove, { passive: false });
    document.addEventListener('touchend', handleMouseUp);
  };

  // ========================================
  // TOUCH MOVE HANDLER FOR MOBILE
  // ========================================
  
  // [UI-CONFIRM-UNIFICATION 2026-07-04]: async for the awaited confirm below.
  // NOTE: this handler is plumbed through ContainerModal/GridComponent props
  // but has NO live call site (dead plumbing — board: UI-DEAD-TOUCHMOVE).
  const handleTouchMove = async (selectedItem, targetX, targetY, targetGridId) => {
    if (!selectedItem) return;
    
    
    // Check if position is valid
    if (!isValidPosition(selectedItem, targetX, targetY, targetGridId, selectedItem.id)) {
      showStatus(`Cannot place ${selectedItem.name} there!`);
      return;
    }
    
    const sourceGrid = selectedItem.sourceGrid;
    
    // Check weight limit when moving to inventory
    // [D.4 APR 2026]: Allow override via confirm dialog.
    if (targetGridId === 'inventory' && sourceGrid !== 'inventory' && !sourceGrid.startsWith('container_')) {
      if (playerStats.useWeightLimit && totalWeight + (selectedItem.weight || 0) > carryCapacity) {
        const proceed = await confirm({
          title: 'Over Carry Capacity',
          message: `Adding ${selectedItem.name || 'this item'} would exceed your carry capacity (${(totalWeight + (selectedItem.weight || 0)).toFixed(1)} / ${carryCapacity} lb). You'll be overencumbered.`,
          confirmText: 'Add Anyway',
        });
        if (!proceed) return;
      }
    }
    
    const movedItem = { ...selectedItem, position: [targetX, targetY] };
    
    // Handle the move based on source and target
    if (sourceGrid === 'inventory' && targetGridId === 'inventory') {
      // Moving within inventory
      setInventory(prev => prev.map(i => i.id === selectedItem.id ? movedItem : i));
      showStatus(`Moved ${selectedItem.name} within inventory`);
      
    } else if (sourceGrid === 'inventory' && targetGridId.startsWith('vault_')) {
      // Moving from inventory to vault
      const vaultId = targetGridId.replace('vault_', '');
      setInventory(prev => prev.filter(i => i.id !== selectedItem.id));
      setVaults(prev => prev.map(vault =>
        vault.id === vaultId
          ? { ...vault, items: [...vault.items, movedItem] }
          : vault
      ));

      // [INTEGRATION 2025-10-24]: Emit INVENTORY_VAULT_TRANSFER for inventory to vault
      systemBus.emit(EVENTS.INVENTORY_VAULT_TRANSFER, {
        item: movedItem,
        quantity: 1,
        vault: vaultId,
        direction: 'to_vault'
      });

      showStatus(`Moved ${selectedItem.name} to vault`);
      
    } else if (sourceGrid.startsWith('vault_') && targetGridId === 'inventory') {
      // Moving from vault to inventory
      const vaultId = sourceGrid.replace('vault_', '');
      setVaults(prev => prev.map(vault =>
        vault.id === vaultId
          ? { ...vault, items: vault.items.filter(i => i.id !== selectedItem.id) }
          : vault
      ));
      setInventory(prev => [...prev, movedItem]);

      // [INTEGRATION 2025-10-24]: Emit INVENTORY_VAULT_TRANSFER for vault to inventory
      systemBus.emit(EVENTS.INVENTORY_VAULT_TRANSFER, {
        item: movedItem,
        quantity: 1,
        vault: vaultId,
        direction: 'from_vault'
      });

      showStatus(`Moved ${selectedItem.name} to inventory`);
      
    } else if (sourceGrid.startsWith('vault_') && targetGridId.startsWith('vault_')) {
      // Moving between vaults
      const sourceVaultId = sourceGrid.replace('vault_', '');
      const targetVaultId = targetGridId.replace('vault_', '');
      
      if (sourceVaultId === targetVaultId) {
        // Moving within same vault
        setVaults(prev => prev.map(vault => 
          vault.id === sourceVaultId
            ? { ...vault, items: vault.items.map(i => i.id === selectedItem.id ? movedItem : i) }
            : vault
        ));
        showStatus(`Moved ${selectedItem.name} within vault`);
      } else {
        // Moving between different vaults
        setVaults(prev => prev.map(vault => {
          if (vault.id === sourceVaultId) {
            return { ...vault, items: vault.items.filter(i => i.id !== selectedItem.id) };
          } else if (vault.id === targetVaultId) {
            return { ...vault, items: [...vault.items, movedItem] };
          }
          return vault;
        }));
        showStatus(`Moved ${selectedItem.name} between vaults`);
      }
    }
    
    // Log the activity
    logActivity(`Moved ${selectedItem.name}`, 'move');
  };

  // ========================================
  // SWIPE GESTURE HANDLING FOR TABS
  // ========================================
  
  const isMobile = useIsMobile();
  const minSwipeDistance = 50;
  
  // [UI POLISH]: Tab order matches MobileNavigation's canonical order for consistent swipe navigation
  const allTabs = ['character', 'inventory', 'spellbook', 'skills', 'feats', 'shop', 'magic', 'vault', 'dice', 'rest', 'downtimes', 'calendar', 'bastion', 'party', 'combat', 'journal', 'tables', 'workshop', 'log', 'settings'];

  // [FIX JAN 2026 - Issue #5]: Filter tabs based on user settings
  // Note: 'character', 'workshop', 'settings' are always visible (essential tabs)
  // Sources is now a section inside Workshop, so Workshop is the always-visible anchor.
  const visibleTabs = allTabs.filter(tab =>
    ['character', 'workshop', 'settings'].includes(tab) || !settings.hiddenTabs?.includes(tab)
  );

  // [FIX MAR 2026]: Track Y coordinates to distinguish horizontal swipes from vertical scrolls
  const touchStartY = useRef(null);
  const touchEndY = useRef(null);

  // [F-003 FIX 2026-05-09]: Long-press handlers for inventory Table View rows.
  // Pre-fix, table rows had only `onContextMenu` which mobile browsers do NOT
  // fire on long-press for `<tr>` elements — the entire item-action surface
  // (Sell/Equip/Split/Combine/Delete) was unreachable on mobile in Table View.
  // Now: 500ms long-press on a row opens the same context menu desktop users
  // get from right-click. Only one timer at a time (only one finger touch).
  const longPressTimerRef = useRef(null);
  const longPressTriggeredRef = useRef(false);
  const handleRowLongPressStart = (item, source) => (e) => {
    if (!isMobile) return;
    longPressTriggeredRef.current = false;
    const touch = e.touches?.[0];
    const startX = touch?.clientX || 0;
    const startY = touch?.clientY || 0;
    clearTimeout(longPressTimerRef.current);
    longPressTimerRef.current = setTimeout(() => {
      longPressTriggeredRef.current = true;
      handleContextMenu({
        preventDefault: () => {},
        stopPropagation: () => {},
        clientX: startX,
        clientY: startY,
      }, item, source);
    }, 500);
  };
  const cancelRowLongPress = () => {
    clearTimeout(longPressTimerRef.current);
  };

  const handleSwipeStart = (e) => {
    if (!isMobile) return;

    // Don't interfere with dragging items or horizontal scroll containers!
    const target = e.target;
    const isItem = target.closest('[data-is-item="true"]');
    const isGrid = target.closest('[data-grid-container="true"]');
    const isNoSwipe = target.closest('[data-no-swipe="true"]');
    if (isItem || isGrid || isNoSwipe) return;

    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
    touchStartY.current = e.targetTouches[0].clientY;
    touchEndY.current = null;
  };

  const handleSwipeMove = (e) => {
    if (!isMobile || !touchStart) return;
    setTouchEnd(e.targetTouches[0].clientX);
    touchEndY.current = e.targetTouches[0].clientY;
  };

  const handleSwipeEnd = () => {
    if (!isMobile || !touchStart || !touchEnd) return;

    const distanceX = touchStart - touchEnd;
    const distanceY = (touchStartY.current || 0) - (touchEndY.current || 0);
    const absX = Math.abs(distanceX);
    const absY = Math.abs(distanceY);

    // [FIX MAR 2026]: Only trigger tab switch when gesture is predominantly horizontal
    // Must travel further horizontally than vertically (ratio > 1.5) to distinguish from scrolling
    const isHorizontalGesture = absX > minSwipeDistance && absX > absY * 1.5;

    if (isHorizontalGesture) {
      const isLeftSwipe = distanceX > 0;
      const isRightSwipe = distanceX < 0;

      // [FIX JAN 2026 - Issue #5]: Use visibleTabs for swipe navigation
      const currentIndex = visibleTabs.findIndex(tab => tab === activeTab);

      if (isLeftSwipe && currentIndex < visibleTabs.length - 1) {
        // Swipe left - next tab
        setActiveTab(visibleTabs[currentIndex + 1]);
        showStatus(`Switched to ${visibleTabs[currentIndex + 1]}`);
      } else if (isRightSwipe && currentIndex > 0) {
        // Swipe right - previous tab
        setActiveTab(visibleTabs[currentIndex - 1]);
        showStatus(`Switched to ${visibleTabs[currentIndex - 1]}`);
      }

      // Haptic feedback for successful swipe
      if (navigator.vibrate) {
        navigator.vibrate(25);
      }
    }

    setTouchStart(null);
    setTouchEnd(null);
    touchStartY.current = null;
    touchEndY.current = null;
  };
  
  // ========================================
  // ITEM MANAGEMENT
  // ========================================

  // [HELPER NOV 25, 2025]: Build items from starter pack WITHOUT updating state
  // [WHY]: Used by handleCharacterWizardComplete to gather ALL items before single state update
  // [RETURNS]: { itemsToAdd: InventoryItem[], itemsNotAdded: string[] }
  const buildStarterPackItems = (packType) => {
    const pack = STARTER_PACKS[packType];
    if (!pack) {
      console.warn(`[buildStarterPackItems] Pack "${packType}" not found`);
      return { itemsToAdd: [], itemsNotAdded: [] };
    }


    const mainContainerInfo = pack.items.find(item => item.isMain);
    let mainContainer = null;
    const itemsNotAdded = [];
    const itemsToAdd = [];

    // === Create main container with pack name ===
    if (mainContainerInfo) {
      mainContainer = new InventoryItem(
        mainContainerInfo.type,
        mainContainerInfo.category,
        { name: pack.name }
      );

      // Place items INSIDE the container
      const itemsForContainer = pack.items.filter(item => item.inContainer);
      const containerCols = mainContainer.containerSlots?.[0] || 6;
      const containerRows = mainContainer.containerSlots?.[1] || 8;

      // [FIX 2026-05-11]: For pack items with `quantity: N` (e.g., 10 torches,
      //   10 rations, 10 pitons), create ONE InventoryItem with quantity=N
      //   stored on it — NOT N separate items. The previous loop pattern
      //   produced 10 individual torch cells inside the backpack instead of
      //   one stack of 10. Mirrors the ammunition path in
      //   handleCharacterWizardComplete which already passes quantity +
      //   bundledQuantity to the InventoryItem constructor.
      itemsForContainer.forEach(itemInfo => {
        const quantity = itemInfo.quantity || 1;
        const newItem = new InventoryItem(
          itemInfo.type,
          itemInfo.category,
          {
            name: EquipmentResolver.formatDisplayName(itemInfo.type),
            quantity,
            bundledQuantity: quantity > 1 ? quantity : undefined,
          }
        );

        let placed = false;
        for (let y = 0; y <= containerRows - newItem.size[1] && !placed; y++) {
          for (let x = 0; x <= containerCols - newItem.size[0] && !placed; x++) {
            const occupied = mainContainer.containedItems.some(existing => {
              if (!existing.position) return false;
              const [ex, ey] = existing.position;
              for (let cx = x; cx < x + newItem.size[0]; cx++) {
                for (let cy = y; cy < y + newItem.size[1]; cy++) {
                  if (cx >= ex && cx < ex + existing.size[0] &&
                      cy >= ey && cy < ey + existing.size[1]) {
                    return true;
                  }
                }
              }
              return false;
            });

            if (!occupied) {
              newItem.position = [x, y];
              mainContainer.containedItems.push(newItem);
              placed = true;
            }
          }
        }

        if (!placed) {
          itemsNotAdded.push(newItem.name);
        }
      });

      itemsToAdd.push(mainContainer);
    }

    // Create items that go directly in inventory (not in container)
    // [FIX 2026-05-11]: Same fix as the container path above — pack items with
    //   `quantity: N` become ONE InventoryItem with quantity=N, not N separate
    //   items. Pre-fix: 10 individual cells. Post-fix: 1 cell with qty=10 badge.
    pack.items.filter(item => !item.inContainer && !item.isMain).forEach(itemInfo => {
      const quantity = itemInfo.quantity || 1;
      const newItem = new InventoryItem(
        itemInfo.type,
        itemInfo.category,
        {
          name: EquipmentResolver.formatDisplayName(itemInfo.type),
          quantity,
          bundledQuantity: quantity > 1 ? quantity : undefined,
        }
      );
      itemsToAdd.push(newItem);
    });

    return { itemsToAdd, itemsNotAdded, packName: pack.name };
  };

  // [REWRITTEN NOV 25, 2025]: Fixed race condition - items were overlapping
  // [WHY]: Previous code called setInventory() multiple times (async), each item saw same empty state
  // [FIX]: Build all items FIRST, then do SINGLE state update with proper collision checking
  const addStarterPack = (packType) => {
    const pack = STARTER_PACKS[packType];
    if (!pack) {
      console.warn(`[addStarterPack] Pack "${packType}" not found`);
      return;
    }


    // Find the main container info
    const mainContainerInfo = pack.items.find(item => item.isMain);
    let mainContainer = null;
    const itemsNotAdded = [];
    const itemsToAddToInventory = []; // Items that go directly in inventory (not in container)

    // === STEP 1: Create main container with pack name ===
    if (mainContainerInfo) {
      // [FIX NOV 25, 2025]: Use pack.name for proper naming ("Explorer's Pack" not "Backpack")
      mainContainer = new InventoryItem(
        mainContainerInfo.type,
        mainContainerInfo.category,
        { name: pack.name }  // Use the actual pack name!
      );

      // === STEP 2: Place items INSIDE the container ===
      const itemsForContainer = pack.items.filter(item => item.inContainer);
      const containerCols = mainContainer.containerSlots?.[0] || 6;
      const containerRows = mainContainer.containerSlots?.[1] || 8;

      itemsForContainer.forEach(itemInfo => {
        const quantity = itemInfo.quantity || 1;
        for (let i = 0; i < quantity; i++) {
          const newItem = new InventoryItem(
            itemInfo.type,
            itemInfo.category,
            { name: EquipmentResolver.formatDisplayName(itemInfo.type) }
          );

          // Find position in container (checking against already-placed items)
          let placed = false;
          for (let y = 0; y <= containerRows - newItem.size[1] && !placed; y++) {
            for (let x = 0; x <= containerCols - newItem.size[0] && !placed; x++) {
              // Check collision against items already in container
              const occupied = mainContainer.containedItems.some(existing => {
                if (!existing.position) return false;
                const [ex, ey] = existing.position;
                // Check if any cell of newItem overlaps with existing item
                for (let cx = x; cx < x + newItem.size[0]; cx++) {
                  for (let cy = y; cy < y + newItem.size[1]; cy++) {
                    if (cx >= ex && cx < ex + existing.size[0] &&
                        cy >= ey && cy < ey + existing.size[1]) {
                      return true; // Collision!
                    }
                  }
                }
                return false;
              });

              if (!occupied) {
                newItem.position = [x, y];
                mainContainer.containedItems.push(newItem);
                placed = true;
              }
            }
          }

          if (!placed) {
            itemsNotAdded.push(newItem.name);
            console.warn(`[addStarterPack] ⚠️ Could not fit "${newItem.name}" in container`);
          }
        }
      });

      itemsToAddToInventory.push(mainContainer);
    }

    // === STEP 3: Create items that go directly in inventory (not in container) ===
    pack.items.filter(item => !item.inContainer && !item.isMain).forEach(itemInfo => {
      const quantity = itemInfo.quantity || 1;
      for (let i = 0; i < quantity; i++) {
        const newItem = new InventoryItem(
          itemInfo.type,
          itemInfo.category,
          { name: EquipmentResolver.formatDisplayName(itemInfo.type) }
        );
        itemsToAddToInventory.push(newItem);
      }
    });

    // === STEP 4: SINGLE state update - place all items with collision checking ===
    // [WHY]: This fixes the race condition - we check against newInventory, not stale state
    setInventory(prev => {
      const newInventory = [...prev];

      itemsToAddToInventory.forEach(item => {
        let placed = false;

        // Find valid position checking against newInventory (not stale `inventory` state!)
        for (let y = 0; y <= gridRows - item.size[1] && !placed; y++) {
          for (let x = 0; x <= gridCols - item.size[0] && !placed; x++) {
            // Check collision against ALL items in newInventory
            const occupied = newInventory.some(existing => {
              if (!existing.position) return false;
              const [ex, ey] = existing.position;
              // Check if any cell of item overlaps with existing item
              for (let cx = x; cx < x + item.size[0]; cx++) {
                for (let cy = y; cy < y + item.size[1]; cy++) {
                  if (cx >= ex && cx < ex + existing.size[0] &&
                      cy >= ey && cy < ey + existing.size[1]) {
                    return true; // Collision!
                  }
                }
              }
              return false;
            });

            if (!occupied) {
              item.position = [x, y];
              newInventory.push(item);
              placed = true;

              // Emit event for activity log
              systemBus.emit(EVENTS.INVENTORY_ITEM_ADDED, {
                item: item,
                quantity: 1,
                source: 'starter_pack'
              });
            }
          }
        }

        if (!placed) {
          itemsNotAdded.push(item.name);
          console.warn(`[addStarterPack] ⚠️ Could not place "${item.name}" in inventory`);
        }
      });

      return newInventory;
    });

    if (itemsNotAdded.length > 0) {
      console.warn(`[addStarterPack] Items that couldn't be placed:`, itemsNotAdded);
    }

  };

  const getEligibleStarterPacks = () => {
    if (!primaryClass || playerStats.hasReceivedStarterPack) {
      return [];
    }

    // Get all packs eligible for this class based on D&D 5e SRD
    const eligiblePacks = [];

    // All classes can choose from these basic packs
    eligiblePacks.push('dungeoneer', 'explorer', 'scholar');

    // Class-specific additions
    switch (primaryClass) {
      case 'rogue':
        eligiblePacks.push('burglar');
        break;
      case 'bard':
        eligiblePacks.push('entertainer');
        break;
      case 'cleric':
      case 'paladin':
        eligiblePacks.push('priest');
        break;
      default:
        break;
    }

    return eligiblePacks.map(packKey => ({ key: packKey, ...STARTER_PACKS[packKey] }));
  };

  const selectStarterPack = (packType) => {
    addStarterPack(packType);
    setPlayerStats(prev => ({
      ...prev,
      hasReceivedStarterPack: true
    }));
  };

  // [PHASE 10 - NOV 20, 2025]: REMOVED DEAD CODE
  // [WHY]: parseEquipmentName function was unused - CharacterCreationWizard has its own copy
  // [NOTE]: Equipment parsing now handled by:
  //   - CharacterCreationWizard::parseEquipmentName (SRD strings → itemData objects)
  //   - handleCharacterWizardComplete::convertEquipmentToItems (itemData → InventoryItem instances)

  // [UI-CONFIRM-UNIFICATION 2026-07-04]: async — the weight-limit override
  // awaits the styled confirm. Callers that consume the boolean result
  // (ItemDialog handleSubmit) await it; the App-level ItemDialog wrapper
  // discards the return in every branch, so its contract is unchanged.
  const addNewItem = async (itemData) => {
    const newItem = new InventoryItem(
      itemData.baseType,
      itemData.category,
      itemData
    );
    
    // [FIX 2025-10-22]: Check if auto-purchasing is enabled and handle cost
    if (settings.autoPurchaseItems && newItem.value > 0 && !itemData.purchaseForFree) {
      // [FIX 2025-10-22]: Use autoPurchase utility for proper currency conversion
      const purchaseResult = autoPurchase(newItem.value, currencies);

      if (!purchaseResult.success) {
        showStatus(`❌ ${purchaseResult.message}`);
        return false;
      }

      // [INTEGRATION 2025-10-24]: Store old currencies for event emission
      const oldCurrencies = { ...currencies };

      // Apply new currency from autoPurchase
      setCurrencies(purchaseResult.newCurrency);

      // [INTEGRATION 2025-10-24]: Emit CURRENCY_CHANGED events for reactive updates
      ['pp', 'gp', 'sp', 'cp'].forEach(type => {
        if (oldCurrencies[type] !== purchaseResult.newCurrency[type]) {
          systemBus.emit(EVENTS.CURRENCY_CHANGED, {
            type,
            oldAmount: oldCurrencies[type],
            newAmount: purchaseResult.newCurrency[type],
            change: purchaseResult.newCurrency[type] - oldCurrencies[type],
            source: 'item_purchase'
          });
        }
      });

      showStatus(`✅ ${purchaseResult.message}`);
      logActivity(`Purchased ${newItem.name} for ${formatCurrency(newItem.value)}`, 'currency', {
        item: newItem.name,
        cost: newItem.value,
        weight: newItem.weight
      });
    } else if (itemData.purchaseForFree && newItem.value > 0) {
      showStatus(`Acquired ${newItem.name} for free (bypassed ${formatCurrency(newItem.value)} cost)`);
      logActivity(`Acquired ${newItem.name} for free`, 'items', {
        item: newItem.name,
        value: newItem.value,
        weight: newItem.weight
      });
    }

    // [D.4 APR 2026]: Allow override via confirm dialog — user can "Add Anyway".
    if (playerStats.useWeightLimit && totalWeight + (newItem.weight || 0) > carryCapacity) {
      const proceed = await confirm({
        title: 'Over Carry Capacity',
        message: `Adding ${newItem.name || 'this item'} would exceed your carry capacity (${(totalWeight + (newItem.weight || 0)).toFixed(1)} / ${carryCapacity} lb). You'll be overencumbered.`,
        confirmText: 'Add Anyway',
      });
      if (!proceed) return false;
    }
    
    for (let y = 0; y <= gridRows - newItem.size[1]; y++) {
      for (let x = 0; x <= gridCols - newItem.size[0]; x++) {
        if (isValidPosition(newItem, x, y, 'inventory')) {
          newItem.position = [x, y];
          setInventory(prev => [...prev, newItem]);

          // [INTEGRATION 2025-10-24]: Emit INVENTORY_ITEM_ADDED for manually created item
          const itemSource = itemData.purchaseForFree ? 'gift' : (settings.autoPurchaseItems && newItem.value > 0 ? 'purchase' : 'manual');
          systemBus.emit(EVENTS.INVENTORY_ITEM_ADDED, {
            item: newItem,
            quantity: 1,
            source: itemSource
          });

          showStatus(`${newItem.name} added to inventory at (${x}, ${y})`);

          // Log item addition if not already logged (purchased items are logged separately)
          if (!settings.autoPurchaseItems || newItem.value === 0 || itemData.purchaseForFree) {
            logActivity(`Added ${newItem.name} to inventory`, 'items', {
              item: newItem.name,
              weight: newItem.weight,
              position: `(${x}, ${y})`,
              value: newItem.value
            });
          }
          
          return true;
        }
      }
    }
    
    showStatus(`No space available in inventory for ${newItem.name} (${newItem.size[0]}x${newItem.size[1]})! Current grid: ${gridCols}x${gridRows}`);
    return false;
  };

  // [PHASE 4B - MIGRATED]: updateItem now comes from InventoryContext (line 370)
  // [REMOVED 40 lines]: Local updateItem function replaced by context action

  // [MIGRATED 2025-10-25]: sellItem, deleteItem moved to InventoryContext
  // See: src/contexts/InventoryContext.js (lines 927-1070)

  // [PURPOSE]: Add item from inventory to shared party vault
  // [WHY]: Enable party loot distribution and collaboration
  // [INTEGRATION]: Connects to useEnhancedPartySync.addToSharedVault
  const handleAddToSharedVault = async (item) => {
    if (!partyId || !addToSharedVault) {
      showStatus('⚠️ You must be in a party to use the shared vault');
      return;
    }

    // Add to shared vault via Firebase FIRST — only remove from inventory on success
    const success = await addToSharedVault(item);
    if (!success) {
      showStatus('❌ Failed to add item to party vault');
      return;
    }

    // Remove item from inventory only after Firebase write succeeded
    setInventory(prev => prev.filter(i => i.id !== item.id));

    // [INTEGRATION 2025-10-24]: Emit INVENTORY_VAULT_TRANSFER for party vault
    systemBus.emit(EVENTS.INVENTORY_VAULT_TRANSFER, {
      item: item,
      quantity: 1,
      vault: 'party_vault',
      direction: 'to_vault'
    });

    // Log activity
    logActivity(`Added ${item.name} to party vault`, 'items', {
      item: item.name,
      value: item.value,
      weight: item.weight,
      partyId
    });

    showStatus(`✅ Added ${item.name} to party vault`);
  };

  // ========================================
  // EQUIPMENT MANAGEMENT
  // ========================================

  // [MIGRATED 2025-10-25]: equipItem, unequipItem moved to InventoryContext
  // See: src/contexts/InventoryContext.js (lines 715-800)

  // [MIGRATED 2025-10-25]: consumeAmmunition moved to InventoryContext
  // See: src/contexts/InventoryContext.js (lines 870-925)

  // [MIGRATED 2025-10-25]: attuneToItem, unattuneItem moved to InventoryContext
  // See: src/contexts/InventoryContext.js (lines 802-868)

  // [MIGRATED 2025-10-25]: getItemDetails, canEquip, getEquipSlots moved to InventoryContext
  // See: src/contexts/InventoryContext.js (lines 495-583)

  // ========================================
  // SPELL MANAGEMENT
  // ========================================
  
  const getItemSpells = (item) => {
    const baseData = getItemDetails(item);
    if (!baseData || !baseData.spells) return [];
    
    // Return item's current spells, defaulting to base data if not customized
    if (item.spells) {
      return item.spells;
    } else {
      return baseData.spells.map(spell => ({ ...spell }));
    }
  };
  
  // Dice rolling functions
  const rollD20 = () => Math.floor(Math.random() * 20) + 1;
  
  const rollDice = (diceStr) => {
    if (!diceStr) return { total: 0, rolls: [], text: '', compact: '' };
    
    // Handle multiple dice expressions like "2d6 + 1d8"
    const parts = diceStr.split(/\s*\+\s*/);
    let totalSum = 0;
    let diceGroups = {}; // Group by die size
    let resultDetails = [];
    
    for (const part of parts) {
      const match = part.trim().match(/(\d+)d(\d+)/);
      if (match) {
        const numDice = parseInt(match[1]);
        const dieSize = parseInt(match[2]);
        
        if (!diceGroups[dieSize]) {
          diceGroups[dieSize] = { count: 0, rolls: [], sum: 0 };
        }
        
        for (let i = 0; i < numDice; i++) {
          const roll = Math.floor(Math.random() * dieSize) + 1;
          diceGroups[dieSize].rolls.push(roll);
          diceGroups[dieSize].sum += roll;
          totalSum += roll;
        }
        diceGroups[dieSize].count += numDice;
        
      } else if (/^\d+$/.test(part.trim())) {
        // It's just a number modifier
        const mod = parseInt(part.trim());
        totalSum += mod;
        if (!diceGroups.modifier) {
          diceGroups.modifier = 0;
        }
        diceGroups.modifier += mod;
      }
    }
    
    // Create compact notation
    let compactParts = [];
    let detailParts = [];
    Object.keys(diceGroups).sort((a, b) => {
      if (a === 'modifier') return 1;
      if (b === 'modifier') return -1;
      return parseInt(b) - parseInt(a);
    }).forEach(key => {
      if (key === 'modifier') {
        compactParts.push(`+${diceGroups[key]}`);
      } else {
        const group = diceGroups[key];
        compactParts.push(`${group.count}d${key}`);
        detailParts.push(`${group.count}d${key}:[${group.rolls.join(',')}]=${group.sum}`);
      }
    });
    
    return {
      total: totalSum,
      compact: compactParts.join(' + '),
      details: detailParts.join(' '),
      text: `${compactParts.join(' + ')} = ${totalSum}`,
      diceGroups: diceGroups  // Include for expanded view
    };
  };
  
  const rollSpellAttack = async () => {
    const roll = rollD20();
    const attackBonus = getSpellAttackBonus();
    
    // Apply quick bonuses (like Bless for attack rolls)
    let quickBonus = 0;
    let quickBonusText = '';
    let individualBonuses = [];
    
    // [Sub-plan 1Q-F-5b-ii-followup — 2026-05-28]: findQuickBonusOfKind replaces
    //   raw b.name.startsWith('Bardic Inspiration') Rule 11/17 anti-pattern carried
    //   in the WEAPON-attack die-size-extraction path. Die size still extracted from
    //   bardic.name (writer convention "Bardic Inspiration d8"); canonicalization to
    //   b.value/b.die deferred to richer-scope arc.
    const bardic = findQuickBonusOfKind(activeQuickBonuses, 'bardicInspiration');
    if (bardic && await confirm({ title: 'Bardic Inspiration', message: 'Use Bardic Inspiration on this attack?', confirmText: 'Use', confirmColor: 'blue' })) {
      // Extract die size from name (e.g., "Bardic Inspiration d8" -> 8)
      const dieMatch = (bardic.name || '').match(/d(\d+)/);
      const dieSize = dieMatch ? parseInt(dieMatch[1]) : 6;
      const bardicRoll = Math.floor(Math.random() * dieSize) + 1;
      quickBonus += bardicRoll;
      quickBonusText += ` + d${dieSize}(${bardicRoll}) Bardic`;
      // Remove the bardic inspiration after use — filter by object reference
      //   (the bardic var IS the found entry from activeQuickBonuses)
      setActiveQuickBonuses(prev => prev.filter(b => b !== bardic));

      // [INTEGRATION 2025-10-24]: Emit QUICK_BONUS_REMOVED event for consumed Bardic Inspiration
      systemBus.emit(EVENTS.QUICK_BONUS_REMOVED, {
        bonusId: 'bardic_inspiration',
        bonusName: 'Bardic Inspiration',
        reason: 'consumed'
      });
    }
    
    // [PHASE B / #013 — STRING-SIN SWEEP / RULE 11]: Property-first detection.
    //   QuickBonusesPanel L208 stamps `type: 'bless'` on every Bless quick-bonus
    //   row. Custom-content Bless equivalents (CW spells with type: 'bless')
    //   trigger the same attack-roll path. Phase B / #039 (2026-05-06): legacy
    //   `b.name === 'Bless'` fallback removed per `feedback_no_users_no_migration.md`
    //   — schema changes are FREE; type field is stamped at every entry path.
    // [Sub-plan 1Q-F-5b-ii — 2026-05-28]: findQuickBonusOfKind replaces raw b.type check.
    const bless = findQuickBonusOfKind(activeQuickBonuses, 'bless');
    if (bless) {
      const blessRoll = Math.floor(Math.random() * 4) + 1;
      quickBonus += blessRoll;
      quickBonusText += ` + d4(${blessRoll}) Bless`;
    }
    
    // Apply custom attack bonuses
    activeQuickBonuses.forEach(bonus => {
      if ((bonus.affects === 'attack' || bonus.affects === 'Attack rolls' || bonus.affects === 'Attack rolls, saving throws') && (bonus.isCustom || bonus.type === 'bless')) {
        if (bonus.bonus.includes('d')) {
          // Parse dice like "10d4" or "d6"
          const match = bonus.bonus.match(/(\d*)d(\d+)/);
          if (match) {
            const numDice = parseInt(match[1]) || 1;
            const dieSize = parseInt(match[2]);
            let rollTotal = 0;
            let rolls = [];
            for (let i = 0; i < numDice; i++) {
              const roll = Math.floor(Math.random() * dieSize) + 1;
              rolls.push(roll);
              rollTotal += roll;
            }
            quickBonus += rollTotal;
            quickBonusText += ` + ${numDice}d${dieSize}(${rolls.join(',')})=${rollTotal} ${bonus.name}`;
          }
        } else {
          // It's a flat bonus
          const value = parseInt(bonus.bonus) || 0;
          quickBonus += value;
          if (value !== 0) {
            quickBonusText += ` + ${value} ${bonus.name}`;
          }
        }
      }
    });
    
    const totalBonus = attackBonus + quickBonus;
    const total = roll + totalBonus;
    const isCrit = roll === 20;
    const isFumble = roll === 1;

    // [PHASE 4B STEP 7 SPELL ATTACKS]: Check if inspiration should be offered
    // [WHY]: If spell attack failed within threshold of DC, prompt player to use inspiration
    // [D&D 5e RULE]: Inspiration can be used on attack rolls (PHB p.174)
    // [INTEGRATION]: Uses spellAttackDC state, setInspirationPrompt from CharacterContext
    if (spellAttackDC && spellAttackDC !== '' && setInspirationPrompt && !isCrit) {
      const dc = parseInt(spellAttackDC);
      const inspirationCount = playerStats.inspiration || 0;
      const threshold = getInspirationThreshold(settings);

      if (shouldOfferInspiration(total, dc, inspirationCount, threshold)) {
        const failureMargin = dc - total;
        setInspirationPrompt({
          result: total,
          dc: dc,
          failureMargin: failureMargin,
          rollType: 'spell attack',
          description: 'Spell Attack',
          onReroll: () => rollSpellAttack() // Reroll same attack
        });
      }
    }

    // Log detailed roll to activity
    logActivity('Spell Attack Roll', 'combat', {
      roll,
      attackBonus,
      quickBonus,
      total,
      isCrit,
      isFumble,
      details: `d20(${roll}) + ${attackBonus} attack${quickBonusText} = ${total}`
    });
    
    return {
      roll,
      bonus: totalBonus,
      total,
      isCrit,
      isFumble,
      quickBonus,
      quickBonusText,
      text: `${isCrit ? '💥 CRIT! ' : isFumble ? '💀 FUMBLE! ' : ''}Attack: ${total}`
    };
  };

  /**
   * [NEW - Week 2, Day 10]: Check if inspiration should be offered and prompt user
   *
   * @param {number} rollResult - The total roll result (d20 + modifiers)
   * @param {number} dc - The DC to beat
   * @param {string} rollType - Type of roll (e.g., "attack roll", "saving throw", "ability check")
   * @param {string} description - Optional description of the roll
   * @param {Function} onReroll - Callback to execute the reroll
   * @returns {boolean} - Whether inspiration prompt was shown
   */
  const checkInspirationAndPrompt = (rollResult, dc, rollType, description, onReroll) => {
    // Check if inspiration auto-prompt is enabled
    if (!settings.inspirationAutoPrompt) return false;

    // Check if player has inspiration
    if (!playerStats.inspiration || playerStats.inspiration <= 0) return false;

    // Check if roll failed
    if (rollResult >= dc) return false;

    // Calculate failure margin
    const failureMargin = dc - rollResult;

    // Check if within threshold
    const threshold = settings.inspirationThreshold || 3;
    if (failureMargin > threshold) return false;

    // Show inspiration prompt
    setInspirationPrompt({
      result: rollResult,
      dc,
      failureMargin,
      rollType,
      description,
      onReroll
    });

    return true;
  };

  /**
   * [NEW - Week 2, Day 10]: Handle inspiration usage
   */
  const handleUseInspiration = () => {
    if (!inspirationPrompt) return;

    const oldInspiration = playerStats.inspiration || 0;
    const newInspiration = Math.max(0, oldInspiration - 1);

    // Consume inspiration
    setPlayerStats(prev => ({
      ...prev,
      inspiration: newInspiration
    }));

    // [INTEGRATION 2025-10-24]: Emit INSPIRATION_CHANGED event for consumed inspiration
    systemBus.emit(EVENTS.INSPIRATION_CHANGED, {
      oldInspiration,
      newInspiration,
      change: -1,
      source: 'consumed',
      context: inspirationPrompt.rollType
    });

    // Log to activity
    logActivity(`Used Inspiration on ${inspirationPrompt.rollType}`, 'character', {
      previousResult: inspirationPrompt.result,
      dc: inspirationPrompt.dc,
      inspirationRemaining: newInspiration
    });

    // Close prompt
    setInspirationPrompt(null);

    // Execute reroll
    if (inspirationPrompt.onReroll) {
      inspirationPrompt.onReroll();
    }

    showStatus('✨ Inspiration used! Rerolling...', 2000);
  };

  /**
   * [NEW - Week 2, Day 10]: Decline inspiration usage
   */
  const handleDeclineInspiration = () => {
    setInspirationPrompt(null);
  };

  const getSpellSaveDC = () => {
    // [FIX phase6-E E-1]: Route through effectCalculator.calculateSpellDC so
    // registered spellDC effects (Rod of the Pact Keeper +1, custom feats with
    // spellDC effects) are picked up. Same silent-gap pattern as a964ea7.
    return effectCalculator.calculateSpellDC(playerStats, inventory, activeQuickBonuses);
  };

  const getSpellAttackBonus = () => {
    // [FIX phase6-E E-1]: Route through effectCalculator.calculateSpellAttackBonus
    // so registered attackBonus effects (Wand of the War Mage +1, custom feats with
    // attackBonus effects) are picked up.
    const fromCalc = effectCalculator.calculateSpellAttackBonus(playerStats, inventory, activeQuickBonuses);
    // Preserve legacy customSpellAttackBonus support for character sheets that
    // wrote a direct numeric override (rare, pre-registry path).
    const customBonus = (playerStats.customSpellAttackBonus || 0);
    return fromCalc + customBonus;
  };

  // [PURPOSE]: Determine if a spell can be cast on allies
  // [WHY]: Some spells are beneficial and should allow party targeting (buffs, heals)
  // [INTEGRATION]: Used in quickcast dialog to show/hide ally targeting UI
  const canSpellTargetAlly = (spell) => {
    if (!spell) return false;

    // Healing spells can always target allies
    if (spell.healing || spell.isHealing) return true;

    // [SPR-005 FIX — MAY 2026]: Property-based detection (Rule 11). Pre-fix used
    //   a 27-name hardcoded buff list (`spellNameLower.includes(buff)`) that
    //   silently rejected CW-authored buff spells from ally targeting. Now we
    //   inspect spell SCHEMA fields:
    //     - spellType === 'buff'        → SRD-tagged or CW-tagged buff
    //     - appliedEffect (truthy)      → spell registers a duration effect
    //                                     (Bless, Aid, Heroism, etc.)
    //     - spellType === 'healing'     → already handled by isHealing above
    //                                     but kept here for completeness
    //   Spells with damage AND a save are NOT ally-targetable (filtered out).
    //   Custom Workshop spells inherit this contract automatically.
    if (spell.spellType === 'buff' || spell.spellType === 'healing') return true;
    if (spell.appliedEffect) return true;

    // Concentration buffs without explicit spellType still need to be allowed —
    // most fall under "no damage, no save, has duration > Instantaneous". Use
    // duration as a fallback heuristic for legacy spell records that lack
    // spellType / appliedEffect tagging.
    const isInstantaneous = !spell.duration || /^\s*instantaneous\s*$/i.test(spell.duration);
    const hasDamage = !!(spell.damage || spell.damage2);
    const hasSave = !!spell.savingThrow || (spell.saveType && spell.saveType !== 'attack');
    if (!isInstantaneous && !hasDamage && !hasSave) return true;

    return false;
  };

  // [NEW NOV 13, 2025]: Check if character can ritual cast this spell
  // [PHB p.114]: Wizards can ritual cast spells in spellbook (even if not prepared)
  // [PHB p.201]: Other classes must have ritual spell prepared
  const canRitualCast = (spell) => {
    if (!spell || !spell.ritual) return false;

    // [Phase 4b BC-13 closeout — APR 2026]: Granted ritual spells unlock
    //   ritual cast even when the player doesn't otherwise know the spell.
    //   Producers: Barbarian Spirit Seeker (Beast Sense, Speak with Animals),
    //   Spirit Walker (Commune with Nature). Future producers register via
    //   the ritualSpell effect type.
    if (effectCalculator.isSpellGrantedAsRitual(playerStats, inventory, spell.name)) {
      return true;
    }

    // [FIX APR 2026 — F-11]: Data-driven spellbook ritual casting
    // [WHY]: Classes with spellbook: true can ritual cast from their spellbook without preparing
    const canCastRitualsFromSpellbook = usesSpellbook(primaryClass) ||
                                         playerStats.classes?.some(c => usesSpellbook(c.className));

    if (canCastRitualsFromSpellbook) {
      // Spellbook classes can ritual cast any ritual spell in their spellbook
      // Check knownSpells for the spell
      return knownSpells.some(s => s.name === spell.name);
    }

    // Other spellcasters must have the ritual spell prepared
    return preparedSpells.some(s => s.name === spell.name);
  };

  const checkSpellComponents = (spellNameOrObject) => {
    // Handle both spell names (strings) and spell objects
    let componentDetails;
    let spellName;
    
    if (typeof spellNameOrObject === 'object' && spellNameOrObject !== null) {
      // Custom spell object with componentDetails
      componentDetails = spellNameOrObject.componentDetails;
      spellName = spellNameOrObject.name;
    } else {
      // Standard spell by name
      componentDetails = getComponentDetails(spellNameOrObject);
      spellName = spellNameOrObject;
    }
    
    // [FIX JAN 13, 2026]: Property-based detection only (ARC-014)
    // Check for focuses that replace non-costly materials
    const hasFocus = inventory.some(item => item.providesFocus);

    // Check for component pouch
    const hasPouch = inventory.some(item =>
      item.providesBasicComponents ||
      item.containerType === 'component_pouch'
    );
    
    // If no special components needed
    if (!componentDetails) {
      if (hasFocus || hasPouch) {
        return { canCast: true, message: null, source: hasFocus ? 'focus' : 'pouch' };
      }
      return { canCast: true, message: null }; // Most basic spells don't need components
    }

    // [FIX Issue 10]: Check for specific custom components FIRST before checking cost
    // Custom spells with specificComponents should validate those directly from inventory
    // without requiring focus/pouch
    if (componentDetails.specificComponents && componentDetails.specificComponents.length > 0) {
      const missingComponents = [];
      let allComponentsFound = true;

      for (const reqComponent of componentDetails.specificComponents) {
        const hasComponent = inventory.some(item => {
          // Check if item name matches and value meets minimum requirement
          const nameMatches = item.name?.toLowerCase().includes(reqComponent.name.toLowerCase()) ||
                             reqComponent.name?.toLowerCase().includes(item.name?.toLowerCase());
          const valueMeets = !reqComponent.cost || reqComponent.cost === 0 || item.value >= reqComponent.cost;
          return nameMatches && valueMeets;
        });

        if (!hasComponent) {
          allComponentsFound = false;
          missingComponents.push(`${reqComponent.name}${reqComponent.cost > 0 ? ` (${formatCurrency(reqComponent.cost)})` : ''}`);
        }
      }

      if (allComponentsFound) {
        return {
          canCast: true,
          message: `Using specific components`,
          componentsNeeded: componentDetails,
          source: 'specific_components'
        };
      }

      // Check if player can afford missing components
      const totalCost = componentDetails.specificComponents
        .filter(c => !inventory.some(item => item.name?.toLowerCase().includes(c.name.toLowerCase())))
        .reduce((sum, c) => sum + (c.cost || 0), 0);

      const totalGold = currencies.gp + (currencies.pp * 10) + (currencies.sp / 10) + (currencies.cp / 100);
      if (totalCost > 0 && totalGold >= totalCost) {
        return {
          canCast: 'needsPurchase',
          message: `Missing: ${missingComponents.join(', ')}. Purchase for ${totalCost}gp?`,
          cost: totalCost,
          componentsNeeded: componentDetails
        };
      }

      return {
        canCast: false,
        message: `Missing required components: ${missingComponents.join(', ')}`,
        componentsNeeded: componentDetails
      };
    }

    // For components WITHOUT specific cost (under 1gp)
    if (!componentDetails.cost || componentDetails.cost < 1) {
      if (settings.trackLowCostComponents) {
        // Manual tracking mode - need actual components
        const hasActualComponent = inventory.some(item => 
          componentDetails.materials.some(mat => 
            item.name?.toLowerCase().includes(mat.toLowerCase())
          )
        );
        
        if (hasActualComponent || hasFocus || hasPouch) {
          return { 
            canCast: true,
            source: hasActualComponent ? 'component' : (hasFocus ? 'focus' : 'pouch'),
            materials: componentDetails.materials
          };
        }
        
        return { 
          canCast: false,
          message: `Missing required components: ${componentDetails.materials.join(', ')}`
        };
      } else {
        // Auto mode - pouch or focus covers it
        if (hasFocus || hasPouch) {
          return { 
            canCast: true,
            source: hasFocus ? 'focus' : 'pouch',
            materials: componentDetails.materials
          };
        }
        
        return { 
          canCast: false,
          message: `Need component pouch or arcane focus for this spell`
        };
      }
    }
    
    // Check for expensive components (1gp+)
    if (componentDetails.cost > 0) {
      // For custom spells with specific component requirements
      if (componentDetails.specificComponents && componentDetails.specificComponents.length > 0) {
        const missingComponents = [];
        let allComponentsFound = true;
        
        for (const reqComponent of componentDetails.specificComponents) {
          const hasComponent = inventory.some(item => {
            // Check if item name matches and value meets minimum requirement
            const nameMatches = item.name?.toLowerCase().includes(reqComponent.name.toLowerCase());
            const valueMeets = item.value >= reqComponent.cost;
            return nameMatches && valueMeets;
          });
          
          if (!hasComponent) {
            allComponentsFound = false;
            missingComponents.push(`${reqComponent.name} (${formatCurrency(reqComponent.cost)})`);
          }
        }
        
        if (allComponentsFound) {
          return { 
            canCast: true, 
            message: `This spell requires specific components`,
            componentsNeeded: componentDetails,
            expensive: true
          };
        }
      } else {
        // Standard spell component checking
        if (hasSpecificComponents(inventory, spellName)) {
          return { 
            canCast: true, 
            message: `This spell requires ${componentDetails.materials?.join(', ') || 'special materials'}`,
            componentsNeeded: componentDetails,
            expensive: true
          };
        }
      }
      
      // Check if player has enough gold to buy components
      const totalGold = currencies.gp + (currencies.pp * 10) + (currencies.sp / 10) + (currencies.cp / 100);
      if (totalGold >= componentDetails.cost) {
        return {
          canCast: 'needsPurchase',
          message: `This spell requires materials worth ${componentDetails.cost}gp. Purchase them?`,
          cost: componentDetails.cost,
          componentsNeeded: componentDetails
        };
      }
      
      // Can't afford components
      return {
        canCast: false,
        message: `Cannot cast - requires ${componentDetails.materials.join(', ')} (${componentDetails.cost}gp)`,
        componentsNeeded: componentDetails
      };
    }
    
    return { canCast: true, message: null };
  };
  
  // Component management functions
  const addCustomComponent = (component) => {
    const newComponent = {
      id: `comp_${Date.now()}`,
      name: component.name,
      cost: component.cost || 0,
      weight: component.weight || 0,
      consumed: component.consumed || false,
      rarity: component.rarity || 'common',
      tags: component.tags || [],
      description: component.description || ''
    };
    
    // Add to custom components state
    setCustomComponents(prev => [...prev, newComponent]);
    
    // Also add to BASE_ITEMS so it can be selected in ItemDialog
    const componentKey = component.name.toLowerCase().replace(/\s+/g, '_');
    if (!BASE_ITEMS.spell_components[componentKey]) {
      BASE_ITEMS.spell_components[componentKey] = {
        icon: '✨',
        size: [1, 1],
        weight: component.weight || 0.1,
        baseValue: component.cost || 0,
        description: component.description || `Material component: ${component.name}`,
        consumedOnUse: component.consumed || false,
        customComponent: true,
        componentId: newComponent.id
      };
    }
    
    logActivity(`Created custom component: ${newComponent.name}`, 'system', {
      component: newComponent.name,
      cost: newComponent.cost,
      consumed: newComponent.consumed
    });
    
    showStatus(`✨ Created component: ${newComponent.name} (${formatCurrency(newComponent.cost)})`);
    
    return newComponent;
  };
  
  const removeCustomComponent = (componentId) => {
    setCustomComponents(prev => prev.filter(c => c.id !== componentId));
  };
  
  const getAllComponents = () => {
    // Combine built-in components with custom ones
    const builtIn = Object.entries(SPELL_COMPONENTS || {}).map(([spell, data]) => ({
      name: data.materials[0], // Use first material as name
      cost: data.cost,
      consumed: data.consumed,
      fromSpell: spell,
      builtin: true
    }));
    
    return [...builtIn, ...customComponents];
  };
  
  // [FIX JAN 13, 2026]: Property-based potion detection (ARC-014)
  // Potions define their effects via the effects array - no string matching needed
  //
  // [Sub-plan 1U-impl-E 2026-05-27]: Multi-effect potion loop refactor.
  //   PRE-REFACTOR: this function returned on the first applied effect (early-return per branch)
  //                 so multi-effect potions silently dropped trailing effects:
  //                   - Heroism [tempHP, spell:Bless grant] → only tempHP fired; Bless never registered.
  //                   - Poison  [damage, condition]         → only damage fired; condition never reached.
  //   POST-REFACTOR: drinkPotion is a thin orchestrator. The pure planPotionEffects helper at
  //                  src/utils/drinkPotionLoop.js iterates ALL effects and returns a declarative plan
  //                  (immediateActions, tempHpGains, appliedEffects, logEntries, statusEntries, etc.).
  //                  drinkPotion applies the plan in order, registers effects[] arrays into effectRegistry,
  //                  emits QUICK_BONUS_ADDED for catch-all entries, and calls consumePotion() exactly once.
  //   BUGS FIXED:    Heroism now produces TWO QB entries (tempHP + Bless), both consumed by readers.
  //                  Poison loop now reaches effect[1] (condition silently dropped, no handler — see
  //                  Sub-plan 1U-impl-H deferral for handler).
  //   RULE 22:       planPotionEffects is imported from production helper; the test file exercises it
  //                  via the canonical producer→register→reader-query→assert pattern. No inline replicas.
  const drinkPotion = (item) => {
    // Helper to remove potion and emit event (fired exactly once at end of orchestration)
    const consumePotion = () => {
      setInventory(prev => prev.filter(i => i.id !== item.id));
      systemBus.emit(EVENTS.INVENTORY_ITEM_REMOVED, {
        item: item,
        quantity: 1,
        reason: 'consumed'
      });
    };

    // Build the declarative plan (pure — no side-effects, no state writes).
    const plan = planPotionEffects(item, { rollDice });

    // ----- Apply immediate HP changes (healing / damage) -----
    // [Sub-plan 1V — 2026-05-29]: Canonical HP field is currentHitPoints (CharacterContext.js L1221).
    // Pre-fix wrote 'hitPoints' (orphan field) → undefined +/- N = NaN. Defense-in-depth `hp: next`
    // mirrors the 1S pattern (VitalStatsCard.js L1137-1146).
    for (const action of plan.immediateActions) {
      if (action.kind === 'healing') {
        setPlayerStats(prev => {
          const next = Math.min(prev.maxHitPoints, (prev.currentHitPoints ?? 0) + action.roll.total);
          return {
            ...prev,
            currentHitPoints: next,
            hp: next
          };
        });
      } else if (action.kind === 'damage') {
        setPlayerStats(prev => {
          const next = Math.max(0, (prev.currentHitPoints ?? 0) - action.roll.total);
          return {
            ...prev,
            currentHitPoints: next,
            hp: next
          };
        });
      }
    }

    // ----- Apply temp HP gains (PHB p.198 non-stacking lives inside gainTempHP) -----
    for (const gain of plan.tempHpGains) {
      gainTempHP(gain.value, gain.source);
    }

    // ----- Append QB entries (batched into a single setActiveQuickBonuses call) -----
    if (plan.appliedEffects.length > 0) {
      setActiveQuickBonuses(prev => [...prev, ...plan.appliedEffects]);

      // [Sub-plan 1U-impl-A 2026-05-27]: Register each entry's effects[] into effectRegistry,
      // mirroring QuickBonusesPanel.js:536. syncAllToRegistry is never called from production paths,
      // so this direct register call is what makes Giant Strength's STR=25 propagate to
      // getEffectiveAbilityScore. For entries without effects[] (tempHP / spell-grant currently),
      // this is a no-op guard.
      for (const ae of plan.appliedEffects) {
        if (ae.effects && Array.isArray(ae.effects)) {
          effectRegistry.register(`buff:${ae.name}`, ae.effects);
        }
      }
    }

    // ----- Emit per-effect activity log + status entries (in plan order) -----
    for (const entry of plan.statusEntries) {
      if (entry.options) {
        showStatus(entry.message, entry.duration, entry.options);
      } else {
        showStatus(entry.message, entry.duration);
      }
    }
    for (const entry of plan.logEntries) {
      if (entry.details) {
        logActivity(entry.message, entry.category, entry.details);
      } else {
        logActivity(entry.message, entry.category);
      }
    }

    // ----- Emit QUICK_BONUS_ADDED for catch-all timed entries (preserved from pre-refactor behavior) -----
    for (const sysbus of plan.systemBusEntries) {
      systemBus.emit(EVENTS.QUICK_BONUS_ADDED, sysbus.payload);
    }

    // ----- Open DC-save modal for cast-type potions with a save (e.g. Animal Friendship, Mind Reading) -----
    // [Sub-plan A1-cast-potions 2026-05-30]: pendingCastModal is non-null only when at least one
    // operation:'cast' effect has a dc. The first (and in current data, only) entry is shown.
    // costSpend is null — the item is consumed below; the modal does not touch inventory.
    if (plan.pendingCastModal && plan.pendingCastModal.length > 0) {
      setCastPotionModalData(plan.pendingCastModal[0]);
      setCastPotionModalOpen(true);
    }

    // ----- Consume the potion once, regardless of effect count -----
    if (plan.didApplyAnything) {
      consumePotion();
    } else if ((item.effects || []).length === 0) {
      // Fallback: potion has no effects[] defined at all — surface a basic toast + log.
      showStatus(`Drank ${item.name}`, 3000);
      logActivity(`Drank ${item.name}`, 'items', {});
      consumePotion();
    }
    // else: effects[] was non-empty but every entry was unknown (e.g., only 'condition' before
    // Sub-plan 1U-impl-H lands). Don't consume — preserves the pre-refactor behavior where a
    // potion with only unhandled effects was silently no-op.
  };

  // [MIGRATED 2025-10-25]: applyItemBonus and removeItemBonus moved to InventoryContext
  // See: src/contexts/InventoryContext.js (lines 589-713)

  // [NEW 2025-10-12]: Spell Copying System - Copy spells from scrolls/spellbooks to wizard's spellbook
  // [PURPOSE]: Implements D&D 5e SRD rules for wizards learning new spells
  // [INTEGRATION]: Works with SpellCopyDialog, deducts gold, updates inventory, syncs knownSpells
  const handleSpellCopy = ({ gold, time, spell, destroySource }) => {
    // Use autoPurchase for standardized currency handling
    const purchaseResult = autoPurchase(gold, currencies);

    if (!purchaseResult.success) {
      showStatus(`❌ ${purchaseResult.message}`, 3000);
      return;
    }

    // Store old currencies for event emission
    const oldCurrencies = { ...currencies };

    // Update currencies
    setCurrencies(purchaseResult.newCurrency);

    // Emit CURRENCY_CHANGED events for each changed denomination
    ['pp', 'gp', 'sp', 'cp'].forEach(type => {
      if (oldCurrencies[type] !== purchaseResult.newCurrency[type]) {
        systemBus.emit(EVENTS.CURRENCY_CHANGED, {
          type,
          oldAmount: oldCurrencies[type],
          newAmount: purchaseResult.newCurrency[type],
          change: purchaseResult.newCurrency[type] - oldCurrencies[type],
          source: 'spell_copy'
        });
      }
    });

    // Add spell to knownSpells
    setKnownSpells(prev => {
      const existingNames = new Set(prev.map(s => s.name || s));
      if (!existingNames.has(spell.name || spell)) {
        return [...prev, spell];
      }
      return prev;
    });

    // Destroy source scroll if needed
    if (destroySource && spellCopyData?.sourceItem) {
      setInventory(prev => prev.filter(item => item.id !== spellCopyData.sourceItem.id));

      // [INTEGRATION 2025-10-24]: Emit INVENTORY_ITEM_REMOVED for destroyed scroll
      systemBus.emit(EVENTS.INVENTORY_ITEM_REMOVED, {
        item: spellCopyData.sourceItem,
        quantity: 1,
        reason: 'destroyed'
      });

      logActivity(`Copied ${spell.name} from scroll (scroll destroyed)`, 'spells', {
        spell: spell.name,
        cost: gold,
        time: time
      });
    } else {
      logActivity(`Copied ${spell.name} to spellbook`, 'spells', {
        spell: spell.name,
        cost: gold,
        time: time
      });
    }

    // Advance game time (for downtime tracking)
    if (time > 0) {
      setPlayerStats(prev => ({
        ...prev,
        downtimeHours: (prev.downtimeHours || 0) + time
      }));
    }
  };

  // [NEW 2025-10-12]: Scroll Scribing System - Craft spell scrolls from prepared spells
  // [PURPOSE]: Implements D&D 5e DMG rules for crafting scrolls during downtime
  // [INTEGRATION]: Works with ScrollScribeDialog, deducts gold and spell slots, creates scroll item
  const handleScrollScribe = ({ gold, time, spell, slotLevel }) => {
    // Use autoPurchase for standardized currency handling
    const purchaseResult = autoPurchase(gold, currencies);

    if (!purchaseResult.success) {
      showStatus(`❌ ${purchaseResult.message}`, 3000);
      return;
    }

    // Store old currencies for event emission
    const oldCurrencies = { ...currencies };

    // Update currencies
    setCurrencies(purchaseResult.newCurrency);

    // Emit CURRENCY_CHANGED events for each changed denomination
    ['pp', 'gp', 'sp', 'cp'].forEach(type => {
      if (oldCurrencies[type] !== purchaseResult.newCurrency[type]) {
        systemBus.emit(EVENTS.CURRENCY_CHANGED, {
          type,
          oldAmount: oldCurrencies[type],
          newAmount: purchaseResult.newCurrency[type],
          change: purchaseResult.newCurrency[type] - oldCurrencies[type],
          source: 'scroll_scribe'
        });
      }
    });

    // Consume spell slot (if not cantrip)
    if (slotLevel > 0) {
      setSpellSlots(prev => ({
        ...prev,
        [slotLevel]: {
          ...prev[slotLevel],
          used: prev[slotLevel].used + 1
        }
      }));
    }

    // Helper function to get ordinal suffix (1st, 2nd, 3rd, etc.)
    const getOrdinalSuffix = (num) => {
      const j = num % 10;
      const k = num % 100;
      if (j === 1 && k !== 11) return 'st';
      if (j === 2 && k !== 12) return 'nd';
      if (j === 3 && k !== 13) return 'rd';
      return 'th';
    };

    // Create scroll item in inventory
    // [BATCH 6 / P0 C-5 — APR 2026]: baseValue is the scroll's DMG p.200
    //   market value (what it sells for), not the scribing cost. The two
    //   diverge — scribing a 1st-level scroll costs 25gp but the scroll
    //   sells for 50gp. Look up via getScrollScribeCost(level).marketGp.
    //   Rarity now also follows DMG p.200 (corrected: 1st = Common, not
    //   2nd; 9th = Legendary, not 'very rare').
    // eslint-disable-next-line global-require
    const { getScrollScribeCost: scribeRow } = require('./data/scrollScribeRules');
    const scrollRow = scribeRow(spell.level || 0);
    const scrollRarity =
      spell.level <= 1 ? 'common'
        : spell.level <= 3 ? 'uncommon'
        : spell.level <= 5 ? 'rare'
        : spell.level <= 8 ? 'very rare'
        : 'legendary';
    const scrollItem = {
      id: `scroll_${Date.now()}`,
      name: `Scroll of ${spell.name}`,
      icon: '📜',
      weight: 0,
      size: [1, 1],
      baseValue: scrollRow.marketGp, // DMG p.200 market value
      isScroll: true,
      containedSpell: spell,
      description: `A spell scroll containing ${spell.name} (${spell.level === 0 ? 'cantrip' : `${spell.level}${getOrdinalSuffix(spell.level)}-level`}). Can be cast by anyone, or copied to a spellbook by a wizard.`,
      rarity: scrollRarity,
      position: null // Grid system will auto-place
    };

    // Add scroll to inventory
    setInventory(prev => [...prev, scrollItem]);
    logActivity(`Scribed ${scrollItem.name}`, 'spells', {
      spell: spell.name,
      cost: gold,
      time: time,
      slotLevel: slotLevel
    });

    // Advance game time (for downtime tracking)
    if (time > 0) {
      setPlayerStats(prev => ({
        ...prev,
        downtimeHours: (prev.downtimeHours || 0) + time
      }));
    }
  };

  // [NEW 2025-10-12]: Open spell copy dialog for scrolls or spellbooks
  // [PURPOSE]: Context menu handler to initiate spell copying
  const openSpellCopyDialog = (sourceItem, spell = null) => {
    // If sourceItem is a scroll, extract the spell from it
    if (sourceItem.isScroll && sourceItem.containedSpell) {
      spell = sourceItem.containedSpell;
    }

    // Find wizard's spellbook in inventory
    const spellbook = inventory.find(item =>
      item.name?.toLowerCase().includes('spellbook') || item.isSpellbook
    );

    if (!spellbook) {
      showStatus('⚠️ You need a spellbook to copy spells!', 3000);
      return;
    }

    if (!spell) {
      showStatus('⚠️ No spell found in this item!', 3000);
      return;
    }

    setSpellCopyData({ sourceItem, spell, targetSpellbook: spellbook });
    setShowSpellCopyDialog(true);
  };

  // [UX-22 - NOV 16, 2025]: Read spell scroll to cast spell once
  // [PURPOSE]: Allow characters to cast spells from scrolls (D&D 5e DMG p.200)
  // [WHY]: Scrolls are single-use spell items that should be consumed when read
  // [D&D 5e RULES]: Spell cast at level written on scroll, no slot consumed, scroll destroyed
  const readScrollSpell = async (scrollItem) => {
    // Validate scroll has a spell
    if (!scrollItem.containedSpell) {
      showStatus('⚠️ This scroll has no spell!', 3000);
      return;
    }

    const spell = scrollItem.containedSpell;
    const spellLevel = spell.level || 0;

    // [Sub-plan 4M — 2026-05-28]: Off-list scroll cast gate (DMG p.139)
    // RAW: if the spell is not on your class list, the scroll is unintelligible.
    // House rule (enableOffListCasting=true): allow an Arcana check DC 10 + level.
    if (!isSpellOnClassList(spell, playerStats)) {
      if (!settings.enableOffListCasting) {
        // RAW: scroll is unintelligible — block cast, do NOT consume scroll
        showStatus(`📜 Scroll unintelligible — ${spell.name} is not on your class list. (DMG p.139)`, 5000);
        logActivity(`Scroll of ${spell.name} unreadable — not on class list (RAW)`, 'items', {
          scrollName: scrollItem.name,
          spellName: spell.name
        });
        return;
      }
      // House rule enabled: show Arcana check modal. The actual cast (or fizzle) happens in
      // the onConfirm/onCancel callbacks below — readScrollSpell returns after opening the modal.
      const stats = getEffectiveStats();
      const intMod = Math.floor((stats.int - 10) / 2);
      // Arcana proficiency level from character data
      const arcanaProfLevel = playerStats.skillProficiencies?.arcana || 0;
      const profBonus = effectCalculator ? effectCalculator.getProficiencyBonus(stats) : 0;
      let arcanaMod = intMod;
      if (arcanaProfLevel === 2) {
        arcanaMod += profBonus * 2; // Expertise
      } else if (arcanaProfLevel === 1) {
        arcanaMod += profBonus;    // Proficient
      }

      setOffListScrollPendingData({ scrollItem, spell, spellLevel, arcanaMod });
      setShowOffListScrollModal(true);
      return; // Modal callbacks drive the rest of the flow
    }

    // [D&D 5e DMG p.200]: Spell is on class list — proceed with normal scroll cast

    // Prepare context for spell casting service
    const context = {
      // Component checking (scrolls may have expensive components in the spell description)
      checkSpellComponents,
      consumeSpellComponents,
      inventory,
      settings,

      // Resources (scrolls don't use spell slots)
      spellSlots,
      setSpellSlots,
      setInventory,

      // Currency
      currencies,
      setCurrencies,

      // Character stats
      playerStats,

      // Rolling functions
      rollSpellAttack: () => {
        const stats = getEffectiveStats();
        const spellcastingMod = Math.floor((stats.int - 10) / 2); // Use INT for scroll reading
        // [FIX APR 2026 — F-4]: Use effectCalculator instead of fallback
        const profBonus = effectCalculator.getProficiencyBonus(stats);
        const bonus = spellcastingMod + profBonus;

        const quickBonus = activeQuickBonuses.reduce((sum, qb) => {
          if (qb.type === 'attackRoll' || qb.type === 'all') {
            return sum + qb.value;
          }
          return sum;
        }, 0);

        const d20 = Math.floor(Math.random() * 20) + 1;
        const total = d20 + bonus + quickBonus;

        let text = `Attack: ${total}`;
        let quickBonusText = '';
        if (quickBonus > 0) {
          text += ` (d20: ${d20} + ${bonus} base + ${quickBonus} bonus)`;
          quickBonusText = ` + ${quickBonus} bonus`;
        } else {
          text += ` (d20: ${d20} + ${bonus})`;
        }

        return {
          roll: d20,
          bonus: bonus + quickBonus,
          total,
          text,
          quickBonus,
          quickBonusText
        };
      },
      getSpellSaveDC: () => {
        const stats = getEffectiveStats();
        const spellcastingMod = Math.floor((stats.int - 10) / 2); // Use INT for scroll reading
        // [FIX APR 2026 — F-4]: Use effectCalculator instead of fallback
        const profBonus = effectCalculator.getProficiencyBonus(stats);
        return 8 + spellcastingMod + profBonus;
      },
      getSpellAttackBonus: () => {
        const stats = getEffectiveStats();
        const spellcastingMod = Math.floor((stats.int - 10) / 2);
        // [FIX APR 2026 — F-4]: Use effectCalculator instead of fallback
        const profBonus = effectCalculator.getProficiencyBonus(stats);
        return spellcastingMod + profBonus;
      },

      // UI
      showStatus,
      logActivity,
      confirm
    };

    // Cast the spell using the service
    // [WHY sourceType 'scroll']: Different from 'item' because scrolls are consumed, not recharged
    const result = await spellCastingService.castSpell(
      spell,
      {
        castLevel: spellLevel,  // Scrolls cast at their written level
        sourceType: 'scroll',   // Mark as scroll for special handling
        sourceId: scrollItem.id,
        castAsRitual: false     // Scrolls cannot be cast as rituals (PHB p.201)
      },
      context
    );

    // [SPR-003 FIX — MAY 2026]: Scroll is consumed only when the cast actually
    //   fired. Pre-fix, cancelling the component-purchase prompt mid-cast
    //   destroyed the scroll anyway (the cast never ran, but the inventory
    //   delete did). DMG p.200 says scroll is consumed whether the spell
    //   SUCCEEDS or FAILS — but a user-cancelled cast never reached either
    //   outcome. Differentiated via result.message === 'Component purchase
    //   cancelled' (the explicit cancel signal from spellCastingService:605).
    const wasCancelled = !result.success
      && result.message === 'Component purchase cancelled';
    if (!wasCancelled) {
      // Delete the scroll from inventory (cast fired or fizzled — scroll consumed)
      setInventory(prevInventory => prevInventory.filter(item => item.id !== scrollItem.id));
    }

    // Show success message
    if (result.success) {
      showStatus(`📜 Read scroll of ${spell.name}! Scroll consumed.`, 3000);
      logActivity(`Read scroll of ${spell.name} (scroll consumed)`, 'items', {
        scrollName: scrollItem.name,
        spellName: spell.name,
        spellLevel: spellLevel
      });
    } else if (wasCancelled) {
      showStatus(`📜 Cancelled — scroll of ${spell.name} preserved.`, 3000);
    } else {
      showStatus(`📜 Scroll of ${spell.name} fizzled! Scroll consumed.`, 3000);
      logActivity(`Failed to read scroll of ${spell.name} (scroll consumed)`, 'items', {
        scrollName: scrollItem.name,
        spellName: spell.name,
        spellLevel: spellLevel,
        reason: result.message
      });
    }

    return result;
  };

  /**
   * [Sub-plan 4M — 2026-05-28]: Handle the result of an off-list scroll Arcana check.
   * Called by OffListScrollCastModal on "Roll" (onConfirm) or "Cancel" (onCancel).
   *
   * SUCCESS path:  roll ≥ DC → proceed with normal cast (re-enter the scroll path,
   *                bypassing the off-list gate since we already know the check passed).
   * FAILURE path:  roll < DC → consume scroll + log fizzle (no cast).
   * CANCEL path:   user dismissed → no cast, no consume.
   */
  const handleOffListScrollResult = async (result) => {
    setShowOffListScrollModal(false);
    const pending = offListScrollPendingData;
    setOffListScrollPendingData(null);

    if (!pending) return;
    const { scrollItem, spell, spellLevel } = pending;

    if (!result) {
      // Cancel — scroll preserved
      showStatus(`📜 Cancelled — scroll of ${spell.name} preserved.`, 3000);
      return;
    }

    if (!result.success) {
      // Failure — scroll consumed + spell fizzles (DMG p.139 house rule)
      setInventory(prevInventory => prevInventory.filter(item => item.id !== scrollItem.id));
      showStatus(`📜 Arcana check failed (${result.total} vs DC ${result.dc}) — scroll of ${spell.name} consumed! Spell fizzles.`, 5000);
      logActivity(
        `Failed off-list Arcana check for scroll of ${spell.name} — scroll consumed (DC ${result.dc}, rolled ${result.total})`,
        'items',
        { scrollName: scrollItem.name, spellName: spell.name, roll: result.rolled, total: result.total, dc: result.dc }
      );
      return;
    }

    // Success — proceed with normal scroll cast by calling the service directly
    // (same code path as the on-list branch — we just bypass the off-list gate)
    const context = {
      checkSpellComponents,
      consumeSpellComponents,
      inventory,
      settings,
      spellSlots,
      setSpellSlots,
      setInventory,
      currencies,
      setCurrencies,
      playerStats,
      rollSpellAttack: () => {
        const stats = getEffectiveStats();
        const spellcastingMod = Math.floor((stats.int - 10) / 2);
        const profBonus = effectCalculator.getProficiencyBonus(stats);
        const d20 = Math.floor(Math.random() * 20) + 1;
        const total = d20 + spellcastingMod + profBonus;
        return { roll: d20, bonus: spellcastingMod + profBonus, total, text: `Attack: ${total} (d20: ${d20} + ${spellcastingMod + profBonus})`, quickBonus: 0, quickBonusText: '' };
      },
      getSpellSaveDC: () => {
        const stats = getEffectiveStats();
        const spellcastingMod = Math.floor((stats.int - 10) / 2);
        return 8 + spellcastingMod + effectCalculator.getProficiencyBonus(stats);
      },
      getSpellAttackBonus: () => {
        const stats = getEffectiveStats();
        const spellcastingMod = Math.floor((stats.int - 10) / 2);
        return spellcastingMod + effectCalculator.getProficiencyBonus(stats);
      },
      showStatus,
      logActivity,
      confirm
    };

    const castResult = await spellCastingService.castSpell(
      spell,
      { castLevel: spellLevel, sourceType: 'scroll', sourceId: scrollItem.id, castAsRitual: false },
      context
    );

    const wasCancelled = !castResult.success && castResult.message === 'Component purchase cancelled';
    if (!wasCancelled) {
      setInventory(prevInventory => prevInventory.filter(item => item.id !== scrollItem.id));
    }
    if (castResult.success) {
      showStatus(`📜 Read scroll of ${spell.name} (off-list)! Arcana check passed (${result.total} vs DC ${result.dc}). Scroll consumed.`, 4000);
      logActivity(`Read off-list scroll of ${spell.name} — Arcana check passed (${result.total} vs DC ${result.dc})`, 'items', {
        scrollName: scrollItem.name, spellName: spell.name, spellLevel, roll: result.rolled, total: result.total, dc: result.dc
      });
    } else if (wasCancelled) {
      showStatus(`📜 Cancelled — scroll of ${spell.name} preserved.`, 3000);
    } else {
      showStatus(`📜 Scroll of ${spell.name} fizzled! Scroll consumed.`, 3000);
      logActivity(`Off-list scroll of ${spell.name} fizzled after Arcana success (${result.total} vs DC ${result.dc})`, 'items', {
        scrollName: scrollItem.name, spellName: spell.name, spellLevel, reason: castResult.message
      });
    }
  };

  /**
   * [Sub-plan A1-cast-potions 2026-05-30]: Handle UsableAbilityModal resolve for cast-type potions.
   *
   * Called by UsableAbilityModal onResolve when the player reports the target's save outcome.
   * The potion was already consumed by drinkPotion → no inventory change here.
   * costSpend is null — no resource to decrement.
   *
   * saveOutcome 'success'  → target resisted the spell effect.
   * saveOutcome 'failure'  → spell effect applies on target.
   */
  const handleCastPotionResolve = (resolution) => {
    if (!castPotionModalData) return;
    const { spellName, dc, itemName } = castPotionModalData;
    const outcome = resolution.saveOutcome; // 'success' | 'failure'
    const targetName = resolution.target || 'target';
    const logMsg = buildCastPotionResolveMessage(resolution, castPotionModalData);

    logActivity(
      logMsg,
      'ability',
      { spellName, itemName, dc, saveOutcome: outcome, target: targetName }
    );
    // [APP-RESULTTEXT-CRASH fix 2026-06-04]: `resultText` was undeclared in this scope
    //   (no-undef build-breaker + runtime ReferenceError on cast-potion resolve). It is
    //   local to buildCastPotionResolveMessage (drinkPotionLoop.js:312-314). Recompute the
    //   short result phrasing here for the transient toast; the activity log keeps logMsg.
    const resultText = outcome === 'success'
      ? `${targetName} resisted (save succeeded)`
      : `${targetName} affected (save failed)`;
    showStatus(
      `${spellName}: ${resultText}`,
      4000
    );

    setCastPotionModalOpen(false);
    setCastPotionModalData(null);
  };

  // [Sub-plan 1H-G-bw-S3a — 2026-06-03]: Handle reaction invocation from the
  // on-take-damage interrupt modal. Decrements the resource pool and logs activity.
  const handleReactionResolve = (resolution) => {
    if (!pendingReactionOffer) return;
    const { reactions, selectedIndex } = pendingReactionOffer;
    const selectedReaction = reactions[selectedIndex];
    if (!selectedReaction) return;

    const { actionName, attachedToResource, raceKey, classKey, cost } = selectedReaction;
    const spendAmount = cost || 1;

    // [DECREMENT POOL]: Update raceResources or classResources based on pool location
    let logMsg = `Used ${actionName}`;
    if (raceKey && attachedToResource) {
      // Race resource pool
      const currentUses = selectedReaction.currentUses || 0;
      const newCurrent = Math.max(0, currentUses - spendAmount);
      logMsg = `Used ${actionName} reaction (${newCurrent}/${selectedReaction.maxUses} uses remaining)`;
      setPlayerStats(prev => {
        const updatedRaceResources = { ...prev.raceResources };
        if (!updatedRaceResources[attachedToResource]) {
          updatedRaceResources[attachedToResource] = {};
        }
        updatedRaceResources[attachedToResource].current = newCurrent;
        return {
          ...prev,
          raceResources: updatedRaceResources
        };
      });
    } else if (classKey && attachedToResource) {
      // Class resource pool
      logMsg = `Used ${actionName} reaction`;
      setPlayerStats(prev => {
        const updatedClasses = [...(prev.classes || [])];
        const classIdx = updatedClasses.findIndex(c => c && c.className === classKey);
        if (classIdx >= 0) {
          const updatedClass = { ...updatedClasses[classIdx] };
          const camelKey = attachedToResource.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
          const pool = updatedClass[attachedToResource] || updatedClass[camelKey];
          if (pool) {
            const currentUses = pool.current || 0;
            updatedClass[attachedToResource] = {
              ...pool,
              current: Math.max(0, currentUses - spendAmount)
            };
          }
          updatedClasses[classIdx] = updatedClass;
        }
        return {
          ...prev,
          classes: updatedClasses
        };
      });
    }

    // [LOG ACTIVITY]: Record the reaction use
    const target = resolution.target || 'self';
    logActivity(
      logMsg,
      'reaction',
      {
        reactionName: actionName,
        target,
        saveOutcome: resolution.saveOutcome || null
      }
    );

    // [SHOW STATUS & CLOSE MODAL]
    showStatus(`⚡ ${actionName} reaction invoked!`, 3000);
    setPendingReactionOffer(null);
  };

  /**
   * Get effective stats with ALL modifications applied
   *
   * [INTEGRATION]: Now uses EffectCalculator service for centralized calculations
   * [BACKWARD COMPATIBLE]: Still returns same structure for existing code
   *
   * Created: Originally in App.jsx
   * Updated: December 10, 2024 - Integrated with EffectCalculator service
   */
  const getEffectiveStats = () => {
    // [FIX]: Check if game data and effectCalculator are loaded before using
    if (!gameDataLoaded || !effectCalculator) {
      // Return default stats while loading
      return {
        str: playerStats.str || 10,
        dex: playerStats.dex || 10,
        con: playerStats.con || 10,
        int: playerStats.int || 10,
        wis: playerStats.wis || 10,
        cha: playerStats.cha || 10,
        strMod: 0,
        dexMod: 0,
        conMod: 0,
        intMod: 0,
        wisMod: 0,
        chaMod: 0,
        proficiencyBonus: 2,
        ac: 10,
        maxHp: playerStats.maxHp || 10,
        initiative: 0,
        speed: 30
        // [PHASE 4.4]: carryCapacity removed - derived separately via useMemo
      };
    }

    // Use EffectCalculator for core stat calculations
    const calculatedStats = effectCalculator.calculateAllStats(playerStats, inventory, activeQuickBonuses);

    // [FIX NOV 23, 2025]: Extract carryCapacity.capacity to avoid React render error
    // [WHY]: calculateAllStats returns carryCapacity as object {capacity, encumberedAt, heavilyEncumberedAt, powerfulBuild}
    //        but components expect a number for rendering
    const { carryCapacity: carryCapacityObj, ...otherCalculatedStats } = calculatedStats;

    // Build stats object that's backward compatible with existing code
    const stats = {
      ...playerStats, // Start with base stats
      ...otherCalculatedStats, // Apply calculated stats (ability scores, HP, AC, etc.)
      carryCapacity: carryCapacityObj?.capacity ?? ((playerStats.strength || 10) * 15), // Extract capacity number

      // Add modifiers for easy access
      strMod: calculatedStats.strMod,
      dexMod: calculatedStats.dexMod,
      conMod: calculatedStats.conMod,
      intMod: calculatedStats.intMod,
      wisMod: calculatedStats.wisMod,
      chaMod: calculatedStats.chaMod
    };

    // [ADDITIONAL]: Calculate attack/damage bonuses from feats and toggles
    // (These are situational and not in EffectCalculator yet)
    let attackBonus = 0;
    let damageBonus = 0;
    let acBonus = 0;
    let initiativeBonus = calculatedStats.initiative - calculatedStats.dexMod; // Already includes DEX

    (playerStats.feats || []).forEach(feat => {
      const featData = SRD_FEATS[feat.id] || playerStats.customFeats?.find(f => f.id === feat.id);
      if (!featData) return;

      // Apply active toggle effects (Great Weapon Master, Sharpshooter, etc.)
      // [FIX NOV 4 SESSION 7]: Add null coalescing to prevent Object.entries crash
      if (featData.toggleEffects && feat.activeToggles) {
        Object.entries(feat.activeToggles || {}).forEach(([toggleId, isActive]) => {
          if (!isActive) return;

          const toggle = featData.toggleEffects[toggleId];
          if (!toggle) return;

          // Power Attack (-5 to hit, +10 damage)
          if (toggle.attackMod) attackBonus += toggle.attackMod;
          if (toggle.damageMod) damageBonus += toggle.damageMod;

          // AC modifications from toggles
          if (toggle.effects?.acPenalty) acBonus += toggle.effects.acPenalty;
          if (toggle.effects?.acBonus) acBonus += toggle.effects.acBonus;
        });
      }
    });

    // Add calculated bonuses to stats
    if (attackBonus !== 0) stats.attackBonus = attackBonus;
    if (damageBonus !== 0) stats.damageBonus = damageBonus;
    if (acBonus !== 0) stats.acBonus = acBonus;
    if (initiativeBonus !== 0) stats.initiativeBonus = initiativeBonus;

    // [BACKWARD COMPATIBLE]: Check for dual wielding (used by AC calculation)
    const mainhand = inventory.find(item => item.equipped && item.equipSlot === 'mainhand');
    const offhand = inventory.find(item => item.equipped && item.equipSlot === 'offhand');
    stats.isDualWielding = mainhand && offhand && mainhand.category === 'weapons' && offhand.category === 'weapons';

    return stats;
  };
  
  const consumeSpellComponents = (spellNameOrObject, componentCheck) => {
    // Handle both spell names and spell objects (for custom spells)
    let componentDetails;
    let spellName;
    
    if (typeof spellNameOrObject === 'object' && spellNameOrObject !== null) {
      componentDetails = spellNameOrObject.componentDetails;
      spellName = spellNameOrObject.name;
    } else {
      componentDetails = getComponentDetails(spellNameOrObject);
      spellName = spellNameOrObject;
    }
    
    // Log component usage based on source
    if (componentCheck && componentCheck.source) {
      if (componentCheck.source === 'focus') {
        logActivity(
          `Cast ${spellName} using arcane focus`,
          'spells',
          { spell: spellName, source: 'arcane focus', components: componentDetails?.materials }
        );
      } else if (componentCheck.source === 'pouch') {
        logActivity(
          `Cast ${spellName} using component pouch`,
          'spells',
          { spell: spellName, source: 'component pouch', components: componentDetails?.materials }
        );
      } else if (componentCheck.source === 'component' && componentDetails) {
        logActivity(
          `Cast ${spellName} using ${componentDetails.materials.join(', ')}`,
          'spells',
          { spell: spellName, components: componentDetails.materials, cost: componentDetails.cost }
        );
      }
    }
    
    if (!componentDetails || !componentDetails.consumed || settings.enforceComponentConsumption === false) return;

    // Consume expensive components
    if (componentDetails.cost >= 1) {
      setInventory(prev => {
        // For custom spells with specific components
        if (componentDetails.specificComponents && componentDetails.specificComponents.length > 0) {
          let newInv = [...prev];
          let consumedAny = false;
          
          for (const reqComponent of componentDetails.specificComponents) {
            if (reqComponent.consumed) {
              const componentIndex = newInv.findIndex(item => 
                item.name?.toLowerCase().includes(reqComponent.name.toLowerCase()) &&
                item.value >= reqComponent.cost
              );
              
              if (componentIndex !== -1) {
                const consumedItem = newInv[componentIndex];
                newInv.splice(componentIndex, 1);
                showStatus(`Consumed: ${consumedItem.name} worth ${formatCurrency(consumedItem.value)}`);
                logActivity(
                  `Consumed ${consumedItem.name} (${formatCurrency(consumedItem.value)}) for ${spellName}`,
                  'spells',
                  { 
                    spell: spellName,
                    component: consumedItem.name,
                    value: consumedItem.value,
                    consumed: true
                  }
                );
                consumedAny = true;
              }
            }
          }
          
          if (consumedAny) return newInv;
        }
        
        // [FIX APR 2026 — F-10]: Data-driven material consumption
        // [WHY]: Uses componentDetails.materials from spellComponents.js instead of hardcoded spell names
        // [HOW]: Check if the consumed material includes "diamond" — works for SRD + custom spells
        const materialText = componentDetails.materials?.join(' ').toLowerCase() || '';
        if (materialText.includes('diamond')) {
          const diamondIndex = prev.findIndex(item => 
            item.name?.toLowerCase().includes('diamond') &&
            item.value >= componentDetails.cost
          );
          
          if (diamondIndex !== -1) {
            const newInv = [...prev];
            const consumedItem = newInv[diamondIndex];
            newInv.splice(diamondIndex, 1);
            showStatus(`Consumed: ${consumedItem.name} worth ${formatCurrency(componentDetails.cost)}`);
            logActivity(
              `Consumed ${consumedItem.name} (${formatCurrency(componentDetails.cost)}) for ${spellName}`,
              'spells',
              { 
                spell: spellName,
                component: consumedItem.name,
                value: componentDetails.cost,
                consumed: true
              }
            );
            return newInv;
          }
        }
        
        // For other consumable components
        const componentIndex = prev.findIndex(item => 
          componentDetails.materials?.some(mat => 
            item.name?.toLowerCase().includes(mat.toLowerCase())
          )
        );
        
        if (componentIndex !== -1) {
          const newInv = [...prev];
          const consumedItem = newInv[componentIndex];
          
          // Reduce quantity or remove
          if (consumedItem.quantity > 1) {
            consumedItem.quantity--;
            showStatus(`Consumed: ${consumedItem.name} (${consumedItem.quantity} remaining)`);
          } else {
            newInv.splice(componentIndex, 1);
            showStatus(`Consumed: ${consumedItem.name}`);
          }
          
          logActivity(
            `Consumed ${componentDetails.materials.join(', ')} (${componentDetails.cost}gp) for ${spellName}`,
            'spells',
            { 
              spell: spellName,
              components: componentDetails.materials,
              value: componentDetails.cost,
              consumed: true
            }
          );
          return newInv;
        }
        
        return prev;
      });
    } else if (settings.trackLowCostComponents && componentCheck.source === 'component') {
      // Consume low-cost components in manual tracking mode
      setInventory(prev => {
        const componentIndex = prev.findIndex(item => 
          componentDetails.materials.some(mat => 
            item.name?.toLowerCase().includes(mat.toLowerCase())
          )
        );
        
        if (componentIndex !== -1 && componentDetails.consumed) {
          const newInv = [...prev];
          const consumedItem = newInv[componentIndex];
          
          if (consumedItem.quantity > 1) {
            consumedItem.quantity--;
          } else {
            newInv.splice(componentIndex, 1);
          }
          
          logActivity(
            `Consumed ${componentDetails.materials.join(', ')} for ${spellName}`,
            'spells',
            { 
              spell: spellName,
              components: componentDetails.materials,
              consumed: true,
              manualTracking: true
            }
          );
          return newInv;
        }
        return prev;
      });
    }
  };
  
  // [REFACTORED]: Using spellCastingService for unified spell casting
  const castSpell = async (itemId, spellIndex) => {
    // Find the item and spell
    const item = inventory.find(i => i.id === itemId);
    if (!item) {
      showStatus('Item not found');
      return;
    }

    const spells = getItemSpells(item);
    const spell = spells[spellIndex];

    if (!spell) {
      showStatus('No charges remaining');
      return;
    }
    // Pool-based check: item has shared charge pool + spell declares costPerCast
    const isPoolBased = item.charges != null && item.maxCharges != null && spell.costPerCast != null;
    if (isPoolBased) {
      if (item.charges < spell.costPerCast) {
        showStatus(`Insufficient charges (need ${spell.costPerCast}, have ${item.charges})`);
        return;
      }
    } else if (spell.currentUses <= 0) {
      // Legacy per-spell counter guard
      showStatus('No charges remaining');
      return;
    }

    // Prepare context for service
    const context = {
      // Component checking
      checkSpellComponents,
      consumeSpellComponents,
      inventory,
      settings,

      // Resources
      spellSlots,
      setSpellSlots,
      setInventory,

      // Currency
      currencies,
      setCurrencies,

      // Character stats
      playerStats,

      // Rolling functions
      rollSpellAttack,
      getSpellSaveDC,

      // UI
      showStatus,
      logActivity,
      confirm
    };

    // Cast using service
    const result = await spellCastingService.castSpell(
      spell,
      {
        castLevel: spell.level,
        sourceType: 'item',
        sourceId: itemId,
        spellIndex: spellIndex
      },
      context
    );

    // Service handles everything: checks, components, rolls, resource consumption, logging
    return result;
  };
  
  const resetDailySpells = () => {
    setInventory(prevInventory =>
      prevInventory.map(item => {
        const baseData = getItemDetails(item);
        if (!baseData || !baseData.spells) return item;
        
        const resetSpells = baseData.spells.map(spell => ({
          ...spell,
          currentUses: spell.usesPerDay
        }));
        
        return { ...item, spells: resetSpells };
      })
    );
  };
  
  const getAvailableSpells = () => {
    const spells = [];
    inventory.forEach(item => {
      if (!item.equipped) return;
      
      const itemSpells = getItemSpells(item);
      itemSpells.forEach((spell, index) => {
        if (spell.currentUses > 0) {
          spells.push({
            ...spell,
            itemName: item.name,
            itemId: item.id,
            spellIndex: index,
            castSpell: () => castSpell(item.id, index)
          });
        }
      });
    });
    return spells;
  };

  // ========================================
  // WIZARD HANDLERS
  // ========================================

  // [PURPOSE]: Handle completion of Character Creation Wizard
  // [WHY]: Applies all wizard data to playerStats AND transfers equipment to inventory
  // [INTEGRATION]: Updates character state, inventory, and logs activity
  // [FIX]: Equipment from wizard now properly added to inventory tab
  const handleCharacterWizardComplete = (newCharacter) => {
    // [FIX NOV 24, 2025]: Clear existing inventory for new character
    // [WHY]: Previous character's items were persisting when creating a new character
    setInventory([]);
    setVaults([]);
    setCurrencies({ gp: 0, sp: 0, cp: 0, pp: 0, ep: 0 });

    // [REWRITTEN NOV 25, 2025]: Fixed race condition - items were overlapping
    // [WHY]: Previous code called addStarterPack() and addNewItem() separately
    //        Each scheduled async setInventory() calls that saw stale state
    // [FIX]: Build ALL items first, then do SINGLE setInventory() with collision checking

    // === STEP 1: Build all items to add (from packs AND equipment) ===
    const allItemsToAdd = [];
    const allItemsNotAdded = [];
    let currencyChanges = { gp: 0, sp: 0, cp: 0, pp: 0, ep: 0 };

    // 1a. Build items from starter packs
    if (newCharacter.starterPacks && Array.isArray(newCharacter.starterPacks)) {
      newCharacter.starterPacks.forEach(packKey => {
        const { itemsToAdd, itemsNotAdded, packName } = buildStarterPackItems(packKey);
        allItemsToAdd.push(...itemsToAdd);
        allItemsNotAdded.push(...itemsNotAdded);
      });
    }

    // 1b. Build items from equipment array
    if (newCharacter.equipment && Array.isArray(newCharacter.equipment)) {
      newCharacter.equipment.forEach(itemData => {
        if (!itemData || !itemData.category || !itemData.baseType) {
          console.warn('[CharacterCreation] Invalid equipment itemData:', itemData);
          return;
        }

        // Verify item exists in BASE_ITEMS
        const baseItem = BASE_ITEMS[itemData.category]?.[itemData.baseType];
        if (!baseItem) {
          console.error(`[CharacterCreation] Item not found: ${itemData.category}.${itemData.baseType}`);
          return;
        }

        // Track contained currency (will apply after)
        if (itemData.containsCurrency) {
          const { amount, type } = itemData.containsCurrency;
          currencyChanges[type] = (currencyChanges[type] || 0) + amount;
        }

        // [v1.0 FIX]: Create InventoryItem with ALL metadata from resolver
        // [WHY]: Previous code only passed {name}, losing quantity, equipSlot, armorType, etc.
        // [INTEGRATION]: InventoryItem constructor reads customData for all properties
        const { category, baseType, source, ...metadata } = itemData;
        const newItem = new InventoryItem(
          itemData.baseType,
          itemData.category,
          {
            name: metadata.name || EquipmentResolver.formatDisplayName(itemData.baseType),
            quantity: metadata.quantity,
            bundledQuantity: metadata.bundledQuantity,
            equipSlot: metadata.equipSlot,
            stackable: metadata.stackable,
            armorType: metadata.armorType,
            maxDexMod: metadata.maxDexMod,
            isContainer: metadata.isContainer,
            containerCapacity: metadata.containerCapacity,
            containsCurrency: metadata.containsCurrency,
          }
        );
        allItemsToAdd.push(newItem);
      });
    }

    // === STEP 2: SINGLE setInventory() call with merge-then-place ===
    // [P2.14 FIX APR 29 2026]: Route the grant through mergeAndPlaceWizardItems
    // so stackable items (daggers, arrows, potions, etc.) merge into a single
    // stack instead of landing as parallel grid entries — mirroring
    // InventoryContext.addItem's name+category merge rule. Re-running the
    // wizard or re-importing starter equipment now tops up the existing stack
    // instead of duplicating it.
    // [WHY single setInventory()]: The Nov 25 2025 rewrite established that
    // ALL items must be placed in one functional updater so each placement
    // sees fresh state — running addItem() in a loop would re-introduce the
    // race condition. The helper preserves that contract.
    let mergeResult = null;
    setInventory(prev => {
      const result = mergeAndPlaceWizardItems(allItemsToAdd, prev, gridCols, gridRows);
      mergeResult = result;
      return result.newInventory;
    });

    // [EVENT EMISSION]: Defer to after the state update so listeners see
    // post-merge inventory. addedItems carries enough context to distinguish
    // stacked top-ups from fresh placements for activity-log clarity.
    if (mergeResult) {
      mergeResult.addedItems.forEach(record => {
        systemBus.emit(EVENTS.INVENTORY_ITEM_ADDED, {
          item: record.item,
          quantity: record.deltaQuantity,
          source: 'character_creation',
          stacked: record.stacked,
        });
      });
      mergeResult.itemsNotAdded.forEach(name => {
        allItemsNotAdded.push(name);
        console.warn(`[CharacterCreation] ⚠️ Could not place "${name}"`);
      });
    }

    // === STEP 3: Apply currency changes ===
    const hasCurrencyChanges = Object.values(currencyChanges).some(v => v > 0);
    if (hasCurrencyChanges) {
      setCurrencies(prev => {
        const newCurrencies = { ...prev };
        Object.entries(currencyChanges).forEach(([type, amount]) => {
          if (amount > 0) {
            newCurrencies[type] = (newCurrencies[type] || 0) + amount;
            systemBus.emit(EVENTS.CURRENCY_CHANGED, {
              type,
              oldAmount: prev[type] || 0,
              newAmount: newCurrencies[type],
              change: amount,
              source: 'starting_equipment_container'
            });
          }
        });
        return newCurrencies;
      });
    }

    if (allItemsNotAdded.length > 0) {
      console.warn('[CharacterCreation] Items that could not be placed:', allItemsNotAdded);
    }

    // [PHASE 2.5 - NOV 11, 2025]: Add starting gold if rolled in character creation wizard
    // [WHY]: Wizard allows rolling for starting gold, but it wasn't being added to currency
    // [INTEGRATION]: Checks newCharacter.startingGoldRolled and adds startingGoldResult to currency
    if (newCharacter.startingGoldRolled && newCharacter.startingGoldResult) {
      const startingGold = typeof newCharacter.startingGoldResult === 'number'
        ? newCharacter.startingGoldResult
        : newCharacter.startingGoldResult.total || newCharacter.startingGoldResult;

      if (startingGold > 0) {
        const oldCurrencies = { ...currencies };
        const newCurrencies = {
          ...oldCurrencies,
          gp: (oldCurrencies.gp || 0) + startingGold
        };

        setCurrencies(newCurrencies);

        // Emit CURRENCY_CHANGED event for starting gold
        systemBus.emit(EVENTS.CURRENCY_CHANGED, {
          type: 'gp',
          oldAmount: oldCurrencies.gp || 0,
          newAmount: newCurrencies.gp,
          change: startingGold,
          source: 'starting_gold_roll'
        });

      }
    }

    // Remove equipment from character data since it goes to inventory state
    const { equipment, ...characterWithoutEquipment } = newCharacter;
    // [A4 FIX 2026-05-20]: Stamp id/characterId immediately so the new
    // character has a stable identifier the moment it lands in state. Mirrors
    // saveCharacter at L8642 — both code paths must produce the same id so
    // the in-memory snapshot, the savedCharacters[] entry, and the autosave
    // blob all carry the same id (was the 2026-05-06 Lvl 4 Barbarian
    // regression: party-create guard fired with "Save your character first"
    // because in-memory state had no id).
    const stampedNewCharacter = stampCharacterIds(characterWithoutEquipment, null);
    setPlayerStats(stampedNewCharacter);

    // [FIX APR 2026 — F35/F54/F64/F65]: Sync skill proficiencies from wizard to SkillsPanel format
    // [WHY]: Wizard stores proficiencies.skills as ['Athletics', 'Stealth'] (name array)
    //        but SkillsPanel reads skillProficiencies as {athletics: 1, stealth: 1} (numeric object)
    //        Without this sync, freshly created characters have ZERO skill proficiency bonuses
    let pendingSkillProfs = null;
    if (stampedNewCharacter.proficiencies?.skills) {
      const converted = convertSkillNamesToObject(stampedNewCharacter.proficiencies.skills);
      pendingSkillProfs = converted;
      setSkillProficiencies(converted);
    }

    // [FIX 2025-10-12]: Extract spells from classes[].spellbook and sync to knownSpells state
    // [WHY]: Wizards store spells in classes[].spellbook, but Spells tab reads from knownSpells
    // [INTEGRATION]: This connects the two systems so spells actually appear after character creation
    let pendingKnownSpells = null;
    if (stampedNewCharacter.classes && Array.isArray(stampedNewCharacter.classes)) {
      const allSpells = [];
      stampedNewCharacter.classes.forEach(classEntry => {
        if (classEntry.spellbook && Array.isArray(classEntry.spellbook)) {
          allSpells.push(...classEntry.spellbook);
        }
      });

      if (allSpells.length > 0) {
        pendingKnownSpells = allSpells;
        setKnownSpells(allSpells);
      }
    }

    // [D3 FIX]: Pre-update stateRef so beforeunload handler captures the new character
    // immediately (stateRef is normally updated by a useEffect that runs after render;
    // if the user closes the app before that render, beforeunload would save stale data)
    // [CLR-004 FIX — MAY 2026]: Sibling state slices (knownSpells, preparedSpells,
    //   skillProficiencies) are updated by the same handler — pre-write them too,
    //   else tab-close in the gap between setState and the next render saves a
    //   character with stale spellbook / prepared / skill data.
    if (stateRef && stateRef.current) {
      stateRef.current = {
        ...stateRef.current,
        playerStats: stampedNewCharacter,
        ...(pendingSkillProfs ? { skillProficiencies: pendingSkillProfs } : {}),
        ...(pendingKnownSpells ? { knownSpells: pendingKnownSpells } : {}),
      };
    }

    // [FIX JAN 12, 2026]: Sync prepared spells from character creation (Druids, Clerics)
    // [WHY]: Prepared casters (Druid, Cleric) need their selected spells synced to preparedSpells state
    // [INTEGRATION]: Connects wizard prepared spell selection to the spell system
    // [FIX]: SpellbookTab.js:521 uses preparedSpells.includes(spell.name) - expects NAMES, not IDs!
    if (newCharacter.preparedSpells && Array.isArray(newCharacter.preparedSpells) && newCharacter.preparedSpells.length > 0) {
      // Add spells to knownSpells first
      newCharacter.preparedSpells.forEach(spell => {
        // Check if spell already in knownSpells
        const existing = knownSpells.find(s => s.name === spell.name);
        if (!existing) {
          // Add to knownSpells
          const newId = Date.now() + Math.random();
          setKnownSpells(prev => [...prev, { ...spell, id: newId, classSource: newCharacter.class }]);
        }
      });

      // [FIX]: Store spell NAMES, not IDs - matches SpellbookTab.js:521 check
      const preparedSpellNames = newCharacter.preparedSpells.map(spell => spell.name);
      setPreparedSpells(preparedSpellNames);
      // [CLR-004 FIX — MAY 2026]: Mirror to stateRef for tab-close safety.
      if (stateRef && stateRef.current) {
        stateRef.current = {
          ...stateRef.current,
          preparedSpells: preparedSpellNames,
        };
      }
    }

    // [INTEGRATION]: Emit events for character creation
    // [WHY]: Allow other systems to react to new character

    // Emit SPELL_LEARNED events for starting spells
    if (newCharacter.spells && newCharacter.spells.length > 0) {
      newCharacter.spells.forEach(spell => {
        systemBus.emit(EVENTS.SPELL_LEARNED, {
          spell: typeof spell === 'string' ? { name: spell } : spell,
          source: 'character_creation',
          spellbook: newCharacter.class
        });
      });
    }

    // [INTEGRATION 2025-10-24]: Emit character creation events
    // [WHY]: Notify components of initial character state for reactive updates

    // Emit RACE_CHANGED event
    if (newCharacter.race) {
      systemBus.emit(EVENTS.RACE_CHANGED, {
        oldRace: null,
        newRace: newCharacter.race,
        source: 'creation'
      });
    }

    // Emit CLASS_CHANGED event
    if (newCharacter.class) {
      systemBus.emit(EVENTS.CLASS_CHANGED, {
        oldClass: null,
        newClass: newCharacter.class,
        level: newCharacter.level || 1,
        isMulticlass: false,
        source: 'creation'
      });
    }

    // Emit BACKGROUND_CHANGED event
    if (newCharacter.background) {
      systemBus.emit(EVENTS.BACKGROUND_CHANGED, {
        oldBackground: null,
        newBackground: newCharacter.background,
        source: 'creation'
      });
    }

    // Clear EffectCalculator cache for new character
    effectCalculator.clearCache();

    // [A4 FIX 2026-05-20]: Register the freshly-created character into
    // savedCharacters[] roster IMMEDIATELY. Pre-fix, handleCharacterWizardComplete
    // only set playerStats and relied on the full-app autosave blob for
    // persistence — the Manage Characters drawer (App.jsx:12937) showed only
    // PRIOR saves, and a second wizard run silently lost the first character
    // because the autosave blob just got overwritten. User reported as A4 on
    // 2026-05-09 (*"when a character is created from the wizard, it should save"*)
    // and implied in A48 (2026-05-16 wizard ↔ drawer ↔ save audit ask).
    //
    // Why inline instead of calling saveCharacter() at L8625: saveCharacter()
    // reads from React state (playerStats, inventory, currencies, ...) which
    // hasn't flushed yet inside this handler — all the setState calls above
    // are queued, not applied. Constructing the savedCharacters entry from
    // the wizard-output values directly avoids the closure-staleness problem
    // and matches the same shape buildCurrentCharacterData() produces at L8573.
    //
    // [PARITY NOTE]: This data block mirrors buildCurrentCharacterData() field
    // by field. If that helper grows new fields, update both. Reserved fields
    // (`_unknownFields` spread) intentionally omitted — fresh character, not
    // a round-trip restore.
    const wizardSaveTimestamp = new Date().toISOString();
    const startingGoldGranted = (newCharacter.startingGoldRolled && newCharacter.startingGoldResult)
      ? (typeof newCharacter.startingGoldResult === 'number'
          ? newCharacter.startingGoldResult
          : (newCharacter.startingGoldResult.total || newCharacter.startingGoldResult))
      : 0;
    const postWizardCurrencies = {
      gp: (currencyChanges.gp || 0) + startingGoldGranted,
      sp: (currencyChanges.sp || 0),
      cp: (currencyChanges.cp || 0),
      pp: (currencyChanges.pp || 0),
      ep: (currencyChanges.ep || 0),
    };
    setSavedCharacters(prev => {
      const filtered = prev.filter(c =>
        c && c.id !== stampedNewCharacter.id &&
        c.name !== (stampedNewCharacter.name || 'Unnamed Character')
      );
      const wizardEntry = {
        id: stampedNewCharacter.id,
        name: stampedNewCharacter.name || 'Unnamed Character',
        characterName: stampedNewCharacter.name || 'Unnamed Character',
        createdAt: wizardSaveTimestamp,
        lastModified: wizardSaveTimestamp,
        totalPlayTime: 0,
        data: {
          playerStats: stampedNewCharacter,
          inventory: mergeResult ? mergeResult.newInventory : [],
          vaults: [],
          currencies: postWizardCurrencies,
          settings,
          knownSpells: pendingKnownSpells || [],
          preparedSpells: (newCharacter.preparedSpells && Array.isArray(newCharacter.preparedSpells))
            ? newCharacter.preparedSpells.map(s => s.name)
            : [],
          spellSlots,
          customSpells,
          manualSpellSlots,
          activeQuickBonuses: [],
          temporaryStats: {},
          timeSystem,
          currentGameDay,
          calendarSettings,
          skillProficiencies: pendingSkillProfs || {},
          customShopTypes,
          customRollTables,
          activeShops,
          activityLog: [],
          journalEntries: [],
          journalRollTables: [],
          rollTables,
          craftingProjects: [],
          dmEvents: [],
        },
      };
      const newList = [...filtered, wizardEntry].sort((a, b) =>
        new Date(b.lastModified) - new Date(a.lastModified)
      );
      if (settings.rememberLastCharacter) {
        safeLocalStorageSet('lastCharacterId', stampedNewCharacter.id);
      }
      return newList;
    });
    // Clear "unsaved" indicator since we just persisted into the roster.
    setHasUnsavedChanges(false);

    setShowCharacterWizard(false);
    logActivity(`Created new character: ${newCharacter.name}`, 'character', {
      race: newCharacter.race,
      class: newCharacter.class,
      level: newCharacter.level,
      startingItems: newCharacter.equipment?.length || 0
    });
    showStatus(`Character ${newCharacter.name} created with ${newCharacter.equipment?.length || 0} starting items!`, 3000);
  };

  // [PURPOSE]: Handle completion of Level-Up Wizard
  // [WHY]: Updates character level, HP, features, feats, and spells
  // [INTEGRATION]: Complex state updates for multiclass characters
  const handleLevelUpComplete = (levelUpData) => {
    setPlayerStats(prev => {
      // [Spec A Task 6 — 2026-05-16]: `let` (not const) because the new
      //   routeHighLevelSpellPicks helper returns a new object that gets
      //   reassigned. All other mutations in this block still use direct
      //   field assignment (updated.foo = bar) — only the helper path
      //   needs the reassignment.
      let updated = { ...prev };

      // 1. Update total character level
      updated.level = levelUpData.newLevel;

      // [PHASE B / #001]: Hoisted classIndex so the wizard-pick blocks below
      //                   (starryForm, spiritTotem, elegantCourtierChoice)
      //                   can address the freshly-modified class entry in
      //                   BOTH branches. Previously declared with `const`
      //                   inside the else branch, which left it out of
      //                   scope at the wizard-pick site (no-undef).
      let classIndex = -1;

      // 2. Update class levels
      if (levelUpData.isMulticlassing && levelUpData.newMulticlassName) {
        // Adding a new multiclass
        updated.classes = updated.classes || [];
        const newClassEntry = {
          className: levelUpData.newMulticlassName,
          level: 1,
          spellbook: [...(levelUpData.newCantrips || []), ...(levelUpData.newSpells || [])]
        };

        // [PURPOSE]: Store subclass for level 1 subclass choices (Cleric, Sorcerer, Warlock)
        // [WHY]: Some classes choose subclass at level 1 when multiclassing
        if (levelUpData.selectedSubclass) {
          newClassEntry.subclass = levelUpData.selectedSubclass;
        }

        // [PURPOSE]: Store subclass choices for new multiclass
        // [WHY]: Persist choices like Dragon Ancestry, Divine Domain, Warlock Patron
        if (levelUpData.subclassChoices) {
          newClassEntry.subclassData = {};

          // Store any subclass choices that were made
          if (levelUpData.subclassChoices.maneuvers?.length > 0) {
            newClassEntry.subclassData.maneuvers = levelUpData.subclassChoices.maneuvers;
          }
          if (levelUpData.subclassChoices.totemSpirit) {
            newClassEntry.subclassData.totemSpirit = levelUpData.subclassChoices.totemSpirit;
          }
          if (levelUpData.subclassChoices.landType) {
            newClassEntry.subclassData.landType = levelUpData.subclassChoices.landType;
          }
          if (levelUpData.subclassChoices.disciplines?.length > 0) {
            newClassEntry.subclassData.disciplines = levelUpData.subclassChoices.disciplines;
          }
          if (levelUpData.subclassChoices.hunterOptions && Object.keys(levelUpData.subclassChoices.hunterOptions).length > 0) {
            newClassEntry.subclassData.hunterOptions = levelUpData.subclassChoices.hunterOptions;
          }
        }

        updated.classes.push(newClassEntry);
        // [PHASE B / #001]: Set classIndex to the just-pushed entry so the
        //                   downstream wizard-pick blocks address it.
        classIndex = updated.classes.length - 1;
      } else {
        // Leveling existing class
        updated.classes = updated.classes || [{ className: updated.class, level: updated.level }];
        classIndex = updated.classes.findIndex(
          c => c.className === levelUpData.classToLevel
        );

        if (classIndex >= 0) {
          updated.classes[classIndex].level += 1;

          // Add new spells to class spellbook
          if (levelUpData.newCantrips || levelUpData.newSpells) {
            updated.classes[classIndex].spellbook = [
              ...(updated.classes[classIndex].spellbook || []),
              ...(levelUpData.newCantrips || []),
              ...(levelUpData.newSpells || [])
            ];
          }

          // Add subclass if selected
          if (levelUpData.selectedSubclass) {
            updated.classes[classIndex].subclass = levelUpData.selectedSubclass;
          }

          // [PURPOSE]: Store subclass-specific choices (maneuvers, totems, disciplines, etc.)
          // [WHY]: Persist player's subclass choices so they can be referenced and displayed
          // [INTEGRATION]: Used by FeatsPanel, CharacterBasicsPanel, and combat systems
          if (levelUpData.subclassChoices) {
            const classData = resolveClass(levelUpData.classToLevel);
            const subclassData = classData?.subclasses?.[levelUpData.selectedSubclass];

            // Initialize subclassData if it doesn't exist
            updated.classes[classIndex].subclassData = updated.classes[classIndex].subclassData || {};

            // Battle Master: Store maneuvers and superiority dice
            if (levelUpData.subclassChoices.maneuvers && levelUpData.subclassChoices.maneuvers.length > 0) {
              updated.classes[classIndex].subclassData.maneuvers = [
                ...(updated.classes[classIndex].subclassData.maneuvers || []),
                ...levelUpData.subclassChoices.maneuvers
              ];

              // Initialize superiority dice if first time choosing maneuvers
              if (!updated.classes[classIndex].subclassData.superiorityDice && subclassData?.superiorityDice) {
                const diceInfo = subclassData.superiorityDice[levelUpData.newLevel];
                if (diceInfo) {
                  updated.classes[classIndex].subclassData.superiorityDice = {
                    max: diceInfo.count,
                    current: diceInfo.count,
                    dieSize: diceInfo.size
                  };
                }
              }
            }

            // Totem Warrior: Store totem spirit choice
            if (levelUpData.subclassChoices.totemSpirit) {
              updated.classes[classIndex].subclassData.totemSpirit = levelUpData.subclassChoices.totemSpirit;
            }

            // Circle of the Land: Store land type (determines circle spells)
            if (levelUpData.subclassChoices.landType) {
              updated.classes[classIndex].subclassData.landType = levelUpData.subclassChoices.landType;
            }

            // Way of Four Elements: Store elemental disciplines
            if (levelUpData.subclassChoices.disciplines && levelUpData.subclassChoices.disciplines.length > 0) {
              updated.classes[classIndex].subclassData.disciplines = [
                ...(updated.classes[classIndex].subclassData.disciplines || []),
                ...levelUpData.subclassChoices.disciplines
              ];
            }

            // Hunter: Store hunter tactical options by level
            if (levelUpData.subclassChoices.hunterOptions && Object.keys(levelUpData.subclassChoices.hunterOptions).length > 0) {
              updated.classes[classIndex].subclassData.hunterOptions = {
                ...(updated.classes[classIndex].subclassData.hunterOptions || {}),
                ...levelUpData.subclassChoices.hunterOptions
              };
            }
          }
        }
      }

      // [Phase 4b wizard-picks — APR 2026]: Merge expertise picks into the
      // character-level expertiseSkills array. The useExpertiseEffects hook
      // (mounted in App.jsx) registers proficiencyLevel:2 effects for each
      // entry. Multiclass Bard + Rogue both append; de-dupe via Set.
      if (Array.isArray(levelUpData.expertisePicks) && levelUpData.expertisePicks.length > 0) {
        const existing = Array.isArray(updated.expertiseSkills) ? updated.expertiseSkills : [];
        const merged = Array.from(new Set([...existing, ...levelUpData.expertisePicks]));
        updated.expertiseSkills = merged;
      }

      // [Phase 4b wizard-picks — APR 2026]: Merge metamagic picks into the
      // character-level metamagicOptions array. spellCastingService reads at
      // cast time to offer the player the metamagic options they've chosen.
      if (Array.isArray(levelUpData.metamagicPicks) && levelUpData.metamagicPicks.length > 0) {
        const existing = Array.isArray(updated.metamagicOptions) ? updated.metamagicOptions : [];
        const merged = Array.from(new Set([...existing, ...levelUpData.metamagicPicks]));
        updated.metamagicOptions = merged;
      }

      // [Spec B Task 12 — 2026-05-16]: Append Artificer infusion picks
      // to playerStats.classKnownSelections.Artificer.infuse_item.
      // DailySetupModal then surfaces the recipe set in its known-options
      // dropdown so the player can bind active infusions each long rest.
      if (Array.isArray(levelUpData.infusionPicks) && levelUpData.infusionPicks.length > 0) {
        const knownSel = { ...(updated.classKnownSelections || {}) };
        const artificer = { ...(knownSel.Artificer || {}) };
        const existing = Array.isArray(artificer.infuse_item) ? artificer.infuse_item : [];
        artificer.infuse_item = Array.from(new Set([...existing, ...levelUpData.infusionPicks]));
        knownSel.Artificer = artificer;
        updated.classKnownSelections = knownSel;
      }

      // [Phase 4b wizard-picks — APR 2026; Spec A Task 6 — 2026-05-16]:
      // Route high-level spell picks to the correct character path. Wizard
      // L18/L20 write to playerStats.customClassPicks (generic, replaces
      // pre-Spec-A Wizard-specific paths). Mystic Arcanum + Magical Secrets
      // continue to write to their feature-specific paths.
      if (Array.isArray(levelUpData.highLevelSpellPicks) && levelUpData.highLevelSpellPicks.length > 0) {
        const ctxClass = levelUpData.classToLevel;
        const ctxLevel = (function () {
          const cls = updated.classes.find(c => c.className === ctxClass);
          return cls?.level || 1;
        })();
        updated = routeHighLevelSpellPicks(updated, ctxClass, ctxLevel, levelUpData.highLevelSpellPicks);
      }

      // [Phase 4b wizard-picks — APR 2026]: Single-pick subclass choices
      // landed in choice-plumbing followup (8fecf26). Wizard captures into
      // levelUpData.subclassChoices.{starryForm, spiritTotem, pactBoon};
      // App.jsx commits to the right storage path. Hooks (useSubclassFeatureEffects,
      // useClassFeatureEffects choices+choiceKey) auto-register the matching effects.
      if (levelUpData.subclassChoices?.starryForm && classIndex >= 0) {
        if (!updated.classes[classIndex].subclassData) {
          updated.classes[classIndex].subclassData = {};
        }
        updated.classes[classIndex].subclassData.starryForm = levelUpData.subclassChoices.starryForm;
      }
      if (levelUpData.subclassChoices?.spiritTotem && classIndex >= 0) {
        if (!updated.classes[classIndex].subclassData) {
          updated.classes[classIndex].subclassData = {};
        }
        updated.classes[classIndex].subclassData.spiritTotem = levelUpData.subclassChoices.spiritTotem;
      }
      if (levelUpData.subclassChoices?.pactBoon) {
        // pactBoon is a top-level character choice (Warlock — single Pact Boon
        // across multiclass), not subclassData. The choiceKey on the Warlock
        // 'Pact Boon' entry in srdClassFeatures.js reads from playerStats.pactBoon.
        updated.pactBoon = levelUpData.subclassChoices.pactBoon;
      }
      if (levelUpData.subclassChoices?.elegantCourtierChoice && classIndex >= 0) {
        // Fighter Samurai L7 — WIS save proficiency OR Insight/Performance/Persuasion skill.
        // Stored at classes[i].subclassData.elegantCourtierChoice; read by
        // useSubclassFeatureEffects via the choiceKey on 'Elegant Courtier' in
        // srdSubclassFeatures.js, which registers the matching proficiency effect.
        if (!updated.classes[classIndex].subclassData) {
          updated.classes[classIndex].subclassData = {};
        }
        updated.classes[classIndex].subclassData.elegantCourtierChoice =
          levelUpData.subclassChoices.elegantCourtierChoice;
      }

      // [Phase 5 wizard-picks — APR 2026]: Pact of the Tome cantrips (Warlock L3).
      // Merges into the top-level pactTomeCantrips array. useClassFeatureEffects
      // registers each as an atWillSpell effect on next mount cycle.
      if (Array.isArray(levelUpData.pactTomeCantrips) && levelUpData.pactTomeCantrips.length > 0) {
        const existing = Array.isArray(updated.pactTomeCantrips) ? updated.pactTomeCantrips : [];
        updated.pactTomeCantrips = Array.from(new Set([...existing, ...levelUpData.pactTomeCantrips]));
      }

      // [Phase 5 wizard-picks — APR 2026]: Favored Enemy (Ranger L1 — PHB p.91).
      // Stores the chosen creature type string at playerStats.favoredEnemy.
      // Displayed as information on the character sheet; no effect registration needed.
      if (levelUpData.favoredEnemy) {
        updated.favoredEnemy = levelUpData.favoredEnemy;
      }

      // [Phase B / #044 — MAY 2026]: Auto stat boosts driven by data.
      //   Pre-fix this branch hardcoded the Barbarian L20 Primal Champion
      //   +4 STR / +4 CON cap-24 bump as a `className === 'Barbarian'` /
      //   `level === 20` match. The bump now lives as two
      //   `levelProgression: [{ atLevel:20, action:'autoStatBoost', ... }]`
      //   entries on SRD_CLASSES.Barbarian, dispatched via the shared
      //   helper. Future classes (homebrew or 2024 PHB) can add their
      //   own auto-bumps purely by editing data.
      //   classLevel is the post-update per-class level (NOT
      //   levelUpData.newLevel, which is the character-total level —
      //   they differ for multiclass characters).
      const leveledClassEntry = Array.isArray(updated.classes)
        ? updated.classes.find((c) => c.className === levelUpData.classToLevel)
        : null;
      if (leveledClassEntry) {
        applyLevelProgressionAutoStatBoosts(
          updated,
          levelUpData.classToLevel,
          leveledClassEntry.level
        );
      }

      // 3. Update HP
      updated.maxHitPoints += levelUpData.rolledHP;
      updated.currentHitPoints += levelUpData.rolledHP;
      // [MAXHP-UNIFICATION — 2026-07-25]: accumulate roll luck so the calculateMaxHP
      // mirror (which uses the PHB average formula for levels 2+) preserves rolled HP.
      // resolveClass(levelUpData.classToLevel) is fetched below for hit dice; reuse it here.
      updated.hpRollDelta = (updated.hpRollDelta || 0) +
        levelUpHpRollDelta(levelUpData, resolveClass(levelUpData.classToLevel));

      // 4. Update hit dice
      // [FIX]: Normalize legacy number format (e.g., d10: 1) to object format ({ max, current })
      // [WHY]: Old autosave data may store hitDice as plain numbers, causing "Cannot create property 'max' on number"
      const classData = resolveClass(levelUpData.classToLevel);
      if (classData) {
        const dieType = classData.hitDice;
        const dieKey = dieType.replace('1', '');
        updated.hitDice = updated.hitDice || {};
        const existing = updated.hitDice[dieKey];
        if (typeof existing === 'number') {
          updated.hitDice[dieKey] = { max: existing, current: existing };
        } else if (!existing || typeof existing !== 'object') {
          updated.hitDice[dieKey] = { max: 0, current: 0 };
        }
        updated.hitDice[dieKey].max += 1;
        updated.hitDice[dieKey].current += 1;
      }

      // 5. Add features
      updated.features = updated.features || [];
      if (levelUpData.newFeatures && levelUpData.newFeatures.length > 0) {
        updated.features = [
          ...updated.features,
          ...levelUpData.newFeatures.map(f => ({ name: f, source: 'class' }))
        ];
      }
      if (levelUpData.subclassFeatures && levelUpData.subclassFeatures.length > 0) {
        updated.features = [
          ...updated.features,
          ...levelUpData.subclassFeatures.map(f => ({ name: f, source: 'subclass' }))
        ];
      }

      // 6. Apply ASI
      if (levelUpData.choiceType === 'asi' && levelUpData.asiConfig && levelUpData.asiConfig.stat1) {
        const { stat1, bonus1, stat2, bonus2 } = levelUpData.asiConfig;
        updated[stat1] = Math.min(20, (updated[stat1] || 10) + bonus1);
        if (stat2 && bonus2 > 0) {
          updated[stat2] = Math.min(20, (updated[stat2] || 10) + bonus2);
        }
      }

      // 7. Add feat
      if (levelUpData.choiceType === 'feat' && levelUpData.selectedFeat) {
        updated.feats = updated.feats || [];
        updated.feats.push({
          id: levelUpData.selectedFeat.id,
          name: levelUpData.selectedFeat.name
        });
      }

      // 8. Update proficiency bonus
      updated.proficiencyBonus = Math.ceil(updated.level / 4) + 1;

      return updated;
    });

    // [FIX 2025-10-12]: Sync new spells from level-up to knownSpells state
    // [WHY]: Level-up wizard stores spells in classes[].spellbook, but Spells tab reads from knownSpells
    // [INTEGRATION]: Add new spells without losing existing ones
    if (levelUpData.newCantrips || levelUpData.newSpells) {
      const newSpellsToAdd = [
        ...(levelUpData.newCantrips || []),
        ...(levelUpData.newSpells || [])
      ];

      if (newSpellsToAdd.length > 0) {
        setKnownSpells(prev => {
          // Avoid duplicates by checking spell names
          const existingNames = new Set(prev.map(s => s.name || s));
          const uniqueNewSpells = newSpellsToAdd.filter(spell => {
            const spellName = spell.name || spell;
            return !existingNames.has(spellName);
          });

          return [...prev, ...uniqueNewSpells];
        });
      }
    }

    // [INTEGRATION]: Emit comprehensive level-up events for system integration
    // [WHY]: Allow other components to react to level-up changes

    // 1. Emit LEVEL_UP event
    systemBus.emit(EVENTS.LEVEL_UP, {
      oldLevel: levelUpData.newLevel - 1,
      newLevel: levelUpData.newLevel,
      className: levelUpData.classToLevel,
      isMulticlass: levelUpData.isMulticlassing || false,
      newMulticlassName: levelUpData.newMulticlassName || null,
      hpGained: levelUpData.rolledHP,
      featuresGained: [
        ...(levelUpData.newFeatures || []),
        ...(levelUpData.subclassFeatures || [])
      ]
    });

    // 2. Emit HP_CHANGED event
    systemBus.emit(EVENTS.HP_CHANGED, {
      oldHP: playerStats.currentHitPoints || 0,
      newHP: (playerStats.currentHitPoints || 0) + levelUpData.rolledHP,
      maxHP: (playerStats.maxHitPoints || 0) + levelUpData.rolledHP,
      damage: 0,
      healing: levelUpData.rolledHP,
      source: 'level_up'
    });

    // 3. Emit ABILITY_SCORE_CHANGED events for ASI applications
    // [INTEGRATION 2025-10-24]: Emit both ABILITY_SCORE_CHANGED and STATS_CHANGED
    // [WHY]: Components subscribe to ABILITY_SCORE_CHANGED, keep STATS_CHANGED for backward compatibility
    if (levelUpData.choiceType === 'asi' && levelUpData.asiConfig) {
      const { stat1, bonus1, stat2, bonus2 } = levelUpData.asiConfig;

      if (stat1 && bonus1 > 0) {
        const oldValue = playerStats[stat1] || 10;
        const newValue = Math.min(20, oldValue + bonus1);

        systemBus.emit(EVENTS.ABILITY_SCORE_CHANGED, {
          ability: stat1,
          oldValue,
          newValue,
          source: 'asi'
        });

        // Also emit general STATS_CHANGED for backward compatibility
        systemBus.emit(EVENTS.STATS_CHANGED, {
          ability: stat1,
          oldValue,
          newValue,
          source: 'asi'
        });
      }

      if (stat2 && bonus2 > 0) {
        const oldValue = playerStats[stat2] || 10;
        const newValue = Math.min(20, oldValue + bonus2);

        systemBus.emit(EVENTS.ABILITY_SCORE_CHANGED, {
          ability: stat2,
          oldValue,
          newValue,
          source: 'asi'
        });

        // Also emit general STATS_CHANGED for backward compatibility
        systemBus.emit(EVENTS.STATS_CHANGED, {
          ability: stat2,
          oldValue,
          newValue,
          source: 'asi'
        });
      }
    }

    // 4. Emit FEAT_ADDED event if feat was selected
    if (levelUpData.choiceType === 'feat' && levelUpData.selectedFeat) {
      const feat = levelUpData.selectedFeat;
      systemBus.emit(EVENTS.FEAT_ADDED, {
        featId: feat.id,
        featName: feat.name,
        passiveEffects: feat.passiveEffects || null,
        activeAbilities: feat.activeAbilities || null,
        toggleEffects: feat.toggleEffects || null,
        customChoices: null,
        source: 'level_up'
      });
    }

    // 5. Emit SPELL_LEARNED events for new spells
    const newSpellsLearned = [
      ...(levelUpData.newCantrips || []),
      ...(levelUpData.newSpells || [])
    ];
    newSpellsLearned.forEach(spell => {
      systemBus.emit(EVENTS.SPELL_LEARNED, {
        spell: typeof spell === 'string' ? { name: spell } : spell,
        source: 'level_up',
        spellbook: levelUpData.classToLevel
      });
    });

    // 6. Emit CLASS_CHANGED event for multiclassing or class level increase
    // [INTEGRATION 2025-10-24]: Notify components of class changes
    if (levelUpData.isMulticlassing && levelUpData.newMulticlassName) {
      systemBus.emit(EVENTS.CLASS_CHANGED, {
        oldClass: primaryClass,
        newClass: levelUpData.newMulticlassName,
        level: 1,
        isMulticlass: true,
        source: 'level_up'
      });
    } else if (levelUpData.classToLevel) {
      // Leveling existing class - still emit to update level-dependent features
      systemBus.emit(EVENTS.CLASS_CHANGED, {
        oldClass: levelUpData.classToLevel,
        newClass: levelUpData.classToLevel,
        level: levelUpData.newLevel,
        isMulticlass: (playerStats.classes?.length || 0) > 1,
        source: 'level_up'
      });
    }

    // 7. Emit SUBCLASS_CHANGED event if subclass was selected
    // [INTEGRATION 2025-10-24]: Notify components of subclass selection
    if (levelUpData.selectedSubclass) {
      systemBus.emit(EVENTS.SUBCLASS_CHANGED, {
        className: levelUpData.classToLevel,
        oldSubclass: null,
        newSubclass: levelUpData.selectedSubclass,
        level: levelUpData.newLevel,
        source: 'level_up'
      });
    }

    // 8. Clear EffectCalculator cache to trigger recalculations
    effectCalculator.clearCache();

    // Log activity
    logActivity(`Leveled up to ${levelUpData.newLevel}!`, 'character', {
      class: levelUpData.classToLevel,
      hpGained: levelUpData.rolledHP,
      featuresGained: (levelUpData.newFeatures?.length || 0) + (levelUpData.subclassFeatures?.length || 0)
    });

    // [GOD-TIER UX]: Trigger epic confetti celebration!
    setLevelUpLevel(levelUpData.newLevel);
    setShowConfetti(true);

    showStatus(`Congratulations! You are now level ${levelUpData.newLevel}!`, 3000);
    setShowLevelUpWizard(false);
  };

  // ========================================
  // CONTEXT MENU
  // ========================================
  
  const handleContextMenu = (e, item, source) => {
    e.preventDefault();
    // [BUG FIX MAY 2026 / #207]: Use clientX/clientY (viewport coords) not
    //   pageX/pageY (document coords). The ContextMenuComponent renders with
    //   `position: fixed`, which is RELATIVE TO THE VIEWPORT — pageX/pageY
    //   include the scroll offset, so on any scrolled page (or any tall
    //   inventory grid) the menu was positioned off-screen below the
    //   viewport. Combined with #207 viewport-clamp in ContextMenuComponent,
    //   right-clicks now always render the menu in-frame.
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      item,
      source
    });
  };
  
  useEffect(() => {
    const handleClick = () => setContextMenu(null);
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);
  
  useEffect(() => {
    if (showVaultModal || openContainer || showItemDialog || showVaultDialog || editingItem || showCustomShopCreator) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showVaultModal, openContainer, showItemDialog, showVaultDialog, editingItem, showCustomShopCreator]);
  
  // [FIX]: Sync top-level race/class with nested basics structure for CharacterBasicsPanel
  // [WHY]: Main Character tab uses playerStats.race, but CharacterBasicsPanel uses character.basics.race
  // [INTEGRATION]: Keeps both data structures in sync when either is modified
  // [FIX APR 2026 Phase 3E]: Loop-safe. Only fire when flat prop is TRUTHY and differs
  // from basics (migration direction: flat → basics). Previously `background !== basics.background`
  // alone fired when flat was undefined and basics was set, creating a latent render loop
  // if someone deleted flat props as part of a cleanup pass. Now symmetric + truthy-gated.
  useEffect(() => {
    const needsSync =
      (playerStats.race && playerStats.race !== playerStats.basics?.race) ||
      (playerStats.class && playerStats.class !== playerStats.basics?.class) ||
      (playerStats.background && playerStats.background !== playerStats.basics?.background);

    if (needsSync) {
      setPlayerStats(prev => ({
        ...prev,
        basics: {
          ...prev.basics,
          race: prev.race || prev.basics?.race || '',
          class: prev.class || prev.basics?.class || '',
          background: prev.background || prev.basics?.background || ''
        }
      }));
    }
  }, [playerStats.race, playerStats.class, playerStats.background]);

  // [GOD-TIER]: Keyboard shortcuts for power users
  useKeyboardShortcuts({
    onHeal: (amount) => {
      const currentHP = playerStats.hp ?? playerStats.maxHp ?? 10;
      const maxHP = playerStats.maxHp || 10;
      const newHP = Math.min(currentHP + amount, maxHP);
      // [FIX]: Must set canonical currentHitPoints — 'hp' alias is overwritten by computed memo
      setPlayerStats(prev => ({ ...prev, currentHitPoints: newHP }));
      showStatus(`💚 Healed ${amount} HP (H key)`, 2000);
      logActivity(`Healed ${amount} HP via keyboard shortcut`, 'combat');
    },
    onDamage: (amount) => {
      // [Sub-plan 4N/4G — 2026-05-29]: route keyboard damage through the on_take_damage firer
      //   (parity with CombatTab.handleHPAdjust) so the character's resistances halve the amount
      //   AND any on_take_damage triggered effects fire + consume. No-op (finalAmount === amount,
      //   no pendingConsumes) for characters without resistances/triggers.
      let effectiveAmount = amount;
      let pendingConsumes = [];
      let offeredReactions = [];
      try {
        const result = applyOnTakeDamageTriggers(playerStats, playerStats?.inventory || [], {
          amount,
          damageType: 'untyped',
          source: 'keyboard-shortcut',
        });
        effectiveAmount = result.finalAmount;
        pendingConsumes = result.pendingConsumes || [];
        offeredReactions = result.offeredReactions || [];
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[App] keyboard on_take_damage firer threw:', e);
      }
      // [5E RULES]: Apply damage to temp HP first, then regular HP
      const currentHP = playerStats.hp ?? playerStats.currentHitPoints ?? playerStats.maxHp ?? 10;
      const tempHP = playerStats.temporaryHitPoints ?? 0;
      let remainingDamage = effectiveAmount;
      let newTempHP = tempHP;
      let newHP = currentHP;

      // Apply damage to temp HP first
      if (tempHP > 0) {
        if (remainingDamage >= tempHP) {
          remainingDamage -= tempHP;
          newTempHP = 0;
        } else {
          newTempHP = tempHP - remainingDamage;
          remainingDamage = 0;
        }
      }

      // Apply remaining damage to regular HP
      if (remainingDamage > 0) {
        newHP = Math.max(currentHP - remainingDamage, 0);
      }

      setPlayerStats(prev => ({
        ...prev,
        hp: newHP,
        currentHitPoints: newHP,
        temporaryHitPoints: newTempHP
      }));
      for (const sourceRef of pendingConsumes) {
        consumeTriggeredSource(sourceRef, { setPlayerStats });
      }

      const tempHPText = tempHP > 0 ? ` (${Math.min(effectiveAmount, tempHP)} absorbed by temp HP)` : '';
      const resistText = effectiveAmount !== amount ? ` [${amount}→${effectiveAmount} after resistance]` : '';
      showStatus(`❤️ Took ${effectiveAmount} damage${tempHPText}${resistText} (D key)`, 2000);
      logActivity(`Took ${effectiveAmount} damage via keyboard shortcut`, 'combat', {
        damage: effectiveAmount,
        rawDamage: amount,
        tempHPAbsorbed: tempHP > 0 ? Math.min(effectiveAmount, tempHP) : 0,
        regularHPLost: remainingDamage
      });

      // [Sub-plan 1H-G-bw-S3a — 2026-06-03]: Offer available reactions when damage is taken.
      // If the character has any reactions available (e.g., Hellish Rebuke), offer them
      // via a modal so the player can choose to invoke them.
      if (Array.isArray(offeredReactions) && offeredReactions.length > 0) {
        // Filter to reactions that have uses remaining (currentUses > 0)
        const availableToInvoke = offeredReactions.filter(r => r.currentUses === null || r.currentUses > 0);
        if (availableToInvoke.length > 0) {
          setPendingReactionOffer({
            reactions: availableToInvoke,
            selectedIndex: 0,
            damageAmount: effectiveAmount,
            showInvocation: availableToInvoke.length === 1  // Auto-open modal if single reaction
          });
        }
      }
    },
    onLevelUp: () => {
      if (playerStats.level < 20) {
        setShowLevelUpWizard(true);
        showStatus('📈 Opening Level-Up Wizard (L key)', 2000);
      }
    },
    onToggleEquipment: () => {
      setActiveTab(prev => prev === 'inventory' ? 'character' : 'inventory');
      showStatus(`🎒 ${activeTab === 'inventory' ? 'Closing' : 'Opening'} Inventory (E key)`, 1500);
    },
    onToggleSpells: () => {
      setActiveTab(prev => prev === 'spellbook' ? 'character' : 'spellbook');
      showStatus(`✨ ${activeTab === 'spellbook' ? 'Closing' : 'Opening'} Spellbook (S key)`, 1500);
    },
    // [UI-KEYBOARD-GAPS G-6 2026-07-04]: the "I" shortcut was advertised in
    // KeyboardShortcutsHelp but never wired — the hook called
    // actions.onFocusInventory?.() and no caller provided it. The inventory
    // search input only exists in Table view, so switch there before focusing.
    onFocusInventory: () => {
      setActiveTab('inventory');
      setShowSpreadsheetView(true);
      showStatus('🔍 Inventory search (I key)', 1500);
      setTimeout(() => {
        document.querySelector('[data-shortcut="inventory-search"]')?.focus();
      }, 150);
    },
    onRest: () => {
      setActiveTab('rest');
      showStatus('🛌 Opening Rest Panel (R key)', 1500);
    },
    onJournal: () => {
      setActiveTab('journal');
      showStatus('📖 Opening Journal (J key)', 1500);
    },
    onShowHelp: () => {
      setShowKeyboardHelp(true);
      showStatus('⌨️ Keyboard Shortcuts Help (? key)', 1500);
    },
    onEscape: () => {
      // Close all modals/panels
      setShowVaultModal(null);
      setOpenContainer(null);
      setShowItemDialog(false);
      setShowVaultDialog(false);
      setEditingItem(null);
      setEditingVault(null);
      setContextMenu(null);
      setShowSpellSelection(false);
      setShowCustomSpellCreator(false);
      setDirectAddToVault(null);
      setIsAddingToShop(false);
      setShowKeyboardHelp(false);
      setShowCharacterWizard(false);
      setShowLevelUpWizard(false);
      setRosterDrawerOpen(false); // [2026-05-11]: Roster drawer
      showStatus('✖️ Closed all modals (Esc key)', 1000);
    }
  }, { enabled: !!settings.keyboardShortcutsEnabled });

  // [PHASE 4D CLEANUP]: calculateMulticlassSpellcasterLevel removed - now using effectCalculator.calculateMulticlassSpellSlots()
  // The multiclass spell slot calculation has been centralized in effectCalculator service
  // This ensures consistent D&D 5e PHB p.164-165 compliance across the entire application
  // Note: We pass null to useClassEffects for backward compatibility

  // [PURPOSE]: Gain temporary hit points following D&D 5e rules
  // [5E RULES]: Temp HP doesn't stack - you choose the higher value
  // [WHY]: Used by spells (Aid, False Life, etc), items, and class features
  // [SIM-TEMPHP-NO-STACKING-RULE — 2026-07-18]: the resolution now lives in
  //   utils/tempHPRule.js so the manual Temp HP input (VitalStatsCard) applies the
  //   IDENTICAL rule — this function's behaviour is unchanged.
  const gainTempHP = (amount, source = 'unknown') => {
    const current = playerStats.temporaryHitPoints ?? 0;
    const resolved = resolveTempHPGrant(current, amount);

    if (resolved > current) {
      // New temp HP is higher - replace current
      setPlayerStats(prev => ({
        ...prev,
        temporaryHitPoints: resolved
      }));
      showStatus(`🎒 Gained ${amount} temporary HP from ${source}`, 2000);
      logActivity(`Gained ${amount} temporary HP from ${source}`, 'combat', {
        tempHP: amount,
        previousTempHP: current,
        source
      });
    } else if (amount === current) {
      showStatus(`🎒 Already have ${current} temporary HP (same amount)`, 2000);
    } else {
      // Current temp HP is higher - keep it
      showStatus(`🎒 Keeping ${current} temporary HP (higher than ${amount} from ${source})`, 2000);
    }
  };

  // [PHASE 7 INTEGRATION 2025-10-10]: Auto-update hooks for seamless integration
  // [PURPOSE]: Automatically apply racial modifiers when race changes
  useRaceEffects(playerStats, setPlayerStats, logActivity);

  // [PURPOSE]: Automatically recalculate spell slots when classes change
  // [PHASE 4D FIX]: Pass null for deprecated calculateMulticlassSpellcasterLevel parameter
  useClassEffects(playerStats, setSpellSlots, null, logActivity);

  // [PURPOSE]: Automatically update AC/senses when equipment changes
  useItemEffects(inventory, playerStats, setPlayerStats, logActivity);

  // [PURPOSE]: Automatically register feat effects with EffectRegistry
  // [FIX JAN 13, 2026]: Re-enabled - TDZ error was caused by something else
  // [UX-20 - NOV 16, 2025]: Fixes Mobile feat not increasing speed
  // [WHY]: FeatsPanel is conditionally rendered, so effects only registered when tab is active
  //        This hook runs in App.jsx (always mounted) to ensure effects work on all tabs
  useFeatEffects(playerStats, logActivity);

  // [Sub-plan 3A — 2026-05-30]: Register custom boon passiveEffects in effectRegistry.
  // Mirrors useFeatEffects — always-mounted in App.jsx so boon effects fire on ALL tabs,
  // not just when a boon-aware panel is rendered. All boons register unconditionally
  // in v1.0 (Fork 3A-Q3 resolution C; active-toggle UI deferred to 3A-followup-active-toggle).
  useBoonEffects(playerStats, logActivity);

  // [Phase 4b bucket 2 — APR 2026]: Register class feature effects (Extra Attack
  // for all 5 martial classes, Monk Martial Arts die scaling, Barbarian Rage
  // damage bonus). Mirrors useFeatEffects — keeps the registry in sync with
  // playerStats.classes regardless of which tab is active.
  useClassFeatureEffects(playerStats);

  // [Phase 4b bucket 4 — APR 2026]: Register race trait effects (darkvision senses,
  // Dwarven Resilience, Hellish Resistance, Gnome Cunning, Brave, Fey Ancestry,
  // Mountain Dwarf armor profs, Dragonborn breath weapon resistance, etc.).
  // Mirrors useClassFeatureEffects — keeps the registry in sync with race/subrace
  // changes regardless of which tab is active.
  useRaceFeatureEffects(playerStats);

  // [Phase 4b wizard-picks — APR 2026]: Register expertise (proficiencyLevel:2)
  // effects from playerStats.expertiseSkills. The wizard step appends skill
  // keys to that array when player picks Bard/Rogue Expertise, Cleric
  // Knowledge Blessings, etc. Hook walks the array and registers proficiency
  // effects so calculateSkillBonus reads them via getGrantedProficiencyLevel.
  useExpertiseEffects(playerStats);

  // [Phase 4b bucket 3 — APR 2026]: Register SRD subclass feature effects.
  // Mirrors useRaceFeatureEffects — keeps the registry in sync with subclass /
  // subclassData (totem choice, land type, hunter options) regardless of which
  // tab is active.
  useSubclassFeatureEffects(playerStats);

  // [Phase B / #336 — Spec 1b Task 18]: Fire a one-time warning toast when the
  // loaded character has non-proficient armor equipped and the enforce flag is
  // on. Default-ON as of #335 means existing saves that suddenly apply PHB p.144
  // penalties need a clear heads-up with an escape hatch (Settings → Game Rules).
  useArmorProficiencyWarning(playerStats, settings, showStatus);

  // [Phase 4b CW-parity (C) follow-up — APR 2026]: Initialize the spell-effect
  //   expiry listener once. Concentration spells get cleaned up by
  //   concentrationSystem.breakConcentration; non-concentration spells (Mage
  //   Armor / Stoneskin / Heroes' Feast / Aid) are cleaned up by this service
  //   on REST_SHORT_STARTED / REST_LONG_STARTED / CALENDAR_DAWN events. Init
  //   is idempotent — the second mount is a no-op.
  useEffect(() => {
    const teardown = initSpellEffectExpiry({ logActivity });
    return teardown;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // empty deps — singleton init

  // Auto-calculate spell slots based on class and level when not in manual mode
  // [PHASE 4D FIX]: Use centralized effectCalculator for multiclass spell slots
  useEffect(() => {
    if (!manualSpellSlots && (primaryClass || playerStats.classes.length > 0)) {
      // [FIX]: Use effectCalculator's centralized multiclass spell slot calculation
      // This correctly implements D&D 5e PHB p.164-165 multiclass spellcaster rules
      const calculatedSlots = effectCalculator.calculateMulticlassSpellSlots(playerStats);

      setSpellSlots(prev => {
        const newSlots = {};
        for (let level = 1; level <= 9; level++) {
          newSlots[level] = {
            max: calculatedSlots[level]?.max || 0,
            used: Math.min(prev[level]?.used || 0, calculatedSlots[level]?.max || 0)
          };
        }
        return newSlots;
      });
    }
  }, [playerStats.class, playerStats.level, playerStats.classes, manualSpellSlots, playerStats.subclass]);
  
  // ========================================
  // EXPORT/IMPORT
  // ========================================
  
  const exportVault = (vaultId) => {
    const vault = vaults.find(v => v.id === vaultId);
    if (!vault) return;
    
    try {
      const data = {
        version: '1.0',
        timestamp: new Date().toISOString(),
        vault: vault
      };
      
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Create filename with vault name and datestamp
      const vaultName = vault.name.replace(/[^a-z0-9]/gi, '_').toLowerCase();
      const dateStr = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      a.download = `vault_${vaultName}_${dateStr}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Vault export failed:', error);
      alert('Failed to export vault. Check console for details.');
    }
  };
  
  const importVault = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target.result);
        if (data.vault) {
          // Give the vault a new ID to avoid conflicts
          const importedVault = {
            ...data.vault,
            id: `vault_${Date.now()}`,
            name: data.vault.name + ' (Imported)'
          };
          setVaults(prev => [...prev, importedVault]);
          showStatus(`✅ Vault "${data.vault.name}" imported successfully!`, 4000);
        } else {
          showStatus('❌ Invalid vault file format - please select a valid vault export file', 5000);
        }
      } catch (error) {
        console.error('Vault import error:', error);
        showStatus('❌ Failed to load vault file - file may be corrupted or invalid', 5000);
      }
    };
    reader.readAsText(file);
    event.target.value = ''; // Reset file input
  };
  
  const exportInventory = () => {
    try {
      const data = {
        version: '1.0',
        timestamp: new Date().toISOString(),
        playerStats,
        inventory,
        vaults,
        currencies,
        settings
      };
      
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Create filename with character name and datestamp
      const characterName = playerStats.name ? 
        playerStats.name.replace(/[^a-z0-9]/gi, '_').toLowerCase() : 
        'character';
      const dateStr = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      a.download = `${characterName}_inventory_${dateStr}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Failed to export inventory. Check console for details.');
    }
  };
  
  const exportAsText = () => {
    let text = '=== D&D INVENTORY ===\n\n';
    text += `Carrying: ${totalWeight.toFixed(1)}/${carryCapacity} lbs (${encumbranceLevel})\n`;
    text += `Gold: ${currencies.gp} | Silver: ${currencies.sp} | Copper: ${currencies.cp}\n\n`;
    
    text += '--- ITEMS ---\n';
    const printItem = (item, indent = '') => {
      text += `${indent}• ${item.name} (${item.weight || 0}lb) - ${formatCurrency(item.value)}\n`;
      if (item.description) text += `${indent}  ${item.description}\n`;
      if (item.isContainer && item.containedItems?.length > 0) {
        text += `${indent}  Contains:\n`;
        item.containedItems.forEach(ci => printItem(ci, indent + '    '));
      }
    };
    inventory.forEach(item => printItem(item));
    
    navigator.clipboard.writeText(text);
    alert('Inventory copied to clipboard!');
  };

  // [P1.2+P1.3 fix - Apr 29, 2026]: DMG_LOOT_TABLES is now imported from
  // src/data/magicItemTables.js as an alias for MAGIC_ITEM_TABLES (the
  // canonical 11-table registry covering DMG Tables A-I + XGtE + TCoE).
  // Pre-fix, this scope held a local 3-table const that diverged from
  // the canonical registry — see commit log for details.

  // Shop Generation Roll Tables with d100 system
  // === PROPER D&D STYLE ROLL TABLES ===
  const SHOP_ROLL_TABLES = {
    // General Store - Basic adventuring gear
    general_dmg: {
      rolls: [
        { min: 1, max: 10, item: 'rope_hempen_50ft', rarity: 'common' },
        { min: 11, max: 20, item: 'rations', rarity: 'common' },
        { min: 21, max: 30, item: 'torch', rarity: 'common' },
        { min: 31, max: 35, item: 'bedroll', rarity: 'common' },
        { min: 36, max: 40, item: 'waterskin', rarity: 'common' },
        { min: 41, max: 45, item: 'backpack', rarity: 'common' },
        { min: 46, max: 50, item: 'crowbar', rarity: 'common' },
        { min: 51, max: 55, item: 'hammer', rarity: 'common' },
        { min: 56, max: 60, item: 'lantern_hooded', rarity: 'common' },
        { min: 61, max: 65, item: 'oil_flask', rarity: 'common' },
        { min: 66, max: 70, item: 'tinderbox', rarity: 'common' },
        { min: 71, max: 75, item: 'blanket', rarity: 'common' },
        { min: 76, max: 80, item: 'grappling_hook', rarity: 'common' },
        { min: 81, max: 85, item: 'potion_healing', rarity: 'uncommon' },
        { min: 86, max: 88, item: 'thieves_tools', rarity: 'uncommon' },
        { min: 89, max: 91, item: 'climbers_kit', rarity: 'uncommon' },
        { min: 92, max: 94, item: 'spyglass', rarity: 'uncommon' },
        { min: 95, max: 96, item: 'magnifying_glass', rarity: 'uncommon' },
        { min: 97, max: 98, item: 'potion_greater_healing', rarity: 'rare' },
        { min: 99, max: 100, item: 'bag_of_holding', rarity: 'rare' }
      ],
      // Fallback for old system compatibility
      common: ['rope_hempen_50ft', 'rations', 'torch', 'bedroll', 'waterskin', 'backpack', 'crowbar', 'hammer', 'lantern_hooded', 'oil_flask', 'tinderbox', 'blanket', 'component_pouch', 'grappling_hook'],
      uncommon: ['potion_healing', 'spell_scroll_1st_level', 'thieves_tools', 'climbers_kit', 'silk_rope_50ft', 'spyglass', 'magnifying_glass', 'manacles'],
      rare: ['potion_greater_healing', 'spell_scroll_2nd_level', 'bag_of_holding', 'rope_of_climbing', 'immovable_rod', 'decanter_of_endless_water']
    },
    // DMG Table: Armorer
    // [APR 30 2026]: Replaced deleted *_plus_N rows with named magical
    // armors (mithral_chain_shirt, dragon_scale_mail, adamantine_splint).
    // Magical +N variants now resolve via DMG tables F+G+H+I templates.
    armor_shop_dmg: {
      common: ['padded_armor', 'leather_armor', 'studded_leather', 'hide_armor', 'chain_shirt', 'scale_mail', 'shield'],
      uncommon: ['breastplate', 'half_plate', 'chain_mail', 'splint_armor', 'plate_armor'],
      rare: ['mithral_chain_shirt', 'dragon_scale_mail', 'adamantine_splint']
    },
    // DMG Table: Weaponsmith
    // [APR 30 2026]: Replaced deleted *_plus_N rows with named magical weapons
    // (flame_tongue, frost_brand, vicious_weapon, silvered_longsword) so the
    // rare pool stays exotic. Magical +N now via DMG tables F+G+H templates.
    weapon_shop_dmg: {
      common: ['club', 'dagger', 'handaxe', 'javelin', 'light_hammer', 'mace', 'quarterstaff', 'sickle', 'spear', 'shortbow', 'sling', 'dart'],
      uncommon: ['battleaxe', 'flail', 'glaive', 'greataxe', 'greatsword', 'halberd', 'lance', 'longsword', 'maul', 'morningstar', 'rapier', 'scimitar', 'shortsword', 'trident', 'war_pick', 'warhammer', 'whip', 'longbow', 'crossbow_heavy'],
      rare: ['flame_tongue', 'frost_brand', 'vicious_weapon', 'silvered_longsword', 'javelin_of_lightning', 'arrow_slaying']
    },
    // Individual Treasure Tables
    individual_treasure: {
      rolls: [
        { min: 1, max: 30, item: 'copper_pieces_5d6', rarity: 'common' },
        { min: 31, max: 60, item: 'silver_pieces_4d6', rarity: 'common' },
        { min: 61, max: 70, item: 'electrum_pieces_3d6', rarity: 'common' },
        { min: 71, max: 95, item: 'gold_pieces_3d6', rarity: 'common' },
        { min: 96, max: 100, item: 'platinum_pieces_1d6', rarity: 'uncommon' }
      ],
      common: ['copper_pieces', 'silver_pieces', 'electrum_pieces', 'gold_pieces'],
      uncommon: ['platinum_pieces'],
      rare: []
    },

    // DMG Tables A–I + sourcebook tables (single source of truth: magicItemTables.js)
    ...MAGIC_ITEM_TABLES,

    // Basic custom shops using simple pools
    general: {
      common: ['rope_hempen', 'rations', 'torch', 'bedroll', 'waterskin', 'backpack', 'crowbar', 'hammer', 'lantern_hooded', 'oil_flask'],
      uncommon: ['potion_healing', 'thieves_tools', 'climbers_kit'],
      rare: ['potion_greater_healing', 'bag_of_holding']
    },
    // [APR 30 2026]: rare pool replacements — *_plus_N rows deleted, see
    // armor_shop_dmg / weapon_shop_dmg notes above.
    weapon_shop: {
      common: ['club', 'dagger', 'handaxe', 'javelin', 'quarterstaff', 'spear', 'shortbow'],
      uncommon: ['longsword', 'rapier', 'scimitar', 'shortsword', 'longbow'],
      rare: ['greatsword', 'flame_tongue']
    },
    armor_shop: {
      common: ['padded', 'leather', 'shield'],
      uncommon: ['chain_shirt', 'scale_mail', 'breastplate'],
      rare: ['plate', 'mithral_chain_shirt']
    },
    // === CUSTOM MERCHANTS (Clearly Marked) ===
    // Custom: Enhanced Armorer
    armor_shop_custom: {
      common: ['leather', 'studded_leather', 'shield', 'padded', 'hide'],
      uncommon: ['chain_shirt', 'scale_mail', 'chain_mail', 'breastplate', 'half_plate'],
      rare: ['mithral_chain_shirt', 'dragon_scale_mail', 'adamantine_splint']
    },
    magic_shop: {
      common: ['potion_healing', 'scroll_cantrip', 'scroll_1st_level', 'component_pouch', 'crystal'],
      uncommon: ['potion_greater_healing', 'scroll_2nd_level', 'wand_cure_wounds', 'pearl_of_power', 'cloak_of_protection'],
      rare: ['potion_superior_healing', 'scroll_3rd_level', 'wand_magic_missiles', 'ring_protection', 'staff_of_power']
    },
    apothecary: {
      common: ['potion_healing', 'antitoxin', 'perfume_vial', 'soap', 'healers_kit', 'herbalism_kit', 'flask'],
      uncommon: ['potion_greater_healing', 'potion_fire_resistance', 'potion_cold_resistance', 'potion_water_breathing', 'oil_of_slipperiness'],
      rare: ['potion_superior_healing', 'potion_giant_strength', 'potion_invisibility', 'elixir_of_health', 'potion_of_longevity']
    },
    blacksmith: {
      common: ['hammer', 'tongs', 'anvil', 'bellows', 'horseshoes', 'nails', 'piton', 'crowbar'],
      uncommon: ['silvered_weapons', 'masterwork_tools', 'cold_iron_weapons', 'adamantine_ammunition'],
      rare: ['adamantine_weapons', 'mithral_armor', 'dwarven_plate', 'hammer_of_thunderbolts']
    },
    tavern: {
      common: ['ale_mug', 'wine_bottle', 'rations', 'bread', 'cheese', 'meat', 'rooms_common'],
      uncommon: ['wine_fine', 'spirits_strong', 'feast', 'rooms_private', 'information'],
      rare: ['wine_elven', 'dwarven_ale', 'rooms_luxury', 'magical_feast', 'rare_information']
    },
    temple: {
      common: ['holy_symbol', 'holy_water', 'incense', 'candle', 'prayer_book', 'blanket'],
      uncommon: ['potion_healing', 'scroll_bless', 'scroll_cure_wounds', 'amulet_of_faith'],
      rare: ['scroll_greater_restoration', 'potion_greater_healing', 'periapt_of_wound_closure', 'ring_of_spell_storing']
    },
    jeweler: {
      common: ['ring_silver', 'necklace_silver', 'bracelet_copper', 'brooch_simple', 'earrings_simple'],
      uncommon: ['ring_gold', 'necklace_gold', 'bracelet_silver', 'brooch_ornate', 'circlet_silver'],
      rare: ['ring_of_protection', 'amulet_of_natural_armor', 'circlet_of_blasting', 'necklace_of_fireballs']
    },
    library: {
      common: ['book_common', 'scroll_blank', 'ink', 'quill', 'parchment', 'sealing_wax'],
      uncommon: ['book_rare', 'map_regional', 'scroll_1st_level', 'tome_of_knowledge'],
      rare: ['spellbook_wizard', 'tome_of_understanding', 'manual_of_quickness', 'book_of_vile_darkness']
    },
    fence: {
      common: ['thieves_tools', 'crowbar', 'grappling_hook', 'silk_rope', 'caltrops', 'ball_bearings'],
      uncommon: ['potion_invisibility', 'cloak_of_stealth', 'boots_of_stealth', 'gloves_of_thievery'],
      rare: ['ring_of_invisibility', 'cloak_of_displacement', 'boots_of_speed', 'deck_of_many_things']
    },
    // Spell Casters
    court_wizard: {
      common: ['scroll_cantrip', 'scroll_1st_level', 'component_pouch', 'crystal', 'spellbook_blank', 'ink', 'quill'],
      uncommon: ['scroll_2nd_level', 'scroll_3rd_level', 'pearl_of_power', 'wand_magic_missiles', 'cloak_of_protection', 'familiar_scroll'],
      rare: ['scroll_4th_level', 'scroll_5th_level', 'staff_of_power', 'ring_of_wizardry', 'tome_of_clear_thought', 'crystal_ball']
    },
    hut_witch: {
      common: ['potion_healing', 'antitoxin', 'herbalism_kit', 'scroll_cantrip', 'component_pouch', 'cauldron', 'dried_herbs'],
      uncommon: ['potion_greater_healing', 'scroll_charm_person', 'scroll_sleep', 'potion_animal_friendship', 'cursed_trinket', 'crystal_ball_minor'],
      rare: ['potion_superior_healing', 'scroll_polymorph', 'hag_eye', 'cauldron_of_rebirth', 'broom_of_flying', 'crystal_ball']
    },
    hermit_sage: {
      common: ['scroll_1st_level', 'book_common', 'herbalism_kit', 'rations', 'waterskin', 'blanket', 'walking_staff'],
      uncommon: ['scroll_2nd_level', 'book_rare', 'potion_greater_healing', 'cloak_of_elvenkind', 'boots_of_elvenkind', 'pearl_of_power'],
      rare: ['scroll_3rd_level', 'tome_of_understanding', 'staff_of_healing', 'ring_of_spell_storing', 'manual_of_bodily_health', 'ioun_stone']
    },
    war_mage: {
      common: ['scroll_1st_level', 'component_pouch', 'dagger', 'leather', 'shield', 'crossbow_light', 'crossbow_bolts'],
      uncommon: ['scroll_2nd_level', 'wand_magic_missiles', 'chain_shirt', 'longsword', 'cloak_of_protection', 'pearl_of_power'],
      // [APR 30 2026]: chain_shirt_plus_1/longsword_plus_1 deleted; use named
      // magical replacements that exist in BASE_ITEMS.
      rare: ['scroll_3rd_level', 'staff_of_power', 'mithral_chain_shirt', 'flame_tongue', 'ring_of_protection', 'robe_of_the_archmagi']
    },
    // Exotic Traders
    planar_merchant: {
      common: ['component_pouch', 'crystal', 'incense', 'perfume_vial', 'silk_rope', 'glass_orb', 'astral_salt'],
      uncommon: ['scroll_planar_binding', 'potion_gaseous_form', 'dimensional_shackles', 'pearl_of_power', 'bag_of_tricks', 'feather_token'],
      rare: ['bag_of_holding', 'portable_hole', 'deck_of_many_things', 'sphere_of_annihilation', 'manual_of_the_planes', 'cubic_gate']
    },
    dragon_hoard_trader: {
      common: ['gold_pieces', 'silver_pieces', 'copper_pieces', 'precious_art', 'jewelry_fine', 'treasure_chest'],
      uncommon: ['gems_precious', 'crown_minor', 'scepter_ornate', 'chalice_golden', 'statue_valuable', 'painting_masterwork'],
      rare: ['ancient_artifact', 'dragon_scale_armor', 'treasure_hoard_legendary', 'crown_of_kings', 'orb_of_dragonkind', 'dragon_slayer_sword']
    },
    underdark_trader: {
      common: ['mushrooms_glowing', 'rope_spider_silk', 'torch_everburning', 'rations_preserved', 'lantern_hooded', 'piton_adamantine'],
      uncommon: ['potion_darkvision', 'cloak_of_elvenkind', 'boots_of_elvenkind', 'drow_poison', 'spider_climb_potion', 'stone_of_good_luck'],
      rare: ['drow_weapons_magical', 'cloak_of_displacement', 'ring_of_invisibility', 'adamantine_armor', 'sunblade', 'rod_of_lordly_might']
    },
    // Specialized Artisans
    artificer_workshop: {
      common: ['tinkers_tools', 'smiths_tools', 'glassblowers_tools', 'jewelers_tools', 'gears_clockwork', 'springs_metal'],
      uncommon: ['bag_of_holding', 'alchemy_jug', 'decanter_of_endless_water', 'immovable_rod', 'sovereign_glue', 'universal_solvent'],
      rare: ['apparatus_of_kwalish', 'iron_bands_of_bilarro', 'sphere_of_annihilation', 'helm_of_brilliance', 'staff_of_the_magi', 'talisman_of_pure_good']
    },
    beast_tamer: {
      common: ['animal_feed', 'rope_hempen', 'net', 'whip', 'leather_collar', 'whistle_silver', 'treat_animal'],
      uncommon: ['potion_animal_friendship', 'figurine_of_wondrous_power', 'bag_of_tricks', 'horseshoes_of_speed', 'saddle_of_the_cavalier', 'bridle_of_control'],
      rare: ['ring_of_animal_influence', 'staff_of_the_woodlands', 'cloak_of_the_bat', 'wings_of_flying', 'griffon_cavalry_lance', 'rod_of_lordly_might']
    },
    elemental_trader: {
      common: ['crystal_fire', 'crystal_ice', 'crystal_lightning', 'crystal_earth', 'elemental_essence', 'binding_circle_chalk'],
      uncommon: ['potion_fire_resistance', 'potion_cold_resistance', 'necklace_of_fireballs', 'wand_of_lightning_bolts', 'elemental_gem', 'bowl_commanding_water_elementals'],
      rare: ['ring_of_fire_elemental_command', 'ring_of_water_elemental_command', 'staff_of_fire', 'staff_of_frost', 'robe_of_the_archmagi', 'efreeti_bottle']
    },
    // Underground/Criminal
    smuggler_den: {
      common: ['thieves_tools', 'crowbar', 'grappling_hook', 'silk_rope', 'caltrops', 'forgery_kit', 'disguise_kit'],
      uncommon: ['potion_invisibility', 'cloak_of_stealth', 'boots_of_stealth', 'gloves_of_thievery', 'hat_of_disguise', 'chime_of_opening'],
      rare: ['ring_of_invisibility', 'cloak_of_displacement', 'boots_of_speed', 'deck_of_many_things', 'crystal_ball', 'apparatus_of_kwalish']
    },
    assassins_guild: {
      common: ['dagger', 'dart', 'thieves_tools', 'poison_basic', 'caltrops', 'ball_bearings', 'rope_silk'],
      // [APR 30 2026]: dagger_plus_1 deleted; use a named magical dagger replacement.
      uncommon: ['dagger_of_venom', 'poison_deadly', 'cloak_of_stealth', 'boots_of_stealth', 'potion_invisibility', 'oil_of_slipperiness'],
      rare: ['assassins_dagger', 'cloak_of_displacement', 'ring_of_invisibility', 'potion_of_poison', 'figurine_of_wondrous_power', 'deck_of_many_things']
    },
    // Religious/Divine
    shrine_keeper: {
      common: ['holy_symbol', 'holy_water', 'incense', 'candle', 'prayer_book', 'blanket', 'oil_flask'],
      uncommon: ['potion_healing', 'scroll_bless', 'scroll_cure_wounds', 'amulet_of_faith', 'periapt_of_health', 'stone_of_good_luck'],
      rare: ['scroll_greater_restoration', 'potion_greater_healing', 'periapt_of_wound_closure', 'ring_of_spell_storing', 'staff_of_healing', 'talisman_of_pure_good']
    },
    cult_supplier: {
      common: ['ritual_dagger', 'black_candles', 'incense_unholy', 'robes_dark', 'chalk_summoning', 'parchment_vile'],
      uncommon: ['scroll_darkness', 'scroll_charm_person', 'potion_poison', 'unholy_symbol', 'cloak_of_protection', 'crystal_ball_minor'],
      rare: ['staff_of_withering', 'robe_of_the_archmagi', 'deck_of_many_things', 'sphere_of_annihilation', 'manual_of_bodily_harm', 'orb_of_dragonkind']
    },
    // Maritime/Coastal
    sea_trader: {
      common: ['rope_hempen', 'net', 'fishing_tackle', 'waterskin', 'rations_hardtack', 'compass', 'spyglass'],
      uncommon: ['potion_water_breathing', 'rope_of_climbing', 'bag_of_holding', 'decanter_of_endless_water', 'cloak_of_the_manta_ray', 'pearl_of_power'],
      rare: ['trident_of_fish_command', 'bowl_commanding_water_elementals', 'helm_of_underwater_action', 'conch_of_teleportation', 'apparatus_of_kwalish', 'sword_of_sharpness']
    },
    // Nomadic/Desert
    caravan_trader: {
      common: ['waterskin', 'rations', 'rope_hempen', 'blanket', 'tent', 'camel_saddle', 'desert_robes'],
      uncommon: ['ring_of_sustenance', 'decanter_of_endless_water', 'cloak_of_protection', 'boots_of_the_winterlands', 'pearl_of_power', 'carpet_of_flying_small'],
      rare: ['ring_of_fire_resistance', 'staff_of_the_desert_winds', 'carpet_of_flying', 'efreeti_bottle', 'manual_of_quickness_of_action', 'talisman_of_the_sphere']
    },

    // === NEW CUSTOM MERCHANTS ===
    // Specialized Artisans
    clockwork_tinker: {
      common: ['clockwork_toy', 'mechanical_compass', 'spring_loaded_device', 'gear_collection', 'tiny_clockwork', 'mechanical_pen'],
      uncommon: ['clockwork_servant', 'mechanical_calculator', 'self_winding_clock', 'automated_crossbow', 'mechanical_familiar'],
      rare: ['clockwork_dragon', 'apparatus_of_kwalish', 'mechanical_servant_advanced', 'clockwork_amulet', 'time_piece_of_precision']
    },
    wandmaker: {
      // [FIX MAY 2026 — Q10]: Six broken BASE_ITEMS keys remapped to canonical names.
      // Pre-fix the shop silently skipped every roll because the keys didn't resolve:
      //   wand_cure_light_wounds → wand_cure_wounds (3.5e legacy name → 5e canonical homebrew per wands audit 2026-05-11)
      //   wand_detect_magic     → wand_of_magic_detection (DMG p.211)
      //   wand_light            → wand_of_secrets         (substitute; "Wand of Light" is non-RAW per DMG p.211 — see Q10 commit msg)
      //   wand_fireball         → wand_of_fireballs       (DMG p.211)
      //   wand_polymorph        → wand_of_polymorph       (DMG p.212 — entry added to BASE_ITEMS in same audit branch)
      //   wand_lightning_bolt   → wand_of_lightning_bolts (DMG p.212)
      common: ['wand_components', 'carved_wood_pieces', 'crystal_chips', 'focus_crystal', 'wand_blank', 'carving_tools'],
      uncommon: ['wand_magic_missiles', 'wand_cure_wounds', 'wand_of_magic_detection', 'wand_of_secrets', 'pearl_of_power'],
      rare: ['wand_of_fireballs', 'wand_of_polymorph', 'wand_of_lightning_bolts', 'staff_of_power', 'rod_of_lordly_might']
    },
    enchanter: {
      common: ['enchanting_chalk', 'silver_dust', 'ritual_components', 'focus_crystal', 'enchanted_ink', 'arcane_implements'],
      uncommon: ['scroll_enhance_ability', 'potion_heroism', 'cloak_of_protection', 'ring_of_protection', 'amulet_of_natural_armor'],
      rare: ['ring_of_spell_storing', 'robe_of_the_archmagi', 'staff_of_power', 'crystal_ball', 'deck_of_many_things']
    },
    gemcutter: {
      common: ['rough_gems', 'polishing_tools', 'gem_dust', 'small_crystals', 'cutting_implements', 'jewelers_loupe'],
      uncommon: ['cut_gems_precious', 'ioun_stone_cracked', 'gem_of_seeing', 'jeweled_crown', 'precious_amulet'],
      rare: ['ioun_stone', 'crystal_ball', 'gem_of_true_seeing', 'crown_of_stars', 'sphere_of_annihilation']
    },

    // Underground Expansion
    black_market: {
      common: ['poison_basic', 'lockpicks', 'disguise_kit', 'forged_documents', 'smugglers_pack', 'thieves_tools'],
      uncommon: ['potion_invisibility', 'cloak_of_elvenkind', 'boots_of_elvenkind', 'bag_of_holding', 'ring_of_mind_shielding'],
      rare: ['assassins_dagger', 'cloak_of_displacement', 'ring_of_invisibility', 'manual_of_quickness', 'deck_of_illusions']
    },
    thieves_quartermaster: {
      common: ['thieves_tools', 'grappling_hook', 'silk_rope', 'caltrops', 'crowbar', 'lock_picks'],
      uncommon: ['immovable_rod', 'bag_of_holding', 'cloak_of_elvenkind', 'boots_of_elvenkind', 'potion_climbing'],
      rare: ['chime_of_opening', 'ring_of_jumping', 'slippers_of_spider_climbing', 'cloak_of_displacement', 'ring_of_telekinesis']
    },

    // Religious Expansion  
    divine_armorer: {
      common: ['holy_symbol', 'blessed_oils', 'sanctified_metal', 'divine_forge_tools', 'prayer_scrolls', 'incense_holy'],
      uncommon: ['armor_of_faith', 'shield_of_faith', 'holy_avenger_components', 'blessed_ammunition', 'divine_focus'],
      rare: ['armor_of_invulnerability', 'holy_avenger', 'staff_of_healing', 'mace_of_disruption', 'talisman_of_pure_good']
    },
    pilgrim_outfitter: {
      common: ['travel_rations', 'walking_staff', 'pilgrims_badge', 'holy_water', 'prayer_book', 'travel_pack'],
      uncommon: ['boots_of_the_winterlands', 'cloak_of_protection', 'ring_of_sustenance', 'pearl_of_power', 'staff_of_healing'],
      rare: ['boots_of_speed', 'winged_boots', 'carpet_of_flying', 'portable_hole', 'sphere_of_annihilation']
    },

    // Entertainment & Travel
    carnival_vendor: {
      common: ['juggling_balls', 'magic_tricks', 'colorful_ribbons', 'face_paint', 'musical_instruments', 'carnival_masks'],
      uncommon: ['bag_of_tricks', 'wand_of_wonder', 'hat_of_disguise', 'pipes_of_haunting', 'instruments_of_the_bards'],
      rare: ['deck_of_illusions', 'robe_of_useful_things', 'portable_hole', 'bag_of_devouring', 'sphere_of_many_things']
    },
    ship_chandler: {
      common: ['rope_hempen', 'block_and_tackle', 'compass', 'sextant', 'nautical_charts', 'ships_supplies'],
      uncommon: ['helm_of_underwater_action', 'cloak_of_the_manta_ray', 'trident', 'net_of_snaring', 'decanter_of_endless_water'],
      rare: ['folding_boat', 'apparatus_of_kwalish', 'conch_of_teleportation', 'bowl_commanding_water_elementals', 'trident_of_fish_command']
    },
    roadside_merchant: {
      common: ['travel_rations', 'waterskin', 'blanket', 'tinderbox', 'rope_hempen', 'basic_supplies'],
      uncommon: ['potion_healing', 'boots_of_elvenkind', 'cloak_of_elvenkind', 'bag_of_holding', 'immovable_rod'],
      rare: ['boots_of_speed', 'cloak_of_displacement', 'portable_hole', 'decanter_of_endless_water', 'carpet_of_flying']
    }
  };
  
  // D&D 5e Spell Lists by Level
  const SPELL_LISTS = {
    cantrips: [
      'Acid Splash', 'Blade Ward', 'Chill Touch', 'Dancing Lights', 'Druidcraft', 'Eldritch Blast',
      'Fire Bolt', 'Guidance', 'Light', 'Mage Hand', 'Magic Stone', 'Minor Illusion', 'Poison Spray',
      'Prestidigitation', 'Produce Flame', 'Ray of Frost', 'Sacred Flame', 'Spare the Dying',
      'Thaumaturgy', 'Thorn Whip', 'Toll the Dead', 'Vicious Mockery'
    ],
    level1: [
      'Bless', 'Cure Wounds', 'Detect Magic', 'Dissonant Whispers', 'Faerie Fire', 'Guiding Bolt',
      'Healing Word', 'Hunter\'s Mark', 'Identify', 'Magic Missile', 'Protection from Evil and Good',
      'Shield', 'Sleep', 'Spiritual Weapon', 'Thunderwave', 'Burning Hands', 'Charm Person',
      'Color Spray', 'Comprehend Languages', 'Disguise Self', 'False Life', 'Find Familiar',
      'Grease', 'Jump', 'Longstrider', 'Mage Armor', 'Silent Image', 'Speak with Animals'
    ],
    level2: [
      'Aid', 'Barkskin', 'Blindness/Deafness', 'Blur', 'Darkness', 'Darkvision', 'Detect Thoughts',
      'Enhance Ability', 'Enlarge/Reduce', 'Heat Metal', 'Hold Person', 'Invisibility', 'Lesser Restoration',
      'Levitate', 'Locate Object', 'Magic Weapon', 'Misty Step', 'Prayer of Healing', 'Scorching Ray',
      'See Invisibility', 'Shatter', 'Shield of Faith', 'Spider Climb', 'Suggestion', 'Web'
    ],
    level3: [
      'Animate Dead', 'Bestow Curse', 'Clairvoyance', 'Counterspell', 'Daylight', 'Dispel Magic',
      'Fear', 'Fireball', 'Fly', 'Gaseous Form', 'Haste', 'Hypnotic Pattern', 'Lightning Bolt',
      'Mass Healing Word', 'Polymorph', 'Protection from Energy', 'Remove Curse', 'Revivify',
      'Slow', 'Spirit Guardians', 'Stinking Cloud', 'Tongues', 'Vampiric Touch', 'Water Breathing'
    ],
    level4: [
      'Banishment', 'Blight', 'Confusion', 'Conjure Minor Elementals', 'Death Ward', 'Dimension Door',
      'Divination', 'Dominate Beast', 'Freedom of Movement', 'Greater Invisibility', 'Ice Storm',
      'Locate Creature', 'Polymorph', 'Stone Shape', 'Stoneskin', 'Wall of Fire'
    ],
    level5: [
      'Animate Objects', 'Cloudkill', 'Cone of Cold', 'Conjure Elemental', 'Contact Other Plane',
      'Dispel Evil and Good', 'Dominate Person', 'Dream', 'Flame Strike', 'Greater Restoration',
      'Hold Monster', 'Legend Lore', 'Mass Cure Wounds', 'Modify Memory', 'Passwall', 'Planar Binding',
      'Raise Dead', 'Scrying', 'Telekinesis', 'Teleportation Circle', 'Tree Stride', 'Wall of Stone'
    ]
  };

  // Generate shop from character inventory (Non-Premium)
  const generateShopFromInventory = () => {
    const { shopName, shopkeeper, markup } = shopSettings;
    
    if (!shopName || !shopkeeper) {
      showStatus('Please enter shop name and shopkeeper name!');
      return;
    }
    
    if (inventory.length === 0) {
      showStatus('❌ No items in inventory to share as shop. Add some items first!', 4000);
      return;
    }
    
    // Create shop items from current inventory
    const shopItems = inventory.filter(item => item.value > 0).map(item => {
      const shopItem = { ...item };
      
      // Store base value and apply markup
      shopItem.baseValue = item.value;
      shopItem.value = Math.ceil(item.value * (markup / 100));
      
      // Keep container contents intact - player might want to sell filled containers
      // Keep customizations - player might want to sell customized items
      
      return shopItem;
    });
    
    if (shopItems.length === 0) {
      showStatus('❌ No valuable items in inventory to share (items need value > 0)', 4000);
      return;
    }
    
    const shop = {
      shopName,
      shopkeeper,
      shopType: 'player_inventory',
      shopSize: 'custom',
      markup,
      items: shopItems,
      generatedAt: new Date().toISOString(),
      source: 'player_inventory',
      id: Date.now()
    };
    
    setGeneratedShop(shop);
    setActiveShops(prev => [...prev, shop]);
    showStatus(`✅ Shop created from your inventory with ${shopItems.length} items!`, 4000);
  };
  
  // Generate shop using d100 roll table system
  // [PHASE B / #057 — 2026-05-07]: settingsOverride lets regenerateShop()
  //   feed an existing shop's settings (size + shopType + selectedTables)
  //   into the d100 roller without first mutating shopSettings (which is
  //   async and would race with the next call). Default is the live
  //   shopSettings — original generateShop call sites stay unchanged.
  const generateShopWithD100 = (settingsOverride = null) => {
    const { shopType, shopSize, shopName, shopkeeper, markup, selectedTables } = settingsOverride || shopSettings;

    // Clear previous rolls for new generation
    setRecentShopRolls([]);
    
    let tablesToUse = [];
    
    // Check if this is a custom shop type with table assignments
    const customShop = SHOP_ROLL_TABLES[shopType];
    if (customShop?.customType) {
      // Use the tables assigned to this custom shop type
      const { commonTables, uncommonTables, rareTables } = customShop;
      
      // Based on shop size, determine which rarity tables to use
      const sizeConfig = {
        small: { common: 4, uncommon: 1, rare: 0 },
        medium: { common: 6, uncommon: 3, rare: 1 },
        large: { common: 8, uncommon: 5, rare: 2 },
        massive: { common: 10, uncommon: 10, rare: 5 }
      };
      
      const config = sizeConfig[shopSize];
      
      // [PHASE B / #051 — 2026-05-07]: findRollTableById dispatches across
      //   DMG_LOOT_TABLES → SHOP_ROLL_TABLES → customRollTables. Pre-fix,
      //   the inline `DMG_LOOT_TABLES[id] || SHOP_ROLL_TABLES[id]` chain
      //   never consulted customRollTables, so player-authored tables
      //   bound to a custom shop type silently dropped — generation
      //   produced 0 items and the user-visible "No items generated!
      //   Roll table items may not exist in BASE_ITEMS." failure.
      const lookupSources = {
        dmgTables: DMG_LOOT_TABLES,
        shopTables: SHOP_ROLL_TABLES,
        customTables: customRollTables,
      };

      // Add tables based on rarity distribution
      for (let i = 0; i < config.common; i++) {
        if (commonTables && commonTables.length > 0) {
          const tableId = commonTables[Math.floor(Math.random() * commonTables.length)];
          const table = findRollTableById(tableId, lookupSources);
          if (table) tablesToUse.push(table);
        }
      }

      for (let i = 0; i < config.uncommon; i++) {
        if (uncommonTables && uncommonTables.length > 0) {
          const tableId = uncommonTables[Math.floor(Math.random() * uncommonTables.length)];
          const table = findRollTableById(tableId, lookupSources);
          if (table) tablesToUse.push(table);
        }
      }

      for (let i = 0; i < config.rare; i++) {
        if (rareTables && rareTables.length > 0) {
          const tableId = rareTables[Math.floor(Math.random() * rareTables.length)];
          const table = findRollTableById(tableId, lookupSources);
          if (table) tablesToUse.push(table);
        }
      }
    } else if (selectedTables && selectedTables.length > 0) {
      // [PHASE B / #051 — 2026-05-07]: Same dispatcher as the customType
      //   branch above. Pre-fix, manually selected custom_table_* ids
      //   from the shop builder were also silently dropped here.
      const lookupSources = {
        dmgTables: DMG_LOOT_TABLES,
        shopTables: SHOP_ROLL_TABLES,
        customTables: customRollTables,
      };
      selectedTables.forEach(tableId => {
        const table = findRollTableById(tableId, lookupSources);
        if (table) tablesToUse.push(table);
      });
    } else {
      // Fall back to shop type table
      const rollTable = SHOP_ROLL_TABLES[shopType];
      if (!rollTable?.rolls) {
        return null;
      }
      tablesToUse = [rollTable];
    }
    
    // Check if we have any tables to use
    if (tablesToUse.length === 0) {
      return null;
    }
    
    // Determine number of rolls based on shop size with minimum guarantees
    const rollCounts = {
      small: 10,  // Minimum 10 items
      medium: 15, // Minimum 15 items
      large: 20,  // Minimum 20 items
      massive: 30 // Minimum 30 items
    };
    
    const numRolls = rollCounts[shopSize];
    const itemCounts = {};
    
    
    let rollsMade = 0;
    let attempts = 0;
    const maxAttempts = numRolls * 3; // Allow extra attempts to ensure we get items
    
    while (rollsMade < numRolls && attempts < maxAttempts) {
      attempts++;
      
      // Pick a random table from the selected tables
      const table = tablesToUse[Math.floor(Math.random() * tablesToUse.length)];
      if (!table || !table.rolls) continue;
      
      const roll = Math.floor(Math.random() * 100) + 1;
      const rawEntry = table.rolls.find(r => roll >= r.min && roll <= r.max);
      // [APR 30 2026]: Template resolver — if the entry references a
      // category template (weapon_plus_n, armor_plus_n, ammunition_plus_n,
      // shield_plus_n, belt_of_giant_strength), resolve it to a concrete
      // base-item ID + properties. Non-template entries pass through
      // unchanged.
      let resolved = null;
      if (rawEntry && rawEntry.template) {
        resolved = resolveTemplateEntry(rawEntry);
      }
      const entry = rawEntry;

      if (entry) {
        const itemName = resolved?.id || entry.item || entry.name;

        // Track this roll for display
        const rarity = entry.rarity || 'common';

        // [PHASE B / #061 — 2026-05-07]: Recent Shop Rolls display also
        //   normalizes via EquipmentResolver. resolved.name is preserved
        //   when set (templateResolvers populate it from entry.name like
        //   "Weapon, +1" — already user-friendly); fallback to formatted
        //   itemName instead of the raw lowercase key.
        setRecentShopRolls(prev => [...prev, {
          roll,
          item:
            resolved?.name
            || EquipmentResolver.formatDisplayName(itemName)
            || itemName.replace(/_/g, ' '),
          rarity,
          table: table.label || table.name
        }]);

        // Find the item in our database or inventory
        let foundItem = null;

        // [APR 30 2026]: Template-resolved entries already carry the full
        // baseItem; merge template properties (magicalBonus, statModifications,
        // requiresAttunement, etc.) onto the cloned base item.
        // [PHASE B / #061 — 2026-05-07]: Normalize the display name via
        //   EquipmentResolver instead of using the raw lowercase id.
        //   Pre-fix `name: resolved.id` set `name = 'longsword_plus_1'`
        //   (or 'belt_hill_giant') and the renderer's first fallback
        //   reads item.name verbatim — shop items rendered as the raw
        //   underscored lowercase key. Smoke-test 2026-05-07: "why are
        //   shop items all lower case still?"
        if (resolved) {
          foundItem = {
            ...resolved.baseItem,
            ...resolved.properties,
            name:
              resolved.name
              || resolved.baseItem.name
              || EquipmentResolver.formatDisplayName(resolved.id)
              || resolved.id.replace(/_/g, ' '),
            rarity: entry.rarity,
            baseValue: resolved.baseItem.baseValue || 1,
          };
        } else if (itemName.startsWith('custom_inv_')) {
          // [PHASE B / #018 — B3-2]: resolveCustomInvRef parses BOTH the
          // top-level shape (`custom_inv_<idx>`) and the contained-item
          // shape (`custom_inv_<idx>_contents_<sub>`). Pre-fix, the
          // inline `parseInt(strip-prefix)` parsed `custom_inv_0_contents_2`
          // as index 0, returning the container instead of the contained
          // item the user authored on the table.
          const resolvedInv = resolveCustomInvRef(itemName, inventory);
          if (resolvedInv) {
            foundItem = {
              ...resolvedInv,
              rarity: entry.rarity,
              baseValue: resolvedInv.value || 1
            };
          }
        } else {
          // Look in BASE_ITEMS via canonical helper.
          // [P1.2+P1.3 fix - Apr 29, 2026]: Replaced open-coded category loop
          // with getItemDefinition() — single source for BASE_ITEMS lookup.
          // [PHASE B / #061 — 2026-05-07]: Normalize the display name via
          //   the same fallback chain used by buildShopItem (Simple-mode path).
          //   Pre-fix `name: itemName` wrote raw lowercase keys like
          //   'longsword' or 'bag_of_holding' into item.name; the renderer's
          //   formatRecentFavoriteName reads item.name first and rendered
          //   them verbatim. Smoke-test 2026-05-07.
          const def = getItemDefinition(itemName);
          if (def) {
            foundItem = {
              ...def.entry,
              name:
                def.entry.name
                || EquipmentResolver.formatDisplayName(itemName)
                || itemName.replace(/_/g, ' '),
              category: def.category,
              rarity: entry.rarity,
              baseValue: def.entry.baseValue || 1,
            };
          }
        }

        if (foundItem) {
          // Stack identical items (template items by resolved.id, flat items by itemName)
          const stackKey = resolved?.id || itemName;
          if (itemCounts[stackKey]) {
            itemCounts[stackKey].quantity = (itemCounts[stackKey].quantity || 1) + 1;
          } else {
            itemCounts[stackKey] = { ...foundItem, quantity: 1 };
          }
          rollsMade++; // Count successful rolls
        } else {
          console.warn(`Item not found: ${itemName}`);
        }
      }
    }
    
    // Convert to items array
    return Object.values(itemCounts);
  };
  
  const generateShop = () => {
    // [FIX APR 2026 Phase 3B.5]: Respect the `generationMode` radio. Previously the
    // UI at ShopTab:533 let premium users choose "Convert character inventory to shop"
    // but generateShop() ignored that selection and always went to roll-table generation.
    // Result: users selected "inventory" mode and got items they didn't own (e.g., the
    // reported "bag of holding" in a shop built 'from my inventory').
    if (shopSettings.generationMode === 'inventory') {
      generateShopFromInventory();
      return;
    }

    // Premium users with `generationMode === 'rolltable'` (or default) get roll table generation
    const { shopType, shopSize, shopName, shopkeeper, markup } = shopSettings;
    
    if (!shopName || !shopkeeper) {
      showStatus('Please enter shop name and shopkeeper name!');
      return;
    }
    
    // Check if this is an imported shop first
    let shopData = null;
    if (generatedShop && generatedShop.shopType === shopType) {
      // Use the imported shop's data
      shopData = {
        items: generatedShop.items,
        isImported: true
      };
    } else {
      // Check if it's a custom shop type that needs re-registration
      const customShop = customShopTypes.find(st => st.id === shopType);
      if (customShop && !SHOP_ROLL_TABLES[shopType]) {
        // Re-register the custom shop in SHOP_ROLL_TABLES
        if (customShop.categories) {
          // Simple Mode shop
          const commonItems = [];
          const uncommonItems = [];
          const rareItems = [];
          
          customShop.categories.forEach(cat => {
            if (BASE_ITEMS[cat]) {
              Object.keys(BASE_ITEMS[cat]).forEach(itemName => {
                const item = BASE_ITEMS[cat][itemName];
                if (item.baseValue < 50) {
                  commonItems.push(itemName);
                } else if (item.baseValue < 500) {
                  uncommonItems.push(itemName);
                } else {
                  rareItems.push(itemName);
                }
              });
            }
          });
          
          SHOP_ROLL_TABLES[shopType] = {
            common: commonItems,
            uncommon: uncommonItems,
            rare: rareItems
          };
        } else {
          // Advanced Mode shop
          SHOP_ROLL_TABLES[shopType] = {
            customType: true,
            commonTables: customShop.commonTables,
            uncommonTables: customShop.uncommonTables,
            rareTables: customShop.rareTables
          };
        }
      }
      
      // Use the roll table
      shopData = SHOP_ROLL_TABLES[shopType];
    }
    
    if (!shopData) {
      showStatus(`Shop type '${shopType}' not found. Please select a valid shop type.`);
      return;
    }
    
    let generatedItems = [];
    
    // Handle imported shops
    if (shopData.isImported) {
      // Imported shop already has items
      generatedItems = shopData.items || [];
    }
    // Check if this is a Simple Mode custom shop (has common/uncommon/rare arrays)
    else if (shopData.common && shopData.uncommon && shopData.rare) {
      // Use Simple Mode generation
      
      // Determine number of items based on shop size with minimums
      const itemCounts = {
        small: { common: 8, uncommon: 2, rare: 0 },    // Min 10 items
        medium: { common: 10, uncommon: 4, rare: 1 },   // Min 15 items  
        large: { common: 12, uncommon: 6, rare: 2 },    // Min 20 items
        massive: { common: 18, uncommon: 9, rare: 3 }   // Min 30 items
      };
      
      const counts = itemCounts[shopSize];
      
      // Generate items from Simple Mode pools
      Object.entries(counts).forEach(([rarity, count]) => {
        const pool = shopData[rarity];
        if (!pool || pool.length === 0) return;
        
        for (let i = 0; i < count; i++) {
          const randomItemName = pool[Math.floor(Math.random() * pool.length)];

          // [PHASE B / #017 — B3-6]: buildShopItem normalizes the display
          // name from the raw itemKey. Pre-fix this site set
          // `name: randomItemName` (e.g. 'leather_armor') which the
          // shop renderer then displayed verbatim — Phase B / #010's
          // fallback chain trusts item.name first.
          const foundItem = buildShopItem(randomItemName, rarity);
          if (foundItem) foundItem.quantity = 1;

          if (foundItem) {
            // Stack identical items
            const existing = generatedItems.find(item => item.name === foundItem.name);
            if (existing) {
              existing.quantity = (existing.quantity || 1) + 1;
            } else {
              generatedItems.push(foundItem);
            }
          } else {
            console.error(`Item '${randomItemName}' not found in BASE_ITEMS! Skipping...`);
          }
        }
      });
    } else {
      // Try d100 system for Advanced Mode or standard shops
      const d100Items = generateShopWithD100();
      if (d100Items && d100Items.length > 0) {
        generatedItems = d100Items;
      } else {
        // Fallback to old system
        
        // Determine number of items based on shop size with minimums
        const itemCounts = {
          small: { common: 8, uncommon: 2, rare: 0 },    // Min 10 items
          medium: { common: 10, uncommon: 4, rare: 1 },   // Min 15 items  
          large: { common: 12, uncommon: 6, rare: 2 },    // Min 20 items
          massive: { common: 18, uncommon: 9, rare: 3 }   // Min 30 items
        };
        
        const counts = itemCounts[shopSize];
        
        // Roll for items with better error handling
        ['common', 'uncommon', 'rare'].forEach(rarity => {
          const itemPool = shopData[rarity] || [];
          const numItems = counts[rarity] || 0;
          
          
          for (let i = 0; i < numItems; i++) {
            if (itemPool.length === 0) {
              console.warn(`No items in ${rarity} pool for ${shopType}`);
              continue;
            }
            
            const randomItemName = itemPool[Math.floor(Math.random() * itemPool.length)];

            // [PHASE B / #017 — B3-6]: buildShopItem normalizes the display
            // name. Same root cause as the Simple-mode site above —
            // pre-fix the rarity-pool fallback also pushed raw lowercase
            // underscored itemKeys into item.name.
            let foundItem = buildShopItem(randomItemName, rarity);

            if (foundItem) {
              // Enhance spell scrolls with actual spells
              if (randomItemName.includes('scroll_')) {
                let spellList = null;
                let spellLevel = '';
            
                if (randomItemName.includes('cantrip')) {
                  spellList = SPELL_LISTS.cantrips;
                  spellLevel = 'Cantrip';
                } else if (randomItemName.includes('1st_level')) {
                  spellList = SPELL_LISTS.level1;
                  spellLevel = '1st Level';
                } else if (randomItemName.includes('2nd_level')) {
                  spellList = SPELL_LISTS.level2;
                  spellLevel = '2nd Level';
                } else if (randomItemName.includes('3rd_level')) {
                  spellList = SPELL_LISTS.level3;
                  spellLevel = '3rd Level';
                } else if (randomItemName.includes('4th_level')) {
                  spellList = SPELL_LISTS.level4;
                  spellLevel = '4th Level';
                } else if (randomItemName.includes('5th_level')) {
                  spellList = SPELL_LISTS.level5;
                  spellLevel = '5th Level';
                }
            
                if (spellList) {
                  const randomSpell = spellList[Math.floor(Math.random() * spellList.length)];
                  foundItem = {
                    ...foundItem,
                    name: `Spell Scroll (${randomSpell})`,
                    description: `A scroll containing the ${spellLevel} spell "${randomSpell}". The spell can be cast once, consuming the scroll.`
                  };
                }
              }
          
              generatedItems.push(foundItem);
            } else {
              console.warn(`Item not found in database: ${randomItemName}`);
            }
          }
        });
      }
    }
    
    // Check if we generated any items
    if (generatedItems.length === 0) {
      showStatus('❌ Failed to generate shop items. Check console for details.', 4000);
      console.error('No items generated! Roll table items may not exist in BASE_ITEMS.');
      return;
    }
    
    const newShop = {
      shopName,
      shopkeeper,
      shopType,
      shopSize,
      markup,
      items: generatedItems,
      totalValue: generatedItems.reduce((sum, item) => sum + Math.ceil(item.baseValue * (markup / 100)), 0),
      generatedAt: new Date().toISOString(),
      id: Date.now()
    };
    
    setGeneratedShop(newShop);
    setActiveShops(prev => [...prev, newShop]);
    showStatus(`✅ Generated ${shopName} with ${generatedItems.length} items!`, 3000);
  };

  // [PHASE B / #057 — 2026-05-07]: Edit Shop callbacks. Smoke-test 2026-05-07:
  //   "cant edit shop after its made; I cant change the type of tables or
  //   item rolls after". `saveEditedShop` patches metadata in place;
  //   `regenerateShopItems` replaces the items array using the existing
  //   shop's type + size, without creating a new shop entry. Both look
  //   up the target shop by id and write back to activeShops + the
  //   active `generatedShop` when matched.
  const saveEditedShop = (updatedShop) => {
    if (!updatedShop || !updatedShop.id) return;
    // Recompute totalValue against current items + the new markup so
    // the totals row tracks the displayed prices.
    const totalValue = (updatedShop.items || []).reduce(
      (sum, item) => sum + Math.ceil(item.baseValue * (updatedShop.markup / 100)),
      0
    );
    const finalShop = { ...updatedShop, totalValue };
    setActiveShops((prev) =>
      prev.map((s) => (s.id === finalShop.id ? finalShop : s))
    );
    if (generatedShop && generatedShop.id === finalShop.id) {
      setGeneratedShop(finalShop);
    }
    showStatus(`✅ Updated ${finalShop.shopName}`, 3000);
  };

  const regenerateShopItems = (updatedShop) => {
    if (!updatedShop || !updatedShop.id) return;
    if (updatedShop.source === 'player_inventory') {
      // Inventory shops re-roll by re-snapshotting the current
      // character inventory (same logic as generateShopFromInventory).
      const shopItems = inventory
        .filter((item) => item.value > 0)
        .map((item) => ({
          ...item,
          baseValue: item.value,
          value: Math.ceil(item.value * (updatedShop.markup / 100)),
        }));
      if (shopItems.length === 0) {
        showStatus('❌ No valuable items in inventory to re-roll', 4000);
        return;
      }
      saveEditedShop({ ...updatedShop, items: shopItems });
      showStatus(`🎲 Re-rolled ${updatedShop.shopName} (${shopItems.length} items)`, 3000);
      return;
    }

    // Roll-table shops: re-run the d100 generator with the shop's
    // existing type + size. We feed settingsOverride directly so we
    // don't have to round-trip through async setShopSettings.
    const settingsOverride = {
      shopType: updatedShop.shopType,
      shopSize: updatedShop.shopSize,
      shopName: updatedShop.shopName,
      shopkeeper: updatedShop.shopkeeper,
      markup: updatedShop.markup,
      selectedTables: shopSettings.selectedTables, // unchanged from current selection
    };
    const d100Items = generateShopWithD100(settingsOverride);
    if (d100Items && d100Items.length > 0) {
      saveEditedShop({ ...updatedShop, items: d100Items });
      showStatus(`🎲 Re-rolled ${updatedShop.shopName} (${d100Items.length} items)`, 3000);
      return;
    }

    // [PHASE B / #063 — 2026-05-07]: Simple-mode fallback. Built-in shop
    //   types like 'weapon_shop' / 'armor_shop' carry only
    //   common/uncommon/rare arrays (no `rolls` array, no `customType`
    //   flag), so generateShopWithD100 returns null. Pre-fix, the
    //   user-facing flow was: open Edit Shop, swap to a built-in type,
    //   click Re-roll Items → "Re-roll produced no items" alert with no
    //   path forward. The simple-mode roller exists inline in
    //   generateShop's main branch; this fallback mirrors it here so
    //   re-roll works for every shop type that generate works for.
    const shopData = SHOP_ROLL_TABLES[updatedShop.shopType];
    if (shopData && shopData.common && shopData.uncommon && shopData.rare) {
      const sizeCounts = {
        small: { common: 8, uncommon: 2, rare: 0 },
        medium: { common: 10, uncommon: 4, rare: 1 },
        large: { common: 12, uncommon: 6, rare: 2 },
        massive: { common: 18, uncommon: 9, rare: 3 },
      };
      const counts = sizeCounts[updatedShop.shopSize] || sizeCounts.medium;
      const simpleItems = [];
      ['common', 'uncommon', 'rare'].forEach((rarity) => {
        const pool = shopData[rarity];
        if (!pool || pool.length === 0) return;
        for (let i = 0; i < (counts[rarity] || 0); i++) {
          const itemKey = pool[Math.floor(Math.random() * pool.length)];
          const item = buildShopItem(itemKey, rarity);
          if (!item) continue;
          // Stack identical items same as Simple-mode generation
          const existing = simpleItems.find((it) => it.name === item.name);
          if (existing) {
            existing.quantity = (existing.quantity || 1) + 1;
          } else {
            simpleItems.push({ ...item, quantity: 1 });
          }
        }
      });
      if (simpleItems.length > 0) {
        saveEditedShop({ ...updatedShop, items: simpleItems });
        showStatus(`🎲 Re-rolled ${updatedShop.shopName} (${simpleItems.length} items)`, 3000);
        return;
      }
    }

    showStatus(`❌ Re-roll produced no items. Check the shop type's table bindings.`, 4000);
  };

  const downloadShop = () => {
    if (!generatedShop) return;
    
    let text = `🏪 ${generatedShop.shopName.toUpperCase()} 🏪\n`;
    text += `═══════════════════════════════════════\n`;
    text += `Proprietor: ${generatedShop.shopkeeper}\n`;
    text += `📊 ${generatedShop.shopType.charAt(0).toUpperCase() + generatedShop.shopType.slice(1).replace('_', ' ')}\n`;
    text += `Size: ${generatedShop.shopSize.charAt(0).toUpperCase() + generatedShop.shopSize.slice(1)}\n`;
    text += `"Welcome, traveler! Browse my finest wares!"\n\n`;
    
    // Group items by rarity
    const rarityGroups = { common: [], uncommon: [], rare: [], veryRare: [], legendary: [] };
    generatedShop.items.forEach(item => {
      const rarity = item.rarity || 'common';
      if (rarityGroups[rarity]) {
        rarityGroups[rarity].push(item);
      } else {
        rarityGroups.common.push(item);
      }
    });
    
    ['common', 'uncommon', 'rare'].forEach(rarity => {
      if (rarityGroups[rarity].length === 0) return;
      
      text += `✨ ${rarity.toUpperCase()} ITEMS\n`;
      text += `${'-'.repeat(rarity.length + 7)}\n`;
      
      rarityGroups[rarity].sort((a, b) => a.name.localeCompare(b.name)).forEach(item => {
        const shopPrice = Math.ceil(item.baseValue * (generatedShop.markup / 100));
        text += `• ${item.name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}`;
        
        if (item.description) {
          text += ` - ${item.description}`;
        }
        
        if (item.magicalBonus) {
          text += ` (+${item.magicalBonus} enhancement)`;
        }
        
        text += `\n  Price: ${shopPrice}gp`;
        
        if (item.weight > 0) {
          text += ` | Weight: ${item.weight}lb`;
        }
        
        text += '\n\n';
      });
    });
    
    text += `═══════════════════════════════════════\n`;
    text += `💰 Total Shop Value: ${generatedShop.totalValue}gp\n`;
    text += `📊 ${generatedShop.items.length} items available\n`;
    text += `🛒 Markup: ${generatedShop.markup - 100}%\n\n`;
    text += `Generated with Tabletop Inventory Manager`;
    
    // Create and download text file
    const textBlob = new Blob([text], { type: 'text/plain' });
    const textUrl = URL.createObjectURL(textBlob);
    const dateStr = new Date().toISOString().split('T')[0];
    
    const downloadFile = (blob, filename) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    };
    
    // Download text version
    downloadFile(textBlob, `${generatedShop.shopName.replace(/\s+/g, '_')}_shop_${dateStr}.txt`);
    
    // Also create and download JSON version for import functionality
    const jsonData = {
      ...generatedShop,
      version: "1.0",
      exportedBy: "Tabletop Inventory Manager",
      exportDate: new Date().toISOString()
    };
    const jsonBlob = new Blob([JSON.stringify(jsonData, null, 2)], { type: 'application/json' });
    downloadFile(jsonBlob, `${generatedShop.shopName.replace(/\s+/g, '_')}_shop_${dateStr}.json`);
  };

  // Shop Purchase System
  // [FIX 2025-10-22]: Now uses autoPurchase() from currencyUtils for proper currency conversion
  const purchaseShopItem = (item, price, shopIndex) => {
    // [FIX 2025-10-22]: Use autoPurchase utility for currency handling
    const purchaseResult = autoPurchase(price, currencies);

    // Check if player has enough money
    if (!purchaseResult.success) {
      showStatus(`❌ ${purchaseResult.message}`);
      return;
    }

    // Check for available inventory space
    const itemWidth = item.size?.[0] || 1;
    const itemHeight = item.size?.[1] || 1;
    
    const requiredSlots = [];
    for (let row = 0; row < itemHeight; row++) {
      for (let col = 0; col < itemWidth; col++) {
        requiredSlots.push(`${0 + col},${0 + row}`);
      }
    }

    // Find first available position
    let positionFound = false;
    let finalPosition = null;
    
    for (let startY = 0; startY < gridRows && !positionFound; startY++) {
      for (let startX = 0; startX < gridCols && !positionFound; startX++) {
        const testSlots = [];
        let canPlace = true;
        
        for (let row = 0; row < itemHeight; row++) {
          for (let col = 0; col < itemWidth; col++) {
            const testX = startX + col;
            const testY = startY + row;
            
            if (testX >= gridCols || testY >= gridRows) {
              canPlace = false;
              break;
            }
            
            const slotKey = `${testX},${testY}`;
            if (inventory.some(invItem => invItem.position?.includes(slotKey))) {
              canPlace = false;
              break;
            }
            testSlots.push(slotKey);
          }
          if (!canPlace) break;
        }
        
        if (canPlace) {
          finalPosition = testSlots;
          positionFound = true;
        }
      }
    }

    if (!positionFound) {
      showStatus(`❌ No space in inventory for ${item.name}!`);
      return;
    }

    // Create the purchased item properly using InventoryItem class
    const purchasedItem = new InventoryItem(
      item.name || 'unknown',
      item.category || 'items',
      {
        ...item,
        position: finalPosition,
        equipped: null
      }
    );

    // [INTEGRATION 2025-10-24]: Store old currencies for event emission
    const oldCurrencies = { ...currencies };

    // [FIX 2025-10-22]: Apply new currency from autoPurchase (handles all conversions automatically)
    setCurrencies(purchaseResult.newCurrency);

    // [INTEGRATION 2025-10-24]: Emit CURRENCY_CHANGED events for reactive updates
    ['pp', 'gp', 'sp', 'cp'].forEach(type => {
      if (oldCurrencies[type] !== purchaseResult.newCurrency[type]) {
        systemBus.emit(EVENTS.CURRENCY_CHANGED, {
          type,
          oldAmount: oldCurrencies[type],
          newAmount: purchaseResult.newCurrency[type],
          change: purchaseResult.newCurrency[type] - oldCurrencies[type],
          source: 'shop_purchase'
        });
      }
    });

    // Add to inventory
    setInventory(prev => [...prev, purchasedItem]);

    // Remove from shop (optional - could keep items in shop for multiple purchases)
    setGeneratedShop(prev => ({
      ...prev,
      items: prev.items.filter((_, index) => index !== shopIndex),
      totalValue: prev.totalValue - item.baseValue
    }));

    // [BATCH 7 / P1 D-12 — APR 2026]: Emit ITEM_PURCHASED so subscribers
    //   (CurrencyPanel, ShopTab) re-render in response. Pre-fix the event
    //   was registered + subscribed but the producer never fired, so two
    //   panels were dead listeners.
    systemBus.emit(EVENTS.ITEM_PURCHASED, {
      item: purchasedItem,
      price,
      shopIndex,
      shopType: generatedShop?.rollTable || 'unknown',
    });

    // Show success message
    showStatus(`✅ Purchased ${item.name} for ${price}gp!`);

    // Log transaction
    logActivity(`Purchased ${item.name} for ${price}gp from shop`, 'currency', {
      item: item.name,
      price,
      shopType: generatedShop?.rollTable || 'unknown',
      weight: item.weight,
      value: item.baseValue
    });
  };
  
  const importShop = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const shopData = JSON.parse(e.target.result);
        
        // Validate that this is a shop file
        if (!shopData.shopName || !shopData.shopkeeper || !shopData.items) {
          showStatus('❌ Invalid shop file format. Please select a valid shop JSON file.');
          return;
        }
        
        // Set the imported shop data
        const importedShop = {
          shopName: shopData.shopName,
          shopkeeper: shopData.shopkeeper,
          shopType: shopData.shopType,
          shopSize: shopData.shopSize || 'medium',
          markup: shopData.markup || 120,
          items: shopData.items,
          totalValue: shopData.totalValue || shopData.items.reduce((sum, item) => sum + Math.ceil(item.baseValue * ((shopData.markup || 120) / 100)), 0),
          source: 'import',
          id: Date.now(),
          generatedAt: shopData.generatedAt || new Date().toISOString()
        };
        
        setGeneratedShop(importedShop);
        setActiveShops(prev => [...prev, importedShop]);
        
        // Update shop settings form with imported values
        setShopSettings(prev => ({
          ...prev,
          shopName: shopData.shopName,
          shopkeeper: shopData.shopkeeper,
          shopType: shopData.shopType || 'general'
        }));
        
        showStatus(`✅ Successfully imported shop: "${shopData.shopName}"`);
        
        // Scroll to shop display after a short delay
        setTimeout(() => {
          const shopDisplay = document.querySelector('[data-shop-display]');
          if (shopDisplay) shopDisplay.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      } catch (error) {
        console.error('Error importing shop:', error);
        showStatus('❌ Error reading shop file. Please make sure it\'s a valid JSON file.');
      }
    };
    
    reader.readAsText(file);
    // Reset the input so the same file can be imported again if needed
    event.target.value = '';
  };
  
  const importInventory = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target.result);
        // [B16]: Run schema migration before reading any field. Mirrors
        // the same call in loadCharacter — every consumer reads migrated data.
        const migratedData = migrate(data);

        // [B16]: Capture unknown top-level keys from the imported JSON so they
        // survive round-trip on next save. Mirror of loadCharacter wiring.
        setUnknownFields(migratedData._unknownFields || {});

        const loadedStats = migratedData.playerStats || playerStats;

        // [FIX APR 2026 — B1]: Sync basics.languages → proficiencies.languages on import.
        // Third instance of this sync — also in loadCharacter() and autosave restore.
        const impBasicsLangs = loadedStats.basics?.languages;
        if (Array.isArray(impBasicsLangs) && impBasicsLangs.length > 0) {
          loadedStats.proficiencies = loadedStats.proficiencies || {};
          const impProfLangs = loadedStats.proficiencies.languages;
          if (!Array.isArray(impProfLangs) || impProfLangs.length === 0) {
            loadedStats.proficiencies.languages = [...impBasicsLangs];
          }
        }

        // [MAY 2026 — IDB image migration]: Inflate `img:<id>` portrait /
        //   customImage tokens and gallery `data` fields. Imported saves
        //   created on another device may reference IDs that don't exist in
        //   THIS device's IDB — restoreImagesIntoState returns null for those
        //   so consumers render the fallback placeholder. Cross-device image
        //   sync is a v1.x roadmap item.
        const inflatedImport = restoreImagesIntoState(loadedStats, migratedData.inventory || []);
        const importedStats = inflatedImport.playerStats;
        const importedInventory = inflatedImport.inventory;

        setPlayerStats(importedStats);

        // [FIX JAN 2026]: Process inventory items with assignPositionsToItems
        // [WHY]: Imported items need positions to display in grid
        const gridCols = importedStats.bagSize?.[0] || 10;
        const gridRows = importedStats.bagSize?.[1] || 10;
        // [SUB-PLAN 1R-RENDERGAP STONE 1 / 2026-05-29]: Promote bare 'ring' equipSlot on import.
        // [STONE 3a / 2026-05-27]: migratePairedSlots also covers bare 'earring' → earring1/earring2.
        // [1R-eyes-data / 2026-05-27]: migrateLensSlots promotes legacy lens/goggle items from
        //   equipSlot:'head' → 'eyes' so they render in the Eyes gear-grid cell, not Head.
        const migratedImportInventory = migrateLensSlots(migratePairedSlots(importedInventory));
        const processedInventory = assignPositionsToItems(migratedImportInventory, gridCols, gridRows);
        setInventory(processedInventory);

        // [FIX JAN 2026]: Process vault items with assignPositionsToItems
        const processedVaults = (migratedData.vaults || []).map(vault => {
          const vaultCols = vault.gridSize?.[0] || 10;
          const vaultRows = vault.gridSize?.[1] || 10;
          return {
            ...vault,
            items: assignPositionsToItems(vault.items || [], vaultCols, vaultRows)
          };
        });
        setVaults(processedVaults);

        // [FIX NOV 26, 2025]: Ensure all currency denominations exist
        setCurrencies({
          cp: 0, sp: 0, ep: 0, gp: 0, pp: 0,
          ...(migratedData.currencies || {})
        });
        // [FIX MAR 2026]: Merge autosave settings instead of replacing — empty settings ({})
        // would wipe isPremium and other global settings
        if (migratedData.settings && Object.keys(migratedData.settings).length > 0) {
          // [FIX P4-14]: Filter out global settings that should never come from character data
          const charSettings = Object.fromEntries(
            Object.entries(migratedData.settings).filter(([k]) => !GLOBAL_SETTINGS_KEYS.includes(k))
          );
          setSettings(prev => ({ ...prev, ...charSettings }));
        }

        // [FIX JAN 2026]: Load knownSpells from JSON (expects proper spell objects)
        // [WHY]: Character JSON files should store spells as full objects with level, school, classSource, etc.
        // [NOTE]: String-to-object conversion removed - character files must be created with correct format
        if (migratedData.knownSpells && migratedData.knownSpells.length > 0) {
          // Ensure each spell has required fields (id, classSource)
          const processedSpells = migratedData.knownSpells.map((spell, index) => {
            if (typeof spell !== 'object' || !spell.name) {
              console.warn(`[Import] Invalid spell format at index ${index}:`, spell);
              return null;
            }
            return {
              ...spell,
              id: spell.id || `imported_${Date.now()}_${index}`,
              classSource: spell.classSource || loadedStats.class || loadedStats.classes?.[0]?.className?.toLowerCase() || 'unknown'
            };
          }).filter(Boolean);

          setKnownSpells(processedSpells);
        }

        // [FIX JAN 2026]: Prepared spells are stored as name strings - keep them as strings
        // [WHY]: preparedSpells is checked via preparedSpells.includes(spell.name)
        if (migratedData.preparedSpells) {
          // Ensure prepared spells are strings (spell names)
          const preparedNames = migratedData.preparedSpells.map(s => typeof s === 'string' ? s : s.name);
          setPreparedSpells(preparedNames);
        }

        if (migratedData.spellSlots) {
          setSpellSlots(migratedData.spellSlots);
        }
        if (migratedData.customSpells) {
          setCustomSpells(migratedData.customSpells);
        }

        // [FIX APR 2026 — F35]: Load skill proficiencies with fallback conversion
        if (migratedData.skillProficiencies && Object.keys(migratedData.skillProficiencies).length > 0) {
          setSkillProficiencies(normalizeSkillProficiencies(migratedData.skillProficiencies));
        } else if (migratedData.playerStats?.proficiencies?.skills) {
          setSkillProficiencies(convertSkillNamesToObject(migratedData.playerStats.proficiencies.skills));
        }

        // [FIX APR 2026 — AUDIT C-3]: Restore 14 fields missing from import
        // [WHY]: buildCurrentCharacterData() exports 25 fields, but importFile() only consumed 11.
        //        Importing a character lost journal, calendar, quick bonuses, crafting, etc.
        // [PATTERN]: Mirrors autosave restore block (lines 6915-6962)
        if (migratedData.manualSpellSlots !== undefined) {
          setManualSpellSlots(migratedData.manualSpellSlots);
        }
        if (migratedData.activeQuickBonuses && Array.isArray(migratedData.activeQuickBonuses)) {
          setActiveQuickBonuses(migratedData.activeQuickBonuses);
        }
        if (migratedData.temporaryStats && typeof migratedData.temporaryStats === 'object') {
          setTemporaryStats(migratedData.temporaryStats);
        }
        if (migratedData.timeSystem) {
          // [TIER 1.3]: Re-hydrate Date fields so canRest checks/format
          //   helpers see Date instances, not ISO strings from the wire.
          setTimeSystem(hydrateTimeSystem(migratedData.timeSystem));
        }
        if (migratedData.currentGameDay) {
          setCurrentGameDay(migratedData.currentGameDay);
        }
        if (migratedData.calendarSettings) {
          setCalendarSettings(migratedData.calendarSettings);
        }
        if (migratedData.journalEntries && Array.isArray(migratedData.journalEntries)) {
          setJournalEntries(migratedData.journalEntries);
        }
        if (migratedData.journalRollTables && Array.isArray(migratedData.journalRollTables)) {
          setJournalRollTables(migratedData.journalRollTables);
        }
        if (migratedData.rollTables && Array.isArray(migratedData.rollTables)) {
          setRollTables(migratedData.rollTables);
        }
        if (migratedData.craftingProjects && Array.isArray(migratedData.craftingProjects)) {
          setCraftingProjects(migratedData.craftingProjects);
        }
        if (migratedData.dmEvents && Array.isArray(migratedData.dmEvents)) {
          setDmEvents(migratedData.dmEvents);
        }
        if (migratedData.activityLog && Array.isArray(migratedData.activityLog)) {
          // [FIX MAY 2026]: Rehydrate timestamp strings back to Date — see
          //   loadCharacter for the same fix. JSON.stringify drops Date type.
          setActivityLog(migratedData.activityLog.map((entry) => ({
            ...entry,
            timestamp: entry?.timestamp instanceof Date
              ? entry.timestamp
              : (entry?.timestamp ? new Date(entry.timestamp) : new Date()),
          })));
        }
        if (migratedData.customShopTypes && Array.isArray(migratedData.customShopTypes)) {
          setCustomShopTypes(migratedData.customShopTypes);
        }
        if (migratedData.customRollTables && Array.isArray(migratedData.customRollTables)) {
          setCustomRollTables(migratedData.customRollTables);
        }
        // [BATCH 7 / P1 D-11 — APR 2026]: Restore activeShops on import.
        if (migratedData.activeShops && Array.isArray(migratedData.activeShops)) {
          setActiveShops(migratedData.activeShops);
        }

        // Emit CHARACTER_LOADED so effectRegistry clears stale effects
        systemBus.emit(EVENTS.CHARACTER_LOADED, {
          characterName: migratedData.playerStats?.name || 'Imported',
          source: 'import'
        });

        showStatus('✅ Character imported successfully!');
      } catch (error) {
        console.error('Import error:', error);
        showStatus('❌ Failed to import character file');
      }
    };
    reader.readAsText(file);
  };

  // ========================================
  // CHARACTER SAVE/LOAD SYSTEM
  // ========================================

  // [FIX MAR 2026]: savedCharacters now initialized from localStorage synchronously
  // in SettingsContext useState(), eliminating race condition where the save effect
  // could overwrite seeded data with [] before the load effect updated state.

  // Load appState from localStorage on app start - DEFENSIVE LOADING
  useEffect(() => {
    // [FIX 2025-10-21]: Wait for game data to load before restoring character
    // This prevents race conditions where character uses game data that isn't loaded yet
    if (!gameDataLoaded) {
      return;
    }
    // [MAY 2026 — IDB image migration]: Wait for image-cache hydration so the
    //   restoreImagesIntoState() call below can synchronously inflate `img:<id>`
    //   tokens into dataUrls. Without this gate, the first render would show
    //   broken portraits until cache filled.
    if (!imageStoreReady) {
      return;
    }


    try {
      const savedAppState = localStorage.getItem('tabletopInventory_autosave');
      if (savedAppState) {
        const appState = JSON.parse(savedAppState);

        // [DUAL STORAGE FIX - NOV 19, 2025]: Migrate character data to canonical structure
        // [PURPOSE]: Eliminate data desync risk between flat and nested properties
        // [WHY]: Race/class/background were stored in multiple locations (playerStats.race vs playerStats.basics.race)
        if (appState.playerStats) {
          appState.playerStats = migrateCharacterData(appState.playerStats);

          // Validate after migration to catch any data integrity issues
          const validation = validateCharacterData(appState.playerStats);
          if (!validation.valid) {
            console.error('❌ [MIGRATION] Character validation failed:', validation.errors);
          } else if (validation.warnings.length > 0) {
            console.warn('⚠️ [MIGRATION] Character validation warnings:', validation.warnings);
          }
        }

        // [FIX NOV 4 SESSION 7]: Validate appState structure BEFORE using
        if (!appState || typeof appState !== 'object' || Array.isArray(appState)) {
          throw new Error('Invalid appState structure: must be object');
        }

        // [FIX NOV 4 SESSION 7]: Validate required fields exist and have correct types
        if (!appState.playerStats || typeof appState.playerStats !== 'object' || Array.isArray(appState.playerStats)) {
          throw new Error('Invalid appState.playerStats: must be object');
        }

        // [FIX NOV 4 SESSION 7]: Provide safe defaults for potentially missing/null fields
        const safePlayerStats = {
          ...appState.playerStats,
          classes: Array.isArray(appState.playerStats.classes) ? appState.playerStats.classes : [],
          level: typeof appState.playerStats.level === 'number' ? appState.playerStats.level : 1
        };

        // [FIX APR 2026 — B1]: Sync basics.languages → proficiencies.languages on autosave restore.
        // Mirrors the same sync in loadCharacter() (~line 7290). Skills tab reads from proficiencies.languages.
        const autoBasicsLangs = safePlayerStats.basics?.languages;
        if (Array.isArray(autoBasicsLangs) && autoBasicsLangs.length > 0) {
          safePlayerStats.proficiencies = safePlayerStats.proficiencies || {};
          const autoProfLangs = safePlayerStats.proficiencies.languages;
          if (!Array.isArray(autoProfLangs) || autoProfLangs.length === 0) {
            safePlayerStats.proficiencies.languages = [...autoBasicsLangs];
          }
        }

        const safeInventory = Array.isArray(appState.inventory) ? appState.inventory : [];
        const safeVaults = Array.isArray(appState.vaults) ? appState.vaults : [];
        const safeCurrencies = (appState.currencies && typeof appState.currencies === 'object' && !Array.isArray(appState.currencies))
          ? appState.currencies
          : { cp: 0, sp: 0, ep: 0, gp: 0, pp: 0 };

        // [FIX MAY 2026]: Recover playerStats.id from characterId on the autosave-restore path.
        // Rule 9 (autosave save/load match) — see src/utils/restoreCharacterId.js for full rationale.
        normalizeRestoredCharacterId(safePlayerStats);

        // [MAY 2026 — IDB image migration]: Inflate `img:<id>` portrait /
        //   customImage tokens back to dataUrls using the hydrated cache so
        //   downstream components don't need to know about IDB. Gallery
        //   entries also regain their `data` field via id lookup.
        const inflated = restoreImagesIntoState(safePlayerStats, safeInventory);
        const inflatedPlayerStats = inflated.playerStats;
        const inflatedInventory = inflated.inventory;

        // Only restore if we have valid player data with a name
        if (inflatedPlayerStats.name && inflatedPlayerStats.name.trim() !== '') {
          setPlayerStats(prev => ({ ...prev, ...inflatedPlayerStats }));

          // [FIX JAN 2026]: Call assignPositionsToItems on autosave restoration
          // [WHY]: Items loaded from autosave were missing positions, making them invisible in grid
          const gridCols = inflatedPlayerStats.bagSize?.[0] || 10;
          const gridRows = inflatedPlayerStats.bagSize?.[1] || 10;
          // [SUB-PLAN 1R-RENDERGAP STONE 1 / 2026-05-29]: Promote bare 'ring' equipSlot to
          // ring1/ring2 so equipped rings are visible in the EquipmentPanel gear grid.
          // [STONE 3a / 2026-05-27]: migratePairedSlots also covers earring → earring1/earring2.
          // [1R-eyes-data / 2026-05-27]: migrateLensSlots promotes legacy lens/goggle items from
          //   equipSlot:'head' → 'eyes' so they render in the Eyes gear-grid cell, not Head.
          const migratedInventory = migrateLensSlots(migratePairedSlots(inflatedInventory));
          const processedInventory = assignPositionsToItems(migratedInventory, gridCols, gridRows);
          setInventory(processedInventory);

          setVaults(safeVaults);
          setCurrencies(prev => ({ ...prev, ...safeCurrencies }));
          // Load settings including mobileInteractionMode - ensure it's preserved
          const loadedSettings = { ...appState.settings };
          // [FIX P4-14]: Filter out global settings from autosave restore
          const filteredSettings = Object.fromEntries(
            Object.entries(loadedSettings).filter(([k]) => !GLOBAL_SETTINGS_KEYS.includes(k))
          );
          setSettings(prev => ({ ...prev, ...filteredSettings }));
          
          // Load custom roll tables
          // [P1.2+P1.3 fix - Apr 29, 2026]: Removed mutation of DMG_LOOT_TABLES.
          // DMG_LOOT_TABLES is now the imported MAGIC_ITEM_TABLES module export;
          // mutating it would corrupt the shared registry. The Advanced Shop
          // builder picker already spreads `customRollTables` directly (see
          // line ~11272), so the mutation was redundant.
          if (appState.customRollTables) {
            setCustomRollTables(appState.customRollTables);
          }
          
          // Load custom shop types
          if (appState.customShopTypes) {
            setCustomShopTypes(appState.customShopTypes);
            // Re-register them in SHOP_ROLL_TABLES
            appState.customShopTypes.forEach(shopType => {
              if (shopType.categories) {
                // Simple Mode shop
                const commonItems = [];
                const uncommonItems = [];
                const rareItems = [];
                
                shopType.categories.forEach(cat => {
                  if (BASE_ITEMS[cat]) {
                    Object.keys(BASE_ITEMS[cat]).forEach(itemName => {
                      const item = BASE_ITEMS[cat][itemName];
                      if (item.baseValue < 50) {
                        commonItems.push(itemName);
                      } else if (item.baseValue < 500) {
                        uncommonItems.push(itemName);
                      } else {
                        rareItems.push(itemName);
                      }
                    });
                  }
                });
                
                SHOP_ROLL_TABLES[shopType.id] = {
                  common: commonItems,
                  uncommon: uncommonItems,
                  rare: rareItems
                };
              } else {
                // Advanced Mode shop
                SHOP_ROLL_TABLES[shopType.id] = {
                  customType: true,
                  commonTables: shopType.commonTables,
                  uncommonTables: shopType.uncommonTables,
                  rareTables: shopType.rareTables
                };
              }
            });
          }

          // [FIX JAN 2026]: Restore spell data from autosave
          // [WHY]: Spell arrays were never persisted/restored, causing spells to disappear
          if (appState.knownSpells) {
            setKnownSpells(appState.knownSpells);
          }
          if (appState.preparedSpells) {
            setPreparedSpells(appState.preparedSpells);
          }
          if (appState.spellSlots) {
            setSpellSlots(appState.spellSlots);
          }
          if (appState.customSpells) {
            setCustomSpells(appState.customSpells);
          }

          // [FIX APR 2026]: Restore autosave fields that were saved but never loaded (P1)
          if (appState.manualSpellSlots !== undefined) {
            setManualSpellSlots(appState.manualSpellSlots);
          }
          if (appState.activeQuickBonuses && Array.isArray(appState.activeQuickBonuses)) {
            setActiveQuickBonuses(appState.activeQuickBonuses);
          }
          if (appState.temporaryStats && typeof appState.temporaryStats === 'object') {
            setTemporaryStats(appState.temporaryStats);
          }

          // [FIX APR 2026 — F67]: Restore 4 fields now included in autosave
          // [WHY]: These were missing from autosave stateRef, so they were never saved/restored
          if (appState.skillProficiencies && Object.keys(appState.skillProficiencies).length > 0) {
            setSkillProficiencies(normalizeSkillProficiencies(appState.skillProficiencies));
          } else if (appState.playerStats?.proficiencies?.skills) {
            // Fallback conversion for autosaves created before this fix
            setSkillProficiencies(convertSkillNamesToObject(appState.playerStats.proficiencies.skills));
          }
          if (appState.timeSystem) {
            // [TIER 1.3]: Re-hydrate Date fields after JSON.parse so
            //   canRest helpers see Date instances, not ISO strings.
            setTimeSystem(hydrateTimeSystem(appState.timeSystem));
          }
          if (appState.currentGameDay) {
            setCurrentGameDay(appState.currentGameDay);
          }
          if (appState.calendarSettings) {
            setCalendarSettings(appState.calendarSettings);
          }

          // [FIX APR 2026 — F-43]: Restore 5 fields included in autosave but never loaded
          // [WHY]: Browser crash/reload lost journal entries, roll tables, crafting projects, DM events
          // [INTEGRATION]: These are saved in stateRef.current (line ~7522) and loaded from character save (line ~7314)
          //                but were missing from the autosave restoration path
          if (appState.journalEntries && Array.isArray(appState.journalEntries)) {
            setJournalEntries(appState.journalEntries);
          }
          if (appState.journalRollTables && Array.isArray(appState.journalRollTables)) {
            setJournalRollTables(appState.journalRollTables);
          }
          if (appState.rollTables && Array.isArray(appState.rollTables)) {
            setRollTables(appState.rollTables);
          }
          if (appState.craftingProjects && Array.isArray(appState.craftingProjects)) {
            setCraftingProjects(appState.craftingProjects);
          }
          if (appState.dmEvents && Array.isArray(appState.dmEvents)) {
            setDmEvents(appState.dmEvents);
          }
          // [BATCH 7 / P1 D-11 — APR 2026]: Restore activeShops on autosave reload.
          if (appState.activeShops && Array.isArray(appState.activeShops)) {
            setActiveShops(appState.activeShops);
          }
          // [GAP 1 FIX — MAY 2026]: activityLog was saved to stateRef but never restored
          //   on autosave-reload, silently resetting the log on every page refresh.
          //   Mirror the rehydration pattern from loadCharacter (~line 8572) exactly —
          //   timestamp strings must become Date instances so ActivityLogTab.js:145
          //   (.toLocaleTimeString) does not crash.
          if (appState.activityLog && Array.isArray(appState.activityLog)) {
            setActivityLog(appState.activityLog.map((entry) => ({
              ...entry,
              timestamp: entry?.timestamp instanceof Date
                ? entry.timestamp
                : (entry?.timestamp ? new Date(entry.timestamp) : new Date()),
            })));
          }
          // [B16]: Restore unknown forward-compat fields captured by migrate() so
          // they survive a browser-close → reload cycle in addition to manual saves.
          if (appState.unknownFields && typeof appState.unknownFields === 'object') {
            setUnknownFields(appState.unknownFields);
          }

          if (appState.playerStats.name && appState.playerStats.name.trim() !== '') {
            showStatus(`Welcome back, ${appState.playerStats.name}!`);
            // [FIX APR 2026]: Emit CHARACTER_LOADED so effectRegistry clears and re-registers
            systemBus.emit(EVENTS.CHARACTER_LOADED, {
              characterName: appState.playerStats.name,
              source: 'autosave'
            });
          }
        } else {
          // [FEB 2026]: Let welcome popup handle the wizard for new users
          // Check persistent tutorial state synchronously — don't rely on async showWelcome state
          const tutorialState = localStorage.getItem('tabletopInventory_tutorialState');
          const isReturningUser = tutorialState && JSON.parse(tutorialState).completedModules?.welcome;
          // [FIX 2026-06-20]: Only force the wizard for a returning user with NO saved characters.
          //   With saved characters present, the auto-load-last effect restores the most recent —
          //   force-opening creation here shoved a new character over the user's roster ("it loads
          //   new character" bug). hasAnySavedCharacters reads localStorage directly: the
          //   savedCharacters React state has not hydrated yet at this point on mount.
          const hasSavedCharacters = hasAnySavedCharacters(localStorage.getItem('savedCharacters'));
          if (shouldForceCreationWizard({ isReturningUser, hasSavedCharacters })) {
            setShowCharacterWizard(true);
            showStatus('👋 Welcome! Let\'s create your first character!', 5000);
          }
          // Otherwise, WelcomePopup (new users) or the auto-load-last effect (returning) handles it.
        }
      } else {
        // [FEB 2026]: Let welcome popup handle the wizard for new users
        // Check persistent tutorial state synchronously — don't rely on async showWelcome state
        const tutorialState = localStorage.getItem('tabletopInventory_tutorialState');
        const isReturningUser = tutorialState && JSON.parse(tutorialState).completedModules?.welcome;
        // [FIX 2026-06-20]: Only force the wizard for a returning user with NO saved characters
        //   (otherwise the auto-load-last effect restores their most-recent character). Reads
        //   localStorage directly — savedCharacters state has not hydrated yet on mount.
        const hasSavedCharacters = hasAnySavedCharacters(localStorage.getItem('savedCharacters'));
        if (shouldForceCreationWizard({ isReturningUser, hasSavedCharacters })) {
          setShowCharacterWizard(true);
          showStatus('👋 Welcome to Tabletop Inventory Manager! Let\'s create your character!', 5000);
        }
        // Otherwise, WelcomePopup (new users) or the auto-load-last effect (returning) handles it.
      }
    } catch (error) {
      console.error('❌ FAILED TO LOAD APP STATE:', error);

      // [FIX]: Add confirmation before clearing data to prevent accidental data loss
      const handleCorruptedData = async () => {
        const userConfirms = await confirm({
          title: 'Corrupted Save Data',
          message: `Save data appears corrupted: ${error.message}\n\nClear corrupted data and start fresh?`,
          confirmText: 'Clear Data',
          cancelText: 'Keep Trying',
          confirmColor: 'red'
        });
        if (userConfirms) {
        // Clear ALL corrupted localStorage keys
        const keysToRemove = ['appState', 'savedCharacters', 'lastCharacterId'];
        keysToRemove.forEach(key => {
          try {
            localStorage.removeItem(key);
          } catch (e) {
            console.error(`Failed to remove ${key}:`, e);
          }
        });
        showStatus('Corrupted data cleared - starting fresh');
      } else {
        showStatus('Corrupted data kept - some features may not work');
      }
      };
      handleCorruptedData();
    }
  }, [showStatus, gameDataLoaded, imageStoreReady, confirm]); // Include showStatus, gameDataLoaded, imageStoreReady, and confirm dependencies

  // Save characters to localStorage whenever savedCharacters changes
  // [MAY 2026 — IDB image migration]: Strip dataUrls out of each character's
  //   playerStats + inventory before persisting. Saves the same images to
  //   IDB keyed by stable `img:<id>` token; persisted JSON keeps only tokens.
  //   Without this, multi-character users hit localStorage quota fast.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const stripped = await Promise.all((savedCharacters || []).map(async (char) => {
          if (!char || !char.data) return char;
          const s = await stripImagesFromState(char.data.playerStats, char.data.inventory);
          return {
            ...char,
            data: {
              ...char.data,
              playerStats: s.playerStats,
              inventory: s.inventory,
            },
          };
        }));
        if (cancelled) return;
        safeLocalStorageSet('savedCharacters', JSON.stringify(stripped));
      } catch (err) {
        console.warn('[savedCharacters persist] strip failed; writing raw payload', err);
        if (!cancelled) {
          safeLocalStorageSet('savedCharacters', JSON.stringify(savedCharacters));
        }
      }
    })();
    return () => { cancelled = true; };
  }, [savedCharacters]);

  // ========================================
  // AUTOSAVE FUNCTIONALITY
  // ========================================

  // FORENSIC DEBUGGING COMPLETED - Removed to fix infinite re-renders
  // The issue was infinite useEffect loops with console.trace() calls

  // ========================================
  // [FIX JAN 2026 v2]: Auto-position AND property restoration for imported items
  // [PURPOSE]: Items MUST have position/size/icon to display in grid
  // [WHY]: GridComponent filters out items without position, renders icon directly
  // [CRITICAL]: Previous version didn't restore icon → items were invisible
  // ========================================
  const assignPositionsToItems = (items, gridCols, gridRows, existingPositionedItems = []) => {
    if (!items || !Array.isArray(items)) return [];

    const positionedItems = [...existingPositionedItems];
    const processedItems = [];

    // Helper to check if position is valid (no collision, within bounds)
    const checkPosition = (item, x, y, size, alreadyPlaced) => {
      const [width, height] = size;

      // Check bounds
      if (x < 0 || y < 0 || x + width > gridCols || y + height > gridRows) {
        return false;
      }

      // Check collision with already placed items
      for (let i = x; i < x + width; i++) {
        for (let j = y; j < y + height; j++) {
          const occupied = alreadyPlaced.some(placedItem => {
            if (!placedItem.position || !placedItem.size) return false;
            const [itemX, itemY] = placedItem.position;
            const [itemW, itemH] = placedItem.size;
            return i >= itemX && i < itemX + itemW && j >= itemY && j < itemY + itemH;
          });
          if (occupied) return false;
        }
      }
      return true;
    };

    // Icon fallbacks by item type
    const getIconForType = (item) => {
      const type = item.type || item.category || '';
      if (type === 'weapon' || type === 'weapons') return '⚔️';
      if (type === 'armor') return '🛡️';
      if (type === 'shield') return '🛡️';
      if (type === 'potion') return '🧪';
      if (type === 'scroll') return '📜';
      if (type === 'wand' || type === 'rod') return '🪄';
      if (type === 'ring') return '💍';
      if (type === 'wondrous') return '✨'; // lint-exempt: emoji lookup for magical-item subtype (distinct from ITEM_CATEGORIES.WONDROUS filter)
      if (type === 'component') return '🧪';
      if (type === 'ammunition') return '🏹';
      if (type === 'gear') return '🎒';
      return '📦'; // Default fallback
    };

    for (const item of items) {
      // Normalize category from type if needed
      const category = item.category || item.type || 'items';
      const baseType = item.baseType || (item.name ? item.name.toLowerCase().replace(/[^a-z0-9]/g, '_') : 'misc');

      // Try to find baseItem in BASE_ITEMS for defaults
      const baseItem = BASE_ITEMS?.[category]?.[baseType] ||
                       BASE_ITEMS?.[item.category]?.[item.baseType] || null;

      // Get/restore size
      let size = item.size;
      if (!size || !Array.isArray(size) || size.length !== 2) {
        size = baseItem?.size ? [...baseItem.size] : [1, 1];
        // Fallback sizes based on item type
        if (!baseItem) {
          const type = item.type || category;
          if (type === 'weapon' || type === 'weapons') size = [1, 2];
          if (type === 'armor') size = [2, 2];
          if (type === 'shield') size = [2, 2];
        }
      }

      // [CRITICAL FIX]: Get/restore icon - THIS WAS MISSING BEFORE!
      let icon = item.icon;
      if (!icon) {
        icon = baseItem?.icon || getIconForType(item);
      }

      // Build processed item with ALL required properties restored
      const processedItem = {
        ...item,
        // Core identifiers
        id: item.id || `item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        category: category,
        baseType: baseType,
        // [CRITICAL]: Icon must exist for GridComponent to render
        icon: icon,
        size: size,
        // Fix common property name issues from old/wrong format
        magicalBonus: item.magicalBonus ?? item.bonus ?? 0,
        requiresAttunement: item.requiresAttunement ?? item.attunement ?? false,
        // Ensure weight exists
        weight: item.weight ?? baseItem?.weight ?? 1,
        // Ensure quantity exists
        quantity: item.quantity ?? 1,
        // [FIX JAN 2026]: Copy stackable from BASE_ITEMS for proper stacking behavior
        stackable: item.stackable ?? baseItem?.stackable ?? false,
        // [FIX JAN 2026]: Copy armor-specific properties from BASE_ITEMS for AC calculation
        // These are CRITICAL for effectCalculator.calculateAC to work correctly
        armorType: item.armorType ?? baseItem?.armorType ?? null,
        maxDexMod: item.maxDexMod ?? baseItem?.maxDexMod ?? null,
        // Set equipSlot for armor items (shields go to 'offhand')
        // [BATCH 11 / P1 A-15 — APR 2026]: Shield detection is property-first
        //   via `armorType === 'shield'`. The legacy baseType strings remain
        //   as fallback so gameData rows without armorType still resolve, but
        //   custom CW shields with non-SRD baseType (e.g. 'tower_shield')
        //   that carry armorType:'shield' now route to 'offhand' correctly.
        //   Mirrors InventoryItem.constructor's defaultEquipSlot logic. Rule 11.
        equipSlot: item.equipSlot ?? (
          (item.armorType === 'shield' || baseItem?.armorType === 'shield' ||
           baseType === 'shield' || baseType === 'wooden_shield')
            ? 'offhand'
            : (category === 'armor' ? 'armor' : null)
        ),
        // [FIX APR 2026 — U1]: Restore container-specific properties from BASE_ITEMS.
        // [WHY]: InventoryItem constructor only sets isContainer/containerSlots/etc. when NEW items
        //        are created. Deserialized items from saved JSON lose these flags. That broke
        //        container detection everywhere that reads i.isContainer (Quick Access, context menu,
        //        drag-drop nesting, etc). Mirrors the NaN-weight fix philosophy: deserialized items
        //        must be made whole at load time, not band-aided at each read site.
        isContainer: item.isContainer ?? (category === 'containers' || !!baseItem?.containerSlots),
        containerSlots: item.containerSlots ?? baseItem?.containerSlots ?? null,
        containedItems: item.containedItems ?? (category === 'containers' ? [] : undefined),
        weightReduction: item.weightReduction ?? baseItem?.weightReduction ?? 0,
        containerSize: item.containerSize ?? baseItem?.containerSize ?? (category === 'containers' ? 'medium' : undefined),
        maxWeight: item.maxWeight ?? baseItem?.maxWeight ?? null,
        extradimensional: item.extradimensional ?? baseItem?.extradimensional ?? false
      };

      // Skip position assignment if already has valid position
      if (item.position && Array.isArray(item.position) && item.position.length === 2) {
        processedItem.position = item.position;
        positionedItems.push(processedItem);
        processedItems.push(processedItem);
        continue;
      }

      // Find empty position
      let found = false;
      for (let y = 0; y <= gridRows - size[1] && !found; y++) {
        for (let x = 0; x <= gridCols - size[0] && !found; x++) {
          if (checkPosition(processedItem, x, y, size, positionedItems)) {
            processedItem.position = [x, y];
            positionedItems.push(processedItem);
            processedItems.push(processedItem);
            found = true;
          }
        }
      }

      if (!found) {
        console.warn(`[loadCharacter] Could not place item: ${item.name} - grid full. Item will not display.`);
        // Still add it but without position - it won't display but won't be lost
        processedItems.push(processedItem);
      }
    }

    return processedItems;
  };

  // Character management functions
  const loadCharacter = useCallback((characterId) => {
    // Enhanced error handling and validation
    if (!characterId || (typeof characterId !== 'string' && typeof characterId !== 'number')) {
      console.warn('Invalid character ID provided to loadCharacter:', characterId);
      showStatus('Invalid character ID provided');
      return;
    }

    const character = savedCharacters.find(char => char && char.id === characterId);
    if (!character) {
      console.warn('Character not found in savedCharacters:', {
        requestedId: characterId,
        availableIds: savedCharacters.map(c => c?.id).filter(Boolean),
        savedCharactersLength: savedCharacters.length
      });
      showStatus('Character not found in saved characters');
      return;
    }

    try {
      // Validate character data structure
      if (!character.data) {
        throw new Error('Character data is missing');
      }

      // [B16]: Run schema migration before any consumer reads character data.
      // migrate() is idempotent on v1+ data and preserves unknown future-version
      // fields via _unknownFields. NOTE: the schemaVersion stamp for pre-v1 data
      // is NOT applied here yet — that arrives in Task 4 via MIGRATIONS[0].
      const migratedData = migrate(character.data);

      // Ensure backward compatibility - add classes array if missing
      const loadedStats = migratedData.playerStats || {};
      // [A42 guard]: Use Array.isArray — a falsy-only check (!loadedStats.classes) misses
      // corrupted/legacy saves where classes was stored as {} or another truthy non-array,
      // causing TypeError ("loadedStats.classes.find is not a function") on the next line.
      if (!Array.isArray(loadedStats.classes)) {
        loadedStats.classes = [];
      }
      
      // Ensure primary class is in the classes array
      if (loadedStats.class && !loadedStats.classes.find(c => c.className === loadedStats.class)) {
        loadedStats.classes.push({
          className: loadedStats.class,
          level: loadedStats.level || 1,
          spellbook: []
        });
      }
      
      // Sync total level with sum of class levels
      if (loadedStats.classes.length > 0) {
        const totalFromClasses = loadedStats.classes.reduce((sum, c) => sum + (c.level || 0), 0);
        if (totalFromClasses > 0) {
          loadedStats.level = totalFromClasses;
        }
      }

      // [FIX MAR 2026]: Ensure playerStats.id exists for party sync
      // [WHY]: useEnhancedPartySync uses playerStats.id as characterId for all Firebase operations.
      // Save function stores it as characterId, but party system reads .id — sync them here.
      loadedStats.id = loadedStats.characterId || character.id || loadedStats.id;

      // [FIX APR 2026 — B1]: Sync basics.languages → proficiencies.languages
      // [WHY]: Character creation stores languages at basics.languages (D&D canonical location).
      //        Skills tab reads proficiencies.languages. Older saves (pre-fix) don't sync these.
      //        Without this sync, languages count shows 0 on Skills tab for all such characters.
      const basicsLangs = loadedStats.basics?.languages;
      if (Array.isArray(basicsLangs) && basicsLangs.length > 0) {
        loadedStats.proficiencies = loadedStats.proficiencies || {};
        const profLangs = loadedStats.proficiencies.languages;
        if (!Array.isArray(profLangs) || profLangs.length === 0) {
          loadedStats.proficiencies.languages = [...basicsLangs];
        }
      }

      // [MAY 2026 — IDB image migration]: Inflate `img:<id>` portrait /
      //   customImage tokens and gallery `data` fields from the cache. The
      //   cache is hydrated at app startup (imageStoreReady gate); if the
      //   user navigates here before hydration finishes, tokens remain as
      //   tokens and re-resolve once the cache fills (no crash).
      const inflatedLoad = restoreImagesIntoState(loadedStats, migratedData.inventory || []);
      const inflatedLoadStats = inflatedLoad.playerStats;
      const inflatedLoadInventory = inflatedLoad.inventory;

      setPlayerStats(inflatedLoadStats);

      // [B16]: Capture unknown top-level keys from the migrated data so they
      // survive round-trip on next save. setUnknownFields({}) clears any stale
      // fields from a previously-loaded character.
      setUnknownFields(migratedData._unknownFields || {});

      // [FIX JAN 2026]: Auto-assign positions to inventory items that lack them
      // [PURPOSE]: Items MUST have position/size to display in grid inventory
      const invGridCols = inflatedLoadStats.bagSize?.[0] || 10;
      const invGridRows = inflatedLoadStats.bagSize?.[1] || 10;
      // [SUB-PLAN 1R-RENDERGAP STONE 1 / 2026-05-29]: Promote bare 'ring' equipSlot to
      // ring1/ring2 so equipped rings render in the EquipmentPanel gear grid.
      // [STONE 3a / 2026-05-27]: migratePairedSlots also covers earring → earring1/earring2.
      // [1R-eyes-data / 2026-05-27]: migrateLensSlots promotes legacy lens/goggle items from
      //   equipSlot:'head' → 'eyes' so they render in the Eyes gear-grid cell, not Head.
      const migratedLoadInventory = migrateLensSlots(migratePairedSlots(inflatedLoadInventory));
      const processedInventory = assignPositionsToItems(
        migratedLoadInventory,
        invGridCols,
        invGridRows
      );
      setInventory(processedInventory);

      // [FIX JAN 2026]: Auto-assign positions to vault items
      const processedVaults = (migratedData.vaults || []).map(vault => {
        const vaultCols = vault.gridSize?.[0] || 10;
        const vaultRows = vault.gridSize?.[1] || 10;
        return {
          ...vault,
          items: assignPositionsToItems(vault.items || [], vaultCols, vaultRows)
        };
      });
      setVaults(processedVaults);
      // [FIX NOV 26, 2025]: Ensure all currency denominations exist (pp, ep may be missing in old saves)
      // [WHY]: Object.entries in CurrencyPanel only shows keys that exist - old saves missing pp/ep
      setCurrencies({
        cp: 0, sp: 0, ep: 0, gp: 0, pp: 0,  // Defaults for all denominations
        ...(migratedData.currencies || {})  // Merge saved values
      });
      // [FIX MAR 2026]: Merge character settings instead of replacing — empty settings ({})
      // would wipe isPremium and other global settings when loading a saved character
      if (migratedData.settings && Object.keys(migratedData.settings).length > 0) {
        // [FIX P4-14]: Filter out global settings that should never come from saved characters
        const charSettings = Object.fromEntries(
          Object.entries(migratedData.settings).filter(([k]) => !GLOBAL_SETTINGS_KEYS.includes(k))
        );
        setSettings(prev => ({ ...prev, ...charSettings }));
      }
      // [FIX APR 2026 — F35]: Load skillProficiencies with fallback conversion
      // [WHY]: Characters saved before this fix have proficiencies.skills (name array) but no skillProficiencies.
      //        Characters saved after have skillProficiencies directly. Normalize booleans → numeric for legacy saves.
      if (migratedData.skillProficiencies && Object.keys(migratedData.skillProficiencies).length > 0) {
        setSkillProficiencies(normalizeSkillProficiencies(migratedData.skillProficiencies));
      } else if (migratedData.playerStats?.proficiencies?.skills) {
        // Fallback: convert from wizard name array for characters saved before this fix
        setSkillProficiencies(convertSkillNamesToObject(migratedData.playerStats.proficiencies.skills));
      } else {
        setSkillProficiencies({});
      }
      // [TIER 1.3 FIX APR 2026]: Re-hydrate Date fields and seed
      //   currentTime if the saved character predates the field. Without
      //   currentTime, the rest-button enable check falls back to wall
      //   clock and stays disabled forever — see CharacterContext.js
      //   Tier 1.3 comment for the full root-cause writeup.
      setTimeSystem(hydrateTimeSystem(migratedData.timeSystem) || {
        currentHour: 8,
        currentTime: new Date(2024, 0, 1, 8, 0, 0),
        lastShortRest: null,
        lastLongRest: null,
        exhaustionLevel: 0,
        timeSinceLastRest: 0
      });
      setCurrentGameDay(migratedData.currentGameDay || 1);
      setCalendarSettings(migratedData.calendarSettings || {
        progressDayOnLongRest: true,
        showSeasonalEffects: true,
        startYear: 1492,
        showLunarPhases: true,
        trackHolidays: true
      });

      // [FIX JAN 2026]: Restore spell data when loading character
      // [WHY]: Spell arrays were never loaded, causing spells to disappear
      if (migratedData.knownSpells) {
        setKnownSpells(migratedData.knownSpells);
      }
      if (migratedData.preparedSpells) {
        setPreparedSpells(migratedData.preparedSpells);
      }
      if (migratedData.spellSlots) {
        setSpellSlots(migratedData.spellSlots);
      }
      if (migratedData.customSpells) {
        setCustomSpells(migratedData.customSpells);
      }

      // [FIX APR 2026]: Restore 3 fields that were saved (via buildCurrentCharacterData) but never loaded
      if (migratedData.manualSpellSlots !== undefined) {
        setManualSpellSlots(migratedData.manualSpellSlots);
      }
      if (migratedData.activeQuickBonuses && Array.isArray(migratedData.activeQuickBonuses)) {
        setActiveQuickBonuses(migratedData.activeQuickBonuses);
      }
      if (migratedData.temporaryStats && typeof migratedData.temporaryStats === 'object') {
        setTemporaryStats(migratedData.temporaryStats);
      }

      // Load character-specific activity log
      // [FIX MAY 2026]: Rehydrate `entry.timestamp` from JSON-roundtrip string
      //   back to Date. Pre-fix, the Export Log button (ActivityLogTab.js:145)
      //   crashed with TypeError calling `.toLocaleTimeString()` on a string.
      //   ActivityLogTab.js:300 had a typeof guard, L145 didn't.
      // [A42 guard]: Use Array.isArray — a truthy-only check (if migratedData.activityLog) misses
      // corrupted/legacy saves where activityLog was stored as {} or another truthy non-array,
      // causing TypeError ("migratedData.activityLog.map is not a function").
      if (Array.isArray(migratedData.activityLog)) {
        setActivityLog(migratedData.activityLog.map((entry) => ({
          ...entry,
          timestamp: entry?.timestamp instanceof Date
            ? entry.timestamp
            : (entry?.timestamp ? new Date(entry.timestamp) : new Date()),
        })));
      } else {
        // Clear activity log for new character
        setActivityLog([]);
      }
      
      // Load character-specific journal entries
      if (migratedData.journalEntries) {
        setJournalEntries(migratedData.journalEntries);
      } else {
        setJournalEntries([]);
      }
      
      // Load journal roll tables
      if (migratedData.journalRollTables) {
        setJournalRollTables(migratedData.journalRollTables);
      } else {
        setJournalRollTables([]);
      }

      // [FIX P4-2]: Load rollTables, craftingProjects, dmEvents from saved character
      if (migratedData.rollTables) {
        setRollTables(migratedData.rollTables);
      } else {
        setRollTables([]);
      }
      if (migratedData.craftingProjects) {
        setCraftingProjects(migratedData.craftingProjects);
      } else {
        setCraftingProjects([]);
      }
      if (migratedData.dmEvents) {
        setDmEvents(migratedData.dmEvents);
      } else {
        setDmEvents([]);
      }

      // Load custom shops and tables
      // [A42 guard]: Use Array.isArray — a truthy-only check misses a customShopTypes stored as
      // {} (corrupted), causing TypeError ("migratedData.customShopTypes.forEach is not a function").
      // Also guard individual shopType entries for null/non-object elements in a sparse array.
      if (Array.isArray(migratedData.customShopTypes)) {
        setCustomShopTypes(migratedData.customShopTypes);
        // Re-register them in SHOP_ROLL_TABLES
        migratedData.customShopTypes.forEach(shopType => {
          if (!shopType || typeof shopType !== 'object') return; // guard sparse/null entries
          if (shopType.categories) {
            // Simple Mode shop
            const commonItems = [];
            const uncommonItems = [];
            const rareItems = [];
            
            shopType.categories.forEach(cat => {
              if (BASE_ITEMS[cat]) {
                Object.keys(BASE_ITEMS[cat]).forEach(itemName => {
                  const item = BASE_ITEMS[cat][itemName];
                  if (item.baseValue < 50) {
                    commonItems.push(itemName);
                  } else if (item.baseValue < 500) {
                    uncommonItems.push(itemName);
                  } else {
                    rareItems.push(itemName);
                  }
                });
              }
            });
            
            SHOP_ROLL_TABLES[shopType.id] = {
              common: commonItems,
              uncommon: uncommonItems,
              rare: rareItems
            };
          } else {
            // Advanced Mode shop
            SHOP_ROLL_TABLES[shopType.id] = {
              customType: true,
              commonTables: shopType.commonTables,
              uncommonTables: shopType.uncommonTables,
              rareTables: shopType.rareTables
            };
          }
        });
      }
      
      if (migratedData.customRollTables) {
        // [P1.2+P1.3 fix - Apr 29, 2026]: Removed mutation of DMG_LOOT_TABLES.
        // See line ~7113 for rationale.
        setCustomRollTables(migratedData.customRollTables);
      }

      // [CLR-001 FIX — MAY 2026]: loadCharacter was missing the activeShops
      //   restore block that importInventory (~line 7305) and autosave-restore
      //   (~line 7554) both have. Saved-character load silently dropped any
      //   in-progress shop dialogs. Parity-restored here.
      if (migratedData.activeShops && Array.isArray(migratedData.activeShops)) {
        setActiveShops(migratedData.activeShops);
      } else {
        setActiveShops([]);
      }

      showStatus(`Character "${character.name || 'Unknown'}" loaded successfully!`);

      // [S6]: Track character load (hashed ID for privacy, no character name)
      trackEvent('character_loaded', {
        character_id: String(characterId).slice(0, 8),
      });

      // [FIX APR 2026]: Emit CHARACTER_LOADED so effectRegistry clears stale effects
      systemBus.emit(EVENTS.CHARACTER_LOADED, {
        characterName: character.name,
        characterId: character.id,
        source: 'save'
      });
    } catch (error) {
      showStatus('Failed to load character data - character may be corrupted');
      reportError(error, {
        area: 'load_character',
        extra: {
          characterId,
          characterName: character?.name,
          hasData: !!character?.data,
        },
      });
    }
  }, [savedCharacters, currencies, settings, showStatus]);

  // Auto-load last character (RESTORED - but only if no appState was loaded)
  useEffect(() => {
    // Only auto-load if no current character and remember setting is on
    if (!settings.rememberLastCharacter || playerStats.name) return;

    // [FIX 2026-06-20]: Prefer the explicit lastCharacterId, but fall back to the
    //   most-recently-played saved character when it's missing/stale, so a returning user
    //   with a blank autosave auto-loads their latest work instead of landing on a blank
    //   sheet (or being shoved into the creation wizard — the "it loads new character" bug).
    //   selectAutoLoadCharacter is pure + unit-tested (utils/characterAutoLoad.js).
    const lastCharacterId = localStorage.getItem('lastCharacterId');
    const target = selectAutoLoadCharacter(savedCharacters, lastCharacterId);
    if (target) {
      loadCharacter(target.id);
      if (target.characterName && target.characterName.trim() !== '') {
        showStatus(`Welcome back, ${target.characterName}!`);
      }
    }
  }, [savedCharacters, settings.rememberLastCharacter, playerStats.name, loadCharacter, showStatus]);

  // [FIX 2025-10-11]: Autosave RE-ENABLED after fixing key mismatch
  // The infinite loop issue was caused by loading from 'appState' but saving to 'tabletopInventory_autosave'
  // Now both load and save use the same key: 'tabletopInventory_autosave'
  //
  // [FIX APR 2026 Phase 3A.2]: stateRef stores latest state for beforeunload + dedupe.
  // lastSavedSerializedRef stores the last successfully-persisted payload so the
  // autosave effect can short-circuit when nothing has actually changed.
  const stateRef = useRef();
  const lastSavedSerializedRef = useRef(null);
  // [AUTOSAVE-DEBOUNCE-STARVATION — 2026-07-18]: when the flush callback last RAN
  // (attempt, not success — a dedupe short-circuit still counts, preventing delay-0
  // hammering). Feeds autosaveDelay so sustained sub-2s activity cannot starve the
  // flush past settings.autosaveInterval (the max-wait). null = no burst in flight.
  const lastFlushAttemptRef = useRef(null);

  // [FIX APR 2026 Phase 3A.2]: Consolidated autosave to a SINGLE debounced effect.
  // [WHY]: Previously had THREE effects racing:
  //   (1) 500ms debounce → setHasUnsavedChanges(true)
  //   (2) 2s debounce → safeLocalStorageSet + setHasUnsavedChanges(false)
  //   (3) 60s interval → safeLocalStorageSet + setHasUnsavedChanges(false)
  // This caused: duplicate writes, thrashing indicator, silent quota-exceed (saveFailure
  // was ignored — setLastAutosave fired regardless, showing "Saved just now" while data
  // was lost). Now one effect with dedupe + proper failure surfacing.
  useEffect(() => {
    if (!settings.autosaveInterval || settings.autosaveInterval === 0) return;
    if (!stateRef.current) return;

    // [MAY 2026 — IDB image migration]: Lift dataUrls out of state and into
    //   IDB before serialization. Returns the same shape with portrait /
    //   customImage / gallery `data` rewritten to compact `img:<id>` tokens.
    //   Strip is async (IDB writes), so the work moves into the timer below.
    let cancelled = false;

    // Optimistic flag (the actual write debounces; user sees "Saving..." now).
    if (!hasUnsavedChanges) setHasUnsavedChanges(true);

    // [AUTOSAVE-DEBOUNCE-STARVATION — 2026-07-18]: the cleanup below clearTimeout()s
    // on EVERY dep change, so sustained sub-2s activity (combat clicks, casts) used to
    // push the flush out FOREVER — crash mid-burst lost everything since the last
    // pause. Now: trailing 2s debounce for bursts, but never later than
    // settings.autosaveInterval past the last flush attempt (the max-wait — which
    // finally makes the SettingsPanel "Every 30 seconds/1 minute/…" labels true).
    // Scheduling decision extracted to utils/autosaveDelay.js (Jest-asserted).
    if (lastFlushAttemptRef.current === null) lastFlushAttemptRef.current = Date.now();
    const delay = autosaveDelay(Date.now(), lastFlushAttemptRef.current, 2000, settings.autosaveInterval);

    const timer = setTimeout(async () => {
      if (cancelled) return;
      lastFlushAttemptRef.current = Date.now();
      let serialized;
      try {
        const stripped = await stripImagesFromState(
          stateRef.current.playerStats,
          stateRef.current.inventory
        );
        if (cancelled) return;
        const persistPayload = {
          ...stateRef.current,
          playerStats: stripped.playerStats,
          inventory: stripped.inventory,
        };
        serialized = JSON.stringify(persistPayload);
      } catch (e) {
        console.error('[Autosave] strip+stringify failed:', e);
        return;
      }
      if (serialized === lastSavedSerializedRef.current) {
        if (hasUnsavedChanges) setHasUnsavedChanges(false);
        return;
      }
      setIsSaving(true);
      const ok = safeLocalStorageSet('tabletopInventory_autosave', serialized);
      setIsSaving(false);
      if (ok) {
        lastSavedSerializedRef.current = serialized;
        setLastAutosave(new Date());
        setHasUnsavedChanges(false);
      } else {
        // DO NOT update lastAutosave — indicator must reflect truth.
        // Show persistent error toast so user knows to export.
        if (showStatus) {
          showStatus('⚠️ Autosave failed — localStorage full. Export your character.', 6000);
        }
        // Leave hasUnsavedChanges=true so indicator stays in "Saving..." state,
        // signaling the user that changes are NOT persisted.
      }
    }, delay);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerStats, inventory, vaults, currencies, customRollTables, customShopTypes,
      knownSpells, preparedSpells, customSpells, spellSlots, manualSpellSlots,
      activityLog, activeQuickBonuses, temporaryStats, journalEntries, journalRollTables,
      skillProficiencies, timeSystem, currentGameDay, calendarSettings,
      rollTables, craftingProjects, dmEvents,
      // [CLR-006 FIX — MAY 2026]: activeShops + unknownFields were missing from
      //   the dep array even though the stateRef builder writes them. Edits to
      //   either could go un-saved up to 2s. Adding them ensures the debounced
      //   autosave timer fires when they change.
      activeShops, unknownFields,
      settings.autosaveInterval]);

  // [MAXHP-UNIFICATION — 2026-07-25, BUG-MAXHP-STORED-VS-COMPUTED-DIVERGE]:
  // stored playerStats.maxHitPoints is a derived mirror of calculateMaxHP() from here on.
  // This effect is the ONLY systematic writer; it runs where all calculator inputs are in
  // scope (below Inventory in the provider order — the reason CharacterContext could never
  // do this itself, see ClassResourcesPanel.js:565's admission). reconcileMaxHP refuses
  // during hydration and no-ops when in sync, so the effect converges in one pass.
  useEffect(() => {
    const computed = effectCalculator.calculateMaxHP(
      playerStats,
      inventory,
      settings.activeQuickBonuses || [],
      settings
    );
    const fix = reconcileMaxHP(playerStats, computed);
    if (!fix) return;
    setPlayerStats(prev => {
      // Re-check against prev — another update may have landed between render and effect.
      const prevFix = reconcileMaxHP(prev, computed);
      if (!prevFix) return prev;
      return {
        ...prev,
        maxHitPoints: prevFix.maxHitPoints,
        ...(prevFix.currentHitPoints !== undefined ? { currentHitPoints: prevFix.currentHitPoints } : {})
      };
    });
    logActivity(fix.note, 'character', {
      storedMaxHP: playerStats.maxHitPoints,
      computedMaxHP: computed,
      currentClamped: fix.currentHitPoints !== undefined
    });
  }, [playerStats, inventory, settings, setPlayerStats, logActivity]);

  // [BUG FIX 2025-10-10]: stateRef declared above. This effect keeps it updated.
  // [WHY]: beforeunload and the autosave effect both read latest state from this ref.
  //
  // [Phase 4b BC-15 — APR 2026]: playerStats._activeFlags (combat toggles —
  //   sneakAttackActive, raging, attackingHexbladeCursedTarget, etc.) ride
  //   along with playerStats automatically because the spread/restore path
  //   preserves all of its fields. Persistence is INTENTIONAL — a player
  //   who activates Sneak Attack mid-combat and closes the app should come
  //   back to the same toggle state. See combatToggles.test.js BC-15 round-
  //   trip suite for the pinning tests.
  //
  // Update ref whenever state changes
  useEffect(() => {
    stateRef.current = {
      version: '1.0',
      timestamp: new Date().toISOString(),
      playerStats,
      inventory,
      vaults,
      currencies,
      settings,
      customRollTables,
      customShopTypes,
      knownSpells,
      preparedSpells,
      customSpells,
      spellSlots,
      manualSpellSlots,
      activityLog,
      activeQuickBonuses,
      temporaryStats,
      journalEntries,
      journalRollTables,
      // [FIX APR 2026 — F67]: Add 4 fields missing from autosave that manual save includes
      // [WHY]: Without these, autosave loses skill proficiencies, calendar state, and time tracking
      skillProficiencies,
      timeSystem,
      currentGameDay,
      calendarSettings,
      // [FIX P4-2]: Add 3 fields missing from autosave — rollTables, craftingProjects, dmEvents
      // [WHY]: These exist in CharacterContext but were never wired into persistence
      rollTables,
      craftingProjects,
      dmEvents,
      // [GAP 2 FIX — MAY 2026]: activeShops was in the dep array (triggering autosave
      //   on every shop change) but was missing from the stateRef builder, so the
      //   payload written to localStorage never included the latest activeShops value.
      activeShops,
      // [B16]: Mirror buildCurrentCharacterData's tail spread so autosave preserves
      // unknown forward-compat fields. Without this, an import-then-reload (autosave
      // restore) drops _unknownFields between sessions — the exact failure mode B16
      // exists to prevent.
      unknownFields
    };
  }, [playerStats, inventory, vaults, currencies, settings, customRollTables, customShopTypes, knownSpells, preparedSpells, customSpells, spellSlots, manualSpellSlots, activityLog, activeQuickBonuses, temporaryStats, journalEntries, journalRollTables, skillProficiencies, timeSystem, currentGameDay, calendarSettings, rollTables, craftingProjects, dmEvents, activeShops, unknownFields]);

  // Save state immediately before the user leaves/refreshes the page
  // [BUG FIX 2025-10-10]: Always save on close, regardless of autosave settings
  // [WHY]: If autosave is disabled, users lose all data when closing the app
  useEffect(() => {
    const handleBeforeUnload = () => {
      try {
        // [FIX]: Always save latest state from ref
        // This ensures data is saved even if autosave is disabled
        if (stateRef.current) {
          // [DUAL STORAGE FIX - NOV 19, 2025]: Validate before saving
          if (stateRef.current.playerStats) {
            const validation = validateCharacterData(stateRef.current.playerStats);
            if (!validation.valid) {
              console.error('❌ [BEFOREUNLOAD] Character validation failed (saving anyway):', validation.errors);
            }
          }

          safeLocalStorageSet('tabletopInventory_autosave', JSON.stringify(stateRef.current));
        }
      } catch (error) {
        console.error('Failed to save on unload:', error);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []); // EMPTY ARRAY - Only register listener once, use stateRef for latest data

  // Nuclear localStorage reset - clears ALL possible keys AND resets state
  const nuclearReset = () => {
    const keysToRemove = [
      'appState',
      'savedCharacters', 
      'lastCharacterId',
      'dnd-inventory-autosave',
      'dnd-inventory-settings', 
      'dnd-saved-characters',
      'dnd-inventory-state',
      'tabletop-inventory-state',
      'tabletopInventory_autosave',
      'playerStats',
      'inventory',
      'vaults',
      'currencies'
    ];
    
    // Remove all known keys
    keysToRemove.forEach(key => localStorage.removeItem(key));
    
    // Reset all state to initial values
    setPlayerStats({
      id: Date.now().toString(),
      name: '',
      class: '',
      level: 1,
      classes: [], // CRITICAL: Must include this for multiclass support!
      armorClass: 10,
      initiative: 0,
      currentHitPoints: 10,
      maxHitPoints: 10,
      speed: 30,
      strength: 10,
      dexterity: 10,
      constitution: 10,
      intelligence: 10,
      wisdom: 10,
      charisma: 10,
      goldPieces: 0,
      silverPieces: 0,
      copperPieces: 0,
      customStats: [],
      customACBonuses: [],
      spellcastingAbility: 'intelligence',
      spellSlotOverride: false,
      // [PHASE 4.4]: carryCapacity removed - derived via effectCalculator
      useWeightLimit: true,
      useBagLimit: true,
      bagSize: [10, 10],
      hasReceivedStarterPack: false,
      startingGoldRolled: false,
      startingGoldResult: null
    });
    setInventory([]);
    setVaults([]);
    setCurrencies({ gp: 0, sp: 0, cp: 0, pp: 0, ep: 0 });
    setKnownSpells([]);
    setPreparedSpells([]);
    setSpellSlots({
      1: { max: 0, used: 0 },
      2: { max: 0, used: 0 },
      3: { max: 0, used: 0 },
      4: { max: 0, used: 0 },
      5: { max: 0, used: 0 },
      6: { max: 0, used: 0 },
      7: { max: 0, used: 0 },
      8: { max: 0, used: 0 },
      9: { max: 0, used: 0 }
    });
    setActivityLog([]);
    setJournalEntries([]);
    setSkillProficiencies({});
    showStatus('All data cleared!');

    // Clear all localStorage for this domain
    localStorage.clear();
    sessionStorage.clear();

    // [FIX APR 2026]: Emit CHARACTER_RESET so effectRegistry clears stale effects
    systemBus.emit(EVENTS.CHARACTER_RESET, { reason: 'new_character' });
  };

  // Manual clear autosave function
  const clearAutosave = async () => {
    if (await confirm({ title: 'Clear Autosave', message: 'This will clear the autosaved data. Your manually saved characters will not be affected. Continue?', confirmText: 'Clear', confirmColor: 'red' })) {
      localStorage.removeItem('tabletopInventory_autosave');
      showStatus('Autosave data cleared');
    }
  };

  // [FIX P4-3]: Single source of truth for current character data
  // [WHY]: exportCharacter only exported 5 fields while saveCharacter saved 21. This helper
  //        ensures both always include the same complete field set, preventing future drift.
  //
  // PARITY PARTNER: KNOWN_TOP_LEVEL_KEYS in src/services/characterMigrations.js.
  // Adding a field here? Add it to that Set too, or the new field will be incorrectly
  // extracted into _unknownFields on the next load, and v1 consumers will read stale data.
  // See the Apr 2026 silent-gap audit lesson (character.equipment typo) for why this matters.
  //
  // [B16]: The trailing `...unknownFields` spread preserves v1.x reserved fields
  // (e.g. combatState, lootHistory) through round-trip. setUnknownFields is called
  // on load/import; this spread closes the loop on save.
  const buildCurrentCharacterData = () => {
    const data = {
      playerStats,
      inventory,
      vaults,
      currencies,
      settings,
      knownSpells,
      preparedSpells,
      spellSlots,
      customSpells,
      // [FIX APR 2026]: 3 fields were in autosave stateRef but missing from buildCurrentCharacterData
      manualSpellSlots,
      activeQuickBonuses,
      temporaryStats,
      timeSystem,
      currentGameDay,
      calendarSettings,
      skillProficiencies,
      customShopTypes,
      customRollTables,
      // [BATCH 7 / P1 D-11 — APR 2026]: activeShops persists with character.
      //   Pre-fix the merchant vanished on reload — items already purchased
      //   survived in inventory; the merchant who sold them did not.
      activeShops,
      activityLog,
      journalEntries,
      journalRollTables,
      rollTables,
      craftingProjects,
      dmEvents,
      // [B16]: Spread preserved v1.x reserved fields back to top level so
      // round-trip is lossless. We deliberately do NOT include _unknownFields
      // as a key — on next load, migrate() re-extracts these keys into a fresh
      // _unknownFields object. KNOWN_TOP_LEVEL_KEYS in characterMigrations.js
      // is the parity partner; its keys are mutually exclusive with anything
      // unknownFields could contain by construction.
      ...unknownFields
    };

    // [D3 / #103]: Runtime gate at the save boundary. Any RESERVED_FIELDS
    //   path with meaningful content in `data` MUST be reference-equal to
    //   the same path inside `unknownFields` — that's the spread-through
    //   round-trip case. A non-spread-through write means v1 source code
    //   built up a reserved-path value somewhere and would now serialize
    //   it. In dev/test the gate throws; in prod it logs and lets the
    //   write proceed (graceful degrade).
    assertNoReservedWrites(data, unknownFields, 'App.buildCurrentCharacterData');

    return data;
  };

  const saveCharacter = (slotName = null) => {
    const characterName = playerStats.name || 'Unnamed Character';
    const timestamp = new Date().toISOString();

    // Check if this character already exists to preserve creation date
    const existingCharacter = savedCharacters.find(char =>
      char.name === (slotName || characterName) || char.id === playerStats.characterId
    );

    // [PHASE B / #021 — 2026-05-06]: Stamp the resolved id + characterId
    // onto the in-memory playerStats so the autosave effect (next render
    // tick) writes a snapshot that already carries them. Pre-fix this
    // function only stamped the savedCharacters[N].data.playerStats entry,
    // so the in-memory state stayed without `.id`, autosave wrote that,
    // and on refresh the SettingsPanel.jsx:2178 party-create guard fired
    // with "Save your character first." User reported the regression
    // 2026-05-06 against a Lvl 4 Barbarian.
    const stampedStats = stampCharacterIds(playerStats, existingCharacter?.id);
    const resolvedId = stampedStats.id;
    setPlayerStats(prev => stampCharacterIds(prev, resolvedId));

    const characterData = {
      id: resolvedId,
      name: slotName || characterName,
      characterName,
      createdAt: existingCharacter?.createdAt || timestamp,
      lastModified: timestamp,
      totalPlayTime: (existingCharacter?.totalPlayTime || 0) + Math.floor((Date.now() - (sessionStartTime || Date.now())) / 1000),
      data: {
        ...buildCurrentCharacterData(),
        // [OVERRIDE]: Stamp character IDs onto the persisted snapshot too,
        // matching the in-memory stamp above. Both produce the same id.
        playerStats: stampedStats,
      }
    };

    setSavedCharacters(prev => {
      // Remove any existing save with the same name or ID
      const filtered = prev.filter(char => 
        char.name !== characterData.name && char.id !== characterData.id
      );
      const newList = [...filtered, characterData].sort((a, b) => 
        new Date(b.lastModified) - new Date(a.lastModified)
      );
      
      // Remember this as the last character if setting is enabled
      if (settings.rememberLastCharacter) {
        safeLocalStorageSet('lastCharacterId', characterData.id);
      }
      
      return newList;
    });

    showStatus(`Character "${characterData.name}" saved successfully!`);
    setHasUnsavedChanges(false);  // Clear unsaved changes indicator
  };


  const deleteCharacter = async (characterId) => {
    const character = savedCharacters.find(char => char.id === characterId);
    if (!character) return;

    if (await confirm({ title: 'Delete Character', message: `Are you sure you want to delete "${character.name}"? This cannot be undone.`, confirmText: 'Delete', confirmColor: 'red' })) {
      setSavedCharacters(prev => prev.filter(char => char.id !== characterId));
      // [S6]: Track character deletion (no PII)
      trackEvent('character_deleted');
      showStatus(`Character "${character.name}" deleted.`);
    }
  };

  // ========================================
  // ENHANCED MULTI-FORMAT EXPORT SYSTEM
  // ========================================
  
  // CSV Export Generator
  const generateCSVExport = (data) => {
    const { playerStats, inventory, vaults, currencies } = data;
    let csv = 'Type,Name,Quantity,Weight,Value,Location,Properties,Notes\n';
    
    // Add inventory items
    inventory.forEach(item => {
      const itemWithMethods = Object.assign(Object.create(InventoryItem.prototype), item);
      const totalWeight = itemWithMethods.getTotalWeight() || 0;
      const totalValue = itemWithMethods.getTotalValue() || 0;

      csv += `Inventory,"${item.name}",${item.quantity || 1},${totalWeight.toFixed(2)},${totalValue.toFixed(2)},Main Inventory,"${item.properties?.join('; ') || ''}","${item.description || ''}"\n`;
      
      // Add contained items
      if (item.containedItems) {
        item.containedItems.forEach(containedItem => {
          const containedWithMethods = Object.assign(Object.create(InventoryItem.prototype), containedItem);
          csv += `Container Item,"${containedItem.name}",${containedItem.quantity || 1},${containedWithMethods.getTotalWeight().toFixed(2)},${containedWithMethods.getTotalValue().toFixed(2)},"${item.name}","${containedItem.properties?.join('; ') || ''}","${containedItem.description || ''}"\n`;
        });
      }
    });
    
    // Add vault items
    vaults.forEach(vault => {
      vault.items.forEach(item => {
        const itemWithMethods = Object.assign(Object.create(InventoryItem.prototype), item);
        csv += `Vault Item,"${item.name}",${item.quantity || 1},${itemWithMethods.getTotalWeight().toFixed(2)},${itemWithMethods.getTotalValue().toFixed(2)},"${vault.name}","${item.properties?.join('; ') || ''}","${item.description || ''}"\n`;
      });
    });
    
    // Add currencies
    csv += `Currency,Copper Pieces,${currencies.cp || 0},${(currencies.cp || 0) * 0.02},${(currencies.cp || 0) * 0.01},Purse,,\n`;
    csv += `Currency,Silver Pieces,${currencies.sp || 0},${(currencies.sp || 0) * 0.02},${(currencies.sp || 0) * 0.1},Purse,,\n`;
    csv += `Currency,Electrum Pieces,${currencies.ep || 0},${(currencies.ep || 0) * 0.02},${(currencies.ep || 0) * 0.5},Purse,,\n`;
    csv += `Currency,Gold Pieces,${currencies.gp || 0},${(currencies.gp || 0) * 0.02},${currencies.gp || 0},Purse,,\n`;
    csv += `Currency,Platinum Pieces,${currencies.pp || 0},${(currencies.pp || 0) * 0.02},${(currencies.pp || 0) * 10},Purse,,\n`;
    
    return csv;
  };
  
  // Text Export Generator
  const generateTextExport = (data) => {
    const { playerStats, inventory, vaults, currencies } = data;
    let text = '';
    
    text += `=======================================\n`;
    text += `D&D CHARACTER SHEET: ${playerStats.name || 'Unnamed Character'}\n`;
    text += `=======================================\n\n`;
    
    text += `CHARACTER STATS:\n`;
    text += `----------------\n`;
    text += `Name: ${playerStats.name || 'Unknown'}\n`;
    text += `Class: ${primaryClass || 'None'}\n`;
    text += `Level: ${playerStats.level || 1}\n`;
    text += `Strength: ${playerStats.strength || 10}\n`;
    text += `Dexterity: ${playerStats.dexterity || 10}\n`;
    text += `Constitution: ${playerStats.constitution || 10}\n`;
    text += `Intelligence: ${playerStats.intelligence || 10}\n`;
    text += `Wisdom: ${playerStats.wisdom || 10}\n`;
    text += `Charisma: ${playerStats.charisma || 10}\n`;
    text += `Carry Capacity: ${carryCapacity} lbs\n\n`;
    
    text += `CURRENCIES:\n`;
    text += `-----------\n`;
    text += `Copper: ${currencies.cp || 0}\n`;
    text += `Silver: ${currencies.sp || 0}\n`;
    text += `Electrum: ${currencies.ep || 0}\n`;
    text += `Gold: ${currencies.gp || 0}\n`;
    text += `Platinum: ${currencies.pp || 0}\n\n`;
    
    text += `INVENTORY (${inventory.length} items):\n`;
    text += `-------------------------------------\n`;
    inventory.forEach((item, index) => {
      const itemWithMethods = Object.assign(Object.create(InventoryItem.prototype), item);
      text += `${index + 1}. ${item.name}\n`;
      text += `   Weight: ${itemWithMethods.getTotalWeight().toFixed(2)} lbs | Value: ${itemWithMethods.getTotalValue().toFixed(2)} gp\n`;
      if (item.description) text += `   Description: ${item.description}\n`;
      if (item.containedItems?.length > 0) {
        text += `   Contains ${item.containedItems.length} items:\n`;
        item.containedItems.forEach(containedItem => {
          text += `     - ${containedItem.name}\n`;
        });
      }
      text += `\n`;
    });
    
    if (vaults.length > 0) {
      text += `VAULTS (${vaults.length} locations):\n`;
      text += `-----------------------------------\n`;
      vaults.forEach((vault, vIndex) => {
        text += `${vIndex + 1}. ${vault.name} (${vault.location || 'Unknown Location'})\n`;
        text += `   Cost: ${vault.cost || 0} gp/month\n`;
        text += `   Items: ${vault.items?.length || 0}\n`;
        vault.items?.forEach((item, iIndex) => {
          text += `     ${iIndex + 1}. ${item.name}\n`;
        });
        text += `\n`;
      });
    }
    
    text += `=======================================\n`;
    text += `Exported: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}\n`;
    text += `Generated by Tabletop Inventory Manager\n`;
    
    return text;
  };
  
  // [BATCH 1.5 — S-8 (2026-05-03)]: HTML export extracted to src/utils/characterExport.js
  // with HTML-escaping on every user-controlled field (character name, item name,
  // item description, contained item names, vault name, vault location, primary class).
  // Pre-fix, the export was a stored XSS surface — sharing a malicious export with
  // a party member could pop their DOM on open. The util enriches each item with the
  // computed totalWeight / totalValue derived from InventoryItem prototype methods so
  // the util itself stays a pure function (no class-prototype dependency).
  const generateHTMLExport = (data) => {
    const { inventory = [], ...rest } = data || {};
    const enrichedInventory = inventory.map((item) => {
      const itemWithMethods = Object.assign(Object.create(InventoryItem.prototype), item);
      return {
        ...item,
        weight: itemWithMethods.getTotalWeight ? itemWithMethods.getTotalWeight() : (item.weight || 0),
        value: itemWithMethods.getTotalValue ? itemWithMethods.getTotalValue() : (item.value || 0),
      };
    });
    return generateHTMLExportFromUtil({ ...rest, inventory: enrichedInventory, primaryClass });
  };

  // ENHANCED EXPORT FUNCTION - Multiple Formats
  const exportCharacter = (characterId = null, format = 'json') => {
    let data;
    let characterName;
    let character;
    
    if (characterId) {
      // Export a saved character - with enhanced error handling
      
      // Validate characterId first
      if (typeof characterId !== 'string' && typeof characterId !== 'number') {
        console.warn('Invalid character ID provided to exportCharacter:', characterId);
        showStatus('Invalid character ID - exporting current character instead');
        // [FIX P4-3]: Use buildCurrentCharacterData() for complete export
        data = buildCurrentCharacterData();
        characterName = playerStats.name || 'character';
      } else {
        // Try to find the character
        character = savedCharacters.find(char => char && char.id === characterId);
        
        if (!character) {
          console.warn('Character not found in savedCharacters:', {
            requestedId: characterId,
            availableIds: savedCharacters.map(c => c?.id).filter(Boolean),
            savedCharactersLength: savedCharacters.length
          });
          
          // Provide more helpful error message and fallback
          showStatus(`Character not found - exporting current character instead`);
          
          // [FIX P4-3]: Use buildCurrentCharacterData() for complete export
          data = buildCurrentCharacterData();
          characterName = playerStats.name || 'character';
        } else {
          // Character found successfully
          data = character.data;
          characterName = character.name || character.characterName || 'character';
        }
      }
    } else {
      // [FIX P4-3]: Export current character with ALL fields (was only 5, now 21)
      data = buildCurrentCharacterData();
      characterName = playerStats.name || 'character';
    }
    
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    const safeCharName = characterName.replace(/[^a-z0-9]/gi, '_').toLowerCase();
    
    try {
      let content, filename, mimeType;
      
      switch (format) {
        case 'json':
          content = JSON.stringify({
            version: '1.0',
            timestamp: new Date().toISOString(),
            ...data
          }, null, 2);
          filename = `${safeCharName}_${timestamp}.json`;
          mimeType = 'application/json';
          break;
          
        case 'csv':
          content = generateCSVExport(data);
          filename = `${safeCharName}_inventory_${timestamp}.csv`;
          mimeType = 'text/csv';
          break;
          
        case 'txt':
          content = generateTextExport(data);
          filename = `${safeCharName}_character_sheet_${timestamp}.txt`;
          mimeType = 'text/plain';
          break;
          
        case 'html':
          content = generateHTMLExport(data);
          filename = `${safeCharName}_character_sheet_${timestamp}.html`;
          mimeType = 'text/html';
          break;
          
        default:
          throw new Error('Unsupported export format');
      }
      
      const blob = new Blob([content], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      showStatus(`Character exported as ${filename} (${format.toUpperCase()})`);
      setHasUnsavedChanges(false);  // Clear unsaved changes after export
      
    } catch (error) {
      console.error('Export failed:', error);
      showStatus(`Failed to export character: ${error.message}`);
    }
  };

  // ========================================
  // DEBUG BUNDLE EXPORT (Sub-plan 4A)
  // [WHY]: Support / diagnostic download that bundles character state + IDB
  //   health + localStorage usage + browser env so users can share one file
  //   when reporting bugs. Pure bundle logic lives in src/utils/debugBundle.js
  //   (Rule 22 — extracted so tests can import it without a DOM context).
  // ========================================
  const exportDebugBundle = async () => {
    try {
      const [characterData, idbHealth] = await Promise.all([
        Promise.resolve(buildCurrentCharacterData()),
        getIdbHealth(),
      ]);
      const timestamp = new Date().toISOString();
      const bundle = buildDebugBundle({ characterData, idbHealth, timestamp });
      const filename = `tti_debug_${timestamp.slice(0, 19).replace(/[:.]/g, '-')}.json`;
      triggerBundleDownload(JSON.stringify(bundle, null, 2), filename);
      logActivity('Exported debug report', 'settings', { filename });
      showStatus('Debug report downloaded');
    } catch (err) {
      console.error('[exportDebugBundle] failed:', err);
      showStatus(`Debug export failed: ${err.message}`);
    }
  };

  // ========================================
  // GOOGLE DRIVE INTEGRATION
  // ========================================
  
  const [isDriveLoaded, setIsDriveLoaded] = useState(false);
  const [driveAccessToken, setDriveAccessToken] = useState(null);
  
  // Load Google Drive API
  const loadGoogleDriveAPI = () => {
    if (window.gapi) {
      return Promise.resolve();
    }
    
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://apis.google.com/js/api.js';
      script.onload = () => {
        window.gapi.load('auth2:client', () => {
          window.gapi.client.init({
            apiKey: 'YOUR_API_KEY', // You'll need to add your Google API key
            clientId: 'YOUR_CLIENT_ID', // You'll need to add your Google Client ID
            discoveryDocs: ['https://www.googleapis.com/discovery/v1/apis/drive/v3/rest'],
            scope: 'https://www.googleapis.com/auth/drive.file'
          }).then(() => {
            setIsDriveLoaded(true);
            resolve();
          }).catch(reject);
        });
      };
      script.onerror = reject;
      document.head.appendChild(script);
    });
  };
  
  // Sign in to Google Drive
  const signInToDrive = async () => {
    try {
      if (!isDriveLoaded) {
        await loadGoogleDriveAPI();
      }
      
      const authInstance = window.gapi.auth2.getAuthInstance();
      const user = await authInstance.signIn();
      const accessToken = user.getAuthResponse().access_token;
      
      setDriveAccessToken(accessToken);
      showStatus('Connected to Google Drive!');
      return true;
    } catch (error) {
      console.error('Google Drive sign-in failed:', error);
      showStatus('Failed to connect to Google Drive');
      return false;
    }
  };
  
  // Upload character to Google Drive
  const backupToDrive = async (characterId = null) => {
    if (!driveAccessToken) {
      const signedIn = await signInToDrive();
      if (!signedIn) return;
    }
    
    try {
      let data, filename;
      
      if (characterId) {
        const character = savedCharacters.find(char => char.id === characterId);
        if (!character) {
          showStatus('Character not found!');
          return;
        }
        data = {
          version: '1.0',
          timestamp: new Date().toISOString(),
          ...character.data
        };
        filename = `${character.characterName}_backup_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.json`;
      } else {
        data = {
          version: '1.0',
          timestamp: new Date().toISOString(),
          playerStats,
          inventory,
          vaults,
          currencies,
          settings
        };
        const characterName = playerStats.name || 'character';
        filename = `${characterName}_backup_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.json`;
      }
      
      const fileContent = JSON.stringify(data, null, 2);
      
      // Create file metadata
      const fileMetadata = {
        name: filename,
        parents: ['appDataFolder'] // Store in app-specific folder
      };
      
      // Upload to Google Drive
      const response = await fetch('https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${driveAccessToken}`,
          'Content-Type': 'multipart/related; boundary="foo_bar_baz"'
        },
        body: [
          '--foo_bar_baz',
          'Content-Type: application/json; charset=UTF-8',
          '',
          JSON.stringify(fileMetadata),
          '--foo_bar_baz',
          'Content-Type: application/json',
          '',
          fileContent,
          '--foo_bar_baz--'
        ].join('\r\n')
      });
      
      if (response.ok) {
        const result = await response.json();
        showStatus(`Character backed up to Google Drive: ${filename}`);
      } else {
        throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
      }
      
    } catch (error) {
      console.error('Google Drive backup failed:', error);
      showStatus('Failed to backup to Google Drive');
    }
  };
  
  // List backups from Google Drive
  const listDriveBackups = async () => {
    if (!driveAccessToken) {
      const signedIn = await signInToDrive();
      if (!signedIn) return [];
    }
    
    try {
      const response = await fetch(
        `https://www.googleapis.com/drive/v3/files?q=parents in 'appDataFolder' and name contains 'backup'&fields=files(id,name,createdTime,modifiedTime)`,
        {
          headers: {
            'Authorization': `Bearer ${driveAccessToken}`
          }
        }
      );
      
      if (response.ok) {
        const result = await response.json();
        return result.files || [];
      } else {
        throw new Error(`Failed to list backups: ${response.status}`);
      }
    } catch (error) {
      console.error('Failed to list Google Drive backups:', error);
      showStatus('Failed to load Google Drive backups');
      return [];
    }
  };
  
  // Download backup from Google Drive
  const restoreFromDrive = async (fileId) => {
    if (!driveAccessToken) {
      showStatus('Not connected to Google Drive');
      return;
    }
    
    try {
      const response = await fetch(`https://www.googleapis.com/drive/v3/files/${fileId}?alt=media`, {
        headers: {
          'Authorization': `Bearer ${driveAccessToken}`
        }
      });
      
      if (response.ok) {
        const backupData = await response.json();
        
        // Import the backup data
        setPlayerStats(backupData.playerStats || playerStats);
        setInventory(backupData.inventory || []);
        setVaults(backupData.vaults || []);
        // [FIX NOV 26, 2025]: Ensure all currency denominations exist
        setCurrencies({
          cp: 0, sp: 0, ep: 0, gp: 0, pp: 0,
          ...(backupData.currencies || {})
        });
        setSettings(backupData.settings || settings);

        showStatus('Character restored from Google Drive!');
      } else {
        throw new Error(`Download failed: ${response.status}`);
      }
    } catch (error) {
      console.error('Failed to restore from Google Drive:', error);
      showStatus('Failed to restore from Google Drive');
    }
  };

  // ========================================
  // PREMIUM ACCESS CHECK
  // ========================================
  // [CLEANED MAR 2026]: Removed old mock premium system (trial, subscription, getPremiumTimeRemaining,
  // startFreeTrial, activatePremiumSubscription, showPremiumUpgradeModal, uploadCustomImage).
  // All premium is now handled by Stripe → stripe-webhook → Firebase → AuthContext.isPremium.

  // [v1.1]: hasPremiumAccess() now delegates to usePremiumAccess hook
  // [WHY]: Centralized in src/hooks/usePremiumAccess.js — used by PremiumGate, GridComponent, etc.
  const hasPremiumAccess = () => hasPremium;

  // [ROSTER GALLERY 2026-05-10]: Archive / unarchive / rename actions for the
  // new Roster tab's ⋮ menu. Pure helpers from src/utils/rosterActions.js
  // keep production + tests on one impl (Rule 22). systemBus + logActivity
  // per Rules 4 + 13.
  const archiveCharacter = useCallback((characterId) => {
    const char = savedCharacters.find(c => c.id === characterId);
    if (!char) return;
    setSavedCharacters(prev => archiveById(prev, characterId));
    // If archiving the active character, fall back to most-recent non-archived
    if (playerStats?.id === characterId) {
      const remaining = savedCharacters.filter(c => c.id !== characterId && !c.archived);
      if (remaining.length > 0) {
        const fallback = [...remaining].sort((a, b) =>
          new Date(b.timestamp || 0) - new Date(a.timestamp || 0)
        )[0];
        loadCharacter(fallback.id);
      }
    }
    systemBus.emit(EVENTS.CHARACTER_ARCHIVED, { characterId, name: char.name });
    showStatus(`Archived "${char.name}".`);
    logActivity(`Archived character: ${char.name}`, 'character', { characterId });
  }, [savedCharacters, setSavedCharacters, playerStats?.id, loadCharacter, showStatus, logActivity]);

  const unarchiveCharacter = useCallback((characterId) => {
    const char = savedCharacters.find(c => c.id === characterId);
    if (!char) return;
    setSavedCharacters(prev => unarchiveById(prev, characterId));
    systemBus.emit(EVENTS.CHARACTER_UNARCHIVED, { characterId, name: char.name });
    showStatus(`Unarchived "${char.name}".`);
    logActivity(`Unarchived character: ${char.name}`, 'character', { characterId });
  }, [savedCharacters, setSavedCharacters, showStatus, logActivity]);

  const renameCharacter = useCallback((characterId, newName) => {
    const char = savedCharacters.find(c => c.id === characterId);
    if (!char) return;
    const oldName = char.name;
    setSavedCharacters(prev => renameById(prev, characterId, newName));
    systemBus.emit(EVENTS.CHARACTER_RENAMED, { characterId, oldName, newName });
    showStatus(`Renamed to "${newName}".`);
    logActivity(`Renamed character: "${oldName}" → "${newName}"`, 'character', { characterId });
  }, [savedCharacters, setSavedCharacters, showStatus, logActivity]);

  const duplicateCharacter = (characterId) => {
    const character = savedCharacters.find(char => char.id === characterId);
    if (!character) {
      showStatus('Character not found!');
      return;
    }

    const duplicatedCharacter = {
      ...character,
      id: Date.now().toString(),
      name: `${character.name} (Copy)`,
      timestamp: new Date().toISOString()
    };

    setSavedCharacters(prev => [...prev, duplicatedCharacter].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)));
    showStatus(`Character "${duplicatedCharacter.name}" created!`);
  };

  // ========================================
  // INVENTORY SORTING AND FILTERING
  // ========================================
  // [REMOVED]: getSortedAndFilteredInventory() function moved to useMemo above (lines 516-566) for performance

  // ========================================
  // CONTAINER EXPANSION FUNCTIONALITY
  // ========================================

  const toggleContainerExpansion = (containerId) => {
    setExpandedContainers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(containerId)) {
        newSet.delete(containerId);
      } else {
        newSet.add(containerId);
      }
      return newSet;
    });
  };
  
  // ========================================
  // UI COMPONENTS
  // ========================================
  
  
  
  

  
  
  
  
  
  
  // ========================================
  // MAIN RENDER
  // ========================================
  
  // [PERFORMANCE FIX CRITICAL]: Show loading screen while game data loads
  // This prevents the app from rendering with null data and blocking the main thread
  if (!gameDataLoaded) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
        <div className="text-center">
          <div className="mb-4">
            <Package size={64} className="text-amber-400 mx-auto animate-pulse" />
          </div>
          <h1 className="text-2xl font-bold mb-2 bg-gradient-to-r from-amber-400 to-orange-500 bg-clip-text text-transparent">
            Tabletop Inventory Manager
          </h1>
          <p className="text-gray-400 mb-4">Loading game data...</p>
          <div className="w-64 h-2 bg-gray-700 rounded-full mx-auto overflow-hidden">
            <div className="h-full bg-gradient-to-r from-amber-500 to-orange-500 rounded-full animate-pulse"></div>
          </div>
          <p className="text-xs text-gray-500 mt-4">
            Initializing D&D 5e SRD data, items, spells, and game rules
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white p-2 sm:p-4" style={{ paddingTop: 'max(env(safe-area-inset-top, 0px), 8px)' }}>
      {/* [UX-3 - NOV 17, 2025]: Restored QuickActionsBar functionality
           [USER REPORT]: "enable/disable the quick actions menu does not enable or disable it" (Line 21)
           [ROOT CAUSE]: QuickActionsBar was archived with comment "replaced by floatable/dockable quick menu button"
                         but the replacement was never implemented, leaving a non-functional toggle in settings
           [FIX]: Uncommented existing QuickActionsBar code which properly checks settings.showQuickActions
      */}
      {/* [FIX JAN 12, 2026]: Consolidated QuickActionsBar - was duplicate at line 12907 */}
      {/* [PROPS ADDED]: inventory, setInspirationPrompt - required for effectCalculator and inspiration prompts */}
      {/* [APR 2026 RE-ADDRESS]: QuickActionsBar is gameplay UI (rolls, saves,
          checks). DEFAULT + SIMPLE only — JUST_INVENTORY's use case is purely
          "drag items around" so there's no roll surface to anchor to. */}
      {settings.showQuickActions && settings.displayMode !== DISPLAY_MODES.JUST_INVENTORY && (
        <QuickActionsBar
          character={playerStats}
          setPlayerStats={setPlayerStats}
          inventory={inventory}
          settings={settings}
          showStatus={showStatus}
          addToActivityLog={logActivity}
          activeQuickBonuses={activeQuickBonuses}
          isDemo={false}
          position={settings.quickActionsPosition}
          savedPosition={settings.quickActionsTabPosition}
          onPositionChange={(newPos) => {
            setSettings(prev => ({
              ...prev,
              quickActionsTabPosition: newPos
            }));
          }}
          setInspirationPrompt={setInspirationPrompt}
        />
      )}

      {draggedItem && dragPosition && (
        <div
          className="fixed pointer-events-none bg-amber-700 border-2 border-amber-900 rounded opacity-75 flex items-center justify-center text-white shadow-2xl"
          style={{
            zIndex: Z.DRAG_GHOST,
            left: dragPosition.x - (draggedItem.size[0] * 24),
            top: dragPosition.y - (draggedItem.size[1] * 24),
            width: `${draggedItem.size[0] * 48}px`,
            height: `${draggedItem.size[1] * 48}px`,
            pointerEvents: 'none',
            userSelect: 'none'
          }}
        >
          <div className="text-center pointer-events-none">
            <div className="text-2xl">{draggedItem.icon}</div>
            <div className="text-[10px] font-bold">{draggedItem.name}</div>
          </div>
        </div>
      )}
      
      {/* Header - Redesigned Compact Menu */}
      <div className="bg-gray-900 border-b border-gray-700 sticky top-0" style={{ zIndex: Z.NAV_BAR }}>
        <div className="max-w-7xl mx-auto px-4 py-2">
          {/* [UI-MOBILE-HEADER-OVERFLOW 2026-07-04]: flex-wrap — the action bar always
              renders ~8 fixed-width buttons; without wrap the row forced 529px of
              document width at a 390px viewport (Sign In/Tours/Settings off-screen,
              app pannable sideways). Wrapping stacks the action bar under the
              character info on phones instead of overflowing. */}
          <div className="flex flex-wrap items-center justify-between gap-y-1">
            {/* Left: Logo, Character Info & Autosave Status */}
            <div className="flex items-center gap-3 min-w-0">
              {/* Logo */}
              <div className="flex items-center gap-2">
                <Package size={24} className="text-amber-400 hidden sm:block" />
                <h1 className="text-lg font-bold bg-gradient-to-r from-amber-400 to-orange-500 bg-clip-text text-transparent hidden lg:block">
                  Tabletop Inventory Manager
                </h1>
                <span className="text-xs px-1.5 py-0.5 bg-purple-700 text-white rounded hidden lg:block">
                  v{APP_VERSION}
                </span>
              </div>
              
              {/* Character Info */}
              {playerStats.name && (
                <div className="flex items-center gap-2 text-sm border-l border-gray-700 pl-3 min-w-0">
                  <span className="font-semibold text-white truncate max-w-[40vw]" title={playerStats.name}>{playerStats.name}</span>
                  <span className="text-xs text-gray-400">
                    Lv{playerStats.level} {primaryClass ? primaryClass.charAt(0).toUpperCase() + primaryClass.slice(1) : 'Adventurer'}
                  </span>
                </div>
              )}
              
              {/* [BUG FIX 2025-10-10]: Autosave indicator removed from header - now floating badge */}
            </div>
            
            {/* Right: Compact Action Bar */}
            {/* [UI-MOBILE-HEADER-OVERFLOW 2026-07-04]: wrap + right-align so the bar
                can break onto its own line (and further) instead of widening the page. */}
            <div className="flex flex-wrap items-center justify-end gap-1">
              {/* Character Management Group */}
              <div className="flex items-center bg-gray-800 rounded-lg p-1 gap-1">
                {/* New Character */}
                {/* [A37 FIX 2026-05-20]: Added visible desktop label ("New").
                    Pre-fix this was an unlabeled green Plus icon — the
                    "+ button garbage" the user called out on 2026-05-10.
                    Mobile keeps icon-only to preserve cramped header space;
                    tooltip + title attribute still serve touch users. */}
                <button
                  onClick={() => {
                    // [2026-05-11]: Non-destructive — opens the Wizard. Current character is autosaved
                    // and remains in savedCharacters. No free-tier limit (unlimited per ungate refactor).
                    setShowCharacterWizard(true);
                  }}
                  className="px-2 py-2 hover:bg-gray-700 rounded transition-colors group relative inline-flex items-center gap-1.5"
                  title="New Character"
                >
                  <Plus size={18} className="text-green-400" />
                  <span className="hidden md:inline text-xs font-medium text-gray-200 whitespace-nowrap">New</span>
                  <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap" style={{ zIndex: Z.DROPDOWN }}>
                    New Character
                  </span>
                </button>
                
                {/* Quick Save */}
                <button
                  onClick={() => saveCharacter()}
                  className="p-2 hover:bg-gray-700 rounded transition-colors group relative"
                  title="Quick Save (Ctrl+S)"
                >
                  <Save size={18} className="text-blue-400" />
                  <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap" style={{ zIndex: Z.DROPDOWN }}>
                    Quick Save
                  </span>
                </button>
                <SaveIndicator
                  lastSavedAt={lastAutosave?.getTime?.() ?? null}
                  isSaving={isSaving}
                  hasUnsaved={hasUnsavedChanges}
                />
                
                {/* [2026-05-11]: Replaces the legacy Load Character dropdown.
                    Opens the Roster as a slide-out drawer. */}
                {/* [A10/A37 FIX 2026-05-20]: Added visible desktop label
                    ("Manage"). Pre-fix this was an unlabeled amber Users icon
                    — the character manager box the user couldn't find
                    (5/11: *"I cannot find the character manager box"*).
                    Mobile keeps icon-only; tooltip serves touch users. */}
                <button
                  onClick={() => setRosterDrawerOpen(true)}
                  className="px-2 py-2 hover:bg-gray-700 rounded transition-colors group relative inline-flex items-center gap-1.5"
                  title="Manage Characters"
                >
                  <Users size={18} className="text-amber-400" />
                  <span className="hidden md:inline text-xs font-medium text-gray-200 whitespace-nowrap">Manage</span>
                  <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap" style={{ zIndex: Z.DROPDOWN }}>
                    Manage Characters
                  </span>
                </button>
              </div>
              
              {/* Import/Export Group */}
              <div className="flex items-center bg-gray-800 rounded-lg p-1 gap-1">
                {/* Export Dropdown — hover (desktop) + tap (mobile) */}
                <div className="relative group" data-header-dropdown="export">
                  <button
                    className="p-2 hover:bg-gray-700 rounded transition-colors"
                    title="Export"
                    onClick={() => setHeaderDropdownOpen(prev => prev === 'export' ? null : 'export')}
                  >
                    <Upload size={18} className="text-green-400 rotate-180" />
                  </button>
                  <div className={`absolute top-full right-0 mt-1 w-48 bg-gray-800 rounded-lg shadow-xl transition-all duration-200 border border-gray-700 ${headerDropdownOpen === 'export' ? 'opacity-100 visible' : 'opacity-0 invisible'} group-hover:opacity-100 group-hover:visible`} style={{ zIndex: Z.DROPDOWN }}>
                    <div className="p-1">
                      <button
                        onClick={() => exportCharacter(null, 'json')}
                        className="w-full text-left px-3 py-2 hover:bg-gray-700 rounded text-sm transition-colors flex items-center gap-2"
                      >
                        <span>📋</span> JSON (Full Data)
                      </button>
                      <button
                        onClick={() => exportCharacter(null, 'txt')}
                        className="w-full text-left px-3 py-2 hover:bg-gray-700 rounded text-sm transition-colors flex items-center gap-2"
                      >
                        <span>📝</span> Text Sheet
                      </button>
                      <button
                        onClick={() => exportCharacter(null, 'html')}
                        className="w-full text-left px-3 py-2 hover:bg-gray-700 rounded text-sm transition-colors flex items-center gap-2"
                      >
                        <span>🌐</span> HTML (Pretty)
                      </button>
                      <button
                        onClick={() => exportCharacter(null, 'csv')}
                        className="w-full text-left px-3 py-2 hover:bg-gray-700 rounded text-sm transition-colors flex items-center gap-2"
                      >
                        <span>📊</span> CSV (Excel)
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* Import */}
                <label className="p-2 hover:bg-gray-700 rounded transition-colors cursor-pointer group relative">
                  <Upload size={18} className="text-yellow-400" />
                  <input 
                    type="file"
                    accept=".json"
                    onChange={importInventory}
                    className="hidden"
                  />
                  <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap" style={{ zIndex: Z.DROPDOWN }}>
                    Import
                  </span>
                </label>
                
                {/* [FIX APR 2026 — AUDIT M-6]: Hide Google Drive UI until credentials configured */}
                {settings?.enableGoogleDrive && (
                <button
                  onClick={() => backupToDrive()}
                  className="p-2 hover:bg-gray-700 rounded transition-colors group relative"
                  title="Backup to Google Drive"
                >
                  <svg className="w-[18px] h-[18px] text-blue-400" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M7.71 3.5L1.15 15l3.43 6h11.55l3.43-6M9.73 15h8.54L15.43 21H6.89m2.28-18H14.84L21.5 15h-5.07"/>
                  </svg>
                  <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap" style={{ zIndex: Z.DROPDOWN }}>
                    Google Drive
                  </span>
                </button>
                )}
              </div>
              
              {/* [v1.0]: Auth / Account button */}
              {isAuthenticated ? (
                <button
                  onClick={() => setActiveTab('settings')}
                  className="p-2 hover:bg-gray-800 rounded transition-colors group relative"
                  title={currentUser?.displayName || 'Account'}
                >
                  <div className="w-[18px] h-[18px] rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center text-[10px] font-bold text-white">
                    {currentUser?.displayName?.[0]?.toUpperCase() || currentUser?.email?.[0]?.toUpperCase() || '?'}
                  </div>
                  <span className="absolute -bottom-8 right-0 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap" style={{ zIndex: Z.DROPDOWN }}>
                    Account
                  </span>
                </button>
              ) : (
                <button
                  onClick={() => setShowAuthModal(true)}
                  className="px-2 py-1 text-xs bg-blue-600/80 hover:bg-blue-500 text-white rounded transition-colors"
                  title="Sign In"
                >
                  Sign In
                </button>
              )}

              {/* [TUTORIAL-1]: Browse / Replay Tours — "?" help button */}
              <button
                onClick={() => setShowTutorialHelpMenu(true)}
                className="p-2 hover:bg-gray-800 rounded transition-colors group relative"
                title="Browse Tours"
                aria-label="Browse Guided Tours"
              >
                <HelpCircle size={18} className="text-gray-400 hover:text-purple-300 transition-colors" />
                <span className="absolute -bottom-8 right-0 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap" style={{ zIndex: Z.DROPDOWN }}>
                  Browse Tours
                </span>
              </button>

              {/* Settings */}
              <button
                onClick={() => setActiveTab(activeTab === 'settings' ? 'inventory' : 'settings')}
                className="p-2 hover:bg-gray-800 rounded transition-colors ml-2 group relative"
                title="Settings"
              >
                <Settings size={18} className={`transition-colors ${activeTab === 'settings' ? 'text-white' : 'text-gray-400 hover:text-white'}`} />
                <span className="absolute -bottom-8 right-0 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap" style={{ zIndex: Z.DROPDOWN }}>
                  Settings
                </span>
              </button>
              
              {/* DEBUG Nuclear Reset - Only in dev mode */}
              {settings.showDebugTools && (
                <button
                  onClick={async () => {
                    if (await confirm({ title: 'NUCLEAR RESET', message: 'This will clear ALL data. Cannot be undone!', confirmText: 'Reset Everything', confirmColor: 'red' })) {
                      try {
                        localStorage.clear();
                        sessionStorage.clear();
                        if ('caches' in window) {
                          caches.keys().then(names => {
                            names.forEach(name => caches.delete(name));
                          });
                        }
                        showStatus('🧨 Nuclear reset complete - reloading...');
                        setTimeout(() => {
                          window.location.href = window.location.href.split('#')[0];
                        }, 200);
                      } catch (error) {
                        console.error('Nuclear reset error:', error);
                      }
                    }
                  }}
                  className="p-2 hover:bg-red-900 rounded transition-colors ml-2"
                  title="DEBUG: Nuclear Reset"
                >
                  <span className="text-red-500">🧨</span>
                </button>
              )}
            </div>
          </div>
        </div>
        {/* [UI-COMBAT-STATUS-STRIP — 2026-07-11]: persistent HP/AC/conditions
            row INSIDE the sticky header so it survives on all 21 tabs (fleet
            ui-qol #2: player tabs away mid-fight and goes blind). The
            component null-renders until a character is loaded. */}
        <CombatStatusStrip onNavigate={() => setActiveTab('character')} />
      </div>

      {/* [REMOVED 2026-05-09 #104]: Floating save badge replaced by inline SaveIndicator in header */}

      {/* Mobile Navigation Component */}
      {/* [PHASE 4B - MIGRATED]: MobileNavigation now uses context hooks for vaults and displayMode */}
      <div className="max-w-7xl mx-auto px-4">
        <MobileNavigation
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />
      </div>
      
      {/* Main Content Area */}
      <div 
        className="max-w-7xl mx-auto pb-20 md:pb-0"
        onTouchStart={handleSwipeStart}
        onTouchMove={handleSwipeMove}
        onTouchEnd={handleSwipeEnd}
      >
        {activeTab === 'character' && !openContainer && !showVaultModal && (
          <div className="bg-gray-800 p-6 rounded-lg">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold">Character Sheet</h2>
              <button
                onClick={() => {
                  setShowCharacterWizard(true);
                }}
                className="px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white rounded-lg font-medium transition-all shadow-lg shadow-purple-900/50 hover:shadow-purple-900/70 hover:scale-105 flex items-center gap-2"
              >
                <Sparkles size={16} />
                New Character
              </button>
            </div>

            {/* [CHARACTER IDENTITY HEADER]: Portrait, Name, Level, XP Progress */}
            {/* [PURPOSE]: Visual anchor showing character identity at a glance */}
            {/* [WHY]: Players need immediate recognition of who they're playing */}
            <div data-tutorial="character-identity">
            <Suspense fallback={
              <div className="bg-gray-900 p-4 rounded-lg mb-6 animate-pulse">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-gray-700 rounded-full"></div>
                  <div className="flex-1">
                    <div className="h-6 bg-gray-700 rounded w-48 mb-2"></div>
                    <div className="h-4 bg-gray-700 rounded w-32"></div>
                  </div>
                </div>
              </div>
            }>
              {/* [PHASE 4B - MIGRATED]: CharacterIdentityHeader now uses context hooks directly */}
              <CharacterIdentityHeader />
            </Suspense>
            </div>

            {/* [VITAL STATS CARD]: Ability scores, HP, AC, Death Saves, Inspiration */}
            {/* [PURPOSE]: The mechanical heart - stats players reference constantly during gameplay */}
            {/* [WHY]: Replaces duplicate Inspiration tracker, StatsPanel, and scattered stat displays */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-4">
                <Suspense fallback={
                  <div className="bg-gray-800 p-4 rounded-lg animate-pulse">
                    <div className="h-6 bg-gray-700 rounded w-32 mb-4"></div>
                    <div className="grid grid-cols-3 gap-3 mb-4">
                      {[...Array(6)].map((_, i) => (
                        <div key={i} className="h-24 bg-gray-700 rounded"></div>
                      ))}
                    </div>
                    <div className="h-32 bg-gray-700 rounded"></div>
                  </div>
                }>
                  {/* [PHASE 4B - MIGRATED]: VitalStatsCard now uses context hooks directly */}
                  {/* [PHASE 4.3]: Wrapped with ErrorBoundary for graceful error handling */}
                  <ErrorBoundary
                    componentName="VitalStatsCard"
                    logActivity={logActivity}
                    fallback={<ComponentErrorFallback componentName="VitalStatsCard" />}
                  >
                    <VitalStatsCard />
                  </ErrorBoundary>
                </Suspense>

                {/* [NEW - Week 2, Days 8-9]: Active Effects from Party Members */}
                {/* [PURPOSE]: Display spell effects cast on this character by party members */}
                {/* [WHY]: Players need to see buffs/debuffs from allies in real-time */}
                {partyId && activeEffects && activeEffects.length > 0 && (
                  <div className="bg-indigo-900/30 border border-indigo-500 rounded-lg p-4">
                    <h3 className="text-sm font-bold text-indigo-300 mb-3 flex items-center">
                      ✨ Active Effects from Party
                    </h3>
                    <div className="space-y-2">
                      {activeEffects.map((effect) => (
                        <div
                          key={effect.id}
                          className="bg-gray-800 p-3 rounded flex justify-between items-start"
                        >
                          <div className="flex-1">
                            <div className="font-bold text-sm text-indigo-200">
                              {effect.effectName}
                            </div>
                            <div className="text-xs text-gray-400 mt-1">
                              Cast by {effect.casterName}
                              {effect.duration > 0 && ` • ${effect.duration} rounds remaining`}
                              {effect.expiresAt && (
                                <span className="ml-1">
                                  • Expires {new Date(effect.expiresAt).toLocaleTimeString()}
                                </span>
                              )}
                            </div>
                            {/* [FIX NOV 4 SESSION 7]: Add null coalescing to Object operations */}
                            {effect.data && Object.keys(effect.data).length > 0 && (
                              <div className="text-xs text-indigo-300 mt-1">
                                {Object.entries(effect.data || {}).map(([key, value]) => (
                                  <span key={key} className="mr-2">
                                    {key}: {JSON.stringify(value)}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                          {removeEffectFromAlly && (
                            <button
                              onClick={() => {
                                // Remove the effect - target is current character
                                removeEffectFromAlly(playerStats.id, effect.id);
                                showStatus(`Dismissed ${effect.effectName}`, 2000);
                              }}
                              className="ml-3 text-red-400 hover:text-red-300 text-sm flex-shrink-0"
                              title="Dismiss effect"
                            >
                              Dismiss
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                    <div className="mt-3 text-xs text-gray-400">
                      💡 These effects are being actively maintained by your party members
                    </div>
                  </div>
                )}

                {/* [MULTICLASS MANAGER]: Intelligent multiclass system with prerequisites */}
                {/* [PURPOSE]: Manage multiple classes, validate prerequisites, calculate spellcaster level */}
                {/* [WHY]: Replaces inline multiclass editor with proper D&D 5e rules */}
                {/* [P2.16 APR 2026]: Heavy panel — DEFAULT only. SIMPLE keeps the
                    streamlined character sheet (vitals + class resources). */}
                {/* [A9 — 2026-05-25]: SIMPLE mode now gets a slim compact-mode
                    variant (chip strip + "+ Level" buttons per class) so
                    multiclass characters can still level secondary classes
                    without DEFAULT-mode clutter. Single-class characters in
                    SIMPLE mode get null from MulticlassManager — no clutter,
                    they level via LevelUpWizard / QuickActions. */}
                <Suspense fallback={
                  <div className="bg-gray-800 p-4 rounded-lg animate-pulse">
                    <div className="h-6 bg-gray-700 rounded w-32 mb-3"></div>
                    <div className="h-20 bg-gray-700 rounded"></div>
                  </div>
                }>
                  {/* [PHASE 4B - MIGRATED]: MulticlassManager now uses context hooks */}
                  <MulticlassManager compactMode={settings.displayMode !== DISPLAY_MODES.DEFAULT} />
                </Suspense>

              </div>

              <div className="space-y-4">
                {/* [MINOR UI/UX FIX #2 - NOV 22, 2025]: Hide starting gold section after wizard completed */}
                {/* [PURPOSE]: Remove clutter from Character tab for established characters */}
                {/* [WHY]: This section is for initial character setup only, not ongoing use */}
                {/* [FIX]: Hide section once gold has been rolled (wizard complete) */}
                {playerStats.level <= 1 && !playerStats.startingGoldRolled && (
                <div className="bg-gray-700 p-4 rounded">
                  <h3 className="font-bold mb-3">Starting Gold</h3>
                  <p className="text-sm text-gray-400 mb-3">
                    Roll for starting gold based on class, or set manually
                  </p>
                  
                  {!playerStats.startingGoldRolled ? (
                    <button
                      onClick={() => {
                        const classStartingGold = {
                          fighter: [5, 4, 10], // 5d4 • 10
                          wizard: [4, 4, 10],  // 4d4 • 10
                          cleric: [5, 4, 10],  // 5d4 • 10
                          rogue: [4, 4, 10],   // 4d4 • 10
                          ranger: [5, 4, 10],  // 5d4 • 10
                          barbarian: [2, 4, 10], // 2d4 • 10
                          bard: [5, 4, 10],    // 5d4 • 10
                          druid: [2, 4, 10],   // 2d4 • 10
                          monk: [5, 4, 1],     // 5d4
                          paladin: [5, 4, 10], // 5d4 • 10
                          sorcerer: [3, 4, 10], // 3d4 • 10
                          warlock: [4, 4, 10]  // 4d4 • 10
                        };
                        
                        const roll = classStartingGold[primaryClass] || [3, 6, 10];
                        let total = 0;
                        for (let i = 0; i < roll[0]; i++) {
                          total += Math.floor(Math.random() * roll[1]) + 1;
                        }
                        total *= roll[2];
                        
                        const rollString = roll[2] === 1 ? `${roll[0]}d${roll[1]}` : `${roll[0]}d${roll[1]} • ${roll[2]}`;

                        const goldAmount = Math.round(total);
                        const oldGold = currencies.gp || 0;

                        setCurrencies(prev => ({ ...prev, gp: goldAmount }));

                        // Emit CURRENCY_CHANGED event for starting gold
                        systemBus.emit(EVENTS.CURRENCY_CHANGED, {
                          type: 'gp',
                          oldAmount: oldGold,
                          newAmount: goldAmount,
                          change: goldAmount - oldGold,
                          source: 'starting_gold_roll'
                        });

                        setPlayerStats(prev => ({
                          ...prev,
                          startingGoldRolled: true,
                          startingGoldResult: { dice: rollString, total }
                        }));
                      }}
                      disabled={!primaryClass}
                      className="w-full bg-amber-700 hover:bg-amber-600 disabled:bg-gray-600 disabled:cursor-not-allowed p-2 rounded transition-colors"
                    >
                      Roll Starting Gold {primaryClass && `(${
                        {
                          fighter: '5d4 • 10',
                          wizard: '4d4 • 10',
                          cleric: '5d4 • 10',
                          rogue: '4d4 • 10',
                          ranger: '5d4 • 10',
                          barbarian: '2d4 • 10',
                          bard: '5d4 • 10',
                          druid: '2d4 • 10',
                          monk: '5d4',
                          paladin: '5d4 • 10',
                          sorcerer: '3d4 • 10',
                          warlock: '4d4 • 10'
                        }[primaryClass] || '3d6 • 10'
                      })`}
                    </button>
                  ) : (
                    <div className="bg-green-900/30 border border-green-600/30 p-3 rounded">
                      <div className="flex justify-between items-center">
                        <span className="text-green-300 font-semibold">Starting Gold Rolled:</span>
                        <span className="text-green-400">{playerStats.startingGoldResult?.total}gp</span>
                      </div>
                      <div className="text-xs text-green-400/70 mt-1">
                        Rolled {playerStats.startingGoldResult?.dice} = {playerStats.startingGoldResult?.total}gp
                      </div>
                    </div>
                  )}
                </div>
                )}

                <div className="bg-gray-700 p-4 rounded">
                  <h3 className="font-bold mb-3">{settings.displayMode === DISPLAY_MODES.JUST_INVENTORY ? 'Character Stats' : 'Quick Stats'}</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Total Weight:</span>
                      <span>{totalWeight.toFixed(1)} lbs</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Encumbrance:</span>
                      <span className={
                        encumbranceLevel === 'light' ? 'text-green-400' :
                        encumbranceLevel === 'medium' ? 'text-yellow-400' :
                        encumbranceLevel === 'heavy' ? 'text-orange-400' :
                        encumbranceLevel === 'encumbered' ? 'text-red-400' :
                        'text-gray-400'
                      }>{encumbranceLevel}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Total Items:</span>
                      <span>{inventory.length}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Gold:</span>
                      <span>{currencies.gp}gp</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
              {/* Strength Input for Just The Inventory mode */}
              {settings.displayMode === DISPLAY_MODES.JUST_INVENTORY && (
                <div className="mt-4 bg-gray-700 p-3 rounded">
                  <label className="block text-sm text-gray-300 mb-1">Strength (for carrying capacity)</label>
                  <input
                    type="number"
                    min="1"
                    max="30"
                    value={playerStats.strength || 10}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === '' || val === undefined) {
                        setPlayerStats(prev => ({
                          ...prev,
                          strength: ''
                          // [PHASE 4.4]: carryCapacity removed - auto-derived from strength
                        }));
                      } else {
                        const str = Math.max(1, Math.min(30, parseInt(val) || 10));
                        setPlayerStats(prev => ({
                          ...prev,
                          strength: str
                          // [PHASE 4.4]: carryCapacity removed - auto-derived from strength
                        }));
                      }
                    }}
                    onBlur={(e) => {
                      // On blur, ensure we have a valid value
                      if (!e.target.value || e.target.value === '') {
                        setPlayerStats(prev => ({
                          ...prev,
                          strength: 10
                          // [PHASE 4.4]: carryCapacity removed - auto-derived from strength
                        }));
                      }
                    }}
                    className="w-full p-2 bg-gray-600 text-white rounded"
                  />
                  <div className="text-xs text-gray-400 mt-1">
                    {/* [INTEGRATION-4 - NOV 15, 2025]: Use calculated carryCapacity (includes item effects) */}
                    Carry Capacity: {carryCapacity} lbs
                  </div>
                </div>
              )}

              {/* Stats Panel - Hidden in Simple AND Just The Inventory modes
                  [P2.16 APR 2026]: Heavy skills panel — DEFAULT only.
                  SIMPLE keeps VitalStatsCard for the at-a-glance summary; the
                  full StatsPanel (skill tree, saves grid, deeper breakdowns)
                  is hidden so SIMPLE feels distinctly lighter. */}
              {settings.displayMode === DISPLAY_MODES.DEFAULT && (
                <div>
                  {/* [PHASE 4B - PARTIAL MIGRATION]: StatsPanel now uses context hooks for state */}
                  {/* [NOTE]: Keeping function/calculated props until Phase 4B Step 6 (Function Migration) */}
                  <StatsPanel
                    totalWeight={totalWeight}
                    encumbranceLevel={encumbranceLevel}
                    getAvailableSpells={getAvailableSpells}
                    resetDailySpells={resetDailySpells}
                  />
                </div>
              )}
              </div>

            {/* [CLOSE GRID]: End of 2-column layout */}
            </div>

            {/* [CONDITIONS & SENSES]: Situational panels */}
            {/* [PURPOSE]: Combat and exploration tracking */}
            {/* [WHY]: Conditions affect gameplay, senses determine what you perceive */}
            {/* [P2.16 APR 2026]: Heavy situational tracking — DEFAULT only.
                SIMPLE players still see the active-conditions summary inside
                VitalStatsCard; the dedicated tracker grid is full-mode UI. */}
            {settings.displayMode === DISPLAY_MODES.DEFAULT && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                {/* Conditions Tracker */}
                <Suspense fallback={
                  <div className="bg-gray-800 p-4 rounded-lg animate-pulse">
                    <div className="h-6 bg-gray-700 rounded w-32 mb-3"></div>
                    <div className="h-24 bg-gray-700 rounded"></div>
                  </div>
                }>
                  {/* [PHASE 4B - MIGRATED]: ConditionsTracker now uses context hooks */}
                  <div data-tutorial="character-conditions">
                  <ConditionsTracker />
                  </div>
                </Suspense>

                {/* Senses & Movement Panel */}
                <Suspense fallback={
                  <div className="bg-gray-800 p-4 rounded-lg animate-pulse">
                    <div className="h-6 bg-gray-700 rounded w-32 mb-3"></div>
                    <div className="h-32 bg-gray-700 rounded"></div>
                  </div>
                }>
                  {/* [PHASE 4B - MIGRATED]: SensesMovementPanel now uses context hooks */}
                  <div data-tutorial="character-senses">
                  <SensesMovementPanel />
                  </div>
                </Suspense>
              </div>
            )}

            {/* [CLASS RESOURCES]: Superiority Dice, Ki Points, Rage, etc. */}
            {/* [PURPOSE]: Track and manage class-specific resources */}
            {/* [WHY]: Battle Masters need superiority dice, Monks need ki, etc. */}
            {/* [APR 2026 RE-ADDRESS]: Class resources are play-the-game UI →
                visible in DEFAULT + SIMPLE. Hidden in JUST_INVENTORY (drag-items
                use case has no need for ki/rage tracking). */}
            {settings.displayMode !== DISPLAY_MODES.JUST_INVENTORY && (
            <div className="mt-6" data-tutorial="character-features">
              {/* [PHASE 4B - MIGRATED]: ClassResourcesPanel now uses context hooks, keeps isDemo prop */}
              <ClassResourcesPanel
                isDemo={false}
              />
              {/* [FIX APR 2026 Task 44]: Warlock Pact Boon + Invocations picker.
                  Self-gates on Warlock level (returns null for non-Warlocks), so
                  safe to always mount. Rendered next to class resources since
                  that's the conceptual sibling (per-class progression choices). */}
              <WarlockPactPanel />
            </div>
            )}

            {/* [CHARACTER BASICS]: Race, Background, Appearance, Personality */}
            {/* [BUG FIX 2025-10-10]: Made collapsible by default in full mode */}
            {/* [WHY]: User reported "section not collapsed called character, that seems irrelevant in full screen mode" */}
            {/* [FIX]: Wrap in CollapsibleSection so it's not always visible, defaults to collapsed */}
            {/* [APR 2026 RE-ADDRESS]: Character Details is character-build UI →
                hidden in JUST_INVENTORY. SIMPLE keeps it (collapsed) so players
                can still consult their backstory/personality during play. */}
            {settings.displayMode !== DISPLAY_MODES.JUST_INVENTORY && (
            <div className="mt-6">
              <Suspense fallback={
                <div className="bg-gray-800 p-4 rounded-lg">
                  <div className="animate-pulse">Loading character details...</div>
                </div>
              }>
                <CollapsibleSection
                  title="Character Details"
                  icon={<User size={18} />}
                  storageKey="character-basics-main"
                  defaultCollapsed={settings.displayMode === DISPLAY_MODES.DEFAULT} // Collapsed by default in full mode
                  badge={null}
                >
                  {/* [PHASE 4B - MIGRATED]: CharacterBasicsPanel now uses context hooks */}
                  <CharacterBasicsPanel />
                </CollapsibleSection>
              </Suspense>
            </div>
            )}

            {/* [PARTY SYNC]: Party Panel (only shows when in a party) */}
            {/* [APR 2026 RE-ADDRESS]: Party panel is multi-user gameplay UI →
                hidden in JUST_INVENTORY (single-user drag-items focus). */}
            {partyId && settings.displayMode !== DISPLAY_MODES.JUST_INVENTORY && (
              <div className="mt-6">
                <Suspense fallback={
                  <div className="bg-gray-800 p-4 rounded-lg">
                    <div className="animate-pulse">Loading party panel...</div>
                  </div>
                }>
                  <PartyPanel
                    partyName={partyName}
                    partyMembers={partyMembers}
                    partyActivityLog={partyActivityLog}
                    isConnected={isConnected}
                    syncError={syncError}
                    currentCharacterId={playerStats.id}
                    onOpenSharedVault={() => setShowSharedVault(true)}
                    isDM={isDM}
                    onClaimDM={async (claim) => {
                      // [A33]: log DM claim/release as a party mechanical action (Rule 13)
                      const result = await claimDM(claim);
                      if (result !== false) {
                        const { message, type, details } = dmStatusMessage(claim);
                        logActivity(message, type, details);
                      }
                      return result;
                    }}
                    partyMetadata={partyMetadata}
                  />
                </Suspense>
              </div>
            )}
          </div>
        )}

        {activeTab === 'inventory' && !openContainer && !showVaultModal && (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Left Panel — pushed below grid on mobile */}
            <div className="space-y-4 order-2 lg:order-1">
              {/* Grid Configuration Panel */}
              <div className="bg-gray-800 p-4 rounded-lg border border-cyan-600">
                <button
                  onClick={() => setGridConfigCollapsed(!gridConfigCollapsed)}
                  className="w-full flex items-center justify-between text-lg font-bold text-cyan-300 mb-3 hover:text-amber-300 transition-colors"
                >
                  <span>📐 Grid Configuration</span>
                  <span className="text-amber-400">
                    {gridConfigCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </span>
                </button>

                {!gridConfigCollapsed && (
                <div className="space-y-3">
                  <label className="flex items-center text-sm text-gray-300">
                    <input 
                      type="checkbox"
                      checked={playerStats.useBagLimit}
                      onChange={(e) => {
                        setPlayerStats(prev => ({
                          ...prev,
                          useBagLimit: e.target.checked
                        }));
                        showStatus(e.target.checked ? 
                          "📐 Grid size limits enabled" : 
                          "♾️ Unlimited grid size enabled", 3000);
                      }}
                      className="mr-2"
                    />
                    Use Grid Size Limits
                  </label>
                
                  {playerStats.useBagLimit && (
                    <div className="space-y-3">
                      {/* Grid Mode Selection */}
                      <div className="bg-gray-700 p-3 rounded">
                        <label className="flex items-center text-sm text-gray-300 mb-2">
                          <input 
                            type="checkbox"
                            checked={settings.useCustomGrid}
                            onChange={(e) => {
                              e.stopPropagation();
                              const newValue = e.target.checked;
                              setSettings(prev => ({
                                ...prev,
                                useCustomGrid: newValue
                              }));
                              showStatus(newValue ? 
                                "📐 Custom grid size enabled" : 
                                "💪 Strength-based grid enabled", 3000);
                            }}
                            onTouchEnd={(e) => {
                              e.stopPropagation();
                            }}
                            className="mr-2"
                          />
                          Use Custom Grid Size
                        </label>
                        {!settings.useCustomGrid && (
                          <div className="text-xs text-cyan-400">
                            💪 Grid size based on Strength: {gridCols}•{gridRows}
                            <div className="text-gray-400 mt-1">
                              {/* [INTEGRATION-4 - NOV 15, 2025]: Show effective STR and carry capacity (includes item effects) */}
                              STR {effectiveStrength} = {carryCapacity} lbs capacity
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Custom Grid Controls - Only show when custom mode is enabled */}
                      {settings.useCustomGrid && (
                        <>
                          <label className="text-sm text-cyan-300 block">Custom Grid Dimensions</label>
                          <div className="flex gap-2 items-center">
                            <input
                              type="number"
                              min="5"
                              max="20"
                              value={playerStats.bagSize?.[0] ?? 10}
                              onChange={(e) => {
                                const val = e.target.value;
                                if (val === '' || val === undefined) {
                                  setPlayerStats(prev => ({
                                    ...prev,
                                    bagSize: ['', prev.bagSize?.[1] ?? 10]
                                  }));
                                } else {
                                  const newWidth = Math.max(5, Math.min(20, parseInt(val) || 10));
                                  setPlayerStats(prev => ({
                                    ...prev,
                                    bagSize: [newWidth, prev.bagSize?.[1] ?? 10]
                                  }));
                                  showStatus(`📏 Grid width set to ${newWidth}`, 2000);
                                }
                              }}
                              onBlur={(e) => {
                                if (!e.target.value || e.target.value === '') {
                                  setPlayerStats(prev => ({
                                    ...prev,
                                    bagSize: [10, prev.bagSize?.[1] ?? 10]
                                  }));
                                }
                              }}
                              className="w-16 p-2 bg-gray-700 text-white rounded text-center"
                              title="Grid width (5-20)"
                            />
                            <span className="text-gray-400">•</span>
                            <input
                              type="number"
                              min="5"
                              max="20"
                              value={playerStats.bagSize?.[1] ?? 10}
                              onChange={(e) => {
                                const val = e.target.value;
                                if (val === '' || val === undefined) {
                                  setPlayerStats(prev => ({
                                    ...prev,
                                    bagSize: [prev.bagSize?.[0] ?? 10, '']
                                  }));
                                } else {
                                  const newHeight = Math.max(5, Math.min(20, parseInt(val) || 10));
                                  setPlayerStats(prev => ({
                                    ...prev,
                                    bagSize: [prev.bagSize?.[0] ?? 10, newHeight]
                                  }));
                                  showStatus(`📏 Grid height set to ${newHeight}`, 2000);
                                }
                              }}
                              onBlur={(e) => {
                                if (!e.target.value || e.target.value === '') {
                                  setPlayerStats(prev => ({
                                    ...prev,
                                    bagSize: [prev.bagSize?.[0] ?? 10, 10]
                                  }));
                                }
                              }}
                              className="w-16 p-2 bg-gray-700 text-white rounded text-center"
                              title="Grid height (5-20)"
                            />
                            <div className="text-xs text-cyan-400 ml-2">
                              = {(playerStats.bagSize?.[0] ?? 10) * (playerStats.bagSize?.[1] ?? 10)} slots
                            </div>
                          </div>
                        </>
                      )}

                      <div className="text-xs text-gray-400 mt-1">
                        💡 Current grid: {gridCols}•{gridRows} = {gridCols * gridRows} slots
                      </div>
                    </div>
                  )}
                  
                  {!playerStats.useBagLimit && (
                    <div className="text-xs text-gray-400">
                      ♾️ Grid size is unlimited (20•20). Enable limits to customize dimensions.
                    </div>
                  )}
                </div>
                )}
              </div>

              {/* Collapsible Stats Panel + always-on weight summary
                  [P2.16 APR 2026]: Three-mode handling on the inventory tab —
                    - DEFAULT: full Character Stats header + StatsPanel skill grid + weight bar
                    - SIMPLE: weight bar only (the summary surface), no skill grid
                    - JUST_INVENTORY: nothing (weight is shown in Quick Stats text above) */}
              {settings.displayMode !== DISPLAY_MODES.JUST_INVENTORY && (
              <div className="bg-gray-800 p-4 rounded-lg">
                {settings.displayMode === DISPLAY_MODES.DEFAULT && (
                  <>
                    <button
                      onClick={() => setStatsCollapsed(!statsCollapsed)}
                      className="w-full flex items-center justify-between text-lg font-bold text-white mb-3 hover:text-amber-300 transition-colors"
                    >
                      <span>Character Stats</span>
                      <span className="text-amber-400">
                        {statsCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </span>
                    </button>

                    {/* [PHASE 4B - PARTIAL MIGRATION]: StatsPanel now uses context hooks for state */}
                    {/* [NOTE]: Keeping function/calculated props until Phase 4B Step 6 (Function Migration) */}
                    {!statsCollapsed && (
                      <StatsPanel
                        totalWeight={totalWeight}
                        encumbranceLevel={encumbranceLevel}
                        getAvailableSpells={getAvailableSpells}
                        resetDailySpells={resetDailySpells}
                      />
                    )}
                  </>
                )}

                {/* Always show weight summary in DEFAULT and SIMPLE — the
                    "summary" surface SIMPLE mode is meant to keep. */}
                <div
                  className={settings.displayMode === DISPLAY_MODES.DEFAULT && !statsCollapsed
                    ? "mt-4 pt-4 border-t border-gray-700"
                    : ""}
                  data-tutorial="inventory-weight"
                >
                  <div className="text-sm text-gray-300 mb-2">
                    <span className="font-medium">Weight:</span> {totalWeight.toFixed(1)} / {carryCapacity} lbs ({encumbranceLevel})
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-2">
                    <div
                      className={`h-full rounded-full transition-all ${
                        encumbranceLevel === 'light' ? 'bg-green-600' :
                        encumbranceLevel === 'medium' ? 'bg-yellow-600' :
                        encumbranceLevel === 'heavy' ? 'bg-orange-600' :
                        encumbranceLevel === 'encumbered' ? 'bg-red-600' :
                        'bg-gray-600'
                      }`}
                      style={{ width: `${Math.min((totalWeight / carryCapacity) * 100, 100)}%` }}
                    />
                  </div>
                </div>
              </div>
              )}

              {/* [PHASE 4B - MIGRATED]: CurrencyPanel now uses context hooks */}
              <div data-tutorial="inventory-currency">
                <CurrencyPanel />
              </div>
              {/* Equipment Panel - Hidden in Simple AND Just The Inventory modes
                  [P2.16 APR 2026]: Equipment is heavy UI (slots, attack/AC
                  breakdown, attune slots). DEFAULT only. SIMPLE keeps the
                  inventory tab focused on the grid. */}
              {settings.displayMode === DISPLAY_MODES.DEFAULT && (
              <div className="bg-gray-800 p-4 rounded-lg" data-tutorial="inventory-equip-area">
                <button
                  onClick={() => setEquipmentCollapsed(!equipmentCollapsed)}
                  className="w-full flex items-center justify-between text-lg font-bold text-white mb-3 hover:text-amber-300 transition-colors"
                >
                  <span>Equipment & Combat</span>
                  <span className="text-amber-400">
                    {equipmentCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </span>
                </button>

{/* [PHASE 4B STEP 6 - 2025-10-25]: EquipmentPanel now uses useInventory hook */}
                {/* [PHASE 4.3]: Wrapped with ErrorBoundary for equipment error handling */}
                {!equipmentCollapsed && (
                  <Suspense fallback={<div className="text-center py-4 text-gray-400">Loading Equipment...</div>}>
                    <ErrorBoundary
                      componentName="EquipmentPanel"
                      logActivity={logActivity}
                      fallback={<ComponentErrorFallback componentName="Equipment Panel" />}
                    >
                      <EquipmentPanel
                        onEditItem={(item) => setEditingItem(item)}
                        compactMode={true}
                      />
                    </ErrorBoundary>
                  </Suspense>
                )}
              </div>
              )}

              {/* [PHASE 4C - NOV 1, 2025]: Quick Bonuses Panel - Extracted to QuickBonusesPanel.js (reduced from 367 inline lines) */}
              {/* [P2.16 APR 2026]: Quick Bonuses is DM-power-user UI — DEFAULT
                  only. SIMPLE keeps the inventory tab focused; players who
                  need ad-hoc modifiers can switch back to DEFAULT. */}
              {settings.displayMode === DISPLAY_MODES.DEFAULT && (
                <Suspense fallback={<div className="mb-4 bg-gray-700 p-4 rounded-lg text-center text-gray-400">Loading Quick Bonuses...</div>}>
                  <QuickBonusesPanel
                    showStatus={showStatus}
                    logActivity={logActivity}
                  />
                </Suspense>
              )}
              
            </div>
            
            {/* Main Grid — comes first on mobile */}
            <div className="lg:col-span-3 order-1 lg:order-2">
              {/* [2026-05-25 refactor]: Inventory header — single Add Item
                  button opens the find-authored-items dialog. Authoring lives
                  in Workshop's "Create Item" (which opens the canonical
                  ItemDialog). Quick Pick drawer + the duplicate full-config
                  Add Item launch were both removed. */}
              <button
                onClick={() => {
                  // [2026-05-25]: Inventory opens ItemDialog in find mode —
                  // edit fields hidden, picker + qty + Add only.
                  setItemDialogMode('find');
                  setShowItemDialog(true);
                }}
                className="w-full mb-3 bg-amber-700 hover:bg-amber-600 p-3 rounded flex items-center justify-center transition-colors text-white"
                data-tutorial="inventory-add-item"
                data-testid="open-inventory-add-item"
              >
                <Plus size={20} className="mr-2" />
                Add Item
              </button>

              {/* Inventory Stats and Controls */}
              <div className="mb-3 bg-gray-800 p-3 rounded-lg flex justify-between items-center flex-wrap gap-2">
                <div className="text-sm text-gray-300">
                  <span className="font-semibold">{inventory.length} items</span>
                  <span className="mx-2">•</span>
                  <span>{formatCurrency(inventory.reduce((sum, item) => {
                    const itemWithMethods = Object.assign(Object.create(InventoryItem.prototype), item);
                    return sum + itemWithMethods.getTotalValue();
                  }, 0))} value</span>
                  <span className="mx-2">•</span>
                  <span>{totalWeight.toFixed(1)}lb total</span>
                </div>
                <div className="flex gap-2">
                  {/* [NEW]: Quick Access to Shared Party Vault */}
                  {/* [FIX NOV 4 SESSION 7]: Add null coalescing to Object.keys calls */}
                  {partyId && Object.keys(partyMembers || {}).length > 0 && (
                    <button
                      onClick={() => setShowSharedVault(true)}
                      className="px-3 py-1 rounded text-xs transition-colors bg-green-700 hover:bg-green-600 text-white flex items-center gap-1 font-medium"
                      title={`Open shared party vault (${Object.keys(sharedVaultItems || {}).length} items)`}
                    >
                      <Archive size={14} />
                      Party Vault ({Object.keys(sharedVaultItems || {}).length})
                    </button>
                  )}
                  <button
                    onClick={() => setShowSpreadsheetView(!showSpreadsheetView)}
                    className={`px-3 py-1 rounded text-xs transition-colors ${
                      showSpreadsheetView
                        ? 'bg-blue-600 hover:bg-blue-500 text-white'
                        : 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                    }`}
                  >
                    {showSpreadsheetView ? '🎯 Grid View' : '📊 Table View'}
                  </button>
                </div>
              </div>

              {showSpreadsheetView ? (
                /* Spreadsheet Table View */
                <div className="bg-gray-800 p-4 rounded-lg">
                  {/* Compact Sort & Filter Panel for Table View */}
                  <div className="mb-4 p-3 bg-gray-700 rounded">
                    <div className="flex gap-2 flex-wrap">
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search items..."
                        data-shortcut="inventory-search"
                        className="flex-1 min-w-[150px] px-2 py-1 text-sm bg-gray-800 text-white rounded"
                      />
                      <select
                        value={filterType}
                        onChange={(e) => setFilterType(e.target.value)}
                        className="px-2 py-1 text-sm bg-gray-800 text-white rounded"
                      >
                        <option value="all">All Types</option>
                        <option value="weapons">Weapons</option>
                        <option value="armor">Armor</option>
                        <option value="consumables">Consumables</option>
                        <option value="containers">Containers</option>
                        <option value="misc">Misc</option>
                      </select>
                      <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        className="px-2 py-1 text-sm bg-gray-800 text-white rounded"
                      >
                        <option value="name">Sort: Name</option>
                        <option value="weight">Sort: Weight</option>
                        <option value="value">Sort: Value</option>
                        <option value="type">Sort: Type</option>
                      </select>
                      <button
                        onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                        className="px-3 py-1 bg-gray-800 hover:bg-gray-600 text-white rounded text-sm"
                        title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}
                      >
                        {sortOrder === 'asc' ? '↑' : '↓'}
                      </button>
                      <button 
                        onClick={exportAsText}
                        className="bg-purple-700 hover:bg-purple-600 px-3 py-1 rounded flex items-center transition-colors text-sm"
                        title="Copy inventory as formatted text"
                      >
                        <Upload size={14} className="mr-1" />
                        Copy Text
                      </button>
                      {(searchQuery || filterType !== 'all') && (
                        <span className="text-xs text-indigo-300 self-center">
                          Showing {sortedAndFilteredInventory.length} of {inventory.length}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" onContextMenu={e => e.preventDefault()}>
                      <thead>
                        <tr className="border-b border-gray-700 text-gray-300">
                          <th className="text-left p-2">Item</th>
                          <th className="text-left p-2">Location</th>
                          <th className="text-right p-2">Qty</th>
                          <th className="text-right p-2">Weight</th>
                          <th className="text-right p-2">Value</th>
                          <th className="text-right p-2">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {/* Main inventory items - sorted and filtered */}
                        {sortedAndFilteredInventory.map(item => {
                          const itemWithMethods = Object.assign(Object.create(InventoryItem.prototype), item);
                          const isContainer = item.isContainer;
                          const isExpanded = expandedContainers.has(item.id);
                          
                          return (
                            <React.Fragment key={item.id}>
                              <tr
                                className="border-b border-gray-700/50 hover:bg-gray-700/30 cursor-pointer"
                                onContextMenu={(e) => handleContextMenu(e, item, 'inventory')}
                                onTouchStart={handleRowLongPressStart(item, 'inventory')}
                                onTouchEnd={cancelRowLongPress}
                                onTouchCancel={cancelRowLongPress}
                                onTouchMove={cancelRowLongPress}
                              >
                                <td className="p-2">
                                  <div className="flex items-center gap-2">
                                    {isContainer && (
                                      <button
                                        onClick={() => toggleContainerExpansion(item.id)}
                                        className="text-gray-400 hover:text-white transition-colors"
                                      >
                                        {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                      </button>
                                    )}
                                    <span>{item.icon}</span>
                                    <span className="text-white">{item.name}</span>
                                    {isContainer && (
                                      <span className="text-xs text-gray-400">
                                        ({item.containedItems?.length || 0} items)
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td className="p-2 text-gray-400">Inventory</td>
                                <td className="p-2 text-right">1</td>
                                <td className="p-2 text-right">{itemWithMethods.getTotalWeight().toFixed(1)}lb</td>
                                <td className="p-2 text-right">{formatCurrency(item.value)}</td>
                                <td className="p-2 text-right font-semibold">{formatCurrency(itemWithMethods.getTotalValue())}</td>
                              </tr>
                              
                              {/* Container contents (collapsible) */}
                              {isContainer && isExpanded && item.containedItems && item.containedItems.map(containedItem => {
                                const containedWithMethods = Object.assign(Object.create(InventoryItem.prototype), containedItem);
                                return (
                                  <tr
                                    key={`${item.id}-${containedItem.id}`}
                                    className="border-b border-gray-700/30 bg-gray-800/50 hover:bg-gray-700/40 cursor-pointer"
                                    onContextMenu={(e) => handleContextMenu(e, containedItem, 'inventory')}
                                    onTouchStart={handleRowLongPressStart(containedItem, 'inventory')}
                                    onTouchEnd={cancelRowLongPress}
                                    onTouchCancel={cancelRowLongPress}
                                    onTouchMove={cancelRowLongPress}
                                  >
                                    <td className="p-2 pl-8">
                                      <div className="flex items-center gap-2 text-sm">
                                        <span className="text-gray-500">🏦</span>
                                        <span>{containedItem.icon}</span>
                                        <span className="text-gray-300">{containedItem.name}</span>
                                      </div>
                                    </td>
                                    <td className="p-2 text-gray-500 text-sm">In {item.name}</td>
                                    <td className="p-2 text-right text-sm">1</td>
                                    <td className="p-2 text-right text-sm">{containedWithMethods.getTotalWeight().toFixed(1)}lb</td>
                                    <td className="p-2 text-right text-sm">{formatCurrency(containedItem.value)}</td>
                                    <td className="p-2 text-right text-sm">{formatCurrency(containedWithMethods.getTotalValue())}</td>
                                  </tr>
                                );
                              })}
                            </React.Fragment>
                          );
                        })}
                        
                        {/* Vault items */}
                        {vaults.flatMap(vault => 
                          vault.items.map(item => {
                            const itemWithMethods = Object.assign(Object.create(InventoryItem.prototype), item);
                            return (
                              <tr
                                key={`vault-${vault.id}-${item.id}`}
                                className="border-b border-gray-700/50 hover:bg-gray-700/30 cursor-pointer"
                                onContextMenu={(e) => handleContextMenu(e, item, `vault-${vault.id}`)}
                                onTouchStart={handleRowLongPressStart(item, `vault-${vault.id}`)}
                                onTouchEnd={cancelRowLongPress}
                                onTouchCancel={cancelRowLongPress}
                                onTouchMove={cancelRowLongPress}
                              >
                                <td className="p-2">
                                  <div className="flex items-center gap-2">
                                    <span className="text-purple-500">🏦</span>
                                    <span>{item.icon}</span>
                                    <span className="text-white">{item.name}</span>
                                  </div>
                                </td>
                                <td className="p-2 text-purple-400">{vault.name}</td>
                                <td className="p-2 text-right">1</td>
                                <td className="p-2 text-right">{itemWithMethods.getTotalWeight().toFixed(1)}lb</td>
                                <td className="p-2 text-right">{formatCurrency(item.value)}</td>
                                <td className="p-2 text-right font-semibold">{formatCurrency(itemWithMethods.getTotalValue())}</td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                /* Normal Grid View - Scrollable on mobile */
                <div className={`md:block overflow-y-visible max-w-full ${
                  draggedItem ? 'overflow-x-hidden' : 'overflow-x-auto'
                }`} data-tutorial="inventory-grid">
                  <div className="min-w-fit">
                    {/* Mobile Mode Indicator - Debug */}
                    {isMobile && (
                      <div className="mb-2 p-2 bg-blue-900/50 rounded text-xs text-blue-200" data-tutorial="inventory-mobile-mode">
                        📱 Mobile Mode: <strong>{settings?.mobileInteractionMode || 'tap'}</strong>
                        {settings?.mobileInteractionMode === 'drag' ? ' (Drag items to move)' : ' (Tap to select, tap to place)'}
                      </div>
                    )}
                    {/* [UNIFIED GRID - MAR 2026]: Single GridComponent with mode flag */}
                    <ErrorBoundary
                      componentName="GridComponent"
                      logActivity={logActivity}
                      fallback={<ComponentErrorFallback componentName="Inventory Grid" />}
                    >
                      <GridComponent
                        gridId="inventory"
                        cols={gridCols}
                        rows={gridRows}
                        items={inventory}
                        title={`Inventory Grid (${gridCols}x${gridRows})`}
                        selectedItem={selectedItem}
                        setSelectedItem={setSelectedItem}
                        selectedItemSource={selectedItemSource}
                        setSelectedItemSource={setSelectedItemSource}
                        isValidPosition={isValidPosition}
                        handleMouseDown={handleMouseDown}
                        handleContextMenu={handleContextMenu}
                        handleTouchMove={handleTouchMove}
                        handleCellClick={handleCellClick}
                        setOpenContainer={setOpenContainer}
                        setEditingItem={setEditingItem}
                        InventoryItem={InventoryItem}
                        enableDragDrop={!isMobile || settings?.mobileInteractionMode !== 'tap'}
                      />
                    </ErrorBoundary>
                  </div>
                </div>
              )}
              
              {/* Free Starter Pack Selection - Only show when class chosen and pack not received */}
              {primaryClass && !playerStats.hasReceivedStarterPack && (
                <div className="mt-4 bg-green-900/30 border border-green-600/30 p-4 rounded-lg">
                  <h3 className="font-bold text-green-300 mb-3 flex items-center">
                    🎒 Free Class Equipment
                  </h3>
                  <p className="text-sm text-green-200 mb-3">
                    Choose your {primaryClass} starter pack (one-time only):
                  </p>
                  <div className="space-y-2">
                    {getEligibleStarterPacks().map(pack => (
                      <button
                        key={pack.key}
                        onClick={() => selectStarterPack(pack.key)}
                        className="w-full p-3 bg-green-800 hover:bg-green-700 rounded text-left transition-colors"
                      >
                        <div className="font-medium text-green-200">{pack.name}</div>
                        <div className="text-xs text-green-300 mt-1">{pack.cost}gp value</div>
                        <div className="text-xs text-gray-400 mt-1 line-clamp-2">{pack.description}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Containers List */}
              <div className="mt-4 bg-gray-800 p-4 rounded-lg" data-tutorial="inventory-containers">
                <h3 className="text-lg font-bold mb-3 text-white">Quick Access</h3>
                <div className="space-y-2">
                  {containerItems.map(container => (
                    <button
                      key={container.id}
                      onClick={() => setOpenContainer(container)}
                      className="w-full text-left bg-gray-700 hover:bg-gray-600 p-2 rounded transition-colors flex items-center justify-between"
                    >
                      <span>
                        {container.icon} {container.name} 
                        <span className="text-xs text-gray-400 ml-2">
                          ({container.containedItems?.length || 0}/{
                            (container.containerSlots?.[0] || 6) * (container.containerSlots?.[1] || 8)
                          } slots)
                        </span>
                      </span>
                      <Eye size={16} className="text-gray-400" />
                    </button>
                  ))}
                  {containerItems.length === 0 && (
                    <p className="text-gray-400 text-sm">
                      No containers in inventory. Add a backpack, pouch, or chest to organize items!
                    </p>
                  )}
                </div>
                <div className="mt-3 text-xs text-gray-400">
                  💡 <strong>Tip:</strong> Click and drag items to move them. Right-click for more options.
                </div>
              </div>
            </div>
          </div>
        )}

        {/* [PHASE 4C - NOV 2, 2025]: Shop Tab - Extracted to ShopTab.js (reduced from 832 inline lines) - ACHIEVES 12,000-LINE GOAL! */}
        {/* [v1.0]: Premium-only feature — Shop Generator */}
        {activeTab === 'shop' && !openContainer && !showVaultModal && (
          <Suspense fallback={<div className="bg-gray-800 p-6 rounded-lg text-center text-gray-400">Loading Shop...</div>}>
            <ShopTab
              generateShop={generateShop}
              purchaseShopItem={purchaseShopItem}
              downloadShop={downloadShop}
              importShop={importShop}
              getAvailableShops={getAvailableShops}
              getShopTemplate={getShopTemplate}
              SHOP_ROLL_TABLES={SHOP_ROLL_TABLES}
              BASE_ITEMS={BASE_ITEMS}
              valuableItemsCount={valuableItemsCount}
            />
          </Suspense>
        )}

        {/* [PHASE 4C - NOV 1, 2025]: Magic Items Tab - Extracted to MagicItemsTab.js (reduced from 568 inline lines) */}
        {activeTab === 'magic' && !openContainer && !showVaultModal && (
          <Suspense fallback={<div className="bg-gray-800 p-6 rounded-lg text-center text-gray-400">Loading Magic Items...</div>}>
            <MagicItemsTab rollSpellAttack={rollSpellAttack} getSpellSaveDC={getSpellSaveDC} />
          </Suspense>
        )}
        
        {activeTab === 'vault' && !showVaultModal && (
          <div className="bg-gray-800 p-6 rounded-lg">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h2 className="text-2xl font-bold">Vault Storage</h2>
                <p className="text-gray-400">
                  Store items securely in vaults. Containers can be stored but must be in inventory to open.
                </p>
              </div>
              <label className="bg-yellow-700 hover:bg-yellow-600 px-4 py-2 rounded flex items-center cursor-pointer transition-colors">
                <Upload size={20} className="mr-2" />
                Import Vault
                <input 
                  type="file"
                  accept=".json"
                  onChange={importVault}
                  className="hidden"
                />
              </label>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* [NEW]: Shared Party Vault Card */}
              {/* [FIX NOV 4 SESSION 7]: Add null coalescing */}
              {partyId && Object.keys(partyMembers || {}).length > 0 && (
                <div className="bg-gradient-to-br from-green-900 to-green-800 p-4 rounded border-2 border-green-500 transition-all hover:shadow-lg">
                  <h3 className="font-bold text-lg flex items-center">
                    <Archive size={20} className="mr-2 text-green-300" />
                    <span className="text-green-100">Party Shared Vault</span>
                  </h3>
                  <p className="text-sm text-green-200">🤝 Collaborative Loot Storage</p>
                  <div className="mt-3 p-2 bg-green-950/50 rounded border border-green-700">
                    <p className="text-xs text-green-300 mb-1">
                      Party: {partyName || 'Unnamed Party'}
                    </p>
                    <p className="text-xs text-green-300 mb-1">
                      Items: {Object.keys(sharedVaultItems).length}
                    </p>
                    <p className="text-xs text-green-300">
                      Members: {Object.keys(partyMembers).length}
                    </p>
                  </div>
                  <div className="mt-3 flex gap-2">
                    <button
                      onClick={() => setShowSharedVault(true)}
                      className="flex-1 bg-green-600 hover:bg-green-500 px-3 py-2 rounded font-bold transition-colors flex items-center justify-center gap-2"
                    >
                      <Archive size={16} />
                      Open Vault
                    </button>
                  </div>
                  <div className="mt-2 text-xs text-green-200 bg-green-900/50 p-2 rounded">
                    💡 Right-click inventory items to add to party vault
                  </div>
                </div>
              )}

              {vaults.map(vault => {
                const vaultWeight = vault.items?.reduce((sum, i) => sum + (i.weight || 0), 0) || 0;
                const vaultValue = vault.items?.reduce((sum, i) => {
                  const itemWithMethods = Object.assign(Object.create(InventoryItem.prototype), i);
                  return sum + itemWithMethods.getTotalValue();
                }, 0) || 0;
                const itemIcons = vault.items?.slice(0, 5).map(i => i.icon).join(' ') || '(empty)';
                
                return (
                  <div 
                    key={vault.id} 
                    className="bg-gray-700 p-4 rounded transition-colors"
                  >
                    <h3 className="font-bold text-lg">📦 {vault.name}</h3>
                    <p className="text-sm text-gray-400">📍 {vault.location}</p>
                    {vault.cost > 0 && (
                      <p className="text-sm text-gray-400">💰 Access: {vault.cost}gp</p>
                    )}
                    <div className="mt-3 p-2 bg-gray-800 rounded">
                      <p className="text-xs text-gray-400 mb-1">
                        Contents: {vault.items?.length || 0} items | {vaultWeight.toFixed(1)}lb | {formatCurrency(vaultValue)}
                      </p>
                      <p className="text-lg">{itemIcons}</p>
                      {vault.items?.length > 5 && (
                        <p className="text-xs text-gray-400">+{vault.items.length - 5} more...</p>
                      )}
                    </div>
                    <div className="mt-3 flex gap-2">
                      <button 
                        onClick={() => setShowVaultModal(vault.id)}
                        className="flex-1 bg-blue-700 hover:bg-blue-600 px-3 py-1 rounded text-sm transition-colors"
                      >
                        Manage
                      </button>
                      <button 
                        onClick={() => exportVault(vault.id)}
                        className="bg-green-700 hover:bg-green-600 px-3 py-1 rounded text-sm transition-colors"
                        title="Export Vault"
                      >
                        <Download size={14} />
                      </button>
                      <button 
                        onClick={async () => {
                          if (await confirm({ title: 'Delete Vault', message: `Delete vault "${vault.name}"? Items will be lost!`, confirmText: 'Delete', confirmColor: 'red' })) {
                            setVaults(prev => prev.filter(v => v.id !== vault.id));
                          }
                        }}
                        className="bg-red-700 hover:bg-red-600 px-3 py-1 rounded text-sm transition-colors"
                        title="Delete Vault"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                );
              })}
              
              <button 
                onClick={() => setShowVaultDialog(true)}
                className="bg-gray-700 hover:bg-gray-600 p-4 rounded transition-colors border-2 border-dashed border-gray-500 flex flex-col items-center justify-center min-h-[200px]"
              >
                <Plus size={32} className="mb-2 text-gray-300" />
                <span className="text-gray-300">Create New Vault</span>
              </button>
            </div>
          </div>
        )}
        
        {/* [PHASE 4C - NOV 2, 2025]: Spellbook Tab - Extracted to SpellbookTab.js (reduced from 616 inline lines) */}
        {/* [PHASE 4.3]: Wrapped with ErrorBoundary for spell system error handling */}
        {activeTab === 'spellbook' && !openContainer && !showVaultModal && (
          <Suspense fallback={<div className="bg-gray-800 p-6 rounded-lg text-center text-gray-400">Loading Spellbook...</div>}>
            <ErrorBoundary
              componentName="SpellbookTab"
              logActivity={logActivity}
              fallback={<ComponentErrorFallback componentName="Spellbook" />}
            >
              <SpellbookTab />
            </ErrorBoundary>
          </Suspense>
        )}

        {/* SKILLS TAB */}
        {/* [PHASE 4B - MIGRATED]: SkillsPanel now uses context hooks */}
        {/* [PHASE 4.3]: Wrapped with ErrorBoundary for skills error handling */}
        {activeTab === 'skills' && !openContainer && !showVaultModal && (
          <Suspense fallback={<div className="bg-gray-800 p-6 rounded-lg text-center py-12 text-gray-400">Loading Skills...</div>}>
            <ErrorBoundary
              componentName="SkillsPanel"
              logActivity={logActivity}
              fallback={<ComponentErrorFallback componentName="Skills Panel" />}
            >
              <SkillsPanel />
            </ErrorBoundary>
          </Suspense>
        )}

        {/* FEATS TAB */}
        {/* [PHASE 4B - MIGRATED]: FeatsPanel now uses context hooks, keeps sourcesManager prop */}
        {/* [PHASE 4.3]: Wrapped with ErrorBoundary for feats error handling */}
        {activeTab === 'feats' && !openContainer && !showVaultModal && (
          <Suspense fallback={<div className="bg-gray-800 p-6 rounded-lg text-center py-12 text-gray-400">Loading Feats...</div>}>
            <ErrorBoundary
              componentName="FeatsPanel"
              logActivity={logActivity}
              fallback={<ComponentErrorFallback componentName="Feats Panel" />}
            >
              <FeatsPanel
                sourcesManager={sourcesManager}
                /* [SUB-PLAN 1B — 2026-05-19]: Delegate "Create Custom Feat"
                   to the Workshop CustomFeatCreator. Same callback the
                   ContentWorkshopSection uses — single source of truth.
                   Inline FeatsPanel creator is kept (per user direction
                   "tokens well spent") but no longer reachable from the
                   default user flow; only dev-mode / debug routes can
                   surface it. */
                onOpenFeatCreator={() => {
                  setEditingCustomFeat(null);
                  setShowCustomFeatCreator(true);
                }}
              />
            </ErrorBoundary>
          </Suspense>
        )}

        {/* DICE TAB - [NEW: Week 2, Day 10] */}
        {activeTab === 'dice' && !openContainer && !showVaultModal && (
          <Suspense fallback={<div className="bg-gray-800 p-6 rounded-lg text-center py-12 text-gray-400">Loading Dice Roller...</div>}>
            {/* [PHASE 4B - MIGRATED]: DiceTab now uses context hooks */}
            <DiceTab />
          </Suspense>
        )}

        {/* REST TAB */}
        {/* [PHASE 4B - MIGRATED]: RestPanel now uses context hooks */}
        {/* [PHASE 4.3]: Wrapped with ErrorBoundary for rest system error handling */}
        {activeTab === 'rest' && !openContainer && !showVaultModal && (
          <Suspense fallback={<div className="bg-gray-800 p-6 rounded-lg text-center py-12 text-gray-400">Loading Rest...</div>}>
            <ErrorBoundary
              componentName="RestPanel"
              logActivity={logActivity}
              fallback={<ComponentErrorFallback componentName="Rest Panel" />}
            >
              <RestPanel />
            </ErrorBoundary>
          </Suspense>
        )}

        {/* DOWNTIMES TAB - [NEW 2025-10-11]: D&D 5e Downtime Activities */}
        {/* [v1.0]: Premium-only feature — Downtime Activities */}
        {activeTab === 'downtimes' && !openContainer && !showVaultModal && (
          <Suspense fallback={<div className="bg-gray-800 p-6 rounded-lg text-center py-12 text-gray-400">Loading Downtimes...</div>}>
            <ErrorBoundary
              componentName="DowntimesPanel"
              logActivity={logActivity}
              fallback={<ComponentErrorFallback componentName="Downtimes Panel" />}
            >
              <DowntimesPanel
                onOpenCalendar={() => setActiveTab('calendar')}
              />
            </ErrorBoundary>
          </Suspense>
        )}

        {/* CALENDAR TAB */}
        {/* [PHASE 4B - MIGRATED]: CalendarPanel now uses context hooks */}
        {/* [PHASE 4.3]: Wrapped with ErrorBoundary for calendar error handling */}
        {activeTab === 'calendar' && !openContainer && !showVaultModal && (
          <Suspense fallback={<div className="bg-gray-800 p-6 rounded-lg text-center py-12 text-gray-400">Loading Calendar...</div>}>
            <ErrorBoundary
              componentName="CalendarPanel"
              logActivity={logActivity}
              fallback={<ComponentErrorFallback componentName="Calendar Panel" />}
            >
              <CalendarPanel />
            </ErrorBoundary>
          </Suspense>
        )}

        {/* BASTION TAB - [NEW 2025-10-10]: D&D 2024 Bastion System */}
        {/* [ARCHITECTURE]: BastionPanel uses context hooks following Phase 4B migration pattern */}
        {/* [PHASE 4.3]: Wrapped with ErrorBoundary for bastion error handling */}
        {/* [v1.0]: Premium-only feature — Bastion System */}
        {activeTab === 'bastion' && !openContainer && !showVaultModal && (
          <Suspense fallback={<div className="flex items-center justify-center p-12"><div className="text-gray-400">Loading Bastion System...</div></div>}>
            <ErrorBoundary
              componentName="BastionPanel"
              logActivity={logActivity}
              fallback={<ComponentErrorFallback componentName="Bastion Panel" />}
            >
              <BastionPanel />
            </ErrorBoundary>
          </Suspense>
        )}

        {/* PARTY TAB */}
        {/* [v1.0]: Premium-only feature — Party Sync */}
        {activeTab === 'party' && !openContainer && !showVaultModal && (
          <PremiumGate featureName="Party Sync" description="Connect with your party in real-time. Share inventories, track effects, and coordinate.">
          <div className="bg-gray-800 p-6 rounded-lg">
            {partyId ? (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-2xl font-bold">Party: {partyName}</h2>
                  {isDM && (
                    <span className="px-3 py-1 bg-purple-700 text-white rounded-full text-sm font-bold">
                      👑 You are the DM
                    </span>
                  )}
                </div>
                <Suspense fallback={<div className="text-center py-12 text-gray-400">Loading party panel...</div>}>
                  <PartyPanel
                    partyName={partyName}
                    partyMembers={partyMembers}
                    partyActivityLog={partyActivityLog}
                    isConnected={isConnected}
                    syncError={syncError}
                    currentCharacterId={playerStats.id}
                    onOpenSharedVault={() => setShowSharedVault(true)}
                    allPartyEffects={allPartyEffects}
                    removeEffectFromAlly={removeEffectFromAlly}
                    isDM={isDM}
                    onClaimDM={async (claim) => {
                      // [A33]: log DM claim/release as a party mechanical action (Rule 13)
                      const result = await claimDM(claim);
                      if (result !== false) {
                        const { message, type, details } = dmStatusMessage(claim);
                        logActivity(message, type, details);
                      }
                      return result;
                    }}
                    partyMetadata={partyMetadata}
                    pushedContent={pushedContent}
                    onAcknowledgePushedItem={(type, id) => markPushedContentViewed(type, id)}
                    onOpenPushedShop={(shopId, shop) => {
                      // [B2 FIX]: Add DM-pushed shop to member's shop list so they can browse it.
                      //   Dedup by shopId — re-clicking "Visit" doesn't add duplicates.
                      if (shop) {
                        setActiveShops(prev => {
                          if (prev.some(s => s.id === shopId)) return prev;
                          return [...prev, { ...shop, id: shopId, fromParty: true }];
                        });
                        // [A33]: log opening a DM-shared shop as a party mechanical action (Rule 13)
                        const { message, type, details } = pushedShopVisitedMessage(shop.name || shop.shopName);
                        logActivity(message, type, details);
                      }
                      markPushedContentViewed('shops', shopId);
                    }}
                    onClaimLootItem={async (rollId, shareCount = 1) => {
                      const entry = await claimLootItem(rollId);
                      if (!entry) return;
                      const result = entry.roll?.result;
                      if (!result) return;
                      const mutation = grantRollResult(result);
                      if (mutation) {
                        if (Array.isArray(mutation.inventoryAdd)) {
                          mutation.inventoryAdd.forEach(item => {
                            addItem(item, item.quantity || 1);
                            // [A33]: log each claimed loot item as a party mechanical action (Rule 13)
                            const { message, type, details } = lootItemClaimedMessage(item.name, item.quantity || 1);
                            logActivity(message, type, details);
                          });
                        }
                        if (mutation.currencyAdd && typeof mutation.currencyAdd === 'object') {
                          // shareCount > 1 = currency split; divide by party size
                          const divisor = shareCount > 1 ? shareCount : 1;
                          Object.entries(mutation.currencyAdd).forEach(([t, a]) => {
                            if (typeof a === 'number' && a !== 0) {
                              addCurrency(t, Math.floor(a / divisor));
                            }
                          });
                          // [A33]: log the claimed currency (post-split) once (Rule 13)
                          const { message, type, details } = lootCurrencyClaimedMessage(mutation.currencyAdd, shareCount);
                          logActivity(message, type, details);
                        }
                      }
                    }}
                  />
                </Suspense>

                {/* [NEW]: DM Tools Section */}
                <div className="mt-6">
                  {/* [PHASE 4B - MIGRATED]: DMToolsPanel now uses context hooks for showStatus */}
                  <DMToolsPanel
                    isDM={isDM}
                    partyMembers={partyMembers}
                    pushedContent={pushedContent}
                    pushShopToParty={pushShopToParty}
                    pushNoteToParty={pushNoteToParty}
                    pushCalendarEventToParty={pushCalendarEventToParty}
                    pushLootRollToParty={pushLootRollToParty}
                    activeShops={activeShops}
                    journalEntries={journalEntries}
                    currentGameDay={currentGameDay}
                    customRollTables={customRollTables}
                    rollTables={rollTables}
                    journalRollTables={journalRollTables}
                    onOpenShop={() => setActiveTab('shop')}
                    onOpenJournal={() => setActiveTab('journal')}
                    onOpenCalendar={() => setActiveTab('calendar')}
                    onOpenRollTables={() => setActiveTab('tables')}
                    /* [PHASE B / #066 — 2026-05-07]: B4-2 stopgap. Translate
                       grantRollResult mutations into local state mutations so
                       the rolled item / currency actually deposits in the DM's
                       inventory. Items go through addItem (honors stack rules);
                       currency goes through addCurrency. Condition / feat /
                       spell grants are deferred — they need side effects
                       beyond simple state writes (effectRegistry, condition
                       system) and the structured roll-table entries don't
                       commonly carry those kinds in v1.0. */
                    onLootGranted={(mutation) => {
                      if (!mutation) return;
                      if (Array.isArray(mutation.inventoryAdd)) {
                        mutation.inventoryAdd.forEach(item => {
                          addItem(item, item.quantity || 1);
                        });
                      }
                      if (mutation.currencyAdd && typeof mutation.currencyAdd === 'object') {
                        Object.entries(mutation.currencyAdd).forEach(([type, amount]) => {
                          if (typeof amount === 'number' && amount !== 0) {
                            addCurrency(type, amount);
                          }
                        });
                      }
                    }}
                  />
                </div>

                {/* [Phase B #111]: Party Bastion — shared stronghold for all members */}
                <Suspense fallback={null}>
                  <PartyBastionPanel
                    partyId={partyId}
                    isDM={isDM}
                    characterId={playerStats.id}
                    characterName={playerStats.basics?.name || playerStats.name || 'Adventurer'}
                    playerLevel={playerStats.level || 1}
                    currencies={currencies}
                    partyMemberCount={Object.keys(partyMembers || {}).length}
                  />
                </Suspense>
              </>
            ) : (
              /* [SPEC 2026-05-11]: Replaces PartyCreateJoinForm with the new
                 PartyCreateJoinSplash. The splash is a presentational state
                 machine (Idle → Busy → PostCreate). Firebase writes happen in
                 createPartyHandler / joinPartyHandler on PartyContext. */
              <div className="py-6">
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-bold mb-2">Party Sync</h2>
                  <p className="text-gray-400">
                    Create a new party as the DM, or join one a friend already started.
                  </p>
                </div>
                <PartyCreateJoinSplash
                  currentUser={currentUser}
                  playerStats={playerStats}
                  currentPartyId={partyId}
                  currentPartyName={partyName}
                  createPartyHandler={createPartyHandler}
                  joinPartyHandler={joinPartyHandler}
                  onCreated={({ partyId: newId, partyName: newName }) => {
                    setPartyId(newId);
                    setPartyName(newName);
                    showStatus(`✅ Party "${newName}" created`, 3000);
                  }}
                  onJoined={({ partyId: newId, partyName: newName }) => {
                    setPartyId(newId);
                    setPartyName(newName);
                    showStatus(`✅ Joined party "${newName}"`, 3000);
                  }}
                />
              </div>
            )}
          </div>
          </PremiumGate>
        )}

        {/* COMBAT TAB — Spec D (2026-05-17): Combat Manager (initiative + token canvas + CPI/SPI difficulty) */}
        {activeTab === 'combat' && !openContainer && !showVaultModal && (
          <div className="p-4 space-y-4">
            <Suspense fallback={<div className="text-center py-12 text-gray-400">Loading Combat Manager...</div>}>
              <CombatTab
                playerStats={playerStats}
                setPlayerStats={setPlayerStats}
                party={Object.values(partyMembers || {})}
                logActivity={logActivity}
              />
              {/* [AUDIT 2026-05-17 #48]: Monster Editor mount + trigger moved to
                  Content Workshop per the Workshop-hosts-creators contract
                  (feedback_content_workshop_contract). Combat tab is a consumer
                  surface — it picks from the catalog via CombatTab's bestiary
                  drawer, it does NOT host the authoring flow. The canonical
                  modal mount now lives below alongside the Spell Creator. */}
            </Suspense>
          </div>
        )}

        {/* SOURCES TAB — removed 2026-05-11; SourcesPanel is now a section
            inside the Workshop tab above ContentWorkshopSection. */}

        {/* [PHASE 4C - NOV 1, 2025]: Activity Log Tab - Extracted to ActivityLogTab.js (reduced from 413 inline lines) */}
        {activeTab === 'log' && !openContainer && !showVaultModal && (
          <Suspense fallback={<div className="bg-gray-800 p-6 rounded-lg text-center text-gray-400">Loading Activity Log...</div>}>
            <ActivityLogTab />
          </Suspense>
        )}

        {/* [PHASE 4C - NOV 1, 2025]: Journal Tab - Extracted to JournalTab.js (reduced from 394 inline lines) */}
        {activeTab === 'journal' && !openContainer && !showVaultModal && (
          <Suspense fallback={<div className="bg-gray-800 p-6 rounded-lg text-center text-gray-400">Loading Campaign Journal...</div>}>
            <JournalTab />
          </Suspense>
        )}
        
        {/* [PHASE 4C - NOV 1, 2025]: Roll Tables Tab - Extracted to RollTablesTab.js (reduced from 507 inline lines) */}
        {activeTab === 'tables' && !openContainer && !showVaultModal && (
          <Suspense fallback={<div className="bg-gray-800 p-6 rounded-lg text-center text-gray-400">Loading Roll Tables...</div>}>
            <RollTablesTab isValidPosition={isValidPosition} />
          </Suspense>
        )}
        
        {activeTab === 'workshop' && !openContainer && !showVaultModal && (
          <div className="p-4">
            {/* [2026-05-11]: Content Workshop first; Sources is the supporting
                "what's enabled" panel below per user direction. */}
            {settings.displayMode === DISPLAY_MODES.DEFAULT ? (
              <>
                {/* [CWR-001 FIX]: route Spell Creator button to canonical App.jsx
                    mount so customComponents + knownSpells integration runs. */}
                <ContentWorkshopSection
                  onOpenSpellCreator={() => {
                    setEditingSpell(null);
                    setTemplateSpell(null);
                    setShowCustomSpellCreator(true);
                  }}
                  onOpenMonsterEditor={() => {
                    setEditingMonster(null); // explicit create-new
                    setShowMonsterEditor(true);
                  }}
                  /* [STONE 9 / #62 + #81 — 2026-05-18]: View+Edit+Template trio. */
                  onOpenLoadMonster={() => setLoadMonsterDrawerOpen(true)}
                  onOpenViewMonster={() => setViewMonsterDrawerOpen(true)}
                  onOpenTemplateMonster={() => setTemplateMonsterDrawerOpen(true)}
                  /* [SUB-PLAN 1E — 2026-05-24]: Item trio callbacks. */
                  onOpenLoadItem={() => setLoadItemDrawerOpen(true)}
                  onOpenViewItem={() => setViewItemDrawerOpen(true)}
                  onOpenTemplateItem={() => setTemplateItemDrawerOpen(true)}
                  /* [SUB-PLAN 1E — 2026-05-24]: Feat trio callbacks. */
                  onOpenLoadFeat={() => setLoadFeatDrawerOpen(true)}
                  onOpenViewFeat={() => setViewFeatDrawerOpen(true)}
                  onOpenTemplateFeat={() => setTemplateFeatDrawerOpen(true)}
                  /* [SUB-PLAN 1E — 2026-05-24]: Spell trio callbacks. */
                  onOpenLoadSpell={() => setLoadSpellDrawerOpen(true)}
                  onOpenViewSpell={() => setViewSpellDrawerOpen(true)}
                  onOpenTemplateSpell={() => setTemplateSpellDrawerOpen(true)}
                  /* [SUB-PLAN 1E]: playerStats + logActivity for local Race/Class/Subclass trios. */
                  playerStats={playerStats}
                  logActivity={logActivity}
                  /* [2026-05-25 items 6+7 refactor]: "Create Item" opens
                     the canonical ItemDialog (item authoring lives in
                     Workshop now). Replaces the deleted CustomItemCreator
                     parallel from b785ead. */
                  onOpenItemCreator={() => {
                    setItemDialogMode('create');
                    setShowItemDialog(true);
                  }}
                  /* [STONE 6 CLOSURE / #67 Option B — 2026-05-18]: Feat Creator
                     opens the Workshop-hosted CustomFeatCreator. The inline
                     creator on the Feats tab is still functional; the
                     navigate-to-Feats stopgap is preserved as a fallback. */
                  onOpenFeatCreator={() => {
                    setEditingCustomFeat(null);
                    setShowCustomFeatCreator(true);
                  }}
                  /* [SUB-PLAN 3B — 2026-05-29]: Condition Creator opens the
                     Workshop-hosted CustomConditionCreator (App.jsx-delegated,
                     same pattern as Feat/Spell). */
                  onOpenConditionCreator={(seed) => {
                    setEditingCustomCondition(seed || null);
                    setShowCustomConditionCreator(true);
                  }}
                  /* [SUB-PLAN 3A — 2026-05-30]: Boon Creator opens the
                     Workshop-hosted CustomBoonCreator (App.jsx-delegated,
                     same pattern as Condition Creator).
                     [CW-BOON-BG-OPERATIONS — 2026-07-11]: accepts a seed so the
                     Boon trio's Load/Template drawers can open edit/fork mode. */
                  onOpenBoonCreator={(seed) => {
                    setEditingCustomBoon(seed || null);
                    setShowCustomBoonCreator(true);
                  }}
                  /* [SUB-PLAN 3C — 2026-05-30]: Background Creator opens the
                     Workshop-hosted CustomBackgroundCreator (App.jsx-delegated,
                     writes playerStats.customBackgrounds; narrative-only Branch A).
                     [CW-BOON-BG-OPERATIONS — 2026-07-11]: accepts a seed for the
                     Background trio's Load/Template drawers. */
                  onOpenBackgroundCreator={(seed) => {
                    setEditingCustomBackground(seed || null);
                    setShowCustomBackgroundCreator(true);
                  }}
                  /* [CW-DELETE-AFFORDANCE — 2026-07-11]: red-confirm deletes for
                     the Workshop-hosted trios whose content lives in playerStats
                     slots. Handlers stay in App.jsx (same delegation pattern as
                     the creators); the trios just report intent. */
                  onDeleteCondition={(c) => deleteCustomContent(c, 'Condition', (prev) => removeCustomEntry(prev, 'customConditions', c.id))}
                  onDeleteBoon={(b) => deleteCustomContent(b, 'Boon', (prev) => removeCustomEntry(prev, 'customBoons', b.id))}
                  onDeleteBackground={(b) => deleteCustomContent(b, 'Background', (prev) => removeCustomEntry(prev, 'customBackgrounds', b.id))}
                  /* [CW-TRIO-SOURCES 2026-07-12]: sources-backed deletes for the
                     trios whose content lives in sourcesManager, not playerStats. */
                  onDeleteRace={(r) => deleteSourceContent(r, 'Race', removeRaceFromSource)}
                  onDeleteClass={(c) => deleteSourceContent(c, 'Class', removeClassFromSource)}
                  onDeleteSubclass={(sc) => deleteSourceContent(sc, 'Subclass', removeSubclassFromSource)}
                  onNavigateToFeats={() => setActiveTab('feats')}
                />
              </>
            ) : (
              <div className="bg-gray-800 p-4 rounded-lg border border-gray-700 text-sm text-gray-400 mb-4">
                <strong className="text-gray-300">Content Workshop</strong> is only available in Default View.
                Switch display modes in Settings to access custom content creation.
              </div>
            )}
            {/* [2026-05-11 USER DIRECTION]: Sources sits BELOW the Workshop creators.
                Shown outside the DEFAULT gate so SIMPLE-mode users can still toggle
                sourcebooks. */}
            <div className="bg-gray-800 p-6 rounded-lg mt-4">
              <h2 className="text-2xl font-bold mb-4">Content Sources</h2>
              <p className="text-gray-400 mb-6">
                Manage which sourcebooks and custom content are available in your game.
              </p>
              {/* [PHASE 4B - MIGRATED]: SourcesPanel now uses context hooks */}
              <SourcesPanel />
            </div>
          </div>
        )}

        {/* [A17 — 2026-05-25]: Roster tab is gated on settings.enableRosterTab
            (default false). MobileNavigation filters the entry out of the nav
            bar; this render gate is the belt-and-suspenders so a stale activeTab
            value doesn't surface a hidden tab via deep-link or storage rehydrate. */}
        {activeTab === 'roster' && settings.enableRosterTab && !openContainer && !showVaultModal && (
          <Suspense fallback={<div className="bg-gray-800 p-6 rounded-lg text-gray-400">Loading roster…</div>}>
            <RosterTab
              savedCharacters={savedCharacters}
              activeCharacterId={playerStats?.id}
              setActiveTab={setActiveTab}
              onSwap={(id) => loadCharacter(id)}
              onNewCharacter={() => {
                setShowCharacterWizard(true);
              }}
              onRename={(id) => setRenameCharacterId(id)}
              onDuplicate={(id) => duplicateCharacter(id)}
              onExport={(id) => exportCharacter(id, 'json')}
              onArchive={(id) => archiveCharacter(id)}
              onUnarchive={(id) => unarchiveCharacter(id)}
              onDelete={(id) => deleteCharacter(id)}
            />
          </Suspense>
        )}

        {activeTab === 'settings' && !openContainer && !showVaultModal && (
          <SettingsPanel
            clearAutosave={clearAutosave}
            nuclearReset={nuclearReset}
            exportCharacter={exportCharacter}
            exportDebugBundle={exportDebugBundle}
            importInventory={importInventory}
            saveCharacter={saveCharacter}
            loadCharacter={loadCharacter}
            deleteCharacter={deleteCharacter}
            duplicateCharacter={duplicateCharacter}
          />
        )}
      </div>

      {/* Quickcast Dialog */}
      {/* [UI-MOBILEMODAL-ADOPTION MOBILEMODAL-APPJSX-INLINE — 2026-07-09]: chrome-swap onto
          MobileModal (was a hand-rolled w-[500px] card w/ own X + no Escape). onClose keeps
          the 5-state reset the old X performed; Z.MODAL(60) → z-[60] literal; card identity
          bg-gray-900 rides className (CustomRaceCreator precedent); body byte-identical. */}
      {showQuickcastDialog && quickcastSpell && (
        <MobileModal
          isOpen={true}
          title={`⚡ Quickcast: ${quickcastSpell.name}`}
          onClose={() => {
            setShowQuickcastDialog(false);
            setQuickcastSpell(null);
            setLastSpellRoll(null);
            setSelectedAllyTarget(null); // Reset target
            setCastAsRitual(false); // [NEW NOV 13, 2025]: Reset ritual checkbox
          }}
          showSaveButton={false}
          overlayClassName="z-[60]"
          maxWidthClass="max-w-[500px]"
          className="bg-gray-900"
        >

            {/* Spell Info */}
            <div className="bg-gray-800 p-4 rounded-lg mb-4">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-gray-400">Level:</span>{' '}
                  <span className="font-bold">{quickcastSpell.level === 0 ? 'Cantrip' : quickcastSpell.level}</span>
                </div>
                <div>
                  <span className="text-gray-400">School:</span>{' '}
                  <span className="font-bold">{quickcastSpell.school}</span>
                </div>
                <div>
                  <span className="text-gray-400">Cast Time:</span>{' '}
                  <span className="font-bold">{quickcastSpell.castTime}</span>
                </div>
                <div>
                  <span className="text-gray-400">Range:</span>{' '}
                  <span className="font-bold">{quickcastSpell.range}</span>
                </div>
              </div>
              {quickcastSpell.description && (
                <p className="mt-3 text-sm text-gray-300 italic">{quickcastSpell.description}</p>
              )}
            </div>

            {/* [NEW]: Party Targeting for Beneficial Spells */}
            {partyId && partyMembers && Object.keys(partyMembers).length > 0 && canSpellTargetAlly(quickcastSpell) && (
              <div className="bg-indigo-900/30 border border-indigo-500 p-4 rounded-lg mb-4">
                <h3 className="font-bold mb-2 text-indigo-300">🎯 Cast On:</h3>
                <div className="flex gap-2 flex-wrap">
                  {/* Self Option */}
                  <button
                    onClick={() => setSelectedAllyTarget(null)}
                    className={`px-3 py-2 rounded border ${
                      selectedAllyTarget === null
                        ? 'bg-indigo-600 border-indigo-400 text-white'
                        : 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    👤 Self ({playerStats.name || 'You'})
                  </button>

                  {/* Party Member Options */}
                  {/* [FIX NOV 4 SESSION 7]: Add null coalescing to prevent Object.entries crash */}
                  {Object.entries(partyMembers || {}).map(([memberId, member]) => {
                    // Don't show self in party list
                    if (memberId === playerStats.id) return null;

                    return (
                      <button
                        key={memberId}
                        onClick={() => setSelectedAllyTarget({ id: memberId, name: member.characterName })}
                        className={`px-3 py-2 rounded border ${
                          selectedAllyTarget?.id === memberId
                            ? 'bg-indigo-600 border-indigo-400 text-white'
                            : 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600'
                        }`}
                      >
                        🤝 {member.characterName}
                        {member.status === 'online' && <span className="ml-1 text-green-400">●</span>}
                      </button>
                    );
                  })}
                </div>

                {selectedAllyTarget && (
                  <div className="mt-2 text-sm text-indigo-200 bg-indigo-900/50 p-2 rounded">
                    ✨ This spell will be cast on <strong>{selectedAllyTarget.name}</strong> and effects will sync to their character
                  </div>
                )}
              </div>
            )}

            {/* Component Requirements */}
            {(() => {
              const componentCheck = checkSpellComponents(quickcastSpell);
              const componentDetails = getComponentDetails(quickcastSpell.name);
              
              if (componentDetails) {
                return (
                  <div className={`p-3 rounded-lg mb-4 ${
                    componentCheck.canCast === false ? 'bg-red-900/50 border border-red-500' :
                    componentCheck.canCast === 'needsPurchase' ? 'bg-yellow-900/50 border border-yellow-500' :
                    'bg-green-900/50 border border-green-500'
                  }`}>
                    <div className="font-bold text-sm mb-1">📦 Material Components:</div>
                    <div className="text-xs">
                      {componentDetails.materials.join(', ')}
                      {componentDetails.consumed && <span className="text-red-400 ml-2">(Consumed)</span>}
                    </div>
                    {componentCheck.canCast === false && (
                      <div className="text-red-400 text-xs mt-1">⚠️ Missing components - cannot cast!</div>
                    )}
                    {componentCheck.canCast === 'needsPurchase' && (
                      <div className="text-yellow-400 text-xs mt-1">💰 Will purchase for {componentDetails.cost}gp when cast</div>
                    )}
                  </div>
                );
              }
              
              // Only show generic component message if there are NO specific component details
              if (quickcastSpell.components && quickcastSpell.components.includes('M') && !componentDetails) {
                // [FIX JAN 13, 2026]: Property-based detection only (ARC-014)
                const hasPouch = inventory.some(item =>
                  item.providesBasicComponents ||
                  item.containerType === 'component_pouch'
                );
                const hasFocus = inventory.some(item => item.providesFocus);
                const hasEither = hasPouch || hasFocus;
                
                return (
                  <div className={`p-3 rounded-lg mb-4 ${
                    hasEither ? 'bg-green-900/50 border border-green-500' : 'bg-yellow-900/50 border border-yellow-500'
                  }`}>
                    <div className="text-xs">
                      <span className="font-bold">Components:</span> {quickcastSpell.components}
                      {!hasEither && <div className="text-yellow-400 mt-1">💡 Tip: Get a component pouch or spellcasting focus</div>}
                    </div>
                  </div>
                );
              }
              
              return null;
            })()}

            {/* [NEW NOV 13, 2025]: Ritual Casting Option */}
            {quickcastSpell.ritual && canRitualCast(quickcastSpell) && (
              <div className="bg-purple-900/30 border border-purple-500 p-4 rounded-lg mb-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={castAsRitual}
                    onChange={(e) => setCastAsRitual(e.target.checked)}
                    className="w-5 h-5 cursor-pointer"
                  />
                  <div className="flex-1">
                    <div className="font-bold text-purple-300">🔮 Cast as Ritual</div>
                    <div className="text-xs text-purple-200 mt-1">
                      Takes 10 minutes longer, but doesn't use a spell slot
                    </div>
                  </div>
                </label>
              </div>
            )}

            {/* Upcast Options (if not cantrip AND not ritual casting) */}
            {quickcastSpell.level > 0 && !castAsRitual && (
              <div className="bg-gray-800 p-4 rounded-lg mb-4">
                <h3 className="font-bold mb-2">Cast at Level:</h3>
                <div className="flex gap-2 flex-wrap">
                  {[...Array(Math.max(0, 10 - (quickcastSpell?.level || 0)))].map((_, i) => {
                    const castLevel = quickcastSpell.level + i;
                    const hasSlot = spellSlots[castLevel] && spellSlots[castLevel].max > spellSlots[castLevel].used;
                    return (
                      <button
                        key={castLevel}
                        onClick={async () => {
                          // [REFACTORED]: Using spellCastingService for unified spell casting
                          const context = {
                            // Component checking
                            checkSpellComponents,
                            consumeSpellComponents,
                            inventory,
                            settings,

                            // Resources
                            spellSlots,
                            setSpellSlots,
                            setInventory,

                            // Currency
                            currencies,
                            setCurrencies,

                            // Character stats
                            playerStats,
                            // [Phase 4b BC-13 follow-up — APR 2026]: castSpell needs
                            //   setPlayerStats so it can decrement _perRestSpellUses
                            //   on a free per-rest cast.
                            setPlayerStats,

                            // Rolling functions
                            rollSpellAttack,
                            getSpellSaveDC,
                            rollDice,

                            // UI
                            showStatus,
                            logActivity,
                            confirm
                          };

                          // [Phase 4b BC-13 follow-up — APR 2026]: Resolve which "free cast"
                          //   path applies (if any) — at-will, per-rest free, or resource-cost.
                          //   Priority: at-will > per-rest > resource-cost > slot. Each is
                          //   independent and only the first matching path consumes its
                          //   resource; the others are skipped.
                          const _spellIsAtWill = effectCalculator.isSpellAtWill(playerStats, inventory, quickcastSpell.name);
                          const _perRestSpells = effectCalculator.getPerRestSpells(playerStats, inventory);
                          const _perRestEntry = _perRestSpells.find(
                            e => e.spellName.toLowerCase() === String(quickcastSpell.name || '').toLowerCase()
                          );
                          const _usePerRestUse = !_spellIsAtWill && _perRestEntry && _perRestEntry.currentUses > 0;
                          // [Phase 4b BC-13 closeout — APR 2026]: Resource-cost cast (Shadow
                          //   Arts ki spells, Eyes of the Dark sorcery-point Darkness).
                          const _resourceCostSpells = effectCalculator.getResourceCostSpells(playerStats, inventory);
                          const _resourceCostEntry = (!_spellIsAtWill && !_usePerRestUse) ? _resourceCostSpells.find(
                            e => e.spellName.toLowerCase() === String(quickcastSpell.name || '').toLowerCase() && e.available
                          ) : null;

                          const result = await spellCastingService.castSpell(
                            quickcastSpell,
                            {
                              castLevel: castLevel,
                              sourceType: 'spellbook',
                              sourceId: null,
                              spellIndex: 0,
                              castAsRitual: castAsRitual, // [NEW NOV 13, 2025]: Pass ritual casting flag
                              // [Phase 4b-CW APR 2026]: Skip slot consumption for at-will spells
                              // (Warlock invocations: beast_speech, eldritch_sight, fiendish_vigor,
                              // mask_of_many_faces, misty_visions; race grants; custom feats)
                              isAtWill: _spellIsAtWill,
                              // [Phase 4b BC-13 follow-up — APR 2026]: Skip slot consumption +
                              //   decrement _perRestSpellUses[spellName] when a free per-rest
                              //   use is available.
                              usePerRestUse: _usePerRestUse,
                              perRestSpellMaxUses: _perRestEntry?.maxUses || 1,
                              // [Phase 4b BC-13 closeout — APR 2026]: Skip slot + spend N
                              //   classResources points (ki / sorcery / etc.) when a
                              //   resource-cost cast is available and affordable.
                              useResourceCost: _resourceCostEntry
                                ? { resource: _resourceCostEntry.resource, cost: _resourceCostEntry.cost }
                                : null,
                            },
                            context
                          );

                          if (result.success) {
                            // [NEW]: If targeting an ally, apply effect to them
                            if (selectedAllyTarget && partyId && applyEffectToAlly) {
                              const effectData = {
                                name: quickcastSpell.name,
                                type: 'spell_effect',
                                duration: quickcastSpell.duration || 0,
                                data: {
                                  spell: quickcastSpell.name,
                                  level: castLevel,
                                  description: quickcastSpell.description,
                                  // For spells like Enhance Ability, add the specific choice
                                  // This would need to be expanded for complex spells
                                  healing: result.results?.damageDetails?.type === 'Healing' ? result.results.damageDetails.total : null,
                                  // [PHASE B / #020 — string-sin batch 2]: Buff
                                  // payload is now data-driven via the spell's
                                  // `appliedEffect` block. Pre-fix this site
                                  // hardcoded `name === 'Enhance Ability'` /
                                  // `name === 'Bless'` / `name === 'Guidance'`.
                                  ...buildAllyBuffPayload(quickcastSpell),
                                }
                              };

                              applyEffectToAlly(selectedAllyTarget.id, effectData);

                              // [C2 FIX]: If healing spell, update ally's party panel HP immediately
                              const healAmount = effectData.data?.healing ?? 0;
                              if (healAmount > 0 && healAllyInParty) {
                                healAllyInParty(selectedAllyTarget.id, healAmount);
                              }

                              showStatus(`✨ ${quickcastSpell.name} cast on ${selectedAllyTarget.name}!${healAmount > 0 ? ` (+${healAmount} HP)` : ''}`, 3000);
                              logActivity(`Cast ${quickcastSpell.name} on ${selectedAllyTarget.name}`, 'spells', {
                                spell: quickcastSpell.name,
                                target: selectedAllyTarget.name,
                                level: castLevel,
                                healing: healAmount > 0 ? healAmount : undefined
                              });
                            }

                            setShowQuickcastDialog(false);
                            setQuickcastSpell(null);
                            setLastSpellRoll(null);
                            setSelectedAllyTarget(null); // Reset target
                            setCastAsRitual(false); // [NEW NOV 13, 2025]: Reset ritual checkbox
                          }
                        }}
                        disabled={castLevel > 0 && !hasSlot}
                        className={`px-3 py-1 rounded ${
                          hasSlot ? 'bg-blue-700 hover:bg-blue-600' : 'bg-gray-700 text-gray-500 cursor-not-allowed'
                        }`}
                      >
                        Level {castLevel}
                        {castLevel > 0 && spellSlots[castLevel] && (
                          <span className="ml-1 text-xs">
                            ({spellSlots[castLevel].max - spellSlots[castLevel].used}/{spellSlots[castLevel].max})
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Attack/Save Info */}
            <div className="bg-gray-800 p-4 rounded-lg mb-4">
              <h3 className="font-bold mb-2">Casting Details:</h3>

              {/* [PHASE 4B STEP 7 SPELL ATTACKS]: DC Input for Inspiration Checks */}
              {quickcastSpell.saveType === 'attack' && (
                <div className="mb-3 pb-3 border-b border-gray-600">
                  <label htmlFor="spellAttackDC" className="text-xs text-gray-400 block mb-1">
                    AC/DC (optional):
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      id="spellAttackDC"
                      type="number"
                      min="1"
                      max="30"
                      value={spellAttackDC}
                      onChange={(e) => setSpellAttackDC(e.target.value)}
                      placeholder="15"
                      className="flex-1 px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:border-purple-500 focus:outline-none"
                    />
                    {spellAttackDC && (
                      <button
                        onClick={() => setSpellAttackDC('')}
                        className="text-xs text-gray-500 hover:text-gray-300 px-2"
                        title="Clear AC/DC"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Set target AC to trigger inspiration prompts on failed attacks
                  </p>
                </div>
              )}

              {quickcastSpell.saveType === 'attack' ? (
                <div>
                  <p className="text-green-400">
                    Spell Attack Bonus: +{getSpellAttackBonus()}
                  </p>
                  <p className="text-sm text-gray-400">Roll 1d20 + {getSpellAttackBonus()} vs target AC</p>
                  
                  {/* Roll Attack Button */}
                  <button
                    onClick={() => {
                      const roll = rollSpellAttack();
                      setLastSpellRoll(roll);
                    }}
                    className="mt-2 bg-green-700 hover:bg-green-600 px-3 py-1 rounded text-sm"
                  >
                    🎲 Roll Attack
                  </button>
                </div>
              ) : quickcastSpell.saveType ? (
                <div>
                  <p className="text-yellow-400">
                    Spell Save DC: {getSpellSaveDC()}
                  </p>
                  <p className="text-sm text-gray-400">Target must make a {quickcastSpell.saveType} saving throw</p>
                </div>
              ) : (
                <p className="text-gray-400">No attack roll or save required</p>
              )}
              
              {/* Display Last Roll Result */}
              {lastSpellRoll && (
                <div className={`mt-3 p-2 rounded ${
                  lastSpellRoll.isCrit ? 'bg-yellow-900 border border-yellow-500' :
                  lastSpellRoll.isFumble ? 'bg-red-900 border border-red-500' :
                  'bg-gray-700'
                }`}>
                  <p className="text-lg font-bold text-center">
                    {lastSpellRoll.text}
                  </p>
                  {lastSpellRoll.isCrit && (
                    <p className="text-sm text-center text-yellow-300 mt-1">Critical Hit! Double damage dice!</p>
                  )}
                  {lastSpellRoll.isFumble && (
                    <p className="text-sm text-center text-red-300 mt-1">Critical Miss!</p>
                  )}
                </div>
              )}
              
              {(quickcastSpell.damage || quickcastSpell.healing) && (
                <div className="mt-2">
                  <p className={quickcastSpell.healing ? "text-green-400" : "text-red-400"}>
                    {quickcastSpell.healing ? 'Healing' : 'Damage'}: {quickcastSpell.damage || quickcastSpell.healing} {quickcastSpell.damageType || ''}
                    {/* [FIX NOV 26, 2025]: Display secondary damage type */}
                    {quickcastSpell.damage2 && ` + ${quickcastSpell.damage2} ${quickcastSpell.damageType2 || ''}`}
                  </p>
                  {quickcastSpell.upcastDamage && (
                    <p className="text-sm text-gray-400">Upcast: {quickcastSpell.upcastDamage} per level above {quickcastSpell.level}</p>
                  )}
                  {/* [G1 REMOVED]: Roll Damage/Healing button removed — the cast result
                      already shows the rolled total in the status bar. The button was
                      orphaned: its output appeared in the faded overlay area below the
                      cast window, was never applied to HP, and confused users about
                      whether healing actually landed. */}
                </div>
              )}
            </div>

            {/* Quick Cast Button for Cantrips */}
            {quickcastSpell.level === 0 && (
              <button
                onClick={() => {
                  // Roll attack or show save DC
                  let rollResult = '';
                  if (quickcastSpell.saveType === 'attack') {
                    // Check for multi-attack spells like Eldritch Blast
                    if (quickcastSpell.multiAttack && quickcastSpell.attacksPerLevel) {
                      const numAttacks = quickcastSpell.attacksPerLevel[Math.min(playerStats.level - 1, 19)] || 1;
                      const attacks = [];
                      for (let i = 0; i < numAttacks; i++) {
                        const attack = rollSpellAttack();
                        attacks.push(attack);
                      }
                      rollResult = ` | ${numAttacks} attacks: ${attacks.map(a => a.total).join(', ')}`;
                      logActivity(`${quickcastSpell.name} Multi-attack`, 'combat', {
                        spell: quickcastSpell.name,
                        attacks: attacks.map(a => ({ roll: a.roll, bonus: a.bonus, total: a.total }))
                      });
                    } else {
                      const attack = rollSpellAttack();
                      rollResult = ` | ${attack.text}`;
                    }
                  } else if (quickcastSpell.saveType) {
                    rollResult = ` | DC ${getSpellSaveDC()} ${quickcastSpell.saveType} save`;
                  }
                  
                  // Roll damage/healing for cantrips if applicable
                  let damageDetails = null;
                  const damageField = quickcastSpell.damage || quickcastSpell.healing;
                  if (damageField) {
                    const damageRoll = rollDice(damageField);
                    // [FIX JAN 13, 2026]: Property-based detection instead of string matching (ARC-014)
                    const isHealing = quickcastSpell.spellType === 'healing' || quickcastSpell.healing;
                    
                    // [FIXED 2025-11-11]: Add spellcasting modifier to healing spells using effectCalculator
                    let healingMod = 0;
                    if (quickcastSpell.healing) {
                      let mod = playerStats.spellcastingModifier || 0;
                      if (!mod && playerStats.spellcastingAbility) {
                        mod = effectCalculator.getEffectiveModifier(
                          playerStats,
                          inventory,
                          activeQuickBonuses,
                          playerStats.spellcastingAbility
                        );
                      }
                      healingMod = mod;
                    }
                    
                    damageDetails = {
                      total: damageRoll.total + healingMod,
                      dice: damageField,
                      rolls: damageRoll.text,
                      diceGroups: damageRoll.diceGroups,
                      modifier: healingMod,
                      type: isHealing ? 'Healing' : (quickcastSpell.damageType || 'Damage')
                    };
                    
                    rollResult = ` | ${isHealing ? '💚' : '💥'} ${damageDetails.total} ${damageDetails.type}`;
                  }
                  
                  // Show attack roll in status for cantrips with attack rolls
                  let attackDetails = null;
                  if (quickcastSpell.saveType === 'attack') {
                    const attack = rollSpellAttack();
                    attackDetails = {
                      d20: attack.roll,
                      bonus: attack.bonus,
                      total: attack.total,
                      quickBonus: attack.quickBonus,
                      breakdown: `d20(${attack.roll}) + ${attack.bonus - attack.quickBonus} base${attack.quickBonusText}`
                    };
                    showStatus(`🎯 Attack Roll: ${attack.total}`, 3000, attackDetails);
                  } else {
                    showStatus(`🎯 ${quickcastSpell.name} cast${rollResult}`, 3000, damageDetails);
                  }
                  logActivity(`Cast ${quickcastSpell.name} (Cantrip)`, 'spells', {
                    spell: quickcastSpell.name,
                    level: 0,
                    school: quickcastSpell.school,
                    damage: damageDetails,
                    attackRoll: attackDetails,
                    saveDC: quickcastSpell.saveType && quickcastSpell.saveType !== 'attack' ? getSpellSaveDC() : null
                  });

                  // [NEW]: If targeting an ally, apply effect to them (cantrip version)
                  if (selectedAllyTarget && partyId && applyEffectToAlly) {
                    const effectData = {
                      name: quickcastSpell.name,
                      type: 'spell_effect',
                      duration: quickcastSpell.duration || 0,
                      data: {
                        spell: quickcastSpell.name,
                        level: 0,
                        description: quickcastSpell.description,
                        healing: damageDetails?.type === 'Healing' ? damageDetails.total : null,
                        // [PHASE B / #020 — string-sin batch 2]: data-driven
                        // via the spell's `appliedEffect` block.
                        ...buildAllyBuffPayload(quickcastSpell),
                      }
                    };

                    applyEffectToAlly(selectedAllyTarget.id, effectData);
                    showStatus(`✨ ${quickcastSpell.name} cast on ${selectedAllyTarget.name}!`, 3000);
                    logActivity(`Cast ${quickcastSpell.name} on ${selectedAllyTarget.name}`, 'spells', {
                      spell: quickcastSpell.name,
                      target: selectedAllyTarget.name,
                      level: 0
                    });
                  }

                  setShowQuickcastDialog(false);
                  setQuickcastSpell(null);
                  setSelectedAllyTarget(null); // Reset target
                }}
                className="w-full bg-purple-700 hover:bg-purple-600 py-3 rounded font-bold"
              >
                Cast {quickcastSpell.name}
              </button>
            )}
        </MobileModal>
      )}

      {/* Modals */}
      <ItemDialog
        mode={itemDialogMode}
        showItemDialog={showItemDialog}
        setShowItemDialog={setShowItemDialog}
        addStarterPack={addStarterPack}
        addNewItem={(itemData) => {
          if (isAddingToShop) {
            // Add item directly to the shop
            if (generatedShop) {
              const shopItem = {
                ...itemData,
                baseValue: itemData.value || 1,
                rarity: itemData.rarity || 'common',
                quantity: 1
              };
              
              setGeneratedShop(prev => ({
                ...prev,
                items: [...prev.items, shopItem]
              }));
              
              // Update active shops
              setActiveShops(prev => prev.map(shop => 
                shop.id === generatedShop.id 
                  ? { ...shop, items: [...shop.items, shopItem] }
                  : shop
              ));
              
              showStatus(`✅ Added ${itemData.name} to ${generatedShop.shopName}`);
            }
            setIsAddingToShop(false);
          } else if (directAddToVault) {
            // Add item directly to the vault
            const targetVault = vaults.find(v => v.id === directAddToVault);
            if (targetVault) {
              const newItem = {
                ...itemData,
                id: Date.now(),
                x: 0,
                y: 0,
                gridId: directAddToVault
              };
              
              // Find first available position in vault
              const vaultCols = (targetVault.gridSize && targetVault.gridSize[0]) || vaultGridSize[0];
              const vaultRows = (targetVault.gridSize && targetVault.gridSize[1]) || vaultGridSize[1];
              let placed = false;
              
              for (let y = 0; y < vaultRows && !placed; y++) {
                for (let x = 0; x < vaultCols && !placed; x++) {
                  if (isValidPosition(directAddToVault, x, y, itemData.width, itemData.height, targetVault.items || [])) {
                    newItem.x = x;
                    newItem.y = y;
                    placed = true;
                  }
                }
              }
              
              if (placed) {
                setVaults(prev => prev.map(v => 
                  v.id === directAddToVault 
                    ? { ...v, items: [...(v.items || []), newItem] }
                    : v
                ));
                showStatus(`✅ Added ${itemData.name} to ${targetVault.name}`);
                setActivityLog(prev => [{
                  timestamp: new Date().toISOString(),
                  action: `Stored ${itemData.name} in ${targetVault.name}`,
                  type: 'item'
                }, ...prev].slice(0, 500));
              } else {
                showStatus(`❌ No space in ${targetVault.name} for ${itemData.name}`);
              }
            }
            setDirectAddToVault(null);
            setShowItemDialog(false);
          } else {
            // Normal add to inventory
            addNewItem(itemData);
            setShowItemDialog(false);
          }
        }}
        BASE_ITEMS={getItemsFromSources() || BASE_ITEMS}
        STARTER_PACKS={STARTER_PACKS}
        getSpellsByClass={getSpellsByClass}
        isAddingToShop={isAddingToShop}
      />
      <VaultDialog 
        showVaultDialog={showVaultDialog}
        setShowVaultDialog={setShowVaultDialog}
        setVaults={setVaults}
      />
      {/* [PHASE 4B - MIGRATED]: EditItemDialog now uses context hooks for settings */}
      <EditItemDialog
        editingItem={editingItem}
        setEditingItem={setEditingItem}
        updateItem={updateItem}
      />
      
      {/* Custom Shop Type Creator Dialog */}
      {/* [UI-MOBILEMODAL-ADOPTION MOBILEMODAL-APPJSX-INLINE — 2026-07-09]: chrome-swap onto
          MobileModal (was a hand-rolled max-w-2xl card, footer-Cancel-only close, no Escape).
          onClose mirrors the footer Cancel; the uncontrolled form + querySelectorAll-on-save
          shape is untouched (body byte-identical). */}
      {/* [PHASE B / #059 — 2026-05-07]: Title swaps to "Edit"
          when editingCustomShopType is set. The defaultValue /
          defaultChecked attributes pre-fill the form from the
          existing entry. The form keeps its uncontrolled +
          querySelectorAll-on-save shape; React's `key` prop
          on the wrapper forces a remount when switching between
          create-new and edit, so the defaultValues actually
          apply (otherwise React keeps the old defaultValue). */}
      {showCustomShopCreator && (
        <MobileModal
          isOpen={true}
          title={editingCustomShopType
            ? `✏️ Edit ${editingCustomShopType.name || 'Shop Type'}`
            : (shopSettings.useSimpleMode ? '✨ Create Simple Shop Type' : '🎲 Create Advanced Shop Type')}
          onClose={() => {
            setShowCustomShopCreator(false);
            setEditingCustomShopType(null);
          }}
          showSaveButton={false}
          overlayClassName="z-[60]"
        >

            <div
              className="space-y-4"
              key={editingCustomShopType ? `edit-${editingCustomShopType.id}` : 'create-new'}
            >
              <div>
                <label className="block text-sm text-gray-300 mb-1">Shop Type Name</label>
                <input
                  type="text"
                  id="custom-shop-name"
                  placeholder="Enchanter's Workshop"
                  defaultValue={editingCustomShopType?.name || ''}
                  className="w-full p-2 bg-gray-700 text-white rounded"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-300 mb-1">Shop Icon</label>
                <select
                  id="custom-shop-icon"
                  className="w-full p-2 bg-gray-700 text-white rounded"
                  defaultValue={editingCustomShopType?.icon || '🏪'}
                >
                  <option value="🏪">🏪 Shop</option>
                  <option value="⚔️">⚔️ Weapons</option>
                  <option value="🎒">🎒 Armor</option>
                  <option value="🔮">🔮 Magic</option>
                  <option value="⚗️">⚗️ Alchemy</option>
                  <option value="📜">📜 Scrolls</option>
                  <option value="💎">💎 Jewelry</option>
                  <option value="🏺">🏺 Potions</option>
                  <option value="📚">📚 Books</option>
                  <option value="🎭">🎭 Costumes</option>
                  <option value="🧙">🧙 Wizard</option>
                  <option value="🏹">🏹 Archery</option>
                  <option value="⛏️">⛏️ Mining</option>
                  <option value="🔨">🔨 Smithy</option>
                  <option value="🍺">🍺 Tavern</option>
                  <option value="🐴">🐴 Stables</option>
                  <option value="⛵">⛵ Maritime</option>
                  <option value="🏕️">🏕️ Camping</option>
                  <option value="🗡️">🗡️ Blades</option>
                  <option value="🦴">🦴 Necromancy</option>
                  <option value="✨">✨ Enchantment</option>
                  <option value="🌟">🌟 Rare</option>
                  <option value="🪙">🪙 Coins</option>
                  <option value="🎒">🎒 Adventure</option>
                  <option value="🧪">🧪 Laboratory</option>
                  <option value="🕯️">🕯️ Occult</option>
                  <option value="🏰">🏰 Royal</option>
                  <option value="🌿">🌿 Herbs</option>
                  <option value="🥾">🥾 Footwear</option>
                  <option value="👗">👗 Clothing</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm text-gray-300 mb-1">Description</label>
                <textarea
                  id="custom-shop-desc"
                  placeholder="A mystical shop specializing in enchanted items and magical components"
                  defaultValue={editingCustomShopType?.description || ''}
                  className="w-full p-2 bg-gray-700 text-white rounded h-20"
                />
              </div>
              
              {/* Simple Mode - Item Categories */}
              {shopSettings.useSimpleMode ? (
                <div>
                  <label className="block text-sm text-gray-300 mb-2">Item Categories (select multiple)</label>
                  <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto p-2 bg-gray-700 rounded">
                    {Object.keys(BASE_ITEMS).map(category => (
                      <label key={category} className="flex items-center text-sm">
                        <input
                          type="checkbox"
                          value={category}
                          defaultChecked={
                            !!(editingCustomShopType?.categories
                              && editingCustomShopType.categories.includes(category))
                          }
                          className="mr-2 custom-shop-category"
                        />
                        {category.replace(/_/g, ' ')}
                      </label>
                    ))}
                  </div>
                </div>
              ) : (
                /* Advanced Mode - Roll Tables by Rarity */
                /* [PHASE B / #056 — 2026-05-07]: Custom tables and DMG
                    tables are now visually segregated — each rarity tier
                    shows "🎲 Your Custom Tables" first (highlighted purple)
                    then "📚 DMG / Sourcebook Tables" beneath. Pre-fix the
                    picker rendered a flat alphabetic mash of MAGIC_ITEM_TABLES
                    + custom tables; the user reported accidentally selecting
                    a DMG table thinking it was their custom "rawr" table,
                    which produced unexpected items in the generated shop
                    (e.g., ring-of-jumping from DMG Table B). Smoke-test
                    2026-05-07. The rarity-tier checkbox className contract
                    (`common-table-select` etc.) is preserved unchanged so
                    the save handler's querySelectorAll calls still work. */
                <div className="space-y-3">
                  <label className="block text-sm text-gray-300 mb-2">Roll Tables by Rarity</label>

                  {[
                    { tier: 'common', label: '🟢 Common Items', color: 'text-green-400' },
                    { tier: 'uncommon', label: '🟡 Uncommon Items', color: 'text-yellow-400' },
                    { tier: 'rare', label: '🔴 Rare Items', color: 'text-red-400' },
                  ].map(({ tier, label: tierLabel, color }) => (
                    <div key={tier} className="bg-gray-700 p-3 rounded">
                      <h4 className={`text-sm font-bold ${color} mb-2`}>{tierLabel}</h4>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {/* [PHASE B / #059 — 2026-05-07]: defaultChecked
                            from editingCustomShopType.commonTables/uncommon/
                            rare. Tier→key mapping: `${tier}Tables`. The
                            inputs stay uncontrolled; the per-render `key`
                            on the wrapper above forces a remount when
                            switching from create→edit so defaultChecked
                            actually applies. */}
                        {customRollTables.length > 0 && (
                          <div className="bg-purple-900 bg-opacity-30 border border-purple-700 rounded p-2">
                            <div className="text-xs font-bold text-purple-300 mb-1">🎲 Your Custom Tables</div>
                            <div className="space-y-1">
                              {customRollTables.map((table) => (
                                <label key={`${tier}-custom-${table.id}`} className="flex items-center text-xs">
                                  <input
                                    type="checkbox"
                                    value={table.id}
                                    defaultChecked={
                                      !!(editingCustomShopType
                                        && Array.isArray(editingCustomShopType[`${tier}Tables`])
                                        && editingCustomShopType[`${tier}Tables`].includes(table.id))
                                    }
                                    className={`mr-2 ${tier}-table-select`}
                                  />
                                  {table.name || table.label}
                                </label>
                              ))}
                            </div>
                          </div>
                        )}
                        <div className="bg-gray-800 bg-opacity-50 border border-gray-600 rounded p-2">
                          <div className="text-xs font-bold text-gray-300 mb-1">📚 DMG / Sourcebook Tables</div>
                          <div className="space-y-1">
                            {Object.entries(MAGIC_ITEM_TABLES).map(([key, table]) => (
                              <label key={`${tier}-dmg-${key}`} className="flex items-center text-xs">
                                <input
                                  type="checkbox"
                                  value={key}
                                  defaultChecked={
                                    !!(editingCustomShopType
                                      && Array.isArray(editingCustomShopType[`${tier}Tables`])
                                      && editingCustomShopType[`${tier}Tables`].includes(key))
                                  }
                                  className={`mr-2 ${tier}-table-select`}
                                />
                                {table.name || table.label}
                              </label>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* [PHASE B / #052 — 2026-05-07]: Roll-table help blurb +
                  Import / Create buttons are Advanced-Mode-only. Simple
                  Mode binds BASE_ITEMS *categories* (weapons, armor,
                  etc.); the roll-table machinery is unused there. Pre-
                  fix the buttons rendered in both modes, leading to the
                  user-reported confusion: "the new table was made, but
                  you cant select it in simple shop - so why are teh
                  buttons even there?" Authors who want to create roll
                  tables outside the shop UI still have the global Roll
                  Tables tab. */}
              {!shopSettings.useSimpleMode && (
                <>
                  <div className="bg-yellow-900 p-3 rounded">
                    <p className="text-sm text-yellow-300">
                      💡 Select which roll tables this shop type will use for each rarity level. The shop will roll on these tables based on shop size.
                    </p>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        const input = document.createElement('input');
                        input.type = 'file';
                        input.accept = '.json';
                        input.onchange = (e) => {
                          const file = e.target.files[0];
                          if (file) {
                            const reader = new FileReader();
                            reader.onload = (event) => {
                              try {
                                const table = JSON.parse(event.target.result);
                                if (table.rolls && table.name) {
                                  const importedTable = {
                                    ...table,
                                    id: `imported_${Date.now()}`
                                  };
                                  // [P1.2+P1.3 fix - Apr 29, 2026]: Removed mutation
                                  // of DMG_LOOT_TABLES. Picker spreads customRollTables
                                  // directly via Object.fromEntries above.
                                  setCustomRollTables(prev => [...prev, importedTable]);
                                  showStatus(`✅ Imported table: ${table.name}`, 3000);
                                }
                              } catch (err) {
                                alert('Invalid table file');
                              }
                            };
                            reader.readAsText(file);
                          }
                        };
                        input.click();
                      }}
                      className="text-xs bg-blue-700 hover:bg-blue-600 px-3 py-1 rounded"
                    >
                      📤 Import Table
                    </button>
                    <button
                      onClick={() => setShowCustomTableCreator(true)}
                      className="text-xs bg-purple-700 hover:bg-purple-600 px-3 py-1 rounded"
                    >
                      🎲 Create New Table
                    </button>
                  </div>
                </>
              )}
            </div>
            
            <div className="flex justify-between gap-2 mt-6">
              {/* [PHASE B / #059 — 2026-05-07]: Delete button visible only
                  in edit mode (editingCustomShopType set). Removes the
                  entry from customShopTypes + SHOP_ROLL_TABLES, then
                  closes the dialog. If the deleted type is the currently-
                  selected shopType, we reset shopSettings.shopType to
                  'general' so the user isn't left pointing at a missing
                  entry. */}
              {editingCustomShopType ? (
                <button
                  onClick={async () => {
                    // [UI-CONFIRM-UNIFICATION 2026-07-04]: styled confirm.
                    const proceed = await confirm({
                      title: 'Delete Shop Type',
                      message: `Delete custom shop type "${editingCustomShopType.name}"? This cannot be undone.`,
                      confirmText: 'Delete',
                      confirmColor: 'red',
                    });
                    if (!proceed) {
                      return;
                    }
                    const deletedId = editingCustomShopType.id;
                    setCustomShopTypes((prev) => prev.filter((s) => s.id !== deletedId));
                    delete SHOP_ROLL_TABLES[deletedId];
                    if (shopSettings.shopType === deletedId) {
                      setShopSettings((prev) => ({ ...prev, shopType: 'general' }));
                    }
                    showStatus(`🗑️ Deleted custom shop type: ${editingCustomShopType.name}`, 3000);
                    setEditingCustomShopType(null);
                    setShowCustomShopCreator(false);
                  }}
                  className="px-4 py-2 bg-red-800 hover:bg-red-700 rounded"
                >
                  🗑️ Delete
                </button>
              ) : (
                <span />
              )}
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setShowCustomShopCreator(false);
                    setEditingCustomShopType(null);
                  }}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    const name = document.getElementById('custom-shop-name').value;
                    const icon = document.getElementById('custom-shop-icon').value || '🏪';
                    const description = document.getElementById('custom-shop-desc').value;

                    if (!name) {
                      alert('Please provide a shop type name');
                      return;
                    }

                    // [PHASE B / #059 — 2026-05-07]: Edit mode replaces in
                    //   place keeping the original id; create mode pushes
                    //   a new entry with a fresh id. The bound shopType in
                    //   shopSettings auto-switches only on create (not edit)
                    //   so the user's currently-selected shop type stays
                    //   stable while they tweak its bindings.
                    const isEditing = !!editingCustomShopType;
                    const id = isEditing ? editingCustomShopType.id : `custom_${Date.now()}`;
                    let newShopType;

                    if (shopSettings.useSimpleMode) {
                      // Simple Mode - Use categories
                      const categories = Array.from(document.querySelectorAll('.custom-shop-category:checked')).map(cb => cb.value);

                      if (categories.length === 0) {
                        alert('Please select at least one category');
                        return;
                      }

                      newShopType = { id, name, icon, description, categories };

                      if (isEditing) {
                        setCustomShopTypes((prev) => prev.map((s) => (s.id === id ? newShopType : s)));
                      } else {
                        setCustomShopTypes((prev) => [...prev, newShopType]);
                      }

                      // Create item pools for the custom shop type
                      const commonItems = [];
                      const uncommonItems = [];
                      const rareItems = [];

                      categories.forEach(cat => {
                        if (BASE_ITEMS[cat]) {
                          Object.keys(BASE_ITEMS[cat]).forEach(itemName => {
                            const item = BASE_ITEMS[cat][itemName];
                            // Distribute items by value
                            if (item.baseValue < 50) {
                              commonItems.push(itemName);
                            } else if (item.baseValue < 500) {
                              uncommonItems.push(itemName);
                            } else {
                              rareItems.push(itemName);
                            }
                          });
                        }
                      });

                      // Add fallback items if pools are empty
                      if (commonItems.length === 0) commonItems.push('torch', 'rations');
                      if (uncommonItems.length === 0) uncommonItems.push('potion_healing');
                      if (rareItems.length === 0) rareItems.push('potion_greater_healing');

                      // Add to SHOP_ROLL_TABLES
                      SHOP_ROLL_TABLES[id] = {
                        common: commonItems,
                        uncommon: uncommonItems,
                        rare: rareItems,
                      };
                    } else {
                      // Advanced Mode - Use roll tables
                      const commonTables = Array.from(document.querySelectorAll('.common-table-select:checked')).map(cb => cb.value);
                      const uncommonTables = Array.from(document.querySelectorAll('.uncommon-table-select:checked')).map(cb => cb.value);
                      const rareTables = Array.from(document.querySelectorAll('.rare-table-select:checked')).map(cb => cb.value);

                      if (commonTables.length === 0 && uncommonTables.length === 0 && rareTables.length === 0) {
                        alert('Please select at least one roll table');
                        return;
                      }

                      newShopType = {
                        id,
                        name,
                        icon,
                        description,
                        commonTables,
                        uncommonTables,
                        rareTables,
                      };

                      if (isEditing) {
                        setCustomShopTypes((prev) => prev.map((s) => (s.id === id ? newShopType : s)));
                      } else {
                        setCustomShopTypes((prev) => [...prev, newShopType]);
                      }

                      // Create a custom generation function for this shop type
                      SHOP_ROLL_TABLES[id] = {
                        customType: true,
                        commonTables,
                        uncommonTables,
                        rareTables,
                      };
                    }

                    showStatus(
                      isEditing
                        ? `✅ Updated custom shop type: ${name}`
                        : `✅ Created custom shop type: ${name}`,
                      3000
                    );
                    setShowCustomShopCreator(false);
                    setEditingCustomShopType(null);

                    // Auto-select the new shop type ONLY when creating
                    if (!isEditing) {
                      setShopSettings(prev => ({ ...prev, shopType: id }));
                    }
                  }}
                  className="px-4 py-2 bg-green-700 hover:bg-green-600 rounded"
                >
                  {editingCustomShopType ? 'Save Changes' : 'Create Shop Type'}
                </button>
              </div>
            </div>
        </MobileModal>
      )}
      
      {/* [BATCH 1.5 S-13]: Custom Roll Table Creator — extracted to state-driven JSX component
          (was ~220 lines of imperative document.createElement / appendChild + getElementById reads).
          The H-7 fix made the textContent path safe, but the architecture was fragile: any future
          maintainer who swapped textContent → innerHTML re-introduces stored XSS via party item names.
          React JSX text auto-escapes, so the equivalent component is XSS-safe by construction. */}
      <CustomRollTableDialog
        open={showCustomTableCreator}
        onClose={() => setShowCustomTableCreator(false)}
        onCreate={(newTable) => {
          // [P1.2+P1.3 fix - Apr 29, 2026]: Removed mutation of DMG_LOOT_TABLES.
          //   Picker reads customRollTables directly.
          // [PHASE B / #049 — 2026-05-07]: Dual-write to BOTH buckets.
          //   Pre-fix, tables created here only landed in customRollTables
          //   (App-local state) and the global Roll Tables tab (which
          //   reads `rollTables` from CharacterContext) never saw them.
          //   The dialog now produces a unified shape with both `rolls`
          //   (shop generator) and `entries` (RollTablesTab + rollOnTable),
          //   so the same table object surfaces in both views. Smoke-test
          //   report 2026-05-07.
          setCustomRollTables((prev) => [...prev, newTable]);
          setRollTables((prev) => [...(Array.isArray(prev) ? prev : []), newTable]);
          showStatus(`✅ Created custom roll table: ${newTable.name}`, 3000);
        }}
        inventory={inventory}
        baseItems={BASE_ITEMS}
      />

      {/* [PHASE B / #057 — 2026-05-07]: Edit Shop dialog — lets the user
          change a generated shop's name / shopkeeper / markup / size,
          and re-roll its items in place using the shop's existing type
          binding. Smoke-test 2026-05-07: "cant edit shop after its
          made; I cant change the type of tables or item rolls after." */}
      <EditShopDialog
        open={showEditShopDialog}
        shop={editingShop}
        customShopTypes={customShopTypes}
        onClose={() => {
          setShowEditShopDialog(false);
          setEditingShop(null);
        }}
        onSave={(updated) => {
          saveEditedShop(updated);
          setShowEditShopDialog(false);
          setEditingShop(null);
        }}
        onReroll={(updated) => {
          regenerateShopItems(updated);
          // Keep the dialog open so the user can verify the re-roll
          // before closing — they may want to re-roll multiple times.
          setEditingShop({ ...updated });
        }}
        onEditType={(targetType) => {
          // [PHASE B / #059 — 2026-05-07]: Cascade — close the Edit Shop
          //   dialog, open the Custom Shop Type Creator in edit mode
          //   pre-filled with the target type. useSimpleMode derives
          //   from whether the type has `categories` (simple) vs
          //   `commonTables` etc. (advanced).
          setShowEditShopDialog(false);
          setEditingShop(null);
          setEditingCustomShopType(targetType);
          setShopSettings((prev) => ({
            ...prev,
            useSimpleMode: !!(targetType && targetType.categories),
          }));
          setShowCustomShopCreator(true);
        }}
      />
      
      <ContainerModal 
        openContainer={openContainer}
        setOpenContainer={setOpenContainer}
        showVaultModal={showVaultModal}
        inventory={inventory}
        gridCols={gridCols}
        gridRows={gridRows}
        draggedItem={draggedItem}
        hoveredGrid={hoveredGrid}
        hoveredCell={hoveredCell}
        selectedItem={selectedItem}
        setSelectedItem={setSelectedItem}
        selectedItemSource={selectedItemSource}
        setSelectedItemSource={setSelectedItemSource}
        moveItem={moveItemSimple}
        isValidPosition={isValidPosition}
        handleMouseDown={handleMouseDown}
        handleContextMenu={handleContextMenu}
        handleCellClick={handleCellClick}
        handleTouchMove={handleTouchMove}
        showStatus={showStatus}
        setEditingItem={setEditingItem}
        settings={settings}
        InventoryItem={InventoryItem}
      />
      <VaultModal
        showVaultModal={showVaultModal}
        setShowVaultModal={setShowVaultModal}
        vaults={vaults}
        vaultGridSize={vaultGridSize}
        setEditingVault={setEditingVault}
        gridCols={gridCols}
        gridRows={gridRows}
        inventory={inventory}
        draggedItem={draggedItem}
        hoveredGrid={hoveredGrid}
        hoveredCell={hoveredCell}
        selectedItem={selectedItem}
        setSelectedItem={setSelectedItem}
        selectedItemSource={selectedItemSource}
        setSelectedItemSource={setSelectedItemSource}
        isValidPosition={isValidPosition}
        handleMouseDown={handleMouseDown}
        handleContextMenu={handleContextMenu}
        showStatus={showStatus}
        setOpenContainer={setOpenContainer}
        setEditingItem={setEditingItem}
        settings={settings}
        InventoryItem={InventoryItem}
        setShowItemDialog={setShowItemDialog}
        setDirectAddToVault={setDirectAddToVault}
      />
      <VaultEditDialog
        editingVault={editingVault}
        setEditingVault={setEditingVault}
        vaults={vaults}
        setVaults={setVaults}
        vaultGridSize={vaultGridSize}
      />

      {/* [NEW]: Shared Party Vault Modal */}
      {/* [ARCHITECTURE]: SharedVaultModal uses context hooks following Phase 4B pattern */}
      <SharedVaultModal
        isOpen={showSharedVault}
        onClose={() => setShowSharedVault(false)}
        vaultItems={sharedVaultItems}
        onAddItem={addToSharedVault}
        onTakeItem={async (itemId) => {
                  const item = await takeFromSharedVault(itemId);
                  if (item) {
                    addItem(item);
                    showStatus(`✅ ${item.name} added to your inventory`);
                  }
                }}
        onRemoveItem={removeFromSharedVault}
        isDM={isDM}
      />

      {/* [PHASE 4B STEP 7 - 2025-10-25]: ContextMenuComponent gets all context values from useInventory */}
      {/* [UX-22 - NOV 16, 2025]: Added readScrollSpell for casting spells from scrolls */}
      {/* [Spec B Task 13 — 2026-05-16, lifted 2026-05-16 evening per Rule 11]:
            castStoredSpell + spellStoringItemBoundIds wired so the context menu
            surfaces "Cast Stored Spell" only on items currently bound via the
            spell_storing_item longRestPicker. ANY class (Artificer SRD or
            CW custom-class) that declares this picker participates — derivation
            scans all classActiveSelections, not a literal class string. */}
      <ContextMenuComponent
        drinkPotion={drinkPotion}
        addToSharedVault={handleAddToSharedVault}
        hasParty={!!partyId && Object.keys(partyMembers).length > 0}
        openSpellCopyDialog={openSpellCopyDialog}
        readScrollSpell={readScrollSpell}
        castStoredSpell={(boundItemId) => {
          // Lazy import to avoid circular dep issues.
          // eslint-disable-next-line global-require
          const { castStoredSpell } = require('./services/spellCastingService');
          const result = castStoredSpell(playerStats, inventory, boundItemId, {
            setPlayerStats,
            logActivity,
          });
          if (typeof showStatus === 'function') {
            showStatus(result.message);
          }
          return result;
        }}
        spellStoringItemBoundIds={Object.values(playerStats?.classActiveSelections || {})
          .flatMap(cs => Array.isArray(cs?.spell_storing_item) ? cs.spell_storing_item : [])
          .map(sel => sel?.boundItemId)
          .filter(Boolean)}
      />
      
      {/* Spell Selection Dialog */}
      {showSpellSelection && (
        <Suspense fallback={<div className="fixed inset-0 bg-black/50 flex items-center justify-center" style={{ zIndex: Z.MODAL }}><div className="bg-gray-900 p-8 rounded-lg">Loading Spells...</div></div>}>
          <SpellSelectionDialog
            onClose={() => {
              setShowSpellSelection(false);
              window.selectingSpellTemplate = false;
            }}
            onAddSpell={(spell) => {
            // If selecting a template, open custom creator with the spell
            if (window.selectingSpellTemplate) {
              setTemplateSpell(spell);
              setShowSpellSelection(false);
              setShowCustomSpellCreator(true);
              window.selectingSpellTemplate = false;
            } else {
              // Normal spell addition - track which class this spell belongs to
              const classToAdd = activeSpellbookClass === 'all' ? primaryClass : activeSpellbookClass;
              setKnownSpells(prev => [...prev, {
                ...spell,
                id: Date.now() + Math.random(),
                classSource: classToAdd // Track which class this spell belongs to
              }]);
              logActivity(`Added ${spell.name} to ${classToAdd} spellbook`, 'spells', {
                spell: spell.name,
                level: spell.level,
                school: spell.school,
                class: classToAdd
              });
              showStatus(`✨ Added ${spell.name} to ${classToAdd} spellbook!`);
            }
          }}
          playerClass={(() => {
            if (window.selectingSpellTemplate) return '';
            if (!settings.classDependentSpellbook) return '';
            const classToUse = activeSpellbookClass === 'all' ? primaryClass : activeSpellbookClass;
            return classToUse;
          })()}
          playerLevel={playerStats.level}
          knownSpells={knownSpells}
          isWizard={usesSpellbook(primaryClass)} // [FIX APR 2026 — AUDIT H-4]: Data-driven spellbook check for custom classes
          playerStats={playerStats} // [FORK-E 2026-05-31]: custom class spellList curation
        />
        </Suspense>
      )}

      {/* [AUDIT 2026-05-17 #48]: Monster Editor canonical mount — was in Combat
          tab, moved here so Content Workshop is the only authoring home. Combat
          tab is now a pure consumer (picks from playerStats.monsterCatalog). */}
      {showMonsterEditor && (
        <Suspense fallback={<div className="fixed inset-0 bg-black/50 flex items-center justify-center" style={{ zIndex: Z.MODAL }}><div className="bg-gray-900 p-8 rounded-lg">Loading Monster Editor...</div></div>}>
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center" style={{ zIndex: Z.MODAL }}>
            <div className="bg-gray-900 rounded-lg shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
              <MonsterEditor
                initialMonster={editingMonster}
                images={playerStats?.imageLibrary?.images || []}
                onSave={(newMonster) => {
                  setPlayerStats((prev) => ({
                    ...prev,
                    monsterCatalog: {
                      ...(prev?.monsterCatalog || {}),
                      [newMonster.id]: newMonster,
                    },
                  }));
                  setShowMonsterEditor(false);
                  setEditingMonster(null);
                  logActivity(`Saved monster: ${newMonster.name}`, 'monster', { monsterId: newMonster.id });
                  showStatus(`✅ Saved monster: ${newMonster.name}`, 3000);
                }}
                onCancel={() => {
                  setShowMonsterEditor(false);
                  setEditingMonster(null);
                }}
              />
            </div>
          </div>
        </Suspense>
      )}

      {/* [STONE 9 / #62 + #81 — 2026-05-18]: View+Edit+Template trio drawers.
          Load drawer = custom monsters only (edit existing). Template drawer
          = SRD + custom (fork into new draft). View drawer = SRD + custom
          (read-only sheet modal). Picks route through the same
          showMonsterEditor / viewingMonster state pair used by the canonical
          editor mount above. */}
      {(loadMonsterDrawerOpen || viewMonsterDrawerOpen || templateMonsterDrawerOpen) && (
        <Suspense fallback={null}>
          <MonsterTrioDrawers
            playerStats={playerStats}
            loadOpen={loadMonsterDrawerOpen}
            viewOpen={viewMonsterDrawerOpen}
            templateOpen={templateMonsterDrawerOpen}
            onCloseLoad={() => setLoadMonsterDrawerOpen(false)}
            onCloseView={() => setViewMonsterDrawerOpen(false)}
            onCloseTemplate={() => setTemplateMonsterDrawerOpen(false)}
            onPickEdit={(monster) => {
              setLoadMonsterDrawerOpen(false);
              setEditingMonster(monster);
              setShowMonsterEditor(true);
            }}
            onPickTemplate={(clonedMonster) => {
              setTemplateMonsterDrawerOpen(false);
              setEditingMonster(clonedMonster);
              setShowMonsterEditor(true);
            }}
            onPickView={(monster) => {
              setViewMonsterDrawerOpen(false);
              setViewingMonster(monster);
            }}
            onDelete={(m) => deleteCustomContent(m, 'Monster', (prev) => removeCustomMonster(prev, m.id))}
            logActivity={logActivity}
          />
        </Suspense>
      )}

      {/* [STONE 9 / #81 — 2026-05-18]: Read-only MonsterSheet modal. The
          Use-as-Template fork button is wired so a DM can browse → view →
          decide-to-fork without re-opening the template drawer. */}
      {viewingMonster && (
        <Suspense fallback={null}>
          <MonsterViewModal
            monster={viewingMonster}
            onClose={() => setViewingMonster(null)}
            onForkToTemplate={(clonedMonster) => {
              setViewingMonster(null);
              setEditingMonster(clonedMonster);
              setShowMonsterEditor(true);
            }}
            logActivity={logActivity}
          />
        </Suspense>
      )}

      {/* [SUB-PLAN 1E — 2026-05-24]: Item trio drawers + view modal. */}
      {(loadItemDrawerOpen || viewItemDrawerOpen || templateItemDrawerOpen) && (
        <Suspense fallback={null}>
          <ItemTrioDrawers
            inventory={inventory}
            loadOpen={loadItemDrawerOpen}
            viewOpen={viewItemDrawerOpen}
            templateOpen={templateItemDrawerOpen}
            onCloseLoad={() => setLoadItemDrawerOpen(false)}
            onCloseView={() => setViewItemDrawerOpen(false)}
            onCloseTemplate={() => setTemplateItemDrawerOpen(false)}
            onPickEdit={(item) => {
              setLoadItemDrawerOpen(false);
              setEditingItem(item);
            }}
            onPickTemplate={(clonedItem) => {
              setTemplateItemDrawerOpen(false);
              setEditingItem(clonedItem);
            }}
            onPickView={(item) => {
              setViewItemDrawerOpen(false);
              setViewingItem(item);
            }}
            logActivity={logActivity}
          />
        </Suspense>
      )}
      {viewingItem && (
        <Suspense fallback={null}>
          <ItemViewModal
            item={viewingItem}
            onClose={() => setViewingItem(null)}
            onForkToTemplate={(clonedItem) => {
              setViewingItem(null);
              setEditingItem(clonedItem);
            }}
            logActivity={logActivity}
          />
        </Suspense>
      )}

      {/* [SUB-PLAN 1E — 2026-05-24]: Feat trio drawers + view modal. */}
      {(loadFeatDrawerOpen || viewFeatDrawerOpen || templateFeatDrawerOpen) && (
        <Suspense fallback={null}>
          <FeatTrioDrawers
            playerStats={playerStats}
            loadOpen={loadFeatDrawerOpen}
            viewOpen={viewFeatDrawerOpen}
            templateOpen={templateFeatDrawerOpen}
            onCloseLoad={() => setLoadFeatDrawerOpen(false)}
            onCloseView={() => setViewFeatDrawerOpen(false)}
            onCloseTemplate={() => setTemplateFeatDrawerOpen(false)}
            onPickEdit={(feat) => {
              setLoadFeatDrawerOpen(false);
              setEditingCustomFeat(feat);
              setShowCustomFeatCreator(true);
            }}
            onPickTemplate={(clonedFeat) => {
              setTemplateFeatDrawerOpen(false);
              setEditingCustomFeat(clonedFeat);
              setShowCustomFeatCreator(true);
            }}
            onPickView={(feat) => {
              setViewFeatDrawerOpen(false);
              setViewingFeat(feat);
            }}
            onDelete={(f) => deleteCustomContent(f, 'Feat', (prev) => removeCustomFeatEverywhere(prev, f.id))}
            logActivity={logActivity}
          />
        </Suspense>
      )}
      {viewingFeat && (
        <Suspense fallback={null}>
          <FeatViewModal
            feat={viewingFeat}
            onClose={() => setViewingFeat(null)}
            onForkToTemplate={(clonedFeat) => {
              setViewingFeat(null);
              setEditingCustomFeat(clonedFeat);
              setShowCustomFeatCreator(true);
            }}
            logActivity={logActivity}
          />
        </Suspense>
      )}

      {/* [SUB-PLAN 1E — 2026-05-24]: Spell trio drawers + view modal. */}
      {(loadSpellDrawerOpen || viewSpellDrawerOpen || templateSpellDrawerOpen) && (
        <Suspense fallback={null}>
          <SpellTrioDrawers
            playerStats={playerStats}
            knownSpells={knownSpells}
            onDelete={(spell) => (spell._legacy
              ? deleteLegacyKnownSpell(spell)
              : deleteSourceContent(spell, 'Spell', removeSpellFromSource))}
            loadOpen={loadSpellDrawerOpen}
            viewOpen={viewSpellDrawerOpen}
            templateOpen={templateSpellDrawerOpen}
            onCloseLoad={() => setLoadSpellDrawerOpen(false)}
            onCloseView={() => setViewSpellDrawerOpen(false)}
            onCloseTemplate={() => setTemplateSpellDrawerOpen(false)}
            onPickEdit={(spell) => {
              setLoadSpellDrawerOpen(false);
              setEditingSpell(spell);
              setTemplateSpell(null);
              setShowCustomSpellCreator(true);
            }}
            onPickTemplate={(clonedSpell) => {
              setTemplateSpellDrawerOpen(false);
              setTemplateSpell(clonedSpell);
              setEditingSpell(null);
              setShowCustomSpellCreator(true);
            }}
            onPickView={(spell) => {
              setViewSpellDrawerOpen(false);
              setViewingSpell(spell);
            }}
            logActivity={logActivity}
          />
        </Suspense>
      )}
      {viewingSpell && (
        <Suspense fallback={null}>
          <SpellViewModal
            spell={viewingSpell}
            onClose={() => setViewingSpell(null)}
            onForkToTemplate={(clonedSpell) => {
              setViewingSpell(null);
              setTemplateSpell(clonedSpell);
              setEditingSpell(null);
              setShowCustomSpellCreator(true);
            }}
            logActivity={logActivity}
          />
        </Suspense>
      )}

      {/* Custom Spell Creator */}
      {showCustomSpellCreator && (
        <Suspense fallback={<div className="fixed inset-0 bg-black/50 flex items-center justify-center" style={{ zIndex: Z.MODAL }}><div className="bg-gray-900 p-8 rounded-lg">Loading Spell Creator...</div></div>}>
          <CustomSpellCreator
            onClose={() => {
              setShowCustomSpellCreator(false);
              setTemplateSpell(null);
              setEditingSpell(null); // [NEW]: Clear editing spell on close
            }}
            templateSpell={editingSpell || templateSpell} // [NEW]: Use editingSpell if editing, otherwise templateSpell
            isEditing={!!editingSpell} // [NEW]: Pass editing flag
          onSelectTemplate={() => {
            setShowCustomSpellCreator(false);
            setShowSpellSelection(true);
            // Set a flag to indicate we're selecting a template
            window.selectingSpellTemplate = true;
          }}
          customComponents={customComponents}
          addCustomComponent={addCustomComponent}
          removeCustomComponent={removeCustomComponent}
          getAllComponents={getAllComponents}
          onSave={(spell) => {
            const classToAdd = activeSpellbookClass === 'all' ? primaryClass : activeSpellbookClass;

            // [NEW]: Handle both create and edit
            if (editingSpell) {
              // Update existing spell
              setKnownSpells(prev => prev.map(s =>
                s.id === editingSpell.id
                  ? { ...spell, id: editingSpell.id, classSource: editingSpell.classSource }
                  : s
              ));
              logActivity(`Updated custom spell: ${spell.name}`, 'spells', {
                spell: spell.name,
                level: spell.level,
                school: spell.school
              });
              showStatus(`✏️ Updated custom spell: ${spell.name}!`);
            } else {
              // Create new spell
              setKnownSpells(prev => [...prev, {
                ...spell,
                id: Date.now() + Math.random(),
                classSource: classToAdd
              }]);
              logActivity(`Created custom spell: ${spell.name} for ${classToAdd}`, 'spells', {
                spell: spell.name,
                level: spell.level,
                school: spell.school,
                class: classToAdd
              });
              showStatus(`✨ Created custom spell: ${spell.name} for ${classToAdd}!`);
            }

            trackFeatureEvent(buildContentAuthoredEvent('spell', { isEdit: !!editingSpell }));

            // [FIX Issue 9]: Add custom components to inventory if requested
            if (spell.componentsToAddToInventory && spell.componentsToAddToInventory.length > 0) {
              spell.componentsToAddToInventory.forEach(comp => {
                const newItem = {
                  ...comp,
                  id: Date.now() + Math.random(),
                  quantity: comp.quantity || 1
                };
                setInventory(prev => [...prev, newItem]);
                systemBus.emit(EVENTS.INVENTORY_ITEM_ADDED, {
                  item: newItem,
                  quantity: newItem.quantity,
                  source: 'spell_component'
                });
                logActivity(`Added spell component to inventory: ${comp.name}`, 'inventory', {
                  itemName: comp.name,
                  spell: spell.name
                });
              });
              showStatus(`📦 Added ${spell.componentsToAddToInventory.length} component(s) to inventory!`);
            }

            setShowCustomSpellCreator(false);
            setTemplateSpell(null);
            setEditingSpell(null); // [NEW]: Clear editing spell
          }}
        />
        </Suspense>
      )}

      {/* [2026-05-25 refactor]: Inventory "Add Item" and Workshop's
          "Create Item" both open the canonical ItemDialog (mounted at
          the bottom of this render). Workshop passes mode='create',
          Inventory passes mode='find'. No parallel dialog. */}

      {/* [2026-05-25 items 6+7 refactor]: CustomItemCreator mount removed.
          Workshop "Create Item" now opens the canonical ItemDialog mount
          below (same mount Inventory's pre-refactor "Add Item" used). */}

      {/* [STONE 6 CLOSURE / #67 Option B — 2026-05-18]: Custom Feat Creator
          (Workshop-hosted). The creator builds featData; this handler writes
          to playerStats.customFeats so the existing FeatsPanel useEffect
          (which re-registers effectRegistry entries off customFeats) picks
          it up automatically. Feat is also surfaced in the Add Feat picker
          via FeatsPanel's existing customFeats consumption — no extra wiring. */}
      {showCustomFeatCreator && (
        <Suspense
          fallback={
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center" style={{ zIndex: Z.MODAL }}>
              <div className="bg-gray-900 p-8 rounded-lg text-white">Loading Feat Creator…</div>
            </div>
          }
        >
          <CustomFeatCreator
            templateFeat={editingCustomFeat}
            isEditing={!!editingCustomFeat}
            onClose={() => {
              setShowCustomFeatCreator(false);
              setEditingCustomFeat(null);
            }}
            onSave={(featData) => {
              setPlayerStats(prev => {
                const list = prev.customFeats || [];
                const idx = list.findIndex(f => f.id === featData.id);
                const nextList = idx >= 0
                  ? list.map(f => f.id === featData.id ? featData : f)
                  : [...list, featData];
                return { ...prev, customFeats: nextList };
              });
              trackFeatureEvent(buildContentAuthoredEvent('feat', { isEdit: !!editingCustomFeat }));
              setShowCustomFeatCreator(false);
              setEditingCustomFeat(null);
              showStatus(`✅ Saved custom feat: ${featData.name}`, 3000);
            }}
          />
        </Suspense>
      )}

      {/* [SUB-PLAN 3B — 2026-05-29]: Custom Condition Creator (Workshop-delegated).
          Upserts playerStats.customConditions (create + edit by id, like the feat
          creator onSave above). ConditionsTracker.getAllConditions (buildConditionsMap)
          + composerCatalogRegistry 'condition' kind read this slot automatically —
          no further wiring needed (verified in A's exec findings cc1). */}
      {showCustomConditionCreator && (
        <Suspense
          fallback={
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center" style={{ zIndex: Z.MODAL }}>
              <div className="bg-gray-900 p-8 rounded-lg text-white">Loading Condition Creator…</div>
            </div>
          }
        >
          <CustomConditionCreator
            isOpen={true}
            templateCondition={editingCustomCondition}
            onClose={() => {
              setShowCustomConditionCreator(false);
              setEditingCustomCondition(null);
            }}
            onSave={(condData) => {
              setPlayerStats(prev => {
                const list = prev.customConditions || [];
                const idx = list.findIndex(c => c.id === condData.id);
                const nextList = idx >= 0
                  ? list.map(c => c.id === condData.id ? condData : c)
                  : [...list, condData];
                return { ...prev, customConditions: nextList };
              });
              trackFeatureEvent(buildContentAuthoredEvent('condition', { isEdit: !!editingCustomCondition }));
              setShowCustomConditionCreator(false);
              setEditingCustomCondition(null);
              showStatus(`✅ Saved custom condition: ${condData.name}`, 3000);
            }}
          />
        </Suspense>
      )}

      {/* [SUB-PLAN 3A — 2026-05-30]: Custom Boon Creator — App.jsx-delegated
          (writes playerStats.customBoons via setPlayerStats). Custom boons register
          passiveEffects automatically via useBoonEffects (always-mounted). */}
      {showCustomBoonCreator && (
        <Suspense
          fallback={
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center" style={{ zIndex: Z.MODAL }}>
              <div className="bg-gray-900 p-8 rounded-lg text-white">Loading Boon Creator…</div>
            </div>
          }
        >
          <CustomBoonCreator
            isOpen={true}
            templateBoon={editingCustomBoon}
            onClose={() => {
              setShowCustomBoonCreator(false);
              setEditingCustomBoon(null);
            }}
            onSave={(boonData) => {
              setPlayerStats(prev => {
                const list = prev.customBoons || [];
                const idx = list.findIndex(b => b.id === boonData.id);
                const nextList = idx >= 0
                  ? list.map(b => b.id === boonData.id ? boonData : b)
                  : [...list, boonData];
                return { ...prev, customBoons: nextList };
              });
              trackFeatureEvent(buildContentAuthoredEvent('boon', { isEdit: !!editingCustomBoon }));
              setShowCustomBoonCreator(false);
              setEditingCustomBoon(null);
              showStatus(`✅ Saved custom boon: ${boonData.name}`, 3000);
            }}
          />
        </Suspense>
      )}

      {/* [SUB-PLAN 3C — 2026-05-30]: Custom Background Creator — App.jsx-delegated
          (writes playerStats.customBackgrounds via setPlayerStats). Branch A narrative-only:
          saves { id, name, featureName, featureDescription, personalityTraits, bonds,
          ideals, flaws, isCustom }. getCatalog('background', playerStats) in
          composerCatalogRegistry merges custom + SRD automatically; getBackgroundById()
          resolves the saved entry for CharacterBasicsPanel + CharacterCreationWizard. */}
      {showCustomBackgroundCreator && (
        <Suspense
          fallback={
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center" style={{ zIndex: Z.MODAL }}>
              <div className="bg-gray-900 p-8 rounded-lg text-white">Loading Background Creator…</div>
            </div>
          }
        >
          <CustomBackgroundCreator
            isOpen={true}
            templateBackground={editingCustomBackground}
            onClose={() => {
              setShowCustomBackgroundCreator(false);
              setEditingCustomBackground(null);
            }}
            onSave={(bgData) => {
              setPlayerStats(prev => {
                const list = prev.customBackgrounds || [];
                const idx = list.findIndex(b => b.id === bgData.id);
                const nextList = idx >= 0
                  ? list.map(b => b.id === bgData.id ? bgData : b)
                  : [...list, bgData];
                return { ...prev, customBackgrounds: nextList };
              });
              trackFeatureEvent(buildContentAuthoredEvent('background', { isEdit: !!editingCustomBackground }));
              setShowCustomBackgroundCreator(false);
              setEditingCustomBackground(null);
              showStatus(`✅ Saved custom background: ${bgData.name}`, 3000);
            }}
          />
        </Suspense>
      )}

      {/* [NEW 2025-10-12]: Spell Copy Dialog - Copy spells from scrolls/spellbooks */}
      {showSpellCopyDialog && spellCopyData && (
        <Suspense fallback={<div className="fixed inset-0 bg-black/50 flex items-center justify-center" style={{ zIndex: Z.MODAL }}><div className="bg-gray-900 p-8 rounded-lg">Loading...</div></div>}>
          {/* [PHASE 4B - MIGRATED]: SpellCopyDialog now uses context hooks */}
          <SpellCopyDialog
            isOpen={showSpellCopyDialog}
            onClose={() => {
              setShowSpellCopyDialog(false);
              setSpellCopyData(null);
            }}
            sourceItem={spellCopyData.sourceItem}
            spellToCopy={spellCopyData.spell}
            targetSpellbook={spellCopyData.targetSpellbook}
            onCopyComplete={handleSpellCopy}
          />
        </Suspense>
      )}

      {/* [NEW 2025-10-12]: Scroll Scribe Dialog - Craft spell scrolls from prepared spells */}
      {showScrollScribeDialog && (
        <Suspense fallback={<div className="fixed inset-0 bg-black/50 flex items-center justify-center" style={{ zIndex: Z.MODAL }}><div className="bg-gray-900 p-8 rounded-lg">Loading...</div></div>}>
          {/* [PHASE 4B - MIGRATED]: ScrollScribeDialog now uses context hooks */}
          <ScrollScribeDialog
            isOpen={showScrollScribeDialog}
            onClose={() => setShowScrollScribeDialog(false)}
            onScribeComplete={handleScrollScribe}
          />
        </Suspense>
      )}

      {/* [APPJSX-MULTICLASS-DEAD — ruled DELETE 2026-07-09]: the Add Multiclass dialog
          lived here; it was a dead UI path (its only opener was an uncalled openDialog
          registry case). Live flow: MulticlassManager's Add-Class picker. Recoverable
          from git (last live @ 0a22ace1).*/}

      {/* Character Creation Wizard Modal */}
      {showCharacterWizard && (
        <Suspense fallback={<div className="fixed inset-0 bg-black/50 flex items-center justify-center" style={{ zIndex: Z.MODAL }}><div className="bg-gray-900 p-8 rounded-lg">Loading Character Creator...</div></div>}>
          {/* [PHASE 4B - MIGRATED]: CharacterCreationWizard now uses context hooks */}
          {/* [FEB 2026]: guidedCreation prop enables tutorial spotlight overlay */}
          <CharacterCreationWizard
            onClose={() => { setShowCharacterWizard(false); setGuidedCreation(false); }}
            onComplete={handleCharacterWizardComplete}
            guidedCreation={guidedCreation}
          />
        </Suspense>
      )}

      {/* [ROSTER GALLERY]: Rename dialog opens from the ⋮ menu on Roster cards */}
      {renameCharacterId && (
        <Suspense fallback={null}>
          <RosterRenameDialog
            isOpen={true}
            currentName={savedCharacters.find(c => c.id === renameCharacterId)?.name || ''}
            onSave={(newName) => { renameCharacter(renameCharacterId, newName); setRenameCharacterId(null); }}
            onClose={() => setRenameCharacterId(null)}
          />
        </Suspense>
      )}

      {/* [2026-05-11]: Character Management Drawer (slide-out from right) */}
      {rosterDrawerOpen && (
        <div
          className="fixed inset-0 z-50 flex justify-end"
          onClick={() => setRosterDrawerOpen(false)}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/60" />
          {/* Drawer panel */}
          <div
            className="relative w-full max-w-xl h-full bg-gray-900 shadow-2xl overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-gray-900 border-b border-gray-700 px-4 py-3 flex items-center justify-between">
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                <Users size={20} className="text-amber-400" />
                Manage Characters
              </h2>
              <button
                onClick={() => setRosterDrawerOpen(false)}
                className="p-1 hover:bg-gray-800 rounded text-gray-400 hover:text-white"
                aria-label="Close drawer"
              >
                <X size={20} />
              </button>
            </div>
            <Suspense fallback={<div className="p-6 text-gray-400">Loading roster…</div>}>
              <RosterTab
                savedCharacters={savedCharacters}
                activeCharacterId={playerStats?.id}
                setActiveTab={setActiveTab}
                onSwap={(id) => { loadCharacter(id); setRosterDrawerOpen(false); }}
                onNewCharacter={() => { setShowCharacterWizard(true); setRosterDrawerOpen(false); }}
                onRename={(id) => { setRenameCharacterId(id); }}
                onDuplicate={(id) => duplicateCharacter(id)}
                onExport={(id) => exportCharacter(id, 'json')}
                onArchive={(id) => archiveCharacter(id)}
                onUnarchive={(id) => unarchiveCharacter(id)}
                onDelete={(id) => deleteCharacter(id)}
              />
            </Suspense>
          </div>
        </div>
      )}

      {/* Level Up Wizard Modal */}
      {showLevelUpWizard && (
        <Suspense fallback={<div className="fixed inset-0 bg-black/50 flex items-center justify-center" style={{ zIndex: Z.MODAL }}><div className="bg-gray-900 p-8 rounded-lg">Loading Level Up Wizard...</div></div>}>
          {/* [PHASE 4B - MIGRATED]: LevelUpWizard now uses context hooks */}
          <LevelUpWizard
            onClose={() => setShowLevelUpWizard(false)}
            onComplete={handleLevelUpComplete}
          />
        </Suspense>
      )}

      {/* [GOD-TIER UX]: Keyboard Shortcuts Help Modal */}
      <KeyboardShortcutsHelp
        isOpen={showKeyboardHelp}
        onClose={() => setShowKeyboardHelp(false)}
      />

      {/* [GOD-TIER UX]: Epic Level-Up Confetti Celebration */}
      <LevelUpConfetti
        show={showConfetti}
        characterLevel={levelUpLevel}
        onComplete={() => setShowConfetti(false)}
      />

      {/* [NEW - Week 2, Day 10]: Inspiration Reroll Prompt Dialog */}
      {inspirationPrompt && (
        <Suspense fallback={null}>
          <InspirationPromptDialog
            rollData={inspirationPrompt}
            onUseInspiration={handleUseInspiration}
            onDecline={handleDeclineInspiration}
            inspirationCount={playerStats.inspiration || 0}
          />
        </Suspense>
      )}

      {/* [S10]: In-app Review / Feedback Prompt — fires once per install */}
      {reviewPrompt.shouldShow && (
        <Suspense fallback={null}>
          <ReviewPromptDialog
            isOpen={reviewPrompt.shouldShow}
            onClose={reviewPrompt.dismiss}
            onPositive={reviewPrompt.recordPositive}
            onNegative={reviewPrompt.recordNegative}
          />
        </Suspense>
      )}

      {/* [S11]: Post-purchase DM tour offer — shown once after first premium recognition */}
      {showDmPurchasePopup && (
        <Suspense fallback={null}>
          <DmPurchasePopup
            isOpen={showDmPurchasePopup}
            onClose={() => setShowDmPurchasePopup(false)}
          />
        </Suspense>
      )}

      {/* [S11]: DM tutorial spotlight anchor — guarantees a stable target for dm-tutorial steps. */}
      {/* [WHY]: Tutorial spotlight hides its panel when target is missing; the DM tutorial can */}
      {/*        run from contexts where party-tab nav buttons aren't in the DOM (mobile More */}
      {/*        menu, welcome screen, post-purchase). This 1×1 invisible anchor is always present. */}
      <div
        data-tutorial="dm-tutorial-anchor"
        aria-hidden="true"
        style={{
          position: 'fixed',
          top: -100,
          left: '50%',
          width: 1,
          height: 1,
          opacity: 0,
          pointerEvents: 'none',
        }}
      />

      {/* [REMOVED JAN 12, 2026]: Duplicate QuickActionsBar was here - consolidated to line 8281 */}
      {/* [WHY]: Two instances caused toggle setting to not work properly */}

      {/* [FEB 2026]: Contextual Tutorial System */}
      <TutorialSpotlight />
      <TutorialPrompt />
      {/* [GATE-CONSENT-WELCOME 2026-07-13]: consent is the higher-priority blocking
          gate (z-165, legally required) — don't mount the welcome popup underneath it.
          Once consent resolves (requiresConsent → false) the popup shows normally. */}
      <Suspense fallback={null}>
        {!requiresConsent && (
          <WelcomePopup onStartWizard={(guided) => {
            setGuidedCreation(guided);
            setShowCharacterWizard(true);
          }} />
        )}
      </Suspense>

      {/* [TUTORIAL-1]: Browse / Replay Tours modal — header "?" button or Settings entry */}
      <Suspense fallback={null}>
        <TutorialHelpMenu
          isOpen={showTutorialHelpMenu}
          onClose={() => setShowTutorialHelpMenu(false)}
        />
      </Suspense>

      {/* [v1.0]: Auth Modal — login/signup overlay */}
      <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />

      {/* [Sub-plan 4M — 2026-05-28]: Off-list scroll casting Arcana check modal */}
      {showOffListScrollModal && offListScrollPendingData && (
        <OffListScrollCastModal
          isOpen={showOffListScrollModal}
          spell={offListScrollPendingData.spell}
          arcanaMod={offListScrollPendingData.arcanaMod}
          onConfirm={handleOffListScrollResult}
          onCancel={() => handleOffListScrollResult(null)}
        />
      )}

      {/* [Sub-plan A1-cast-potions 2026-05-30]: DC save modal for cast-type potions.
          Only opens for Animal Friendship (WIS DC 13) and Detect Thoughts / Mind Reading (INT DC 13).
          costSpend=null — potion already consumed by drinkPotion before this modal opens.
          No rolls[] — these spells deal no damage; modal shows only the save prompt. */}
      {castPotionModalOpen && castPotionModalData && (
        <UsableAbilityModal
          isOpen={castPotionModalOpen}
          onClose={() => { setCastPotionModalOpen(false); setCastPotionModalData(null); }}
          title={`${castPotionModalData.spellName} (from ${castPotionModalData.itemName})`}
          rollSpec={{
            rolls: [],
            save: {
              ability: castPotionModalData.saveAbility,
              dc: castPotionModalData.dc,
              halfOnSave: false
            },
            targetMode: 'enemy'
          }}
          costSpend={null}
          rollProtocol="d20"
          maxPoolHp={null}
          onResolve={handleCastPotionResolve}
        />
      )}

      {/* [Sub-plan 1H-G-bw-S3a — 2026-06-03]: Reaction offer on-take-damage interrupt.
          When damage is taken, available reactions (e.g., Hellish Rebuke) are offered
          to the player. If multiple reactions available, show a picker. Then open
          UsableAbilityModal for the selected reaction. */}
      {pendingReactionOffer && pendingReactionOffer.reactions.length > 0 && (() => {
        const { reactions, selectedIndex, showInvocation } = pendingReactionOffer;
        const selectedReaction = reactions[selectedIndex];
        if (!selectedReaction) return null;

        // [Sub-plan 1H-G-bw-S3a-dc-extract]: Compute save DC if not already present
        // DC formula: 8 + ability modifier + proficiency bonus
        // This mirrors the ClassResourcesPanel calculation (see trace §C)
        const computeReactionDC = (reaction) => {
          if (!reaction.rollSpec) return null;
          const save = reaction.rollSpec.save;
          if (!save) return null;

          // If DC is already baked, use it
          if (save.dc != null) return save.dc;

          // Otherwise compute from dcAbility
          if (!save.dcAbility) return null;
          const abilityShort = save.dcAbility.substring(0, 3).toLowerCase(); // 'charisma' -> 'cha'
          const abilityModKey = `${abilityShort}Mod`; // e.g., 'chaMod'
          const effStats = getEffectiveStats();
          const abilityMod = effStats?.[abilityModKey] || 0;
          const prof = playerStats?.proficiencyBonus || 2;
          return 8 + abilityMod + prof;
        };

        const reactionRollSpec = selectedReaction.rollSpec ? {
          ...selectedReaction.rollSpec,
          save: selectedReaction.rollSpec.save ? {
            ...selectedReaction.rollSpec.save,
            dc: computeReactionDC(selectedReaction)
          } : null
        } : null;

        return (
          <>
            {/* Reaction selection modal (shown if >1 reaction available and not yet invoking) */}
            {reactions.length > 1 && !showInvocation && (
              <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ backgroundColor: '#1f2937', border: '2px solid #3b82f6', borderRadius: '8px', padding: '24px', maxWidth: '400px', maxHeight: '80vh', overflowY: 'auto' }}>
                  <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '16px', color: '#e5e7eb' }}>Available Reactions</h2>
                  <p style={{ color: '#d1d5db', marginBottom: '16px' }}>You took {pendingReactionOffer.damageAmount} damage. Choose a reaction:</p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
                    {reactions.map((reaction, idx) => (
                      <button
                        key={idx}
                        onClick={() => setPendingReactionOffer({ ...pendingReactionOffer, selectedIndex: idx })}
                        style={{
                          padding: '12px',
                          borderRadius: '4px',
                          border: selectedIndex === idx ? '2px solid #10b981' : '1px solid #4b5563',
                          backgroundColor: selectedIndex === idx ? 'rgba(16, 185, 129, 0.2)' : 'rgba(75, 85, 99, 0.3)',
                          color: '#e5e7eb',
                          cursor: 'pointer',
                          textAlign: 'left',
                          fontSize: '0.875rem',
                          transition: 'all 0.2s'
                        }}
                      >
                        <div style={{ fontWeight: 'bold' }}>{reaction.actionName}</div>
                        <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '4px' }}>{reaction.description || 'No description'}</div>
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() => setPendingReactionOffer(null)}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '4px',
                      border: '1px solid #6b7280',
                      backgroundColor: 'rgba(107, 114, 128, 0.2)',
                      color: '#e5e7eb',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      marginRight: '8px'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => setPendingReactionOffer({ ...pendingReactionOffer, showInvocation: true })}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '4px',
                      border: '1px solid #10b981',
                      backgroundColor: 'rgba(16, 185, 129, 0.2)',
                      color: '#e5e7eb',
                      cursor: 'pointer',
                      fontSize: '0.875rem'
                    }}
                  >
                    Use {selectedReaction.actionName}
                  </button>
                </div>
              </div>
            )}

            {/* UsableAbilityModal for the selected reaction */}
            {(showInvocation || reactions.length === 1) && selectedReaction && (
              <UsableAbilityModal
                isOpen={true}
                onClose={() => setPendingReactionOffer(null)}
                title={selectedReaction.actionName}
                rollSpec={reactionRollSpec}
                costSpend={{
                  type: 'raceResource',
                  poolId: selectedReaction.attachedToResource,
                  raceKey: selectedReaction.raceKey,
                  classKey: selectedReaction.classKey
                }}
                rollProtocol="d20"
                maxPoolHp={null}
                onResolve={handleReactionResolve}
              />
            )}
          </>
        );
      })()}

      {/* [S5]: Consent prompt — blocks app for existing users who haven't accepted ToS */}
      <ConsentPrompt />

      {/* [UI POLISH]: Status Message — color-coded by type with close button */}
      {statusMessage && (() => {
        const statusColors = {
          error: { bg: 'bg-red-800', border: 'border-red-700' },
          success: { bg: 'bg-green-800', border: 'border-green-700' },
          warning: { bg: 'bg-amber-800', border: 'border-amber-700' },
          info: { bg: 'bg-blue-800', border: 'border-blue-700' },
        };
        const colors = statusColors[statusType] || statusColors.info;
        return (
          <div
            className={`fixed bottom-4 left-1/2 transform -translate-x-1/2 ${colors.bg} text-white px-4 py-2 rounded-lg shadow-lg max-w-md text-center cursor-help`}
            style={{ zIndex: Z.STATUS_BAR }}
            role="status"
            aria-live="polite"
            onMouseEnter={() => {
              if (statusTooltip && !statusTooltip.expanded) {
                setStatusTooltip({ ...statusTooltip, expanded: true });
              }
            }}
            onMouseLeave={() => {
              if (statusTooltip && statusTooltip.expanded) {
                setStatusTooltip({ ...statusTooltip, expanded: false });
              }
            }}
            onClick={() => {
              if (statusTooltip) {
                setStatusTooltip({ ...statusTooltip, expanded: !statusTooltip.expanded });
              }
            }}
          >
            <div className="flex items-center gap-2">
              <span className="flex-1">{statusMessage}</span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setStatusMessage('');
                  setStatusTooltip(null);
                }}
                className="text-white/70 hover:text-white text-lg leading-none flex-shrink-0"
                aria-label="Dismiss"
              >
                &times;
              </button>
            </div>
            {statusTooltip && (
              <div className={`text-xs mt-1 text-gray-200 border-t ${colors.border} pt-1`}>
                {statusTooltip.expanded ? (
                  <div className="text-left mt-1 space-y-1">
                    {statusTooltip.breakdown && <div>{statusTooltip.breakdown}</div>}
                    {statusTooltip.dice && <div>Dice: {statusTooltip.dice}</div>}
                    {statusTooltip.rolls && <div>Rolls: {statusTooltip.rolls}</div>}
                    {statusTooltip.modifier !== undefined && statusTooltip.modifier !== 0 && <div>Modifier: +{statusTooltip.modifier}</div>}
                    {statusTooltip.bonuses && <div>Bonuses: {statusTooltip.bonuses}</div>}
                    {statusTooltip.total && <div className="font-bold">Total: {statusTooltip.total}</div>}

                    {/* Save roll buttons */}
                    <div className={`flex gap-2 mt-2 pt-2 border-t ${colors.border}`}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const rollText = `${statusMessage}\n${Object.entries(statusTooltip)
                            .filter(([key, value]) => key !== 'expanded' && value)
                            .map(([key, value]) => `${key}: ${value}`)
                            .join('\n')}`;
                          saveRollAsParchment(rollText, 'Roll Result');
                        }}
                        className="px-2 py-1 bg-amber-700 hover:bg-amber-600 rounded text-xs"
                      >
                        📜 Save to Inventory
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const rollText = `${statusMessage}\n${Object.entries(statusTooltip)
                            .filter(([key, value]) => key !== 'expanded' && value)
                            .map(([key, value]) => `${key}: ${value}`)
                            .join('\n')}`;
                          saveRollToJournal(rollText, 'Roll Result');
                        }}
                        className="px-2 py-1 bg-blue-700 hover:bg-blue-600 rounded text-xs"
                      >
                        📖 Save to Journal
                      </button>
                    </div>
                  </div>
                ) : (
                  <span className="italic">Hover or click for details</span>
                )}
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
};

// [PHASE 4B - OCT 24, 2025]: Export wrapper provides all React Contexts
// [WHY]: Wraps MainApp with 5 contexts + ErrorBoundary in correct dependency order
// [INTEGRATION]: Makes useCharacter, useInventory, useParty, useUI, useSettings available to all components
// [PROVIDER ORDER]: ErrorBoundary → Settings → Character → Inventory → Party → UI
export default function TabletopInventoryManager() {
  return (
    <AppProviders>
      <MainApp />
    </AppProviders>
  );
}
